import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  AiEditChatTurn,
  AiEditPlanSource,
  AiEditTarget,
  Api,
  BlogPost,
  BlogPostInput,
  EditorPage,
  ImportedCms,
  ImportedCmsEntry,
  MediaItem,
  MediaStorage,
  PageBlock,
  ThemeInput,
} from "../api";
import { previewUrlFor } from "../api";
import { PageVersionHistory } from "./PageVersionHistory";

interface SiteEditorProps {
  api: Api;
  hostname: string | null;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
}

interface EditorChatStorage {
  getItem: (key: string) => string | null;
  removeItem: (key: string) => void;
  setItem: (key: string, value: string) => void;
}

interface BlogPostEditorDraft {
  author: string;
  contentBody: string;
  excerpt: string;
  featuredImage: string;
  id: string | null;
  seoMetaDescription: string;
  seoMetaTitle: string;
  slug: string;
  status: BlogPost["status"];
  tagsText: string;
  title: string;
}

interface BlockFrame {
  id: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ElementFrame {
  blockId: string;
  key: string;
  label: string;
  sectionLabel: string;
  top: number;
  left: number;
  width: number;
  height: number;
  target: EditableElementTarget | null;
}

const PREVIEW_ELEMENT_SELECTOR =
  "h1,h2,h3,h4,h5,h6,p,a,img,button,input,textarea,select,label,summary,li,blockquote,form,.gb-card,[class*='__item']";
const RICH_TEXT_EDITABLE_SELECTOR = "h1,h2,h3,h4,h5,h6,p,li,blockquote,a,img";
const RICH_TEXT_PATH_RE = /^htmlNodes\.(\d+)\.(text|href|src|alt)$/;
const EDITABLE_TARGET_MIN_HEIGHT = 24;
const EDITABLE_TARGET_MIN_WIDTH = 32;
const SITE_NAV_LINK_LIMIT = 12;
const SITE_NAV_LINK_LABEL_MAX_LENGTH = 80;
const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;
const CONTACT_FORM_TEXT_DEFAULTS: Record<string, string> = {
  nameLabel: "Name",
  emailLabel: "Email",
  phoneLabel: "Phone",
  messageLabel: "Message",
  submitLabel: "Send message",
  successMessage: "Thanks — we'll be in touch shortly.",
};

type EditableFieldKind = "image" | "link" | "text" | "textarea";
type CanvasMode = "edit" | "browse";
type CanvasSize = "desktop" | "tablet" | "mobile";
type OverlayPanelId = "agent" | "blog" | "focus" | "media" | "theme";
type OverlayToolDockItemId = Exclude<OverlayPanelId, "blog" | "media"> | "hide";
type OverlayToolDockIcon = "agent" | "hide" | "inspector" | "theme";
type SectionTemplateId =
  | "content"
  | "cta"
  | "faq"
  | "gallery"
  | "hours"
  | "leadForm"
  | "news"
  | "pricing"
  | "services"
  | "stats"
  | "team"
  | "testimonials";

interface EditableElementTarget {
  blockId: string;
  path: string;
  label: string;
  kind: EditableFieldKind;
  hrefPath?: string;
  altPath?: string;
}

type PathSegment = number | string;

interface LinkSummary {
  blockId: string | null;
  href: string;
  hrefPath: string | null;
  internalSlug: string | null;
  key: string;
  label: string;
  labelPath: string | null;
  source: string;
  targetPage: EditorPage | null;
}

interface SiteNavLink {
  href: string;
  label: string;
}

type PageConnectivityStatus = "attention" | "healthy" | "single" | "warning";

interface PageConnectivity {
  incomingCount: number;
  navLinkedCount: number;
  otherPageCount: number;
  outgoingCount: number;
  status: PageConnectivityStatus;
  statusLabel: string;
}

interface CanvasSelectionFrame {
  editable: boolean;
  height: number;
  kind: "block" | "element";
  label: string;
  sectionLabel: string;
  left: number;
  target: EditableElementTarget | null;
  top: number;
  width: number;
}

interface SelectionLinkTarget {
  href: string;
  label: string;
  status: "empty" | "external" | "internal" | "missing";
  targetPageId?: string;
  targetPageSlug?: string;
}

interface LinkRelationship {
  actionLabel: string | null;
  canRepair: boolean;
  copy: string;
  label: string;
  status:
    | "connected"
    | "empty"
    | "external"
    | "missing"
    | "needs-return"
    | "self";
  targetPageId?: string;
  targetPageSlug?: string;
}

interface CanvasHudPosition {
  left: number;
  maxWidth: number;
  top: number;
}

interface FloatingPanelPosition {
  left: number;
  top: number;
  width: number;
}

type SelectionShortcutAction =
  | "ask-agent"
  | "close-element-editor"
  | "edit-selection"
  | "exit-clear-view"
  | "inspect-selection"
  | "next-element"
  | "previous-element"
  | "select-parent"
  | null;

interface SelectionShortcutState {
  hasDraft: boolean;
  hasOpenElementEditor: boolean;
  hasSelectedElement: boolean;
  hasSelection: boolean;
  isClearView: boolean;
  isEditMode: boolean;
}

interface AgentChatSelection {
  blockName: string;
  canPatchField: boolean;
  fieldLabel: string | null;
  fieldValue: string;
}

interface AgentPromptSuggestion {
  action?: "open_blog_cms" | "open_media_library" | "open_theme_panel";
  label: string;
  prompt: string;
  run: boolean;
}

interface AgentPromptSuggestionGroups {
  primary: AgentPromptSuggestion[];
  secondary: AgentPromptSuggestion[];
}

interface AgentChatPanelChromeState {
  mode: "conversation" | "starter";
  showChatLog: boolean;
  showContext: boolean;
  showNewChatAction: boolean;
  showSuggestions: boolean;
}

interface AgentChatSubmitState {
  ariaLabel: string;
  className: string;
  disabled: boolean;
  label: string;
  tone: "idle" | "ready";
}

export interface AgentDraftReviewSummary {
  focusBlockId: string | null;
  label: string;
}

export interface AgentDraftReviewState {
  hint: string;
  sourceLabel: string;
  stateLabel: string;
  title: string;
}

export interface EditorTopActionChrome {
  primaryActions: Array<"fullscreen" | "publishReview" | "publishSite">;
  secondaryActions: Array<
    | "history"
    | "viewLive"
    | "fullEditToggle"
    | "saveDraft"
    | "updatePreview"
    | "openPreview"
    | "publishPage"
  >;
  statusTone: "dirty" | "saved";
  summaryLabel: string;
}

export function editorTopActionChrome({
  isAdvancedMode,
  isDraftDirty,
  liveUrl,
}: {
  isAdvancedMode: boolean;
  isDraftDirty: boolean;
  liveUrl: string | null;
}): EditorTopActionChrome {
  const secondaryActions: EditorTopActionChrome["secondaryActions"] = [
    "history",
  ];
  if (liveUrl) secondaryActions.push("viewLive");
  secondaryActions.push(
    "fullEditToggle",
    "saveDraft",
    // preview is for everyone — guided-mode SMB users are exactly who "test
    // safely before publish" protects (advanced mode has inline buttons).
    "updatePreview",
    "openPreview",
  );
  if (isAdvancedMode) secondaryActions.push("publishPage");

  return {
    primaryActions: [
      "fullscreen",
      isAdvancedMode ? "publishSite" : "publishReview",
    ],
    secondaryActions,
    statusTone: isDraftDirty ? "dirty" : "saved",
    summaryLabel: isAdvancedMode ? "Page controls" : "More actions",
  };
}

export interface PublishReadinessItem {
  detail: string;
  id: "content" | "forms" | "navigation" | "seo";
  label: string;
  status: "attention" | "ready";
}

export function publishReadinessChecklist({
  blockCount,
  hasLeadCapture,
  navigationStatus,
  pageCount,
  pagesMissingSeo,
  warningCount,
}: {
  blockCount: number;
  hasLeadCapture: boolean;
  navigationStatus: PageConnectivityStatus | "healthy";
  pageCount: number;
  pagesMissingSeo: number;
  warningCount: number;
}): {
  allReady: boolean;
  items: PublishReadinessItem[];
  readyCount: number;
  summaryLabel: string;
} {
  const contentReady = blockCount > 0 && warningCount === 0;
  const navigationReady =
    navigationStatus === "healthy" || navigationStatus === "single";
  const seoReady = pageCount > 0 && pagesMissingSeo === 0;
  const items: PublishReadinessItem[] = [
    {
      detail: contentReady
        ? `${blockCount} section${blockCount === 1 ? "" : "s"} ready to review.`
        : warningCount > 0
          ? `${warningCount} import issue${warningCount === 1 ? "" : "s"} need a look.`
          : "Add at least one section before publishing.",
      id: "content",
      label: "Content reviewed",
      status: contentReady ? "ready" : "attention",
    },
    {
      detail: navigationReady
        ? "Navigation is connected for this site."
        : "Open Tools to repair or review page links.",
      id: "navigation",
      label: "Navigation checked",
      status: navigationReady ? "ready" : "attention",
    },
    {
      detail: hasLeadCapture
        ? "Lead capture form is present."
        : "Add a lead capture form before relying on inbound leads.",
      id: "forms",
      label: "Forms ready",
      status: hasLeadCapture ? "ready" : "attention",
    },
    {
      detail: seoReady
        ? "SEO title and description are present."
        : pagesMissingSeo === 1
          ? "1 page is missing SEO basics."
          : `${pagesMissingSeo || pageCount} pages are missing SEO basics.`,
      id: "seo",
      label: "SEO basics present",
      status: seoReady ? "ready" : "attention",
    },
  ];
  const readyCount = items.filter((item) => item.status === "ready").length;
  return {
    allReady: readyCount === items.length,
    items,
    readyCount,
    summaryLabel:
      readyCount === items.length
        ? "Ready to publish"
        : `${readyCount}/${items.length} checks ready`,
  };
}

export function agentDraftSourceLabel(source: AiEditPlanSource | null): string {
  if (source === "model") return "AI model draft";
  if (source === "fallback") return "Built-in draft preview";
  if (source === "local") return "Built-in draft preview";
  return "AI draft preview";
}

export function agentDraftReviewState({
  isDraftDirty,
  reviewLabel,
  source,
}: {
  isDraftDirty: boolean;
  reviewLabel: string | null;
  source: AiEditPlanSource | null;
}): AgentDraftReviewState {
  return {
    hint: isDraftDirty
      ? "Review the change on the page. Save it before publishing."
      : "Saved as a draft. Publish when it is ready.",
    sourceLabel: agentDraftSourceLabel(source),
    stateLabel: isDraftDirty ? "Preview only" : "Saved draft",
    title: reviewLabel ?? "Draft preview",
  };
}

function blockReviewKey(block: PageBlock): string {
  return JSON.stringify({
    blockType: block.blockType,
    data: block.data,
    variant: block.variant,
  });
}

export function agentDraftReviewSummary({
  after,
  before,
}: {
  after: PageBlock[];
  before: PageBlock[];
}): AgentDraftReviewSummary {
  const beforeById = new Map(before.map((block) => [block.id, block]));
  const addedBlock = after.find((block) => !beforeById.has(block.id));
  if (addedBlock) {
    return {
      focusBlockId: addedBlock.id,
      label: `Added ${blockLabel(addedBlock.blockType)}`,
    };
  }

  const changedBlock = after.find((block) => {
    const previous = beforeById.get(block.id);
    return previous
      ? blockReviewKey(previous) !== blockReviewKey(block)
      : false;
  });
  if (changedBlock) {
    return {
      focusBlockId: changedBlock.id,
      label: `Updated ${blockLabel(changedBlock.blockType)}`,
    };
  }

  return {
    focusBlockId: null,
    label: "Draft preview",
  };
}

interface OverlayToolDockItem {
  active: boolean;
  disabled: boolean;
  icon: OverlayToolDockIcon;
  id: OverlayToolDockItemId;
  label: string;
}

const EDITOR_CHAT_STORAGE_PREFIX = "groundbeam:site-editor:chat:v1";
export const AGENT_CHAT_INITIAL_PROMPT = "";
const MAX_PERSISTED_CHAT_MESSAGES = 80;
const MAX_PERSISTED_CHAT_MESSAGE_CHARS = 2000;

export function aiEditChatHistory(messages: ChatMessage[]): AiEditChatTurn[] {
  return messages
    .map((message) => ({
      content: message.text.trim().replace(/\s+/g, " ").slice(0, 1000),
      role: message.role,
    }))
    .filter((message) => message.content.length > 0)
    .slice(-12);
}

export function editorChatStorageKey({
  pageId,
  siteId,
}: {
  pageId: string | null | undefined;
  siteId: string | null | undefined;
}): string | null {
  if (!siteId || !pageId) return null;
  return `${EDITOR_CHAT_STORAGE_PREFIX}:${encodeURIComponent(
    siteId,
  )}:${encodeURIComponent(pageId)}`;
}

export function persistedChatMessages(
  value: unknown,
  limit = MAX_PERSISTED_CHAT_MESSAGES,
): ChatMessage[] {
  if (!Array.isArray(value)) return [];
  return value
    .flatMap((item, index): ChatMessage[] => {
      if (!item || typeof item !== "object") return [];
      const candidate = item as Partial<ChatMessage>;
      const role = candidate.role;
      if (role !== "assistant" && role !== "user") return [];
      const text =
        typeof candidate.text === "string"
          ? candidate.text.trim().slice(0, MAX_PERSISTED_CHAT_MESSAGE_CHARS)
          : "";
      if (!text) return [];
      return [
        {
          id:
            typeof candidate.id === "string" && candidate.id.trim()
              ? candidate.id
              : `persisted_${index}`,
          role,
          text,
        },
      ];
    })
    .slice(-limit);
}

export function readEditorChatHistory(
  storage: Pick<EditorChatStorage, "getItem"> | null,
  key: string | null,
): ChatMessage[] {
  if (!storage || !key) return [];
  try {
    return persistedChatMessages(JSON.parse(storage.getItem(key) ?? "[]"));
  } catch {
    return [];
  }
}

export function writeEditorChatHistory(
  storage: EditorChatStorage | null,
  key: string | null,
  messages: ChatMessage[],
): void {
  if (!storage || !key) return;
  try {
    const persisted = persistedChatMessages(messages);
    if (persisted.length === 0) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(persisted));
  } catch {
    // Private mode and storage quotas should not block editing.
  }
}

function editorChatStorage(): EditorChatStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

interface GuidedEditorState {
  arePanelsHidden: boolean;
  isElementNavigatorCollapsed: boolean;
  isOutlineVisible: boolean;
  isPageRailCollapsed: boolean;
  isStructurePanelOpen: boolean;
  isTargetMapVisible: boolean;
}

interface PreviewViewport {
  height: number;
  width: number;
}

interface SectionTemplate {
  blockType: string;
  createData: (blockId: string, pageTitle: string) => Record<string, unknown>;
  description: string;
  id: SectionTemplateId;
  label: string;
  variant: string;
}

export function scopeElementFramesForCanvas<T extends { blockId: string }>(
  frames: T[],
  selectedBlockId: string | null,
  targetMapVisible: boolean,
): T[] {
  if (targetMapVisible || !selectedBlockId) return frames;
  return frames.filter((frame) => frame.blockId === selectedBlockId);
}

export function visibleElementFramesForCanvas<
  T extends { key: string; target: unknown | null },
>(
  frames: T[],
  selectedElementKey: string | null,
  hoveredElementKey: string | null,
  targetMapVisible: boolean,
  showIdleEditableTargets = true,
): T[] {
  if (targetMapVisible) return frames;
  return frames.filter(
    (frame) =>
      (showIdleEditableTargets && Boolean(frame.target)) ||
      frame.key === selectedElementKey ||
      frame.key === hoveredElementKey,
  );
}

export function elementNavigatorFrames<
  T extends {
    key: string;
    label: string;
    sectionLabel: string;
    target: unknown | null;
    valuePreview?: string | null;
  },
>({
  filter,
  frames,
  hoveredElementKey,
  query,
  selectedElementKey,
}: {
  filter: "all" | "editable";
  frames: T[];
  hoveredElementKey: string | null;
  query: string;
  selectedElementKey: string | null;
}): T[] {
  const visibleFrames =
    filter === "all"
      ? frames
      : frames.filter(
          (frame) =>
            Boolean(frame.target) ||
            frame.key === selectedElementKey ||
            frame.key === hoveredElementKey,
        );
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return visibleFrames;
  return visibleFrames.filter(
    (frame) =>
      `${frame.label} ${frame.sectionLabel} ${frame.key} ${
        frame.target ? "field editable" : "inspect"
      } ${frame.valuePreview ?? ""}`
        .toLowerCase()
        .includes(normalizedQuery) ||
      frame.key === selectedElementKey ||
      frame.key === hoveredElementKey,
  );
}

export function canvasOutlineSections<
  T extends { blockType: string; id: string; variant: string },
>({
  blocks,
  query,
  selectedBlockId,
}: {
  blocks: T[];
  query: string;
  selectedBlockId: string | null;
}): { block: T; index: number }[] {
  const normalizedQuery = query.trim().toLowerCase();
  const rows = blocks.map((block, index) => ({ block, index }));
  if (!normalizedQuery) return rows;
  return rows.filter(({ block, index }) => {
    const searchText = [
      blockLabel(block.blockType),
      block.blockType,
      block.variant,
      block.id,
      String(index + 1),
    ]
      .join(" ")
      .toLowerCase();
    return searchText.includes(normalizedQuery) || block.id === selectedBlockId;
  });
}

export function canvasOutlineElementFrames<
  T extends {
    key: string;
    label: string;
    sectionLabel: string;
    target: unknown | null;
    valuePreview?: string | null;
  },
>({
  frames,
  query,
  selectedElementKey,
}: {
  frames: T[];
  query: string;
  selectedElementKey: string | null;
}): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return frames;
  return frames.filter((frame) => {
    const searchText = [
      frame.label,
      frame.sectionLabel,
      frame.key,
      frame.target ? "field editable" : "inspect",
      frame.valuePreview ?? "",
    ]
      .join(" ")
      .toLowerCase();
    return (
      searchText.includes(normalizedQuery) || frame.key === selectedElementKey
    );
  });
}

export function canvasOutlineSectionElementSummary<
  T extends { blockId: string; target: unknown | null },
>(blockId: string, frames: T[]): string {
  const sectionFrames = frames.filter((frame) => frame.blockId === blockId);
  const editableCount = sectionFrames.filter((frame) => frame.target).length;
  const totalCount = sectionFrames.length;
  if (totalCount === 0) return "No targets";
  if (editableCount === 0) {
    return `${totalCount} inspect${totalCount === 1 ? "" : "s"}`;
  }
  if (editableCount === totalCount) {
    return `${editableCount} field${editableCount === 1 ? "" : "s"}`;
  }
  return `${editableCount}/${totalCount} fields`;
}

export function canvasElementActionBarState({
  arePanelsHidden,
  canvasMode,
  hasOverlayPanelOpen = false,
  hoveredElementHasTarget,
  hoveredElementKey,
  inlineEditingKey,
  quickInspectorTargetKey,
  selectedElementHasTarget,
  selectedElementKey,
}: {
  arePanelsHidden: boolean;
  canvasMode: CanvasMode;
  hasOverlayPanelOpen?: boolean;
  hoveredElementHasTarget: boolean;
  hoveredElementKey: string | null;
  inlineEditingKey: string | null;
  quickInspectorTargetKey: string | null;
  selectedElementHasTarget: boolean;
  selectedElementKey: string | null;
}): "hidden" | "hovered" | "selected" {
  if (
    canvasMode !== "edit" ||
    hasOverlayPanelOpen ||
    inlineEditingKey ||
    quickInspectorTargetKey
  ) {
    return "hidden";
  }
  if (arePanelsHidden && hoveredElementKey && hoveredElementHasTarget) {
    return hoveredElementKey === selectedElementKey ? "selected" : "hovered";
  }
  if (selectedElementKey && selectedElementHasTarget) return "selected";
  return "hidden";
}

export function canvasSelectionPersistentChromeVisible({
  hasFocusedElementActions,
  selectionKind,
}: {
  hasFocusedElementActions: boolean;
  selectionKind: "block" | "element" | null;
}): boolean {
  return Boolean(
    selectionKind && (selectionKind !== "element" || !hasFocusedElementActions),
  );
}

export function canvasSelectionChromeSurface({
  arePanelsHidden,
  hasFocusedElementActions,
  hasFocusedElementEditor,
  hasOverlayPanelOpen = false,
  isFullscreen,
  isFullscreenChromeHidden,
  selectionKind,
}: {
  arePanelsHidden: boolean;
  hasFocusedElementActions: boolean;
  hasFocusedElementEditor: boolean;
  hasOverlayPanelOpen?: boolean;
  isFullscreen: boolean;
  isFullscreenChromeHidden: boolean;
  selectionKind: "block" | "element" | null;
}): "dock" | "hidden" | "status" | "toolbar" {
  if (hasOverlayPanelOpen) return "hidden";
  if (hasFocusedElementEditor) return "hidden";
  if (
    !canvasSelectionPersistentChromeVisible({
      hasFocusedElementActions,
      selectionKind,
    })
  ) {
    return "hidden";
  }
  if (isFullscreen && (isFullscreenChromeHidden || !arePanelsHidden)) {
    return "dock";
  }
  return arePanelsHidden ? "status" : "toolbar";
}

export function canvasSelectionSuppressed(
  suppressedUntilMs: number,
  nowMs: number,
): boolean {
  return nowMs < suppressedUntilMs;
}

export function canvasSelectionDockActionGroups({
  isAdvancedMode,
}: {
  isAdvancedMode: boolean;
}): {
  showAdvancedActions: boolean;
} {
  return {
    showAdvancedActions: isAdvancedMode,
  };
}

export function canvasSiteFlowChromeMode({
  isAdvancedMode = true,
  isCompact,
  status,
}: {
  isAdvancedMode?: boolean;
  isCompact: boolean;
  status: "attention" | "healthy" | "warning";
}): "compact" | "open" | "quiet" {
  if (!isAdvancedMode) return status === "healthy" ? "quiet" : "compact";
  if (isCompact) return "compact";
  return status === "healthy" ? "quiet" : "open";
}

export function canvasSiteFlowStripVisible({
  arePanelsHidden,
  canvasMode,
  connectivityStatus,
  focusedElementEditorMode,
  hasOverlayPanelOpen,
  isAdvancedMode,
  isFullscreenChromeHidden,
  outlineVisible,
  targetMapVisible,
}: {
  arePanelsHidden: boolean;
  canvasMode: CanvasMode;
  connectivityStatus: "attention" | "healthy" | "single" | "warning";
  focusedElementEditorMode: CanvasFocusedElementEditorMode;
  hasOverlayPanelOpen: boolean;
  isAdvancedMode: boolean;
  isFullscreenChromeHidden: boolean;
  outlineVisible: boolean;
  targetMapVisible: boolean;
}): boolean {
  if (hasOverlayPanelOpen) return false;
  if (canvasMode !== "edit" || focusedElementEditorMode) return false;
  if (!isAdvancedMode) return false;
  const hasNavigationIssue =
    connectivityStatus !== "healthy" && connectivityStatus !== "single";
  if (!hasNavigationIssue && !outlineVisible && !targetMapVisible) return false;
  if (
    isFullscreenChromeHidden &&
    arePanelsHidden &&
    !outlineVisible &&
    !targetMapVisible
  ) {
    return false;
  }
  return true;
}

export function canvasActionRailVisible({
  arePanelsHidden,
  canvasMode,
  focusedElementEditorMode,
  hasSelection,
  isAdvancedMode,
  outlineVisible,
  targetMapVisible,
}: {
  arePanelsHidden: boolean;
  canvasMode: CanvasMode;
  focusedElementEditorMode: CanvasFocusedElementEditorMode;
  hasSelection: boolean;
  isAdvancedMode: boolean;
  outlineVisible: boolean;
  targetMapVisible: boolean;
}): boolean {
  return Boolean(
    canvasMode === "edit" &&
      arePanelsHidden &&
      isAdvancedMode &&
      !focusedElementEditorMode &&
      (hasSelection || outlineVisible || targetMapVisible),
  );
}

export function navigationIssueSummary({
  disconnectedPageCount,
  missingLinkCount,
  needsReturnLinkCount,
  pageCount,
}: {
  disconnectedPageCount: number;
  missingLinkCount: number;
  needsReturnLinkCount: number;
  pageCount: number;
}): {
  canAutoRepair: boolean;
  primaryActionLabel: string | null;
  status: "attention" | "healthy" | "warning";
  summary: string;
} {
  if (pageCount < 2) {
    return {
      canAutoRepair: false,
      primaryActionLabel: null,
      status: "healthy",
      summary: "Single page draft",
    };
  }
  if (missingLinkCount > 0) {
    return {
      canAutoRepair: false,
      primaryActionLabel: "Review links",
      status: "warning",
      summary: `${missingLinkCount} page link${
        missingLinkCount === 1 ? "" : "s"
      } need review`,
    };
  }
  if (disconnectedPageCount > 0) {
    return {
      canAutoRepair: true,
      primaryActionLabel: "Repair navigation",
      status: "warning",
      summary: `${disconnectedPageCount} page${
        disconnectedPageCount === 1 ? "" : "s"
      } not in navigation`,
    };
  }
  if (needsReturnLinkCount > 0) {
    return {
      canAutoRepair: true,
      primaryActionLabel: "Repair navigation",
      status: "attention",
      summary:
        needsReturnLinkCount === 1
          ? "1 page needs a return link"
          : `${needsReturnLinkCount} pages need return links`,
    };
  }
  return {
    canAutoRepair: false,
    primaryActionLabel: null,
    status: "healthy",
    summary: "Pages connected",
  };
}

export function guidedNavigationPrimaryAction({
  canAutoRepair,
  disconnectedPageCount,
  needsReturnLinkCount,
  pageCount,
  primaryActionLabel,
}: {
  canAutoRepair: boolean;
  disconnectedPageCount: number;
  needsReturnLinkCount: number;
  pageCount: number;
  primaryActionLabel: string | null;
}): { kind: "repair" | "review"; label: string } | null {
  if (!primaryActionLabel) return null;
  const hasOverflowReturnLinkRemainder =
    pageCount > SITE_NAV_LINK_LIMIT + 1 &&
    disconnectedPageCount === 0 &&
    needsReturnLinkCount > 0;
  if (hasOverflowReturnLinkRemainder) {
    return { kind: "review", label: "Review links" };
  }
  return {
    kind: canAutoRepair ? "repair" : "review",
    label: primaryActionLabel,
  };
}

export function canvasElementActionChrome({
  canInlineEdit,
  isAdvancedMode = true,
  isSelected,
  linkActionLabel,
  targetActionLabel,
}: {
  canInlineEdit: boolean;
  isAdvancedMode?: boolean;
  isSelected: boolean;
  linkActionLabel: string | null;
  targetActionLabel: string;
}): {
  primaryAction: "inspect" | "select" | "inline-edit";
  primaryLabel: string;
  showContextDetails: boolean;
  showDetailsAction: boolean;
  showFocusNavigation: boolean;
  showLinkAction: boolean;
  showSecondaryActions: boolean;
} {
  if (!isSelected) {
    return {
      primaryAction: "select",
      primaryLabel: "Select",
      showContextDetails: isAdvancedMode,
      showDetailsAction: false,
      showFocusNavigation: false,
      showLinkAction: false,
      showSecondaryActions: false,
    };
  }
  return {
    primaryAction: canInlineEdit ? "inline-edit" : "inspect",
    primaryLabel: targetActionLabel,
    showContextDetails: isAdvancedMode,
    showDetailsAction: isAdvancedMode && canInlineEdit,
    showFocusNavigation: !isAdvancedMode,
    showLinkAction: isAdvancedMode && Boolean(linkActionLabel),
    showSecondaryActions: isAdvancedMode,
  };
}

export function previewElementClickShouldOpenEditor({
  hasTarget,
  isAdvancedMode,
  isAlreadySelected,
}: {
  hasTarget: boolean;
  isAdvancedMode: boolean;
  isAlreadySelected: boolean;
}): boolean {
  if (!hasTarget) return false;
  if (!isAdvancedMode) return true;
  return isAlreadySelected;
}

type CanvasFocusedElementEditorMode = "inline" | "inspector" | null;

export function canvasFocusedElementEditorMode({
  inlineEditingKey,
  quickInspectorTargetKey,
  selectedElementKey,
}: {
  inlineEditingKey: string | null;
  quickInspectorTargetKey: string | null;
  selectedElementKey: string | null;
}): CanvasFocusedElementEditorMode {
  if (!selectedElementKey) return null;
  if (inlineEditingKey === selectedElementKey) return "inline";
  if (quickInspectorTargetKey === selectedElementKey) return "inspector";
  return null;
}

export function canvasSectionHitTargetsEnabled({
  canvasMode,
  focusedElementEditorMode,
}: {
  canvasMode: CanvasMode;
  focusedElementEditorMode: CanvasFocusedElementEditorMode;
}): boolean {
  return canvasMode === "edit" && !focusedElementEditorMode;
}

export function canvasFieldTargetLabelMode({
  arePanelsHidden,
  canvasMode,
  focusedElementEditorMode = null,
  hasFocusedFieldSurface = false,
  isAdvancedMode,
  targetMapVisible,
}: {
  arePanelsHidden: boolean;
  canvasMode: CanvasMode;
  focusedElementEditorMode?: CanvasFocusedElementEditorMode;
  hasFocusedFieldSurface?: boolean;
  isAdvancedMode: boolean;
  targetMapVisible: boolean;
}): "minimal" | "shown" | "tucked" {
  if (
    canvasMode === "edit" &&
    (focusedElementEditorMode || hasFocusedFieldSurface) &&
    !targetMapVisible
  ) {
    return "minimal";
  }
  return canvasMode === "edit" &&
    arePanelsHidden &&
    !isAdvancedMode &&
    !targetMapVisible
    ? "tucked"
    : "shown";
}

export function canvasOutlineSectionRowTone({
  blockId,
  hoveredBlockId,
  selectedBlockId,
}: {
  blockId: string;
  hoveredBlockId: string | null;
  selectedBlockId: string | null;
}): "active" | "hovered" | "idle" {
  if (blockId === selectedBlockId) return "active";
  if (blockId === hoveredBlockId) return "hovered";
  return "idle";
}

export function previewElementActionBadgeLabel({
  baseLabel,
  editorMode,
  frameKey,
  selectedElementKey,
}: {
  baseLabel: string;
  editorMode: CanvasFocusedElementEditorMode;
  frameKey: string;
  selectedElementKey: string | null;
}): string {
  if (!editorMode || frameKey !== selectedElementKey) return baseLabel;
  return editorMode === "inline" ? "Editing" : "Inspecting";
}

export function inlineEditorHeaderSummary({
  sectionLabel,
  targetLabel,
}: {
  sectionLabel: string;
  targetLabel: string;
}): { context: string; title: string } {
  const title = targetLabel.trim() || "Selected field";
  const normalizedSection = sectionLabel.trim() || "Selected section";
  const context = /section$/i.test(normalizedSection)
    ? normalizedSection
    : `${normalizedSection} section`;
  return { context, title };
}

export function canvasElementItemActionsVisible({
  actionFrameKey,
  hasItemActions,
  selectedElementKey,
}: {
  actionFrameKey: string | null;
  hasItemActions: boolean;
  selectedElementKey: string | null;
}): boolean {
  return Boolean(
    hasItemActions && actionFrameKey && actionFrameKey === selectedElementKey,
  );
}

export function agentChatSelectionSummary({
  isWorking,
  messageCount,
  selection,
}: {
  isWorking: boolean;
  messageCount: number;
  selection: AgentChatSelection | null;
}): {
  emptyState: string;
  placeholder: string;
  scopeKind: string;
  scopeLabel: string;
  statusLabel: string;
  valuePreview: string | null;
} {
  const normalizedValue = selection?.fieldValue.trim().replace(/\s+/g, " ");
  const valuePreview = normalizedValue
    ? normalizedValue.length > 180
      ? `${normalizedValue.slice(0, 177)}...`
      : normalizedValue
    : null;
  const scopeKind = selection?.fieldLabel
    ? "Selected field"
    : selection
      ? "Selected area"
      : "Current page";
  const scopeLabel = selection?.fieldLabel
    ? `${selection.blockName} / ${selection.fieldLabel}`
    : (selection?.blockName ?? "Current page");
  const statusLabel = isWorking
    ? "Applying change"
    : messageCount === 0
      ? selection
        ? "Ready for this selection"
        : "Ready to build"
      : `${messageCount} message${messageCount === 1 ? "" : "s"}`;
  const placeholder = selection?.fieldLabel
    ? `Rewrite ${selection.fieldLabel.toLowerCase()}`
    : selection
      ? `Improve ${selection.blockName.toLowerCase()}`
      : "Tell me what to change on this page";
  const emptyState = selection?.fieldLabel
    ? "Ask for a focused rewrite of the selected field."
    : selection
      ? "Ask for an edit to this area or add a related area."
      : "Ask the builder agent to improve this page or add a section.";

  return {
    emptyState,
    placeholder,
    scopeKind,
    scopeLabel,
    statusLabel,
    valuePreview,
  };
}

export function agentChatSubmitState({
  prompt,
  working,
}: {
  prompt: string;
  working: string | null;
}): AgentChatSubmitState {
  const hasPrompt = prompt.trim().length > 0;
  const isSending = working === "chat";
  const tone = hasPrompt ? "ready" : "idle";

  return {
    ariaLabel: hasPrompt
      ? "Send builder agent request"
      : "Type a request before sending",
    className: `agent-send-button ${tone}`,
    disabled: working !== null || !hasPrompt,
    label: isSending ? "Sending..." : "Send",
    tone,
  };
}

export function agentChatPromptSuggestions(
  selection: AgentChatSelection | null,
): AgentPromptSuggestion[] {
  if (selection?.canPatchField) {
    const fieldName = selection.fieldLabel?.toLowerCase() ?? "field";
    return [
      {
        label: "Rewrite...",
        prompt: `Rewrite selected ${fieldName} to `,
        run: false,
      },
      {
        label: "Shorter",
        prompt: "Make selected text shorter",
        run: true,
      },
      {
        label: "More local",
        prompt: "Make selected text more specific to the local service area",
        run: true,
      },
      {
        label: "More proof",
        prompt: "Add credibility and proof to the selected text",
        run: true,
      },
    ];
  }

  if (selection) {
    const sectionName = selection.blockName.toLowerCase();
    return [
      {
        label: "Rewrite...",
        prompt: `Rewrite selected ${sectionName} heading to `,
        run: false,
      },
      {
        label: "Shorter",
        prompt: "Make this area shorter",
        run: true,
      },
      {
        label: "More local",
        prompt: "Make this area more specific to the local service area",
        run: true,
      },
      {
        label: "More proof",
        prompt: "Add credibility and proof to this area",
        run: true,
      },
      ...pageLevelAgentPromptSuggestions(),
    ];
  }

  return pageLevelAgentPromptSuggestions();
}

export function agentPromptSuggestionGroups(
  suggestions: AgentPromptSuggestion[],
  primaryLimit = 4,
): AgentPromptSuggestionGroups {
  return {
    primary: suggestions.slice(0, primaryLimit),
    secondary: suggestions.slice(primaryLimit),
  };
}

export function agentChatPanelChromeState({
  hasPendingAgentChange,
  hasSelection,
  isWorking,
  messageCount,
}: {
  hasPendingAgentChange: boolean;
  hasSelection: boolean;
  isWorking: boolean;
  messageCount: number;
}): AgentChatPanelChromeState {
  const hasConversation = messageCount > 0;
  return {
    mode:
      hasConversation || hasPendingAgentChange || isWorking
        ? "conversation"
        : "starter",
    showChatLog: hasConversation,
    showContext: hasSelection || hasConversation,
    showNewChatAction: hasConversation,
    showSuggestions: !isWorking && !hasPendingAgentChange && messageCount === 0,
  };
}

export function themeColorInputValue(value: string): string {
  const trimmed = value.trim();
  return HEX_COLOR_RE.test(trimmed) ? trimmed.toLowerCase() : "#000000";
}

export function themeColorFieldClassName(label: string): string {
  const tone = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
  return ["color-field", tone ? `tone-${tone}` : ""].filter(Boolean).join(" ");
}

function pageLevelAgentPromptSuggestions(): AgentPromptSuggestion[] {
  return [
    {
      label: "Lead form",
      prompt: "Add a lead capture form",
      run: true,
    },
    {
      label: "Services",
      prompt: "Add services",
      run: true,
    },
    {
      label: "Reviews",
      prompt: "Add testimonials",
      run: true,
    },
    {
      label: "Stats",
      prompt: "Add stats",
      run: true,
    },
    {
      label: "Pricing",
      prompt: "Add pricing packages",
      run: true,
    },
    {
      label: "Team",
      prompt: "Add team section",
      run: true,
    },
    {
      label: "Hours",
      prompt: "Add opening hours",
      run: true,
    },
    {
      label: "News",
      action: "open_blog_cms",
      prompt: "Add a blog or news update",
      run: false,
    },
    {
      label: "Images",
      action: "open_media_library",
      prompt: "Open the image library",
      run: false,
    },
    {
      label: "Style",
      action: "open_theme_panel",
      prompt: "Open style settings",
      run: false,
    },
    {
      label: "Gallery",
      prompt: "Add a photo gallery",
      run: true,
    },
    {
      label: "FAQ",
      prompt: "Add FAQs",
      run: true,
    },
  ];
}

export function fullscreenCanvasEntryState(): {
  chromeHidden: boolean;
  outlineVisible: boolean;
  panelsHidden: boolean;
  targetMapVisible: boolean;
} {
  return {
    chromeHidden: true,
    outlineVisible: false,
    panelsHidden: true,
    targetMapVisible: false,
  };
}

export function guidedEditorEntryState(): GuidedEditorState {
  return {
    arePanelsHidden: true,
    isElementNavigatorCollapsed: true,
    isOutlineVisible: false,
    isPageRailCollapsed: true,
    isStructurePanelOpen: false,
    isTargetMapVisible: false,
  };
}

export function fullEditorEntryState(): GuidedEditorState {
  return {
    arePanelsHidden: true,
    isElementNavigatorCollapsed: true,
    isOutlineVisible: false,
    isPageRailCollapsed: false,
    isStructurePanelOpen: false,
    isTargetMapVisible: false,
  };
}

export function editorOverlayStackState({
  activePanel,
  panelsHidden,
}: {
  activePanel: OverlayPanelId | null;
  panelsHidden: boolean;
}): "dock-only" | "hidden" | "panel-open" {
  if (panelsHidden) return "hidden";
  return activePanel ? "panel-open" : "dock-only";
}

export function visualOverlayStackClassName({
  activePanel,
  overlayState,
}: {
  activePanel: OverlayPanelId | null;
  overlayState: "dock-only" | "hidden" | "panel-open";
}): string {
  return [
    "visual-overlay-stack",
    overlayState,
    activePanel ? `panel-${activePanel}` : "panel-none",
  ].join(" ");
}

export function overlayPanelScrollOptions({
  isFullscreen,
  overlayState,
}: {
  isFullscreen: boolean;
  overlayState: "dock-only" | "hidden" | "panel-open";
}): ScrollIntoViewOptions | null {
  if (isFullscreen || overlayState === "hidden") return null;
  return { block: "nearest", inline: "nearest" };
}

export function overlayToolDockItems({
  activePanel,
  canOpenAgent,
  canOpenFocus,
}: {
  activePanel: OverlayPanelId | null;
  canOpenAgent: boolean;
  canOpenFocus: boolean;
}): OverlayToolDockItem[] {
  const items: OverlayToolDockItem[] = [
    {
      active: activePanel === "focus",
      disabled: !canOpenFocus,
      icon: "inspector",
      id: "focus",
      label: "Open inspector panel",
    },
    {
      active: activePanel === "agent",
      disabled: !canOpenAgent,
      icon: "agent",
      id: "agent",
      label: "Ask builder agent",
    },
    {
      active: activePanel === "theme",
      disabled: false,
      icon: "theme",
      id: "theme",
      label: "Open theme panel",
    },
    {
      active: false,
      disabled: false,
      icon: "hide",
      id: "hide",
      label: "Hide editor panels",
    },
  ];
  return items.filter((item) => item.active || !item.disabled);
}

export function canvasToolbarLayoutMode({
  isFullscreen,
}: {
  isClearView: boolean;
  isFullscreen: boolean;
  panelsHidden: boolean;
}): "clean-fullscreen" | "standard" {
  return isFullscreen ? "clean-fullscreen" : "standard";
}

export function canvasClearViewToolbarSections({
  toolbarLayoutMode,
}: {
  toolbarLayoutMode: "clean-fullscreen" | "standard";
}): {
  showPrimarySizeControl: boolean;
  showSecondarySizeControl: boolean;
} {
  const isCleanFullscreen = toolbarLayoutMode === "clean-fullscreen";
  return {
    showPrimarySizeControl: !isCleanFullscreen,
    showSecondarySizeControl: isCleanFullscreen,
  };
}

export function canvasNavigationWarningPlacement({
  hasLinkIssues,
  isAdvancedMode,
  toolbarLayoutMode,
}: {
  hasLinkIssues: boolean;
  isAdvancedMode: boolean;
  toolbarLayoutMode: "clean-fullscreen" | "standard";
}): "hidden" | "primary" | "secondary" {
  if (isAdvancedMode || !hasLinkIssues) return "hidden";
  const placementByMode: Record<"clean-fullscreen" | "standard", "secondary"> =
    {
      "clean-fullscreen": "secondary",
      standard: "secondary",
    };
  return placementByMode[toolbarLayoutMode];
}

export function canvasToolbarSecondaryVisible({
  hasSecondaryNavigationWarning,
  isAdvancedMode,
  toolbarLayoutMode,
}: {
  hasSecondaryNavigationWarning: boolean;
  isAdvancedMode: boolean;
  toolbarLayoutMode: "clean-fullscreen" | "standard";
}): boolean {
  return (
    toolbarLayoutMode === "clean-fullscreen" ||
    isAdvancedMode ||
    hasSecondaryNavigationWarning ||
    !isAdvancedMode
  );
}

export function canvasToolbarSecondaryState({
  isAdvancedMode,
  isOpen,
  isVisible,
  toolbarLayoutMode,
}: {
  isAdvancedMode: boolean;
  isOpen: boolean;
  isVisible: boolean;
  toolbarLayoutMode: "clean-fullscreen" | "standard";
}): {
  hidden: boolean;
  showToggle: boolean;
} {
  const isProgressive =
    toolbarLayoutMode === "clean-fullscreen" || !isAdvancedMode;
  return {
    hidden: isProgressive && isVisible && !isOpen,
    showToggle: isProgressive && isVisible,
  };
}

export function canvasPageOptionLabel({
  isAdvancedMode,
  isAgentPanelActive = false,
  page,
}: {
  isAdvancedMode: boolean;
  isAgentPanelActive?: boolean;
  page: Pick<EditorPage, "slug" | "title">;
}): string {
  return isAdvancedMode && !isAgentPanelActive
    ? `${page.title} (${page.slug})`
    : page.title;
}

export function canvasLinkHealthButtonLabel({
  disconnectedCount,
  unresolvedCount,
}: {
  disconnectedCount: number;
  linkedPageCount: number;
  unresolvedCount: number;
}): string {
  if (unresolvedCount > 0) {
    return unresolvedCount === 1
      ? "Links: 1 issue"
      : `Links: ${unresolvedCount} issues`;
  }
  if (disconnectedCount > 0) {
    return disconnectedCount === 1
      ? "Links: 1 page to add"
      : `Links: ${disconnectedCount} pages to add`;
  }
  return "Links OK";
}

export function linkReviewChromeState({
  disconnectedPageCount,
  editableMissingLinkCount,
  missingLinkCount,
  needsReturnLinkCount,
}: {
  disconnectedPageCount: number;
  editableMissingLinkCount: number;
  missingLinkCount: number;
  needsReturnLinkCount: number;
}): {
  detailsOpenByDefault: boolean;
  showDisconnectedPages: boolean;
  showEditableMissingTargets: boolean;
  showMissingLinks: boolean;
  showRepairAction: boolean;
  showReturnLinkAction: boolean;
} {
  const hasMissingLinks = missingLinkCount > 0;

  return {
    detailsOpenByDefault: false,
    showDisconnectedPages: disconnectedPageCount > 0,
    showEditableMissingTargets: editableMissingLinkCount > 0,
    showMissingLinks: hasMissingLinks,
    showRepairAction: disconnectedPageCount > 0,
    showReturnLinkAction: needsReturnLinkCount > 0 && !hasMissingLinks,
  };
}

export function linkDetailsDisclosureCopy({
  status,
}: {
  status: "attention" | "healthy" | "warning";
}): {
  description: string;
  title: string;
} {
  if (status === "healthy") {
    return {
      description: "Open the full page map and link fields.",
      title: "Review all links",
    };
  }

  return {
    description: "Open the full page map, repair tools, and publish controls.",
    title: "Advanced link tools",
  };
}

export function linkReviewNextStepCopy({
  disconnectedPageCount,
  editableMissingLinkCount,
  missingLinkCount,
  needsReturnLinkCount,
  pageCount,
}: {
  disconnectedPageCount: number;
  editableMissingLinkCount: number;
  missingLinkCount: number;
  needsReturnLinkCount: number;
  pageCount: number;
}): {
  eyebrow: string;
  title: string;
  description: string;
} {
  const manualMissingLinkCount = Math.max(
    0,
    missingLinkCount - editableMissingLinkCount,
  );

  if (editableMissingLinkCount > 0) {
    return {
      description:
        editableMissingLinkCount === 1
          ? "Match this imported link to the right Groundbeam page."
          : `Match these ${editableMissingLinkCount} imported links to the right Groundbeam pages.`,
      eyebrow: "Start here",
      title: "Choose page targets",
    };
  }

  if (manualMissingLinkCount > 0) {
    return {
      description:
        manualMissingLinkCount === 1
          ? "This imported link needs a manual copy or URL check."
          : `These ${manualMissingLinkCount} imported links need a manual copy or URL check.`,
      eyebrow: "Needs review",
      title: "Review imported links",
    };
  }

  if (disconnectedPageCount > 0) {
    return {
      description:
        disconnectedPageCount === 1
          ? "Add this page to the top navigation or footer so visitors can reach it."
          : `Add these ${disconnectedPageCount} pages to the top navigation or footer so visitors can reach them.`,
      eyebrow: "Next decision",
      title: "Add pages to navigation",
    };
  }

  if (needsReturnLinkCount > 0) {
    return {
      description:
        needsReturnLinkCount === 1
          ? "Give visitors a path back after they follow this page link."
          : `Give visitors a path back after they follow these ${needsReturnLinkCount} page links.`,
      eyebrow: "Next decision",
      title: "Add return links",
    };
  }

  return {
    description:
      pageCount <= 1
        ? "There are no navigation decisions on this one-page draft."
        : "No link decisions need attention right now.",
    eyebrow: "Ready",
    title: pageCount <= 1 ? "Single page draft" : "Navigation looks connected",
  };
}

export function canvasLinkPopoverChromeState(
  input: Parameters<typeof linkReviewChromeState>[0],
): ReturnType<typeof linkReviewChromeState> {
  return linkReviewChromeState(input);
}

export function canvasLinkMenuToggleState(isOpen: boolean): {
  clearsTaskSurfaces: boolean;
  nextOpen: boolean;
} {
  const nextOpen = !isOpen;
  return {
    clearsTaskSurfaces: nextOpen,
    nextOpen,
  };
}

export function editorInitialSelectedBlockId({
  blockIds,
  currentBlockId,
  renderedBlockIds,
}: {
  blockIds: string[];
  currentBlockId: string | null;
  renderedBlockIds?: ReadonlySet<string>;
}): string | null {
  const isAvailable = (id: string) =>
    blockIds.includes(id) && (!renderedBlockIds || renderedBlockIds.has(id));
  if (currentBlockId && isAvailable(currentBlockId)) return currentBlockId;
  return null;
}

export function initialCanvasSizeForViewport(
  viewportWidth: number | null | undefined,
): CanvasSize {
  return viewportWidth !== null &&
    viewportWidth !== undefined &&
    viewportWidth <= 640
    ? "mobile"
    : "desktop";
}

export function pageRailConnectionRepairState(
  pageId: string,
  currentPageId: string,
  pageCount: number,
  status: PageConnectivityStatus,
): "hidden" | "isolated" | "partial" {
  if (pageId === currentPageId || pageCount < 2) return "hidden";
  if (status === "warning") return "isolated";
  if (status === "attention") return "partial";
  return "hidden";
}

export function selectionShortcutAction(
  event: Pick<
    KeyboardEvent,
    "altKey" | "ctrlKey" | "key" | "metaKey" | "shiftKey"
  >,
  state: SelectionShortcutState,
): SelectionShortcutAction {
  if (event.altKey || event.ctrlKey || event.metaKey) return null;
  if (state.isClearView) {
    return event.key === "Escape" ? "exit-clear-view" : null;
  }
  if (!state.hasDraft || !state.isEditMode) return null;
  if (event.key === "Escape" && state.hasOpenElementEditor) {
    return "close-element-editor";
  }
  if (event.key === "Escape" && state.hasSelectedElement) {
    return "select-parent";
  }
  if (event.key === "[") return "previous-element";
  if (event.key === "]") return "next-element";
  if (!state.hasSelection) return null;
  if (event.key === "Enter") return "edit-selection";

  const key = event.key.toLowerCase();
  if (key === "e") return "edit-selection";
  if (key === "i") return "inspect-selection";
  if (key === "a") return "ask-agent";
  return null;
}

interface DraftHistory {
  future: EditorPage[];
  past: EditorPage[];
}

interface CollectionTarget {
  collection: "items" | "legalLinks" | "links";
  index: number;
  itemLabel: string;
  maxItems: number;
  minItems: number;
}

interface CmsCollectionItemSummary {
  imageAlt: string | null;
  imageSrc: string | null;
  itemLabel: string;
  meta: string;
  title: string;
  tone: "item" | "media" | "news";
}

export interface MediaOption {
  alt: string;
  label: string;
  source: string;
  src: string;
  usageCount: number;
}

interface MediaUpdateInput {
  alt?: string | null;
  filename?: string;
}

interface NavigationLinkSummary {
  href: string;
  itemLabel: string;
  meta: string;
  status: "anchor" | "empty" | "external" | "internal" | "missing";
  statusLabel: string;
  title: string;
}

interface SelectionItemActions {
  canDuplicate: boolean;
  canRemove: boolean;
  itemLabel: string;
  itemPosition: string;
  onDuplicate: () => void;
  onRemove: () => void;
}

interface DraftHistoryStatus {
  canRedo: boolean;
  canUndo: boolean;
}

interface ClearViewSnapshot {
  canvasMode: CanvasMode;
  chromeHidden: boolean;
  outlineVisible: boolean;
  panelsHidden: boolean;
  structurePanelOpen: boolean;
  targetMapVisible: boolean;
}

const DRAFT_HISTORY_LIMIT = 80;

const THEME_PRESETS = [
  {
    id: "blueprint",
    name: "Blueprint",
    colors: {
      primary: "#1f5fbf",
      secondary: "#16396f",
      accent: "#0f766e",
      background: "#f5f7fa",
      surface: "#ffffff",
      text: "#171b24",
      textMuted: "#637083",
      border: "#d8dee8",
    },
  },
  {
    id: "studio",
    name: "Studio",
    colors: {
      primary: "#a83232",
      secondary: "#3d2f2f",
      accent: "#d89b2b",
      background: "#faf7f2",
      surface: "#ffffff",
      text: "#231f20",
      textMuted: "#746a5f",
      border: "#e4d8c7",
    },
  },
  {
    id: "field",
    name: "Field",
    colors: {
      primary: "#1f7a4d",
      secondary: "#164633",
      accent: "#c2772d",
      background: "#f3f7f1",
      surface: "#ffffff",
      text: "#17221b",
      textMuted: "#667465",
      border: "#d6e0d2",
    },
  },
] as const;

const BLOCK_VARIANTS: Record<string, { id: string; name: string }[]> = {
  header: [
    { id: "standard", name: "Standard" },
    { id: "centered", name: "Centered" },
  ],
  heroSection: [
    { id: "centered", name: "Centered" },
    { id: "split", name: "Split with image" },
  ],
  services: [
    { id: "grid-2", name: "Two columns" },
    { id: "grid-3", name: "Three columns" },
  ],
  stats: [
    { id: "strip", name: "Strip" },
    { id: "cards", name: "Cards" },
  ],
  testimonials: [
    { id: "cards", name: "Cards" },
    { id: "spotlight", name: "Spotlight" },
  ],
  newsList: [
    { id: "cards", name: "Cards" },
    { id: "list", name: "List" },
  ],
  gallery: [
    { id: "grid", name: "Grid" },
    { id: "mosaic", name: "Mosaic" },
  ],
  pricing: [
    { id: "cards", name: "Cards" },
    { id: "featured", name: "Featured cards" },
  ],
  team: [
    { id: "cards", name: "Cards" },
    { id: "compact", name: "Compact" },
  ],
  openingHours: [
    { id: "list", name: "List" },
    { id: "cards", name: "Cards" },
  ],
  richText: [
    { id: "prose", name: "Prose" },
    { id: "narrow", name: "Narrow" },
  ],
  textImage: [
    { id: "image-end", name: "Image after text" },
    { id: "image-start", name: "Image before text" },
  ],
  faqAccordion: [
    { id: "card", name: "Cards" },
    { id: "list", name: "List" },
  ],
  ctaBanner: [
    { id: "inline", name: "Inline" },
    { id: "centered", name: "Centered" },
  ],
  contactForm: [
    { id: "card", name: "Card" },
    { id: "bare", name: "Bare" },
  ],
  footer: [
    { id: "simple", name: "Simple" },
    { id: "columns", name: "Columns" },
  ],
};

const COLLECTION_TARGET_META: Record<
  string,
  Partial<
    Record<
      CollectionTarget["collection"],
      Omit<CollectionTarget, "collection" | "index">
    >
  >
> = {
  faqAccordion: {
    items: { itemLabel: "question", maxItems: 20, minItems: 1 },
  },
  footer: {
    legalLinks: { itemLabel: "legal link", maxItems: 6, minItems: 0 },
    links: { itemLabel: "footer link", maxItems: 12, minItems: 0 },
  },
  header: {
    links: { itemLabel: "navigation link", maxItems: 12, minItems: 0 },
  },
  gallery: {
    items: { itemLabel: "image", maxItems: 12, minItems: 1 },
  },
  newsList: {
    items: { itemLabel: "update", maxItems: 6, minItems: 1 },
  },
  openingHours: {
    items: { itemLabel: "hours row", maxItems: 14, minItems: 1 },
  },
  pricing: {
    items: { itemLabel: "plan", maxItems: 6, minItems: 1 },
  },
  services: {
    items: { itemLabel: "service", maxItems: 12, minItems: 1 },
  },
  stats: {
    items: { itemLabel: "stat", maxItems: 6, minItems: 1 },
  },
  team: {
    items: { itemLabel: "team member", maxItems: 12, minItems: 1 },
  },
  testimonials: {
    items: { itemLabel: "testimonial", maxItems: 6, minItems: 1 },
  },
};

const SECTION_TEMPLATES: SectionTemplate[] = [
  {
    id: "leadForm",
    label: "Lead form",
    description: "Capture name, email, phone, and message.",
    blockType: "contactForm",
    variant: "card",
    createData: (blockId) => ({
      heading: "Request a quote",
      intro: "Send a few details and we will get back to you shortly.",
      formId: formIdFromBlockId(blockId),
      askPhone: true,
      submitLabel: "Send request",
      successMessage: "Thanks. We will be in touch shortly.",
    }),
  },
  {
    id: "services",
    label: "Services",
    description: "Add a three-card services grid.",
    blockType: "services",
    variant: "grid-3",
    createData: () => ({
      heading: "What we can help with",
      intro: "Highlight the main services customers can choose from.",
      items: [
        {
          title: "Consultation",
          description:
            "A clear first conversation to understand the job and recommend the next step.",
        },
        {
          title: "Implementation",
          description:
            "Practical delivery focused on the details that matter for your business.",
        },
        {
          title: "Ongoing support",
          description:
            "Follow-up support so the work keeps performing after launch.",
        },
      ],
    }),
  },
  {
    id: "pricing",
    label: "Pricing",
    description: "Add service packages, plans, or starting prices.",
    blockType: "pricing",
    variant: "cards",
    createData: () => ({
      heading: "Simple ways to get started",
      intro:
        "Help visitors understand the right next step before they enquire.",
      items: [
        {
          name: "Starter",
          price: "From 99",
          cadence: "per visit",
          description:
            "A focused option for smaller jobs or first appointments.",
          features: [
            "Initial consultation",
            "Clear recommendation",
            "Written quote",
          ],
          ctaLabel: "Ask about Starter",
          ctaHref: "#contact",
        },
        {
          name: "Complete",
          price: "Custom quote",
          cadence: "per project",
          description:
            "A fuller package for customers who need end-to-end help.",
          features: [
            "Planning and delivery",
            "Priority scheduling",
            "Aftercare support",
          ],
          ctaLabel: "Request a quote",
          ctaHref: "#contact",
        },
      ],
    }),
  },
  {
    id: "cta",
    label: "Call to action",
    description: "Add a conversion banner with one button.",
    blockType: "ctaBanner",
    variant: "inline",
    createData: (_blockId, pageTitle) => ({
      heading: `Ready to work with ${pageTitle || "us"}?`,
      text: "Tell us what you need and we will get back to you.",
      cta: { label: "Get in touch", href: "#contact" },
    }),
  },
  {
    id: "testimonials",
    label: "Testimonials",
    description: "Add customer quotes and review snippets.",
    blockType: "testimonials",
    variant: "cards",
    createData: () => ({
      heading: "What customers say",
      intro: "A few words from people who trusted the team.",
      items: [
        {
          quote:
            "The team responded quickly, explained the options clearly, and made the whole process easy.",
          name: "Sarah K.",
          role: "Local customer",
        },
        {
          quote:
            "Professional, tidy, and easy to work with from the first message to the finished job.",
          name: "David R.",
          role: "Verified customer",
        },
      ],
    }),
  },
  {
    id: "team",
    label: "Team",
    description: "Introduce owners, staff, clinicians, or specialists.",
    blockType: "team",
    variant: "cards",
    createData: () => ({
      heading: "Meet the team",
      intro: "Show the real people customers will speak to or meet.",
      items: [
        {
          name: "Alex Morgan",
          role: "Owner",
          bio: "Leads the work and keeps customers updated from first message to finish.",
        },
        {
          name: "Sam Taylor",
          role: "Customer support",
          bio: "Helps schedule visits, answer questions, and keep every job moving.",
        },
      ],
    }),
  },
  {
    id: "stats",
    label: "Stats",
    description: "Add proof points, ratings, or running numbers.",
    blockType: "stats",
    variant: "cards",
    createData: () => ({
      heading: "Why customers choose us",
      intro: "Quick proof points for visitors who are comparing options.",
      items: [
        {
          value: "24/7",
          label: "Fast response",
          description: "Available when urgent work cannot wait.",
        },
        {
          value: "500+",
          label: "Jobs completed",
          description: "Practical experience across local customer needs.",
        },
        {
          value: "4.9",
          label: "Average rating",
          description: "Trusted by customers who value clear service.",
        },
      ],
    }),
  },
  {
    id: "hours",
    label: "Opening hours",
    description: "Add business hours, emergency cover, or appointment windows.",
    blockType: "openingHours",
    variant: "list",
    createData: () => ({
      heading: "Opening hours",
      intro: "Let visitors know when they can call, visit, or expect a reply.",
      items: [
        { day: "Monday - Friday", hours: "8:00 - 18:00" },
        { day: "Saturday", hours: "9:00 - 14:00" },
        {
          day: "Sunday",
          hours: "Closed",
          note: "Emergency enquiries by form.",
        },
      ],
    }),
  },
  {
    id: "news",
    label: "News",
    description: "Add blog posts, news, or recent updates.",
    blockType: "newsList",
    variant: "cards",
    createData: () => ({
      heading: "Latest updates",
      intro: "Share recent news, helpful articles, or timely notes.",
      items: [
        {
          title: "Spring availability is open",
          excerpt:
            "New appointment slots are available for customers planning work this month.",
          href: "/news/spring-availability",
          date: "March 2026",
          tag: "News",
        },
        {
          title: "How we quote projects",
          excerpt:
            "A short guide to what we check before sending a clear quote.",
          href: "/news/quote-checklist",
          tag: "Guide",
        },
      ],
    }),
  },
  {
    id: "gallery",
    label: "Gallery",
    description: "Add a portfolio or recent work image grid.",
    blockType: "gallery",
    variant: "grid",
    createData: () => ({
      heading: "Recent work",
      intro:
        "A quick look at projects, spaces, or results customers can expect.",
      items: [
        {
          src: "/images/project-1.webp",
          alt: "Completed project example",
          title: "Finished project",
          caption:
            "Use this card to show a real result, venue, product, or job.",
        },
        {
          src: "/images/project-2.webp",
          alt: "Customer project detail",
          title: "Project detail",
          caption:
            "Add a short caption that explains what visitors are seeing.",
        },
        {
          src: "/images/project-3.webp",
          alt: "Recent work example",
          title: "Recent work",
          caption: "Replace the image with your own work or imported media.",
        },
      ],
    }),
  },
  {
    id: "faq",
    label: "FAQ",
    description: "Add expandable questions and answers.",
    blockType: "faqAccordion",
    variant: "card",
    createData: () => ({
      heading: "Frequently asked questions",
      items: [
        {
          question: "How quickly can you help?",
          answer:
            "Send a message and the team will follow up with the right next step.",
        },
        {
          question: "Can I request a quote online?",
          answer:
            "Yes. Share the details through the contact form and we will respond directly.",
        },
      ],
    }),
  },
  {
    id: "content",
    label: "Content",
    description: "Add a simple editable copy section.",
    blockType: "richText",
    variant: "prose",
    createData: () => ({
      html: "<h2>New section</h2><p>Add supporting copy here.</p>",
    }),
  },
];

function blockLabel(type: string): string {
  return (
    {
      heroSection: "Hero",
      header: "Header",
      services: "Services",
      stats: "Stats",
      testimonials: "Testimonials",
      newsList: "News",
      gallery: "Gallery",
      pricing: "Pricing",
      team: "Team",
      openingHours: "Opening hours",
      richText: "Content",
      textImage: "Text + image",
      faqAccordion: "FAQ",
      ctaBanner: "Call to action",
      contactForm: "Lead form",
      footer: "Footer",
    }[type] ?? type
  );
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function bool(value: unknown): boolean {
  return typeof value === "boolean" ? value : false;
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function arrayOfObjects(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          item !== null && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}

function blogPostDraftFromPost(post: BlogPost): BlogPostEditorDraft {
  return {
    author: post.author ?? "",
    contentBody: post.content.body,
    excerpt: post.excerpt ?? "",
    featuredImage: post.featuredImage ?? "",
    id: post.id,
    seoMetaDescription: post.seo.metaDescription,
    seoMetaTitle: post.seo.metaTitle,
    slug: post.slug,
    status: post.status,
    tagsText: post.tags.join(", "),
    title: post.title,
  };
}

function emptyBlogPostDraft(): BlogPostEditorDraft {
  return {
    author: "",
    contentBody: "",
    excerpt: "",
    featuredImage: "",
    id: null,
    seoMetaDescription: "",
    seoMetaTitle: "",
    slug: "",
    status: "draft",
    tagsText: "",
    title: "",
  };
}

function blogPostInputFromDraft(draft: BlogPostEditorDraft): BlogPostInput {
  const tags = draft.tagsText
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  return {
    author: draft.author.trim() || null,
    content: {
      body: draft.contentBody,
      format: "markdown",
    },
    excerpt: draft.excerpt.trim() || null,
    featuredImage: draft.featuredImage.trim() || null,
    seo: {
      metaDescription: draft.seoMetaDescription.trim(),
      metaTitle: draft.seoMetaTitle.trim() || draft.title.trim(),
    },
    slug: draft.slug.trim() || undefined,
    status: draft.status,
    tags,
    title: draft.title.trim(),
  };
}

export function blogPostEditorSummary(post: BlogPost): {
  meta: string;
  statusLabel: string;
  title: string;
} {
  const statusLabel = post.status === "published" ? "Published" : "Draft";
  const details = [
    statusLabel,
    post.author ? `By ${post.author}` : "",
    post.tags.slice(0, 2).join(", "),
    post.slug,
  ].filter(Boolean);
  return {
    meta: details.join(" - "),
    statusLabel,
    title: post.title.trim() || "Untitled post",
  };
}

export function contentLibraryPanelChromeState({
  editorOpen,
  isOverlay,
  postCount,
}: {
  editorOpen?: boolean;
  isOverlay: boolean;
  postCount: number;
}): {
  editToggleLabel: string;
  emptyLabel: string;
  showEditor: boolean;
  startsEditorOpen: boolean;
} {
  const startsEditorOpen = !isOverlay || postCount === 0;
  const isEditorOpen = editorOpen ?? startsEditorOpen;
  return {
    editToggleLabel: isEditorOpen ? "Review posts" : "Edit post",
    emptyLabel: isOverlay
      ? "No posts yet. Create one here, then publish it with the site."
      : "No posts yet.",
    showEditor: !isOverlay || isEditorOpen,
    startsEditorOpen,
  };
}

export function blockFocusPanelChromeState({
  hasSelectedTarget,
  toolsOpen,
}: {
  hasSelectedTarget: boolean;
  toolsOpen?: boolean;
}): {
  showSectionTools: boolean;
  showToolsToggle: boolean;
  startsToolsOpen: boolean;
  toolsToggleLabel: string;
} {
  const startsToolsOpen = !hasSelectedTarget;
  const isToolsOpen = toolsOpen ?? startsToolsOpen;
  return {
    showSectionTools: !hasSelectedTarget || isToolsOpen,
    showToolsToggle: hasSelectedTarget,
    startsToolsOpen,
    toolsToggleLabel: isToolsOpen ? "Hide tools" : "Section tools",
  };
}

export function elementInspectorHeaderSummary({
  sectionLabel,
  target,
}: {
  sectionLabel?: string | null;
  target: Pick<EditableElementTarget, "kind" | "label" | "path">;
}): { detail: string; kindLabel: string; title: string } {
  const title = target.label.trim() || "Selected field";
  const section = sectionLabel?.trim()
    ? `${sectionLabel.trim()}${/section$/i.test(sectionLabel.trim()) ? "" : " section"}`
    : "Selected section";
  const kindLabel =
    target.kind === "image"
      ? "Image"
      : target.kind === "link"
        ? "Link"
        : target.kind === "textarea"
          ? "Long copy"
          : "Copy";
  return {
    detail: parseRichTextPath(target.path)
      ? `Rich text in ${section}`
      : section,
    kindLabel,
    title,
  };
}

export function cmsCollectionItemSummary(
  blockType: string,
  item: Record<string, unknown>,
  index: number,
): CmsCollectionItemSummary {
  const position = index + 1;
  if (blockType === "newsList") {
    const title = text(item.title).trim() || `Update ${position}`;
    const src = text(item.image).trim();
    const details = [
      text(item.tag),
      text(item.date),
      text(item.author) ? `By ${text(item.author)}` : "",
      text(item.href),
    ]
      .map((value) => value.trim())
      .filter(Boolean);
    return {
      imageAlt: src ? text(item.imageAlt).trim() || title : null,
      imageSrc: src || null,
      itemLabel: `Update ${position}`,
      meta: details.join(" - ") || "Draft update",
      title,
      tone: "news",
    };
  }

  if (blockType === "gallery") {
    const src = text(item.src).trim();
    const alt = text(item.alt).trim();
    const title = text(item.title).trim() || alt || `Image ${position}`;
    const details = [
      text(item.caption).trim(),
      text(item.href).trim() ? `Links to ${text(item.href).trim()}` : "",
    ].filter(Boolean);
    return {
      imageAlt: alt || title,
      imageSrc: src || null,
      itemLabel: `Image ${position}`,
      meta: details.join(" - ") || (src ? "Image ready" : "Needs image"),
      title,
      tone: "media",
    };
  }

  return {
    imageAlt: null,
    imageSrc: null,
    itemLabel: `Item ${position}`,
    meta: "Content item",
    title:
      text(item.title).trim() || text(item.name).trim() || `Item ${position}`,
    tone: "item",
  };
}

function mediaLabel(value: string, fallback: string): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (normalized) return normalized;
  const path = fallback.split(/[?#]/)[0] ?? "";
  const fileName = path.split("/").filter(Boolean).pop();
  return fileName || "Media";
}

export function mediaOptionsForBlocks(blocks: PageBlock[]): MediaOption[] {
  const bySrc = new Map<string, MediaOption>();
  const add = ({
    alt,
    label,
    source,
    src,
  }: {
    alt?: unknown;
    label?: unknown;
    source: string;
    src?: unknown;
  }) => {
    const normalizedSrc = text(src).trim();
    if (!normalizedSrc) return;
    const normalizedAlt = text(alt).trim();
    const normalizedLabel = mediaLabel(text(label), normalizedSrc);
    const existing = bySrc.get(normalizedSrc);
    if (existing) {
      const sources = existing.source.split(", ");
      bySrc.set(normalizedSrc, {
        ...existing,
        alt: existing.alt || normalizedAlt,
        label: existing.label || normalizedLabel,
        source: sources.includes(source)
          ? existing.source
          : `${existing.source}, ${source}`,
        usageCount: existing.usageCount + 1,
      });
      return;
    }
    bySrc.set(normalizedSrc, {
      alt: normalizedAlt,
      label: normalizedLabel,
      source,
      src: normalizedSrc,
      usageCount: 1,
    });
  };

  blocks.forEach((block) => {
    if (block.blockType === "heroSection" || block.blockType === "textImage") {
      const image = object(block.data.image);
      add({
        alt: image.alt,
        label: image.alt || blockLabel(block.blockType),
        source: blockLabel(block.blockType),
        src: image.src,
      });
    }
    if (block.blockType === "newsList") {
      arrayOfObjects(block.data.items).forEach((item, index) => {
        add({
          alt: item.imageAlt,
          label: item.title,
          source: `News ${index + 1}`,
          src: item.image,
        });
      });
    }
    if (block.blockType === "gallery") {
      arrayOfObjects(block.data.items).forEach((item, index) => {
        add({
          alt: item.alt,
          label: item.title || item.alt,
          source: `Gallery ${index + 1}`,
          src: item.src,
        });
      });
    }
    if (block.blockType === "team") {
      arrayOfObjects(block.data.items).forEach((item, index) => {
        add({
          alt: item.imageAlt,
          label: item.name,
          source: `Team ${index + 1}`,
          src: item.image,
        });
      });
    }
  });

  return Array.from(bySrc.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function mediaOptionsWithLibrary(
  pageOptions: MediaOption[],
  libraryItems: MediaItem[],
): MediaOption[] {
  const bySrc = new Map(pageOptions.map((option) => [option.src, option]));
  for (const item of libraryItems) {
    const label = mediaLabel(item.alt ?? item.filename, item.url);
    const existing = bySrc.get(item.url);
    if (existing) {
      const sources = existing.source.split(", ");
      bySrc.set(item.url, {
        ...existing,
        alt: existing.alt || item.alt || "",
        label: existing.label || label,
        source: sources.includes("Library")
          ? existing.source
          : `Library, ${existing.source}`,
      });
      continue;
    }
    bySrc.set(item.url, {
      alt: item.alt ?? "",
      label,
      source: "Library",
      src: item.url,
      usageCount: 0,
    });
  }
  return Array.from(bySrc.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function navigationLinkSummary(
  link: Record<string, unknown>,
  pages: EditorPage[],
  hostname: string | null,
  index: number,
  itemLabel = "Link",
): NavigationLinkSummary {
  const position = index + 1;
  const href = text(link.href).trim();
  const title = text(link.label).trim() || `${itemLabel} ${position}`;
  const label = `${itemLabel} ${position}`;

  if (!href) {
    return {
      href,
      itemLabel: label,
      meta: "Choose a page or URL",
      status: "empty",
      statusLabel: "Needs target",
      title,
    };
  }

  if (href.startsWith("#")) {
    return {
      href,
      itemLabel: label,
      meta: `Jumps to ${href}`,
      status: "anchor",
      statusLabel: "Page anchor",
      title,
    };
  }

  const targetPage = pageForHref(pages, href, hostname);
  if (targetPage) {
    const slug = normalizeSlugPath(targetPage.slug);
    return {
      href,
      itemLabel: label,
      meta: `${targetPage.title.trim() || slug} (${slug})`,
      status: "internal",
      statusLabel: "Internal page",
      title,
    };
  }

  const internalSlug = internalSlugFromHref(href, hostname);
  if (internalSlug) {
    return {
      href,
      itemLabel: label,
      meta: `No page exists at ${internalSlug}`,
      status: "missing",
      statusLabel: "Missing page",
      title,
    };
  }

  const compactHref =
    href
      .replace(/^https?:\/\//i, "")
      .replace(/^(mailto|tel|sms|whatsapp):/i, "")
      .replace(/\/$/, "") || href;
  return {
    href,
    itemLabel: label,
    meta: compactHref,
    status: "external",
    statusLabel: /^(mailto|tel|sms|whatsapp):/i.test(href)
      ? "Contact link"
      : "External URL",
    title,
  };
}

function richTextPath(
  index: number,
  property: "alt" | "href" | "src" | "text",
): string {
  return `htmlNodes.${index}.${property}`;
}

function parseRichTextPath(
  path: string,
): { index: number; property: "alt" | "href" | "src" | "text" } | null {
  const match = path.match(RICH_TEXT_PATH_RE);
  if (!match?.[1] || !match[2]) return null;
  return {
    index: Number(match[1]),
    property: match[2] as "alt" | "href" | "src" | "text",
  };
}

function richTextRoot(html: string): HTMLElement {
  const doc = new DOMParser().parseFromString(
    `<div data-gb-richtext-root>${html}</div>`,
    "text/html",
  );
  const root = doc.body.querySelector<HTMLElement>("[data-gb-richtext-root]");
  if (root) return root;
  const fallback = doc.createElement("div");
  fallback.innerHTML = html;
  return fallback;
}

function richTextEditableNodes(root: ParentNode): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(RICH_TEXT_EDITABLE_SELECTOR),
  ).filter((node) => {
    const tag = node.tagName.toLowerCase();
    if (tag === "a" || tag === "img") return true;
    if (node.querySelector("a,img,button,input,textarea,select")) {
      return false;
    }
    return Boolean(node.textContent?.trim());
  });
}

function richTextNodeLabel(node: HTMLElement, index: number): string {
  const tag = node.tagName.toLowerCase();
  if (tag === "a") return `Prose link ${index + 1}`;
  if (tag === "img") return `Prose image ${index + 1}`;
  if (tag === "blockquote") return `Quote ${index + 1}`;
  if (tag === "li") return `List item ${index + 1}`;
  if (tag.startsWith("h")) return `Prose heading ${index + 1}`;
  return `Paragraph ${index + 1}`;
}

function targetPathLabel(target: EditableElementTarget): string {
  return parseRichTextPath(target.path) ? "Rich text element" : target.path;
}

function collectionTargetFor(
  block: PageBlock,
  target: EditableElementTarget,
): CollectionTarget | null {
  const match = target.path.match(/^(items|legalLinks|links)\.(\d+)\./);
  if (!match?.[1] || !match[2]) return null;
  const collection = match[1] as CollectionTarget["collection"];
  const index = Number(match[2]);
  const meta = COLLECTION_TARGET_META[block.blockType]?.[collection];
  if (!meta) return null;
  const items = arrayOfObjects(block.data[collection]);
  if (index < 0 || index >= items.length) return null;
  return { collection, index, ...meta };
}

function duplicateCollectionItem(
  data: Record<string, unknown>,
  target: CollectionTarget,
): Record<string, unknown> {
  const items = arrayOfObjects(data[target.collection]);
  const source = items[target.index];
  if (!source || items.length >= target.maxItems) return data;
  const nextItems = [...items];
  nextItems.splice(target.index + 1, 0, cloneBlockData(source));
  return { ...data, [target.collection]: nextItems };
}

function removeCollectionItem(
  data: Record<string, unknown>,
  target: CollectionTarget,
): Record<string, unknown> {
  const items = arrayOfObjects(data[target.collection]);
  if (items.length <= target.minItems) return data;
  return {
    ...data,
    [target.collection]: items.filter((_, index) => index !== target.index),
  };
}

function richTextTargetValue(
  data: Record<string, unknown>,
  path: string,
): string | null {
  const parsed = parseRichTextPath(path);
  if (!parsed) return null;
  const root = richTextRoot(text(data.html));
  const node = richTextEditableNodes(root)[parsed.index];
  if (!node) return "";
  if (parsed.property === "href") return node.getAttribute("href") ?? "";
  if (parsed.property === "src") return node.getAttribute("src") ?? "";
  if (parsed.property === "alt") return node.getAttribute("alt") ?? "";
  return node.textContent ?? "";
}

function setRichTextTargetValue(
  data: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> | null {
  const parsed = parseRichTextPath(path);
  if (!parsed) return null;
  const root = richTextRoot(text(data.html));
  const node = richTextEditableNodes(root)[parsed.index];
  if (!node) return data;
  const nextValue = text(value);
  if (parsed.property === "href") {
    node.setAttribute("href", nextValue);
  } else if (parsed.property === "src") {
    node.setAttribute("src", nextValue);
  } else if (parsed.property === "alt") {
    node.setAttribute("alt", nextValue);
  } else {
    node.textContent = nextValue;
  }
  return { ...data, html: root.innerHTML.trim() };
}

function richTextLinks(html: string): {
  href: string;
  index: number;
  label: string;
}[] {
  const root = richTextRoot(html);
  return richTextEditableNodes(root)
    .map((node, index) => ({ index, node }))
    .filter(
      (entry): entry is { index: number; node: HTMLAnchorElement } =>
        entry.node.tagName.toLowerCase() === "a",
    )
    .map(({ index, node }) => ({
      href: node.getAttribute("href") ?? "",
      index,
      label: node.textContent?.trim() ?? "",
    }))
    .filter((link) => link.href);
}

function optionalLink(
  current: unknown,
  patch: Record<string, unknown>,
): Record<string, string> | undefined {
  const next = { ...object(current), ...patch };
  const label = text(next.label).trim();
  const href = text(next.href).trim();
  return label && href ? { label, href } : undefined;
}

function optionalNonEmpty(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function blockTypeIdPart(blockType: string): string {
  return blockType
    .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    .replace(/^_/, "")
    .replace(/[^a-z0-9_]/g, "_");
}

function newEditorBlockId(blockType: string): string {
  const suffix = crypto.randomUUID().replaceAll("-", "").slice(0, 10);
  return `blk_ui_${blockTypeIdPart(blockType)}_${suffix}`.slice(0, 64);
}

function formIdFromBlockId(blockId: string): string {
  return blockId.replace(/^blk_/, "form_").slice(0, 64);
}

function cloneBlockData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  if (typeof structuredClone === "function") return structuredClone(data);
  return JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
}

function cloneEditorPage(page: EditorPage): EditorPage {
  if (typeof structuredClone === "function") return structuredClone(page);
  return JSON.parse(JSON.stringify(page)) as EditorPage;
}

function draftVersionKey(page: EditorPage): string {
  return JSON.stringify({
    blocks: reorder(page.blocks),
    seo: page.seo,
    title: page.title,
  });
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== "function") {
    return false;
  }
  const element = target as Element;
  const tagName = element.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    Boolean(element.closest("[contenteditable='true']"))
  );
}

function normalizeSlugPath(pathname: string): string {
  const [path = ""] = pathname.split(/[?#]/);
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  if (withSlash === "/" || withSlash === "") return "/";
  return withSlash.replace(/\/+$/, "");
}

function suggestedPageSlug(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug ? `/${slug}` : "/services";
}

function internalSlugFromHref(
  href: string,
  hostname: string | null,
): string | null {
  const trimmed = href.trim();
  if (
    !trimmed ||
    trimmed.startsWith("#") ||
    /^(mailto|tel|sms|whatsapp):/i.test(trimmed)
  ) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (!hostname || url.hostname !== hostname) return null;
      return normalizeSlugPath(url.pathname);
    } catch {
      return null;
    }
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  return normalizeSlugPath(trimmed);
}

function pageForHref(
  pages: EditorPage[],
  href: string,
  hostname: string | null,
): EditorPage | null {
  const slug = internalSlugFromHref(href, hostname);
  if (!slug) return null;
  return pages.find((page) => normalizeSlugPath(page.slug) === slug) ?? null;
}

export function describeSelectedLinkTarget(
  block: PageBlock | null,
  target: EditableElementTarget | null,
  pages: EditorPage[],
  hostname: string | null,
): SelectionLinkTarget | null {
  if (!block || target?.kind !== "link" || !target.hrefPath) return null;
  const href = text(getPathValue(block.data, target.hrefPath)).trim();
  if (!href) {
    return { href: "", label: "No target", status: "empty" };
  }

  const targetPage = pageForHref(pages, href, hostname);
  if (targetPage) {
    return {
      href,
      label: targetPage.title.trim() || normalizeSlugPath(targetPage.slug),
      status: "internal",
      targetPageId: targetPage.id,
      targetPageSlug: normalizeSlugPath(targetPage.slug),
    };
  }

  const internalSlug = internalSlugFromHref(href, hostname);
  if (internalSlug) {
    return { href, label: `Missing ${internalSlug}`, status: "missing" };
  }

  return {
    href,
    label: href.replace(/^https?:\/\//i, "").replace(/\/$/, "") || href,
    status: "external",
  };
}

export function linkRelationshipForHref({
  currentPageId,
  hostname,
  href,
  pages,
}: {
  currentPageId: string | null;
  hostname: string | null;
  href: string;
  pages: EditorPage[];
}): LinkRelationship {
  const trimmed = href.trim();
  if (!trimmed) {
    return {
      actionLabel: null,
      canRepair: false,
      copy: "Choose a page or URL before repairing site flow.",
      label: "No link target",
      status: "empty",
    };
  }

  const internalSlug = internalSlugFromHref(trimmed, hostname);
  if (!internalSlug) {
    return {
      actionLabel: null,
      canRepair: false,
      copy: "External, phone, email, and anchor links do not need return links.",
      label: "External link",
      status: "external",
    };
  }

  const currentPage = currentPageId
    ? (pages.find((page) => page.id === currentPageId) ?? null)
    : null;
  const targetPage =
    pages.find((page) => normalizeSlugPath(page.slug) === internalSlug) ?? null;
  if (!targetPage) {
    return {
      actionLabel: null,
      canRepair: false,
      copy: `No Groundbeam page currently uses ${internalSlug}.`,
      label: "Missing page",
      status: "missing",
    };
  }

  const label = targetPage.title.trim() || normalizeSlugPath(targetPage.slug);
  if (!currentPage || targetPage.id === currentPage.id) {
    return {
      actionLabel: null,
      canRepair: false,
      copy:
        targetPage.id === currentPage?.id
          ? "This link stays on the current page."
          : "Open the page in the builder before repairing its return link.",
      label,
      status: targetPage.id === currentPage?.id ? "self" : "connected",
      targetPageId: targetPage.id,
      targetPageSlug: normalizeSlugPath(targetPage.slug),
    };
  }

  if (pageLinksToPage(targetPage, currentPage, pages, hostname)) {
    return {
      actionLabel: null,
      canRepair: false,
      copy: `${label} already links back to this page.`,
      label,
      status: "connected",
      targetPageId: targetPage.id,
      targetPageSlug: normalizeSlugPath(targetPage.slug),
    };
  }

  return {
    actionLabel: "Add return link",
    canRepair: true,
    copy: `${label} does not link back to this page yet.`,
    label,
    status: "needs-return",
    targetPageId: targetPage.id,
    targetPageSlug: normalizeSlugPath(targetPage.slug),
  };
}

export function linkRelationshipRepairActionLabel({
  mode,
  relationship,
}: {
  mode: "inline" | "inspector";
  relationship: LinkRelationship | null;
}): string | null {
  if (!relationship?.canRepair) return null;
  return mode === "inline"
    ? "Save + add return link"
    : relationship.actionLabel;
}

export function canvasLinkTargetActionLabel(
  linkTarget: SelectionLinkTarget | null,
): string | null {
  if (!linkTarget) return null;
  if (linkTarget.status === "internal" && linkTarget.targetPageId) {
    return "Open page";
  }
  if (linkTarget.status === "missing") return "Fix link";
  if (linkTarget.status === "empty") return "Set target";
  return null;
}

export function newPageConnectionHint({
  existingPageCount,
  shouldConnect,
  sourceTitle,
}: {
  existingPageCount: number;
  shouldConnect: boolean;
  sourceTitle: string | null;
}): string {
  if (!shouldConnect) return "Create without site links";
  if (existingPageCount < 1) return "First page; no return link needed";
  const source = sourceTitle?.trim();
  return source
    ? `Navigation + return link to ${source}`
    : "Navigation + return link";
}

export function collectLinks(
  blocks: PageBlock[],
  pages: EditorPage[],
  hostname: string | null,
): LinkSummary[] {
  const links: Omit<LinkSummary, "internalSlug" | "key" | "targetPage">[] = [];
  const add = (
    href: unknown,
    label: unknown,
    source: string,
    meta: {
      blockId?: string;
      hrefPath?: string;
      labelPath?: string;
    } = {},
  ) => {
    const target = text(href).trim();
    if (!target) return;
    links.push({
      blockId: meta.blockId ?? null,
      href: target,
      hrefPath: meta.hrefPath ?? null,
      label: text(label).trim() || target,
      labelPath: meta.labelPath ?? null,
      source,
    });
  };

  for (const block of blocks) {
    const data = block.data;
    if (block.blockType === "header") {
      arrayOfObjects(data.links).forEach((link, index) => {
        add(link.href, link.label, `Header link ${index + 1}`, {
          blockId: block.id,
          hrefPath: `links.${index}.href`,
          labelPath: `links.${index}.label`,
        });
      });
      add(data.ctaHref, data.ctaLabel, "Header action", {
        blockId: block.id,
        hrefPath: "ctaHref",
        labelPath: "ctaLabel",
      });
    }
    if (block.blockType === "heroSection") {
      const primary = object(data.primaryCta);
      const secondary = object(data.secondaryCta);
      add(primary.href, primary.label, "Hero primary", {
        blockId: block.id,
        hrefPath: "primaryCta.href",
        labelPath: "primaryCta.label",
      });
      add(secondary.href, secondary.label, "Hero secondary", {
        blockId: block.id,
        hrefPath: "secondaryCta.href",
        labelPath: "secondaryCta.label",
      });
    }
    if (block.blockType === "services") {
      arrayOfObjects(data.items).forEach((item, index) => {
        add(item.href, item.title, `Service ${index + 1}`, {
          blockId: block.id,
          hrefPath: `items.${index}.href`,
          labelPath: `items.${index}.title`,
        });
      });
    }
    if (block.blockType === "newsList") {
      arrayOfObjects(data.items).forEach((item, index) => {
        add(item.href, item.title, `News item ${index + 1}`, {
          blockId: block.id,
          hrefPath: `items.${index}.href`,
          labelPath: `items.${index}.title`,
        });
      });
    }
    if (block.blockType === "gallery") {
      arrayOfObjects(data.items).forEach((item, index) => {
        add(item.href, item.title || item.alt, `Gallery image ${index + 1}`, {
          blockId: block.id,
          hrefPath: `items.${index}.href`,
          labelPath: `items.${index}.title`,
        });
      });
    }
    if (block.blockType === "pricing") {
      arrayOfObjects(data.items).forEach((item, index) => {
        add(item.ctaHref, item.ctaLabel || item.name, `Plan ${index + 1}`, {
          blockId: block.id,
          hrefPath: `items.${index}.ctaHref`,
          labelPath: `items.${index}.ctaLabel`,
        });
      });
    }
    if (block.blockType === "textImage") {
      const cta = object(data.cta);
      add(cta.href, cta.label, "Text + image button", {
        blockId: block.id,
        hrefPath: "cta.href",
        labelPath: "cta.label",
      });
    }
    if (block.blockType === "ctaBanner") {
      const cta = object(data.cta);
      add(cta.href, cta.label, "CTA button", {
        blockId: block.id,
        hrefPath: "cta.href",
        labelPath: "cta.label",
      });
    }
    if (block.blockType === "footer") {
      arrayOfObjects(data.links).forEach((link, index) => {
        add(link.href, link.label, `Footer link ${index + 1}`, {
          blockId: block.id,
          hrefPath: `links.${index}.href`,
          labelPath: `links.${index}.label`,
        });
      });
      arrayOfObjects(data.legalLinks).forEach((link, index) => {
        add(link.href, link.label, `Legal link ${index + 1}`, {
          blockId: block.id,
          hrefPath: `legalLinks.${index}.href`,
          labelPath: `legalLinks.${index}.label`,
        });
      });
    }
    if (block.blockType === "richText") {
      richTextLinks(text(data.html)).forEach((link) => {
        add(link.href, link.label, `Prose link ${link.index + 1}`, {
          blockId: block.id,
          hrefPath: richTextPath(link.index, "href"),
          labelPath: richTextPath(link.index, "text"),
        });
      });
    }
  }

  return links.map((link, index) => ({
    ...link,
    internalSlug: internalSlugFromHref(link.href, hostname),
    key: `${link.source}-${index}-${link.href}`,
    targetPage: pageForHref(pages, link.href, hostname),
  }));
}

function boundedNavLinkLabel(value: string, fallback: string): string {
  const normalized = (value.trim() || fallback.trim() || "Page").replace(
    /\s+/g,
    " ",
  );
  if (normalized.length <= SITE_NAV_LINK_LABEL_MAX_LENGTH) return normalized;
  return `${normalized
    .slice(0, SITE_NAV_LINK_LABEL_MAX_LENGTH - 3)
    .trimEnd()}...`;
}

export function navLinkLabelForPage(
  page: Pick<EditorPage, "slug" | "title">,
): string {
  const slug = normalizeSlugPath(page.slug);
  return boundedNavLinkLabel(page.title, slug);
}

function normalizeSiteNavLink(link: {
  href?: unknown;
  label?: unknown;
}): SiteNavLink | null {
  const href = text(link.href).trim();
  if (!href) return null;
  const label = boundedNavLinkLabel(text(link.label), href);
  return { href, label };
}

function mergeSiteNavLinks(
  links: { href?: unknown; label?: unknown }[],
  limit = SITE_NAV_LINK_LIMIT,
): SiteNavLink[] {
  const next: SiteNavLink[] = [];
  const seen = new Set<string>();
  for (const link of links) {
    const normalized = normalizeSiteNavLink(link);
    if (!normalized) continue;
    const key = normalizeSlugPath(normalized.href);
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(normalized);
    if (next.length >= limit) break;
  }
  return next;
}

function appendSiteNavLink(
  links: { href?: unknown; label?: unknown }[],
  link: { href?: unknown; label?: unknown },
  limit = SITE_NAV_LINK_LIMIT,
): SiteNavLink[] {
  const normalized = normalizeSiteNavLink(link);
  if (!normalized) return mergeSiteNavLinks(links, limit);
  const key = normalizeSlugPath(normalized.href);
  const current = mergeSiteNavLinks(links, limit).filter(
    (candidate) => normalizeSlugPath(candidate.href) !== key,
  );
  return [...current.slice(0, Math.max(0, limit - 1)), normalized];
}

function pageNavLinks(pages: EditorPage[]): SiteNavLink[] {
  return pages.slice(0, SITE_NAV_LINK_LIMIT).map((page) => ({
    href: normalizeSlugPath(page.slug),
    label: navLinkLabelForPage(page),
  }));
}

function fallbackBusinessName(
  hostname: string | null,
  pages: EditorPage[],
): string {
  const home = pages.find((page) => normalizeSlugPath(page.slug) === "/");
  const title = home?.title.trim() || pages[0]?.title.trim();
  if (title) return title;
  if (!hostname) return "Business";
  return hostname.replace(/^www\./, "").split(".")[0] || "Business";
}

function footerPageIds(
  page: EditorPage,
  pages: EditorPage[],
  hostname: string | null,
): Set<string> {
  const footer = page.blocks.find((block) => block.blockType === "footer");
  if (!footer) return new Set();
  return new Set(
    arrayOfObjects(footer.data.links)
      .map((link) => pageForHref(pages, text(link.href), hostname)?.id)
      .filter((id): id is string => Boolean(id)),
  );
}

function headerPageIds(
  page: EditorPage,
  pages: EditorPage[],
  hostname: string | null,
): Set<string> {
  const header = page.blocks.find((block) => block.blockType === "header");
  if (!header) return new Set();
  return new Set(
    arrayOfObjects(header.data.links)
      .map((link) => pageForHref(pages, text(link.href), hostname)?.id)
      .filter((id): id is string => Boolean(id)),
  );
}

export function summarizePageConnectivity(
  pages: EditorPage[],
  hostname: string | null,
): Map<string, PageConnectivity> {
  const linksByPageId = new Map(
    pages.map((page) => [page.id, collectLinks(page.blocks, pages, hostname)]),
  );
  const incomingByPageId = new Map<string, Set<string>>(
    pages.map((page) => [page.id, new Set<string>()]),
  );

  for (const sourcePage of pages) {
    const links = linksByPageId.get(sourcePage.id) ?? [];
    for (const link of links) {
      const targetId = link.targetPage?.id;
      if (!targetId || targetId === sourcePage.id) continue;
      incomingByPageId.get(targetId)?.add(sourcePage.id);
    }
  }

  return new Map(
    pages.map((page) => {
      const otherPageCount = Math.max(0, pages.length - 1);
      const outgoingPageIds = new Set(
        (linksByPageId.get(page.id) ?? [])
          .map((link) => link.targetPage?.id)
          .filter((id): id is string => Boolean(id) && id !== page.id),
      );
      const navLinkedPageIds = new Set([
        ...Array.from(headerPageIds(page, pages, hostname)),
        ...Array.from(footerPageIds(page, pages, hostname)),
      ]);
      navLinkedPageIds.delete(page.id);
      const incomingPageIds = incomingByPageId.get(page.id) ?? new Set();
      const outgoingCount = outgoingPageIds.size;
      const incomingCount = incomingPageIds.size;
      const navLinkedCount = navLinkedPageIds.size;
      const status: PageConnectivityStatus =
        pages.length < 2
          ? "single"
          : navLinkedCount >= otherPageCount && incomingCount > 0
            ? "healthy"
            : outgoingCount > 0 || incomingCount > 0 || navLinkedCount > 0
              ? "attention"
              : "warning";
      const statusLabel =
        status === "single"
          ? "Single page"
          : status === "healthy"
            ? "Connected"
            : status === "attention"
              ? "Partial"
              : "Isolated";

      return [
        page.id,
        {
          incomingCount,
          navLinkedCount,
          otherPageCount,
          outgoingCount,
          status,
          statusLabel,
        },
      ];
    }),
  );
}

function syncHeaderNavigationForPage(
  page: EditorPage,
  pages: EditorPage[],
  hostname: string | null,
): EditorPage {
  const headerIndex = page.blocks.findIndex(
    (block) => block.blockType === "header",
  );
  const currentHeader = headerIndex >= 0 ? page.blocks[headerIndex] : undefined;
  const currentData = currentHeader ? currentHeader.data : {};
  const navLinks = pageNavLinks(pages);
  const pageSlugs = new Set(
    navLinks.map((link) => normalizeSlugPath(link.href)),
  );
  const preservedLinks = arrayOfObjects(currentData.links)
    .filter((link) => {
      const slug = internalSlugFromHref(text(link.href), hostname);
      return !slug || !pageSlugs.has(slug);
    })
    .map((link) => ({ href: text(link.href), label: text(link.label) }))
    .filter((link) => link.href && link.label);
  const nextHeader: PageBlock = {
    id: currentHeader?.id ?? newEditorBlockId("header"),
    blockType: "header",
    variant: currentHeader?.variant ?? "standard",
    order: currentHeader?.order ?? 0,
    data: {
      ...currentData,
      brandName:
        text(currentData.brandName) || fallbackBusinessName(hostname, pages),
      links: mergeSiteNavLinks([...navLinks, ...preservedLinks]),
    },
  };
  const nextBlocks =
    headerIndex >= 0
      ? page.blocks.map((block, index) =>
          index === headerIndex ? nextHeader : block,
        )
      : [nextHeader, ...page.blocks];
  return { ...page, blocks: reorder(nextBlocks) };
}

function syncFooterNavigationForPage(
  page: EditorPage,
  pages: EditorPage[],
  hostname: string | null,
): EditorPage {
  const footerIndex = page.blocks.findIndex(
    (block) => block.blockType === "footer",
  );
  const currentFooter = footerIndex >= 0 ? page.blocks[footerIndex] : undefined;
  const currentData = currentFooter ? currentFooter.data : {};
  const navLinks = pageNavLinks(pages);
  const pageSlugs = new Set(
    navLinks.map((link) => normalizeSlugPath(link.href)),
  );
  const preservedLinks = arrayOfObjects(currentData.links)
    .filter((link) => {
      const slug = internalSlugFromHref(text(link.href), hostname);
      return !slug || !pageSlugs.has(slug);
    })
    .map((link) => ({ href: text(link.href), label: text(link.label) }))
    .filter((link) => link.href && link.label);
  const nextFooter: PageBlock = {
    id: currentFooter?.id ?? newEditorBlockId("footer"),
    blockType: "footer",
    variant: currentFooter?.variant ?? "simple",
    order: currentFooter?.order ?? page.blocks.length,
    data: {
      ...currentData,
      businessName:
        text(currentData.businessName) || fallbackBusinessName(hostname, pages),
      links: mergeSiteNavLinks([...navLinks, ...preservedLinks]),
    },
  };
  const nextBlocks =
    footerIndex >= 0
      ? page.blocks.map((block, index) =>
          index === footerIndex ? nextFooter : block,
        )
      : [...page.blocks, nextFooter];
  return { ...page, blocks: reorder(nextBlocks) };
}

function addFooterBackLinkForPage(
  page: EditorPage,
  targetPage: EditorPage,
  pages: EditorPage[],
  hostname: string | null,
): EditorPage {
  const footerIndex = page.blocks.findIndex(
    (block) => block.blockType === "footer",
  );
  const currentFooter = footerIndex >= 0 ? page.blocks[footerIndex] : undefined;
  const currentData = currentFooter ? currentFooter.data : {};
  const links = arrayOfObjects(currentData.links);
  const targetSlug = normalizeSlugPath(targetPage.slug);
  const hasTarget = links.some((link) => {
    const slug =
      internalSlugFromHref(text(link.href), hostname) ??
      normalizeSlugPath(text(link.href));
    return slug === targetSlug;
  });
  if (hasTarget) return page;

  const nextFooter: PageBlock = {
    id: currentFooter?.id ?? newEditorBlockId("footer"),
    blockType: "footer",
    variant: currentFooter?.variant ?? "simple",
    order: currentFooter?.order ?? page.blocks.length,
    data: {
      ...currentData,
      businessName:
        text(currentData.businessName) || fallbackBusinessName(hostname, pages),
      links: appendSiteNavLink(links, {
        href: targetPage.slug,
        label: navLinkLabelForPage(targetPage),
      }),
    },
  };
  const nextBlocks =
    footerIndex >= 0
      ? page.blocks.map((block, index) =>
          index === footerIndex ? nextFooter : block,
        )
      : [...page.blocks, nextFooter];
  return { ...page, blocks: reorder(nextBlocks) };
}

function pageLinksToPage(
  page: EditorPage,
  targetPage: EditorPage,
  pages: EditorPage[],
  hostname: string | null,
): boolean {
  return collectLinks(page.blocks, pages, hostname).some(
    (link) => link.targetPage?.id === targetPage.id,
  );
}

export function connectCreatedPageToSite(
  pages: EditorPage[],
  createdPageId: string,
  sourcePageId: string | null,
  hostname: string | null,
): EditorPage[] {
  if (pages.length < 2) return pages;
  const withSyncedNav = pages.map((page) =>
    syncFooterNavigationForPage(
      syncHeaderNavigationForPage(page, pages, hostname),
      pages,
      hostname,
    ),
  );
  const byId = new Map(withSyncedNav.map((page) => [page.id, page]));
  const createdPage = byId.get(createdPageId);
  if (!createdPage) return withSyncedNav;

  const sourcePage =
    (sourcePageId && sourcePageId !== createdPageId
      ? byId.get(sourcePageId)
      : null) ??
    withSyncedNav.find(
      (page) =>
        page.id !== createdPageId && normalizeSlugPath(page.slug) === "/",
    ) ??
    withSyncedNav.find((page) => page.id !== createdPageId) ??
    null;
  if (!sourcePage) return withSyncedNav;

  const currentPages = () => Array.from(byId.values());
  const hasIncomingToCreated = currentPages().some(
    (page) =>
      page.id !== createdPageId &&
      pageLinksToPage(
        page,
        byId.get(createdPageId) ?? createdPage,
        currentPages(),
        hostname,
      ),
  );
  if (!hasIncomingToCreated) {
    byId.set(
      sourcePage.id,
      addFooterBackLinkForPage(
        byId.get(sourcePage.id) ?? sourcePage,
        byId.get(createdPageId) ?? createdPage,
        currentPages(),
        hostname,
      ),
    );
  }

  const latestCreated = byId.get(createdPageId) ?? createdPage;
  const latestSource = byId.get(sourcePage.id) ?? sourcePage;
  if (!pageLinksToPage(latestCreated, latestSource, currentPages(), hostname)) {
    byId.set(
      createdPageId,
      addFooterBackLinkForPage(
        latestCreated,
        latestSource,
        currentPages(),
        hostname,
      ),
    );
  }

  return withSyncedNav.map((page) => byId.get(page.id) ?? page);
}

export function prepareSitePagesForPublish(
  pages: EditorPage[],
  hostname: string | null,
): EditorPage[] {
  const withSyncedNav = pages.map((page) =>
    syncFooterNavigationForPage(
      syncHeaderNavigationForPage(page, pages, hostname),
      pages,
      hostname,
    ),
  );
  const byId = new Map(withSyncedNav.map((page) => [page.id, page]));

  for (const sourcePage of withSyncedNav) {
    const currentPages = Array.from(byId.values());
    const linkedTargets = new Map<string, EditorPage>();
    collectLinks(sourcePage.blocks, currentPages, hostname).forEach((link) => {
      if (link.targetPage && link.targetPage.id !== sourcePage.id) {
        linkedTargets.set(link.targetPage.id, link.targetPage);
      }
    });

    for (const targetPage of linkedTargets.values()) {
      const currentTarget = byId.get(targetPage.id) ?? targetPage;
      const latestPages = Array.from(byId.values());
      if (pageLinksToPage(currentTarget, sourcePage, latestPages, hostname)) {
        continue;
      }
      byId.set(
        targetPage.id,
        addFooterBackLinkForPage(
          currentTarget,
          sourcePage,
          latestPages,
          hostname,
        ),
      );
    }
  }

  return withSyncedNav.map((page) => byId.get(page.id) ?? page);
}

function reorder(blocks: PageBlock[]): PageBlock[] {
  return blocks.map((block, order) => ({ ...block, order }));
}

function parsePath(path: string): PathSegment[] {
  return path
    .split(".")
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part));
}

function getPathValue(data: Record<string, unknown>, path: string): unknown {
  const richTextValue = richTextTargetValue(data, path);
  if (richTextValue !== null) return richTextValue;
  return parsePath(path).reduce<unknown>((current, segment) => {
    if (current === null || current === undefined) return undefined;
    if (typeof segment === "number") {
      return Array.isArray(current) ? current[segment] : undefined;
    }
    return typeof current === "object"
      ? (current as Record<string, unknown>)[segment]
      : undefined;
  }, data);
}

function editablePathText(block: PageBlock, path: string): string {
  const value = text(getPathValue(block.data, path));
  if (value) return value;
  if (block.blockType !== "contactForm") return value;
  return CONTACT_FORM_TEXT_DEFAULTS[path] ?? value;
}

export function canvasTargetValuePreview(
  block: PageBlock | null,
  target: {
    altPath?: string;
    hrefPath?: string;
    kind: "image" | "link" | "text" | "textarea";
    path: string;
  } | null,
  maxLength = 96,
): string | null {
  if (!block || !target) return null;

  const value = editablePathText(block, target.path).trim();
  const href =
    target.kind === "link" && target.hrefPath
      ? text(getPathValue(block.data, target.hrefPath)).trim()
      : "";
  const alt =
    target.kind === "image" && target.altPath
      ? text(getPathValue(block.data, target.altPath)).trim()
      : "";
  const preview =
    target.kind === "image"
      ? [alt ? `Alt: ${alt}` : "", value].filter(Boolean).join(" ")
      : [value, href && href !== value ? `-> ${href}` : ""]
          .filter(Boolean)
          .join(" ");
  const normalizedPreview = preview.replace(/\s+/g, " ").trim();
  if (!normalizedPreview) return null;

  const limit = Math.max(16, maxLength);
  if (normalizedPreview.length <= limit) return normalizedPreview;
  return `${normalizedPreview.slice(0, limit - 3).trimEnd()}...`;
}

export function canvasElementFrameValuePreview(
  blocks: PageBlock[],
  frame: { blockId: string; target: EditableElementTarget | null } | null,
  maxLength = 72,
): string | null {
  if (!frame?.target) return null;
  const block = blocks.find((candidate) => candidate.id === frame.blockId);
  return canvasTargetValuePreview(block ?? null, frame.target, maxLength);
}

function setPathValue(
  data: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const richTextValue = setRichTextTargetValue(data, path, value);
  if (richTextValue !== null) return richTextValue;
  const parts = parsePath(path);
  if (parts.length === 0) return data;

  const setAt = (current: unknown, index: number): unknown => {
    const part = parts[index];
    if (part === undefined) return current;
    const isLeaf = index === parts.length - 1;
    if (typeof part === "number") {
      const next = Array.isArray(current) ? [...current] : [];
      next[part] = isLeaf ? value : setAt(next[part], index + 1);
      return next;
    }

    const next =
      current && typeof current === "object" && !Array.isArray(current)
        ? { ...(current as Record<string, unknown>) }
        : {};
    next[part] = isLeaf ? value : setAt(next[part], index + 1);
    return next;
  };

  return setAt(data, 0) as Record<string, unknown>;
}

function findPreviewBlock(doc: Document, id: string): HTMLElement | undefined {
  return Array.from(
    doc.querySelectorAll<HTMLElement>("[data-gb-block-id]"),
  ).find((node) => node.dataset.gbBlockId === id);
}

function previewElementLabel(node: HTMLElement): string {
  const tag = node.tagName.toLowerCase();
  if (tag === "img") return "Image";
  if (tag === "form") return "Form";
  if (tag === "a") return "Link";
  if (tag === "button") return "Button";
  if (["input", "textarea", "select", "label"].includes(tag)) return "Field";
  if (tag.startsWith("h")) return "Heading";
  if (tag === "li" || node.classList.contains("gb-card")) return "Item";
  return "Text";
}

function previewElementKey(
  blockId: string,
  node: HTMLElement,
  blockNode: HTMLElement,
): string | null {
  const elements = Array.from(
    blockNode.querySelectorAll<HTMLElement>(PREVIEW_ELEMENT_SELECTOR),
  );
  const index = elements.indexOf(node);
  return index >= 0 ? `${blockId}:${index}` : null;
}

export function previewElementIndexFromKey(
  blockId: string,
  key: string,
): number | null {
  const prefix = `${blockId}:`;
  if (!key.startsWith(prefix)) return null;
  const rawIndex = key.slice(prefix.length);
  if (!/^\d+$/.test(rawIndex)) return null;
  const index = Number(rawIndex);
  return Number.isSafeInteger(index) ? index : null;
}

function findPreviewElementByKey(
  doc: Document,
  blockId: string,
  key: string,
): HTMLElement | null {
  const blockNode = findPreviewBlock(doc, blockId);
  const index = previewElementIndexFromKey(blockId, key);
  if (!blockNode || index === null) return null;
  return (
    blockNode.querySelectorAll<HTMLElement>(PREVIEW_ELEMENT_SELECTOR)[index] ??
    null
  );
}

function closestPreviewElement(
  target: Element,
  blockNode: HTMLElement,
): HTMLElement | null {
  const node = target.closest<HTMLElement>(PREVIEW_ELEMENT_SELECTOR);
  if (!node || node === blockNode || !blockNode.contains(node)) return null;
  return node;
}

function readEditableTarget(
  blockId: string,
  node: HTMLElement,
): EditableElementTarget | null {
  const path = node.dataset.gbFieldPath;
  const label = node.dataset.gbFieldLabel;
  const kind = node.dataset.gbFieldKind as EditableFieldKind | undefined;
  if (!path || !label || !kind) return null;
  return {
    blockId,
    path,
    label,
    kind,
    hrefPath: node.dataset.gbHrefPath,
    altPath: node.dataset.gbAltPath,
  };
}

function canInlineEditTarget(
  target: EditableElementTarget | null,
): target is EditableElementTarget {
  return Boolean(
    target &&
      (target.kind === "text" ||
        target.kind === "textarea" ||
        target.kind === "link"),
  );
}

function canTargetAiEdit(
  target: EditableElementTarget | null,
): target is EditableElementTarget {
  return Boolean(target && !parseRichTextPath(target.path));
}

function markEditable(
  node: HTMLElement | null | undefined,
  target: Omit<EditableElementTarget, "blockId">,
): void {
  if (!node) return;
  node.dataset.gbFieldPath = target.path;
  node.dataset.gbFieldLabel = target.label;
  node.dataset.gbFieldKind = target.kind;
  if (target.hrefPath) node.dataset.gbHrefPath = target.hrefPath;
  if (target.altPath) node.dataset.gbAltPath = target.altPath;
}

function annotatePreviewEditableFields(
  doc: Document,
  blocks: PageBlock[],
): void {
  for (const block of blocks) {
    const blockNode = findPreviewBlock(doc, block.id);
    if (!blockNode) continue;
    annotateBlockFields(blockNode, block);
  }
}

function applyPreviewEditingState(
  doc: Document,
  selectedTarget: EditableElementTarget | null,
  mode: CanvasMode,
): void {
  doc.documentElement.dataset.gbCanvasMode = mode;
  let style = doc.getElementById("gb-editor-selection-style");
  if (!style) {
    style = doc.createElement("style");
    style.id = "gb-editor-selection-style";
    doc.head.appendChild(style);
  }
  style.textContent = `
    [data-gb-field-path] {
      cursor: ${mode === "edit" ? "crosshair" : "inherit"};
    }
    html[data-gb-canvas-mode="edit"] [data-gb-field-path]:hover {
      outline: 2px solid rgba(15, 118, 110, 0.78);
      outline-offset: 4px;
    }
    html[data-gb-canvas-mode="edit"] [data-gb-field-selected="true"] {
      outline: 2px solid rgba(15, 118, 110, 0.9);
      outline-offset: 4px;
    }
    html[data-gb-canvas-mode="browse"] a[href] {
      cursor: pointer;
    }
  `;

  for (const node of Array.from(
    doc.querySelectorAll<HTMLElement>("[data-gb-field-selected]"),
  )) {
    delete node.dataset.gbFieldSelected;
  }
  if (!selectedTarget) return;
  for (const node of Array.from(
    doc.querySelectorAll<HTMLElement>("[data-gb-field-path]"),
  )) {
    const block = node.closest<HTMLElement>("[data-gb-block-id]");
    if (
      block?.dataset.gbBlockId === selectedTarget.blockId &&
      node.dataset.gbFieldPath === selectedTarget.path
    ) {
      node.dataset.gbFieldSelected = "true";
      return;
    }
  }
}

function annotateBlockFields(blockNode: HTMLElement, block: PageBlock): void {
  const first = <T extends HTMLElement>(selector: string) =>
    blockNode.querySelector<T>(selector);
  const all = <T extends HTMLElement>(selector: string) =>
    Array.from(blockNode.querySelectorAll<T>(selector));

  if (block.blockType === "header") {
    markEditable(first(".gb-header__brand"), {
      path: "brandName",
      label: "Brand name",
      kind: "text",
    });
    all<HTMLAnchorElement>(".gb-header__nav a").forEach((link, index) => {
      markEditable(link, {
        path: `links.${index}.label`,
        hrefPath: `links.${index}.href`,
        label: `Header link ${index + 1}`,
        kind: "link",
      });
    });
    markEditable(first(".gb-header__cta"), {
      path: "ctaLabel",
      hrefPath: "ctaHref",
      label: "Header action",
      kind: "link",
    });
    return;
  }

  if (block.blockType === "heroSection") {
    markEditable(first("h1"), {
      path: "headline",
      label: "Hero headline",
      kind: "text",
    });
    markEditable(first(".gb-hero__sub"), {
      path: "subheadline",
      label: "Hero subheadline",
      kind: "textarea",
    });
    const buttons = all<HTMLAnchorElement>(".gb-hero__ctas a");
    markEditable(buttons[0], {
      path: "primaryCta.label",
      hrefPath: "primaryCta.href",
      label: "Primary button",
      kind: "link",
    });
    markEditable(buttons[1], {
      path: "secondaryCta.label",
      hrefPath: "secondaryCta.href",
      label: "Secondary button",
      kind: "link",
    });
    markEditable(first<HTMLImageElement>("img"), {
      path: "image.src",
      altPath: "image.alt",
      label: "Hero image",
      kind: "image",
    });
    return;
  }

  if (block.blockType === "services") {
    markEditable(first(".gb-services__header h2"), {
      path: "heading",
      label: "Services heading",
      kind: "text",
    });
    markEditable(first(".gb-services__header p"), {
      path: "intro",
      label: "Services intro",
      kind: "textarea",
    });
    all(".gb-services__item").forEach((item, index) => {
      const heading = item.querySelector<HTMLElement>("h3");
      const link = item.querySelector<HTMLAnchorElement>("h3 a");
      markEditable(link ?? heading, {
        path: `items.${index}.title`,
        hrefPath: `items.${index}.href`,
        label: `Service ${index + 1} title`,
        kind: link ? "link" : "text",
      });
      markEditable(item.querySelector<HTMLElement>("p"), {
        path: `items.${index}.description`,
        label: `Service ${index + 1} description`,
        kind: "textarea",
      });
    });
    return;
  }

  if (block.blockType === "stats") {
    markEditable(first(".gb-stats__header h2"), {
      path: "heading",
      label: "Stats heading",
      kind: "text",
    });
    markEditable(first(".gb-stats__header p"), {
      path: "intro",
      label: "Stats intro",
      kind: "textarea",
    });
    all(".gb-stats__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLElement>("dt"), {
        path: `items.${index}.value`,
        label: `Stat ${index + 1} value`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-stats__label"), {
        path: `items.${index}.label`,
        label: `Stat ${index + 1} label`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-stats__description"), {
        path: `items.${index}.description`,
        label: `Stat ${index + 1} description`,
        kind: "textarea",
      });
    });
    return;
  }

  if (block.blockType === "testimonials") {
    markEditable(first(".gb-testimonials__header h2"), {
      path: "heading",
      label: "Testimonials heading",
      kind: "text",
    });
    markEditable(first(".gb-testimonials__header p"), {
      path: "intro",
      label: "Testimonials intro",
      kind: "textarea",
    });
    all(".gb-testimonials__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLElement>("blockquote"), {
        path: `items.${index}.quote`,
        label: `Testimonial ${index + 1} quote`,
        kind: "textarea",
      });
      markEditable(item.querySelector<HTMLElement>("figcaption strong"), {
        path: `items.${index}.name`,
        label: `Testimonial ${index + 1} name`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>("figcaption span"), {
        path: `items.${index}.role`,
        label: `Testimonial ${index + 1} role`,
        kind: "text",
      });
    });
    return;
  }

  if (block.blockType === "newsList") {
    markEditable(first(".gb-news__header h2"), {
      path: "heading",
      label: "News heading",
      kind: "text",
    });
    markEditable(first(".gb-news__header p"), {
      path: "intro",
      label: "News intro",
      kind: "textarea",
    });
    all(".gb-news__item").forEach((item, index) => {
      const heading = item.querySelector<HTMLElement>("h3");
      const link = item.querySelector<HTMLAnchorElement>("h3 a");
      markEditable(link ?? heading, {
        path: `items.${index}.title`,
        hrefPath: `items.${index}.href`,
        label: `Update ${index + 1} title`,
        kind: link ? "link" : "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-news__excerpt"), {
        path: `items.${index}.excerpt`,
        label: `Update ${index + 1} excerpt`,
        kind: "textarea",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-news__meta span"), {
        path: `items.${index}.tag`,
        label: `Update ${index + 1} category`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-news__meta time"), {
        path: `items.${index}.date`,
        label: `Update ${index + 1} date`,
        kind: "text",
      });
    });
    return;
  }

  if (block.blockType === "gallery") {
    markEditable(first(".gb-gallery__header h2"), {
      path: "heading",
      label: "Gallery heading",
      kind: "text",
    });
    markEditable(first(".gb-gallery__header p"), {
      path: "intro",
      label: "Gallery intro",
      kind: "textarea",
    });
    all(".gb-gallery__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLImageElement>("img"), {
        path: `items.${index}.src`,
        altPath: `items.${index}.alt`,
        label: `Image ${index + 1}`,
        kind: "image",
      });
      const title = item.querySelector<HTMLElement>(".gb-gallery__caption h3");
      const link = item.matches("a")
        ? (item as HTMLAnchorElement)
        : item.querySelector<HTMLAnchorElement>("a");
      markEditable(title, {
        path: `items.${index}.title`,
        hrefPath: link ? `items.${index}.href` : undefined,
        label: `Image ${index + 1} title`,
        kind: link ? "link" : "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-gallery__caption p"), {
        path: `items.${index}.caption`,
        label: `Image ${index + 1} caption`,
        kind: "textarea",
      });
    });
    return;
  }

  if (block.blockType === "pricing") {
    markEditable(first(".gb-pricing__header h2"), {
      path: "heading",
      label: "Pricing heading",
      kind: "text",
    });
    markEditable(first(".gb-pricing__header p"), {
      path: "intro",
      label: "Pricing intro",
      kind: "textarea",
    });
    all(".gb-pricing__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLElement>("h3"), {
        path: `items.${index}.name`,
        label: `Plan ${index + 1} name`,
        kind: "text",
      });
      markEditable(
        item.querySelector<HTMLElement>(".gb-pricing__price strong"),
        {
          path: `items.${index}.price`,
          label: `Plan ${index + 1} price`,
          kind: "text",
        },
      );
      markEditable(item.querySelector<HTMLElement>(".gb-pricing__cadence"), {
        path: `items.${index}.cadence`,
        label: `Plan ${index + 1} cadence`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>("p.gb-muted"), {
        path: `items.${index}.description`,
        label: `Plan ${index + 1} description`,
        kind: "textarea",
      });
      Array.from(item.querySelectorAll<HTMLElement>("li")).forEach(
        (feature, featureIndex) => {
          markEditable(feature, {
            path: `items.${index}.features.${featureIndex}`,
            label: `Plan ${index + 1} feature ${featureIndex + 1}`,
            kind: "text",
          });
        },
      );
      markEditable(item.querySelector<HTMLAnchorElement>("a"), {
        path: `items.${index}.ctaLabel`,
        hrefPath: `items.${index}.ctaHref`,
        label: `Plan ${index + 1} button`,
        kind: "link",
      });
    });
    return;
  }

  if (block.blockType === "team") {
    markEditable(first(".gb-team__header h2"), {
      path: "heading",
      label: "Team heading",
      kind: "text",
    });
    markEditable(first(".gb-team__header p"), {
      path: "intro",
      label: "Team intro",
      kind: "textarea",
    });
    all(".gb-team__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLImageElement>("img"), {
        path: `items.${index}.image`,
        altPath: `items.${index}.imageAlt`,
        label: `Team member ${index + 1} image`,
        kind: "image",
      });
      markEditable(item.querySelector<HTMLElement>("h3"), {
        path: `items.${index}.name`,
        label: `Team member ${index + 1} name`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-team__role"), {
        path: `items.${index}.role`,
        label: `Team member ${index + 1} role`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>("p.gb-muted"), {
        path: `items.${index}.bio`,
        label: `Team member ${index + 1} bio`,
        kind: "textarea",
      });
    });
    return;
  }

  if (block.blockType === "openingHours") {
    markEditable(first(".gb-hours__header h2"), {
      path: "heading",
      label: "Hours heading",
      kind: "text",
    });
    markEditable(first(".gb-hours__header p"), {
      path: "intro",
      label: "Hours intro",
      kind: "textarea",
    });
    all(".gb-hours__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLElement>("dt"), {
        path: `items.${index}.day`,
        label: `Hours row ${index + 1} day`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-hours__time"), {
        path: `items.${index}.hours`,
        label: `Hours row ${index + 1} time`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-hours__note"), {
        path: `items.${index}.note`,
        label: `Hours row ${index + 1} note`,
        kind: "text",
      });
    });
    return;
  }

  if (block.blockType === "textImage") {
    markEditable(first(".gb-text-image__content h2"), {
      path: "heading",
      label: "Section heading",
      kind: "text",
    });
    markEditable(first(".gb-text-image__content p"), {
      path: "body",
      label: "Body text",
      kind: "textarea",
    });
    markEditable(first(".gb-text-image__content a"), {
      path: "cta.label",
      hrefPath: "cta.href",
      label: "Section button",
      kind: "link",
    });
    markEditable(first<HTMLImageElement>("img"), {
      path: "image.src",
      altPath: "image.alt",
      label: "Section image",
      kind: "image",
    });
    return;
  }

  if (block.blockType === "ctaBanner") {
    markEditable(first(".gb-cta-banner__copy h2"), {
      path: "heading",
      label: "CTA heading",
      kind: "text",
    });
    markEditable(first(".gb-cta-banner__text"), {
      path: "text",
      label: "CTA text",
      kind: "textarea",
    });
    markEditable(first(".gb-cta-banner a"), {
      path: "cta.label",
      hrefPath: "cta.href",
      label: "CTA button",
      kind: "link",
    });
    return;
  }

  if (block.blockType === "contactForm") {
    markEditable(first(".gb-contact h2"), {
      path: "heading",
      label: "Form heading",
      kind: "text",
    });
    markEditable(first(".gb-contact__intro"), {
      path: "intro",
      label: "Form intro",
      kind: "textarea",
    });
    markEditable(first(".gb-label[for$='-name']"), {
      path: "nameLabel",
      label: "Name label",
      kind: "text",
    });
    markEditable(first("input[name='name']"), {
      path: "namePlaceholder",
      label: "Name placeholder",
      kind: "text",
    });
    markEditable(first(".gb-label[for$='-email']"), {
      path: "emailLabel",
      label: "Email label",
      kind: "text",
    });
    markEditable(first("input[name='email']"), {
      path: "emailPlaceholder",
      label: "Email placeholder",
      kind: "text",
    });
    markEditable(first(".gb-label[for$='-phone']"), {
      path: "phoneLabel",
      label: "Phone label",
      kind: "text",
    });
    markEditable(first("input[name='phone']"), {
      path: "phonePlaceholder",
      label: "Phone placeholder",
      kind: "text",
    });
    markEditable(first(".gb-label[for$='-message']"), {
      path: "messageLabel",
      label: "Message label",
      kind: "text",
    });
    markEditable(first("textarea[name='message']"), {
      path: "messagePlaceholder",
      label: "Message placeholder",
      kind: "text",
    });
    markEditable(first(".gb-form button[type='submit']"), {
      path: "submitLabel",
      label: "Submit button",
      kind: "text",
    });
    return;
  }

  if (block.blockType === "faqAccordion") {
    markEditable(first(".gb-faq h2"), {
      path: "heading",
      label: "FAQ heading",
      kind: "text",
    });
    all(".gb-faq__item").forEach((item, index) => {
      markEditable(item.querySelector<HTMLElement>("summary"), {
        path: `items.${index}.question`,
        label: `Question ${index + 1}`,
        kind: "text",
      });
      markEditable(item.querySelector<HTMLElement>(".gb-faq__body"), {
        path: `items.${index}.answer`,
        label: `Answer ${index + 1}`,
        kind: "textarea",
      });
    });
    return;
  }

  if (block.blockType === "footer") {
    markEditable(first(".gb-footer__name"), {
      path: "businessName",
      label: "Business name",
      kind: "text",
    });
    markEditable(first(".gb-footer__tagline"), {
      path: "tagline",
      label: "Footer tagline",
      kind: "text",
    });
    all<HTMLAnchorElement>(".gb-footer__links a").forEach((link, index) => {
      markEditable(link, {
        path: `links.${index}.label`,
        hrefPath: `links.${index}.href`,
        label: `Footer link ${index + 1}`,
        kind: "link",
      });
    });
    all<HTMLAnchorElement>(".gb-footer__legal-links a").forEach(
      (link, index) => {
        markEditable(link, {
          path: `legalLinks.${index}.label`,
          hrefPath: `legalLinks.${index}.href`,
          label: `Legal link ${index + 1}`,
          kind: "link",
        });
      },
    );
    return;
  }

  if (block.blockType === "richText") {
    const prose = first(".gb-prose");
    if (!prose) return;
    const nodes = richTextEditableNodes(prose);
    nodes.forEach((node, index) => {
      const tag = node.tagName.toLowerCase();
      if (tag === "img") {
        markEditable(node, {
          path: richTextPath(index, "src"),
          altPath: richTextPath(index, "alt"),
          label: richTextNodeLabel(node, index),
          kind: "image",
        });
        return;
      }
      if (tag === "a") {
        markEditable(node, {
          path: richTextPath(index, "text"),
          hrefPath: richTextPath(index, "href"),
          label: richTextNodeLabel(node, index),
          kind: "link",
        });
        return;
      }
      markEditable(node, {
        path: richTextPath(index, "text"),
        label: richTextNodeLabel(node, index),
        kind: tag.startsWith("h") || tag === "li" ? "text" : "textarea",
      });
    });
    if (nodes.length === 0) {
      markEditable(prose, {
        path: "html",
        label: "Rich text content",
        kind: "textarea",
      });
    }
  }
}

function previewElementFrame(
  blockId: string,
  node: HTMLElement,
  blockNode: HTMLElement,
): ElementFrame | null {
  const key = previewElementKey(blockId, node, blockNode);
  const rect = node.getBoundingClientRect();
  if (!key || rect.width <= 0 || rect.height <= 0) return null;
  const target = readEditableTarget(blockId, node);
  const width = target
    ? Math.max(rect.width, EDITABLE_TARGET_MIN_WIDTH)
    : rect.width;
  const height = target
    ? Math.max(rect.height, EDITABLE_TARGET_MIN_HEIGHT)
    : rect.height;
  const left = Math.max(0, rect.left - (width - rect.width) / 2);
  const top = Math.max(0, rect.top - (height - rect.height) / 2);
  return {
    blockId,
    key,
    label:
      node.dataset.gbFieldLabel ?? target?.label ?? previewElementLabel(node),
    sectionLabel: blockLabel(blockNode.dataset.gbBlockType ?? "") || "Section",
    top,
    left,
    width,
    height,
    target,
  };
}

function elementFrameActionLabel(frame: ElementFrame): string {
  return targetActionLabel(frame.target);
}

function targetActionLabel(target: EditableElementTarget | null): string {
  if (!target) return "Inspect";
  if (target.kind === "image") return "Edit image";
  if (target.kind === "link") return "Edit link";
  return "Edit copy";
}

function targetKindLabel(target: EditableElementTarget | null): string {
  if (!target) return "Inspect";
  if (target.kind === "image") return "Image";
  if (target.kind === "link") return "Link";
  return "Copy";
}

function selectionKindLabel(frame: CanvasSelectionFrame): string {
  return frame.kind === "block" ? "Area" : targetKindLabel(frame.target);
}

export function selectionStepperSummary({
  elementCount,
  elementIndex,
  selectionKind,
}: {
  elementCount: number;
  elementIndex: number;
  selectionKind: "block" | "element";
}): string {
  if (elementCount <= 0) return "0";
  if (selectionKind === "block" || elementIndex < 0) {
    return `${elementCount} field${elementCount === 1 ? "" : "s"}`;
  }
  return `${elementIndex + 1} / ${elementCount}`;
}

function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function canvasHudPosition(
  frame: CanvasSelectionFrame,
  viewport: PreviewViewport,
): CanvasHudPosition | null {
  if (viewport.width <= 0 || viewport.height <= 0) return null;

  const edge = 12;
  const estimatedHeight = frame.kind === "block" ? 94 : 58;
  const maxWidth = Math.min(560, Math.max(180, viewport.width - edge * 2));
  const maxLeft = Math.max(edge, viewport.width - maxWidth - edge);
  const left = clampValue(
    frame.left + frame.width / 2 - maxWidth / 2,
    edge,
    maxLeft,
  );

  const belowTop = frame.top + frame.height + 10;
  const aboveTop = frame.top - estimatedHeight - 10;
  const fallbackTop = frame.kind === "block" ? frame.top + 10 : frame.top + 4;
  const top =
    belowTop + estimatedHeight + edge <= viewport.height
      ? belowTop
      : aboveTop >= edge
        ? aboveTop
        : clampValue(
            fallbackTop,
            edge,
            Math.max(edge, viewport.height - estimatedHeight - edge),
          );

  return {
    left: Math.round(left),
    maxWidth: Math.round(maxWidth),
    top: Math.round(top),
  };
}

export function floatingPanelPosition(
  frame: Pick<ElementFrame, "height" | "left" | "top" | "width">,
  viewport: PreviewViewport,
  options: {
    estimatedHeight: number;
    gap?: number;
    maxWidth: number;
    minWidth: number;
  },
): FloatingPanelPosition {
  const edge = 8;
  const gap = options.gap ?? 10;
  const viewportWidth = Math.max(0, viewport.width);
  const viewportHeight = Math.max(0, viewport.height);
  const availableWidth =
    viewportWidth > 0
      ? Math.max(120, viewportWidth - edge * 2)
      : options.maxWidth;
  const preferredWidth = Math.max(
    options.minWidth,
    Math.min(frame.width, options.maxWidth),
  );
  const width = Math.round(Math.min(preferredWidth, availableWidth));
  const maxLeft =
    viewportWidth > 0
      ? Math.max(edge, viewportWidth - width - edge)
      : Math.max(edge, Math.round(frame.left));
  const left = clampValue(
    Math.round(frame.left + frame.width / 2 - width / 2),
    edge,
    maxLeft,
  );

  if (viewportHeight <= 0) {
    return {
      left,
      top: Math.max(edge, Math.round(frame.top)),
      width,
    };
  }

  const estimatedHeight = Math.max(40, options.estimatedHeight);
  const belowTop = frame.top + frame.height + gap;
  const aboveTop = frame.top - estimatedHeight - gap;
  const maxTop = Math.max(edge, viewportHeight - estimatedHeight - edge);
  const top =
    belowTop + estimatedHeight + edge <= viewportHeight
      ? belowTop
      : aboveTop >= edge
        ? aboveTop
        : clampValue(frame.top, edge, maxTop);

  return {
    left,
    top: Math.round(top),
    width,
  };
}

export function canvasElementActionPosition(
  frame: Pick<ElementFrame, "height" | "left" | "top" | "width">,
  viewport: PreviewViewport,
  options: { placement?: "near-target" | "mobile-bottom-dock" } = {},
): FloatingPanelPosition {
  if (options.placement === "mobile-bottom-dock" && viewport.width > 0) {
    const edge = 10;
    const width = Math.max(220, viewport.width - edge * 2);
    const estimatedHeight = 62;
    return {
      left: edge,
      top: Math.max(edge, Math.round(viewport.height - estimatedHeight - edge)),
      width,
    };
  }

  return floatingPanelPosition(frame, viewport, {
    estimatedHeight: 48,
    gap: 8,
    maxWidth: 360,
    minWidth: 220,
  });
}

export function floatingEditorChipPosition(
  frame: Pick<ElementFrame, "height" | "left" | "top" | "width">,
  viewport: PreviewViewport,
): FloatingPanelPosition {
  return floatingPanelPosition(frame, viewport, {
    estimatedHeight: 42,
    gap: 6,
    maxWidth: 300,
    minWidth: 180,
  });
}

export function canvasRailInspectorLabel(
  frame: Pick<CanvasSelectionFrame, "kind" | "target"> | null,
): string {
  return frame?.kind === "element" && frame.target
    ? "Open element inspector"
    : "Open inspector panel";
}

function selectionStatusLabel(
  frame: CanvasSelectionFrame,
  sectionPosition?: number | null,
  totalSections?: number,
): string {
  if (frame.kind === "block") {
    return sectionPosition && totalSections
      ? `Area ${sectionPosition} of ${totalSections}`
      : "Area";
  }
  if (!frame.target) return "Inspect only";
  if (frame.target.kind === "image") return "Image source and alt text";
  if (frame.target.kind === "link") return "Text and link target";
  return frame.target.kind === "textarea" ? "Long-form copy" : "Text copy";
}

export function SiteEditor({ api, hostname }: SiteEditorProps) {
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const previewCleanupRef = useRef<(() => void) | null>(null);
  const overlayStackRef = useRef<HTMLDivElement | null>(null);
  const selectionShortcutHandlerRef = useRef<
    ((event: KeyboardEvent) => void) | null
  >(null);
  const draftHistoryRef = useRef<DraftHistory>({ future: [], past: [] });
  const clearViewSnapshotRef = useRef<ClearViewSnapshot | null>(null);
  const selectedBlockIdRef = useRef<string | null>(null);
  const hoveredElementClearTimerRef = useRef<number | null>(null);
  const suppressCanvasSelectionUntilRef = useRef(0);
  const canvasModeRef = useRef<CanvasMode>("edit");
  const hostnameRef = useRef<string | null>(hostname);
  const pagesRef = useRef<EditorPage[]>([]);
  const switchPageRef = useRef<(pageId: string) => void>(() => {});
  const [pages, setPages] = useState<EditorPage[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaStorage, setMediaStorage] = useState<MediaStorage | null>(null);
  const [importedCms, setImportedCms] = useState<ImportedCms | null>(null);
  const [selectedBlogPostId, setSelectedBlogPostId] = useState<string | null>(
    null,
  );
  const [blogDraft, setBlogDraft] = useState<BlogPostEditorDraft | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditorPage | null>(null);
  const [theme, setTheme] = useState<ThemeInput | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [selectedElementKey, setSelectedElementKey] = useState<string | null>(
    null,
  );
  const [selectedElementTarget, setSelectedElementTarget] =
    useState<EditableElementTarget | null>(null);
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);
  const [hoveredElementFrame, setHoveredElementFrame] =
    useState<ElementFrame | null>(null);
  const [blockFrames, setBlockFrames] = useState<BlockFrame[]>([]);
  const [elementFrames, setElementFrames] = useState<ElementFrame[]>([]);
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>({
    height: 0,
    width: 0,
  });
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [canvasMode, setCanvasMode] = useState<CanvasMode>("edit");
  const [canvasSize, setCanvasSize] = useState<CanvasSize>(() =>
    initialCanvasSizeForViewport(
      typeof window === "undefined" ? null : window.innerWidth,
    ),
  );
  const [isStructurePanelOpen, setIsStructurePanelOpen] = useState(
    () => guidedEditorEntryState().isStructurePanelOpen,
  );
  const [isPageRailCollapsed, setIsPageRailCollapsed] = useState(
    () => guidedEditorEntryState().isPageRailCollapsed,
  );
  const [isTargetMapVisible, setIsTargetMapVisible] = useState(
    () => guidedEditorEntryState().isTargetMapVisible,
  );
  const [arePanelsHidden, setArePanelsHidden] = useState(
    () => guidedEditorEntryState().arePanelsHidden,
  );
  const [isOutlineVisible, setIsOutlineVisible] = useState(
    () => guidedEditorEntryState().isOutlineVisible,
  );
  const [isElementNavigatorCollapsed, setIsElementNavigatorCollapsed] =
    useState(() => guidedEditorEntryState().isElementNavigatorCollapsed);
  const [isClearView, setIsClearView] = useState(false);
  const [isCanvasChromeHidden, setIsCanvasChromeHidden] = useState(false);
  const [quickInspectorTargetKey, setQuickInspectorTargetKey] = useState<
    string | null
  >(null);
  const [inlineEditingKey, setInlineEditingKey] = useState<string | null>(null);
  const [isFocusCollapsed, setIsFocusCollapsed] = useState(true);
  const [isChatCollapsed, setIsChatCollapsed] = useState(true);
  const [isBlogCollapsed, setIsBlogCollapsed] = useState(true);
  const [isMediaCollapsed, setIsMediaCollapsed] = useState(true);
  const [isThemeCollapsed, setIsThemeCollapsed] = useState(true);
  const [chatPrompt, setChatPrompt] = useState(AGENT_CHAT_INITIAL_PROMPT);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatStorageKey, setChatStorageKey] = useState<string | null>(null);
  const [hasPendingAgentChange, setHasPendingAgentChange] = useState(false);
  const [agentDraftSource, setAgentDraftSource] =
    useState<AiEditPlanSource | null>(null);
  const [agentDraftReviewLabel, setAgentDraftReviewLabel] = useState<
    string | null
  >(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [previewRenderErrors, setPreviewRenderErrors] = useState<
    { blockType: string; error: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** True once a preview snapshot exists — gates the "Open preview" link. */
  const [hasPreview, setHasPreview] = useState(false);
  const [draftHistoryStatus, setDraftHistoryStatus] =
    useState<DraftHistoryStatus>({
      canRedo: false,
      canUndo: false,
    });
  const [savedDraftKey, setSavedDraftKey] = useState<string | null>(null);
  const [newPageTitle, setNewPageTitle] = useState("");
  const [newPageSlug, setNewPageSlug] = useState("");
  const [shouldLinkNewPage, setShouldLinkNewPage] = useState(true);

  const setCanvasPanelsHidden = useCallback((hidden: boolean) => {
    setArePanelsHidden(hidden);
    if (!hidden) return;
    setIsElementNavigatorCollapsed(true);
    setIsOutlineVisible(false);
    setIsTargetMapVisible(false);
  }, []);

  const setFullEditMode = useCallback(
    (enabled: boolean) => {
      setIsAdvancedMode(enabled);
      const nextState = enabled
        ? fullEditorEntryState()
        : guidedEditorEntryState();
      setIsPageRailCollapsed(nextState.isPageRailCollapsed);
      setIsStructurePanelOpen(nextState.isStructurePanelOpen);
      setCanvasPanelsHidden(nextState.arePanelsHidden);
      setIsOutlineVisible(nextState.isOutlineVisible);
      setIsTargetMapVisible(nextState.isTargetMapVisible);
      setIsElementNavigatorCollapsed(nextState.isElementNavigatorCollapsed);
      setIsFocusCollapsed(true);
      setIsChatCollapsed(true);
      setIsThemeCollapsed(true);
    },
    [setCanvasPanelsHidden],
  );

  const keepHoveredElementFrame = useCallback((frame: ElementFrame) => {
    if (hoveredElementClearTimerRef.current !== null) {
      window.clearTimeout(hoveredElementClearTimerRef.current);
      hoveredElementClearTimerRef.current = null;
    }
    setHoveredElementFrame(frame);
  }, []);

  const scheduleHoveredElementFrameClear = useCallback((key: string) => {
    if (hoveredElementClearTimerRef.current !== null) {
      window.clearTimeout(hoveredElementClearTimerRef.current);
    }
    hoveredElementClearTimerRef.current = window.setTimeout(() => {
      setHoveredElementFrame((current) =>
        current?.key === key ? null : current,
      );
      hoveredElementClearTimerRef.current = null;
    }, 120);
  }, []);

  useEffect(
    () => () => {
      if (hoveredElementClearTimerRef.current !== null) {
        window.clearTimeout(hoveredElementClearTimerRef.current);
      }
    },
    [],
  );

  const refreshDraftHistoryStatus = useCallback(() => {
    setDraftHistoryStatus({
      canRedo: draftHistoryRef.current.future.length > 0,
      canUndo: draftHistoryRef.current.past.length > 0,
    });
  }, []);

  const resetDraftHistory = useCallback((page: EditorPage | null) => {
    draftHistoryRef.current = { future: [], past: [] };
    setDraftHistoryStatus({ canRedo: false, canUndo: false });
    setSavedDraftKey(page ? draftVersionKey(page) : null);
  }, []);

  const selectedPage = useMemo(
    () => pages.find((page) => page.id === selectedId) ?? null,
    [pages, selectedId],
  );
  const mediaOptions = useMemo(
    () =>
      draft
        ? mediaOptionsWithLibrary(
            mediaOptionsForBlocks(draft.blocks),
            mediaItems,
          )
        : mediaOptionsWithLibrary([], mediaItems),
    [draft, mediaItems],
  );

  const selectedBlock = useMemo(
    () =>
      selectedBlockId
        ? (draft?.blocks.find((block) => block.id === selectedBlockId) ?? null)
        : null,
    [draft, selectedBlockId],
  );

  const selectedBlockIndex = useMemo(
    () =>
      draft && selectedBlock
        ? draft.blocks.findIndex((block) => block.id === selectedBlock.id)
        : -1,
    [draft, selectedBlock],
  );
  const selectedBlockVariants = useMemo(
    () =>
      selectedBlock ? (BLOCK_VARIANTS[selectedBlock.blockType] ?? []) : [],
    [selectedBlock],
  );

  const selectedElement = useMemo(
    () =>
      elementFrames.find((frame) => frame.key === selectedElementKey) ?? null,
    [elementFrames, selectedElementKey],
  );
  const effectiveTargetMapVisible =
    isAdvancedMode && isTargetMapVisible && isChatCollapsed;
  const isCleanFullscreenCanvas =
    isFullscreen &&
    isCanvasChromeHidden &&
    arePanelsHidden &&
    canvasMode === "edit" &&
    !isClearView &&
    !effectiveTargetMapVisible;
  const overlayElementFrameSource = useMemo(
    () =>
      isCleanFullscreenCanvas && selectedBlock?.id
        ? scopeElementFramesForCanvas(elementFrames, selectedBlock.id, false)
        : elementFrames,
    [elementFrames, isCleanFullscreenCanvas, selectedBlock?.id],
  );

  const overlayElementFrames = useMemo(
    () =>
      visibleElementFramesForCanvas(
        overlayElementFrameSource,
        selectedElementKey,
        hoveredElementFrame?.key ?? null,
        effectiveTargetMapVisible,
      ),
    [
      effectiveTargetMapVisible,
      hoveredElementFrame?.key,
      overlayElementFrameSource,
      selectedElementKey,
    ],
  );

  const navigableElementFrames = useMemo(
    () =>
      scopeElementFramesForCanvas(
        elementFrames,
        selectedBlock?.id ?? null,
        effectiveTargetMapVisible,
      ),
    [effectiveTargetMapVisible, elementFrames, selectedBlock?.id],
  );

  const selectedElementIndex = useMemo(
    () =>
      selectedElementKey
        ? navigableElementFrames.findIndex(
            (frame) => frame.key === selectedElementKey,
          )
        : -1,
    [navigableElementFrames, selectedElementKey],
  );

  const canvasElementActionBarVisibility = canvasElementActionBarState({
    arePanelsHidden,
    canvasMode,
    hasOverlayPanelOpen:
      !isFocusCollapsed ||
      !isChatCollapsed ||
      !isBlogCollapsed ||
      !isMediaCollapsed ||
      !isThemeCollapsed,
    hoveredElementHasTarget: Boolean(hoveredElementFrame?.target),
    hoveredElementKey: hoveredElementFrame?.key ?? null,
    inlineEditingKey,
    quickInspectorTargetKey,
    selectedElementHasTarget: Boolean(selectedElement?.target),
    selectedElementKey,
  });
  const focusedElementEditorMode = canvasFocusedElementEditorMode({
    inlineEditingKey,
    quickInspectorTargetKey,
    selectedElementKey,
  });
  const fieldTargetLabelMode = canvasFieldTargetLabelMode({
    arePanelsHidden,
    canvasMode,
    focusedElementEditorMode,
    hasFocusedFieldSurface: !isFocusCollapsed && Boolean(selectedElementKey),
    isAdvancedMode,
    targetMapVisible: effectiveTargetMapVisible,
  });
  const canvasElementActionFrame = useMemo(() => {
    if (canvasElementActionBarVisibility === "hidden") return null;
    if (canvasElementActionBarVisibility === "hovered") {
      return hoveredElementFrame?.target ? hoveredElementFrame : null;
    }
    if (selectedElement?.target) return selectedElement;
    return hoveredElementFrame?.target ? hoveredElementFrame : null;
  }, [canvasElementActionBarVisibility, hoveredElementFrame, selectedElement]);
  const canvasElementActionIndex = useMemo(
    () =>
      canvasElementActionFrame
        ? navigableElementFrames.findIndex(
            (frame) => frame.key === canvasElementActionFrame.key,
          )
        : -1,
    [canvasElementActionFrame, navigableElementFrames],
  );
  const canvasElementActionLinkTarget = useMemo(() => {
    if (!draft || !canvasElementActionFrame?.target) return null;
    const block =
      draft.blocks.find(
        (candidate) => candidate.id === canvasElementActionFrame.blockId,
      ) ?? null;
    return describeSelectedLinkTarget(
      block,
      canvasElementActionFrame.target,
      pages,
      hostname,
    );
  }, [canvasElementActionFrame, draft, hostname, pages]);
  const canvasElementActionValuePreview = useMemo(() => {
    if (!draft || !canvasElementActionFrame?.target) return null;
    const block =
      draft.blocks.find(
        (candidate) => candidate.id === canvasElementActionFrame.blockId,
      ) ?? null;
    return canvasTargetValuePreview(block, canvasElementActionFrame.target);
  }, [canvasElementActionFrame, draft]);
  const hasFocusedElementActions = Boolean(
    canvasElementActionBarVisibility === "selected" &&
      canvasElementActionFrame?.target &&
      canvasElementActionFrame.key === selectedElementKey,
  );

  const activeElementTarget = useMemo(
    () => selectedElement?.target ?? selectedElementTarget,
    [selectedElement, selectedElementTarget],
  );

  const activeCollectionTarget = useMemo(
    () =>
      selectedBlock && activeElementTarget
        ? collectionTargetFor(selectedBlock, activeElementTarget)
        : null,
    [activeElementTarget, selectedBlock],
  );

  const activeCollectionItems = useMemo(
    () =>
      selectedBlock && activeCollectionTarget
        ? arrayOfObjects(selectedBlock.data[activeCollectionTarget.collection])
        : [],
    [activeCollectionTarget, selectedBlock],
  );

  const canInlineEditSelection = canInlineEditTarget(activeElementTarget);
  const aiEditTarget = useMemo<AiEditTarget | undefined>(() => {
    if (!canTargetAiEdit(activeElementTarget)) return undefined;
    return {
      blockId: activeElementTarget.blockId,
      path: activeElementTarget.path,
      label: activeElementTarget.label,
      kind: activeElementTarget.kind,
      ...(activeElementTarget.hrefPath
        ? { hrefPath: activeElementTarget.hrefPath }
        : {}),
      ...(activeElementTarget.altPath
        ? { altPath: activeElementTarget.altPath }
        : {}),
    };
  }, [activeElementTarget]);

  const selectedBlockFrame = useMemo(
    () =>
      selectedBlock
        ? (blockFrames.find((frame) => frame.id === selectedBlock.id) ?? null)
        : null,
    [blockFrames, selectedBlock],
  );

  const canvasSelectionFrame = useMemo<CanvasSelectionFrame | null>(() => {
    if (selectedElement) {
      return {
        editable: Boolean(activeElementTarget),
        height: selectedElement.height,
        kind: "element",
        label: selectedElement.label,
        left: selectedElement.left,
        sectionLabel: selectedElement.sectionLabel,
        target: activeElementTarget,
        top: selectedElement.top,
        width: selectedElement.width,
      };
    }
    if (!selectedBlock || !selectedBlockFrame) return null;
    return {
      editable: false,
      height: selectedBlockFrame.height,
      kind: "block",
      label: blockLabel(selectedBlock.blockType),
      left: selectedBlockFrame.left,
      sectionLabel: blockLabel(selectedBlock.blockType),
      target: null,
      top: selectedBlockFrame.top,
      width: selectedBlockFrame.width,
    };
  }, [activeElementTarget, selectedBlock, selectedBlockFrame, selectedElement]);
  const selectionChromeSurface = canvasSelectionChromeSurface({
    arePanelsHidden,
    hasFocusedElementActions,
    hasFocusedElementEditor: Boolean(focusedElementEditorMode),
    hasOverlayPanelOpen:
      !isFocusCollapsed ||
      !isChatCollapsed ||
      !isBlogCollapsed ||
      !isMediaCollapsed ||
      !isThemeCollapsed,
    isFullscreen,
    isFullscreenChromeHidden: isFullscreen && isCanvasChromeHidden,
    selectionKind: canvasSelectionFrame?.kind ?? null,
  });
  const selectionDockActionGroups = canvasSelectionDockActionGroups({
    isAdvancedMode,
  });

  const selectionHudPosition = useMemo(
    () =>
      canvasSelectionFrame
        ? canvasHudPosition(canvasSelectionFrame, previewViewport)
        : null,
    [canvasSelectionFrame, previewViewport],
  );

  const selectedAiContext = useMemo(() => {
    if (!selectedBlock) return null;
    const blockName = blockLabel(selectedBlock.blockType);
    if (!activeElementTarget) {
      return {
        blockName,
        canPatchField: false,
        fieldLabel: null,
        fieldValue: "",
      };
    }
    return {
      blockName,
      canPatchField: Boolean(aiEditTarget),
      fieldLabel: activeElementTarget.label,
      fieldValue: editablePathText(selectedBlock, activeElementTarget.path),
    };
  }, [activeElementTarget, aiEditTarget, selectedBlock]);

  const pagesForAnalysis = useMemo(
    () =>
      draft
        ? pages.map((page) => (page.id === draft.id ? draft : page))
        : pages,
    [draft, pages],
  );

  const selectedLinkTarget = useMemo(
    () =>
      describeSelectedLinkTarget(
        selectedBlock,
        activeElementTarget,
        pagesForAnalysis,
        hostname,
      ),
    [activeElementTarget, hostname, pagesForAnalysis, selectedBlock],
  );

  const outgoingLinks = useMemo(
    () => (draft ? collectLinks(draft.blocks, pagesForAnalysis, hostname) : []),
    [draft, hostname, pagesForAnalysis],
  );

  const inboundLinks = useMemo(
    () =>
      draft
        ? pagesForAnalysis
            .filter((page) => page.id !== draft.id)
            .flatMap((page) =>
              collectLinks(page.blocks, pagesForAnalysis, hostname)
                .filter((link) => link.targetPage?.id === draft.id)
                .map((link) => ({
                  ...link,
                  key: `${page.id}-${link.key}`,
                  targetPage: page,
                  source: `${page.title} · ${link.source}`,
                })),
            )
        : [],
    [draft, hostname, pagesForAnalysis],
  );

  const pageConnectivity = useMemo(
    () => summarizePageConnectivity(pagesForAnalysis, hostname),
    [hostname, pagesForAnalysis],
  );
  const connectedPageCount = useMemo(
    () =>
      Array.from(pageConnectivity.values()).filter(
        (summary) => summary.status === "healthy",
      ).length,
    [pageConnectivity],
  );

  const liveUrl =
    hostname && draft
      ? `https://${hostname}${draft.slug === "/" ? "/" : draft.slug}`
      : null;

  const newPageSlugPlaceholder = useMemo(
    () => suggestedPageSlug(newPageTitle),
    [newPageTitle],
  );
  const newPageConnectionLabel = useMemo(
    () =>
      newPageConnectionHint({
        existingPageCount: pages.length,
        shouldConnect: shouldLinkNewPage,
        sourceTitle: draft?.title ?? pages[0]?.title ?? null,
      }),
    [draft?.title, pages, shouldLinkNewPage],
  );

  const isDraftDirty = useMemo(
    () =>
      Boolean(
        draft && savedDraftKey && draftVersionKey(draft) !== savedDraftKey,
      ),
    [draft, savedDraftKey],
  );
  const activeChatStorageKey = useMemo(
    () =>
      editorChatStorageKey({
        pageId: draft?.id,
        siteId: draft?.siteId,
      }),
    [draft?.id, draft?.siteId],
  );

  useEffect(() => {
    let disposed = false;

    async function loadEditor() {
      setLoading(true);
      setError(null);
      try {
        const [
          pageResult,
          themeResult,
          blogResult,
          mediaResult,
          importedCmsResult,
        ] = await Promise.all([
          api.listPages(),
          api.getTheme(),
          api.listBlogPosts(),
          api.listMedia(),
          api.getImportedCms(),
        ]);
        if (disposed) return;
        const nextSelected = pageResult.pages[0]?.id ?? null;
        const nextDraft = pageResult.pages[0] ?? null;
        const nextPost = blogResult.posts[0] ?? null;
        setPages(pageResult.pages);
        setHasPreview((pageResult.site.previewEpoch ?? 0) > 0);
        setBlogPosts(blogResult.posts);
        setMediaItems(mediaResult.items);
        setMediaStorage(mediaResult.storage);
        setImportedCms(importedCmsResult);
        setSelectedBlogPostId(nextPost?.id ?? null);
        setBlogDraft(nextPost ? blogPostDraftFromPost(nextPost) : null);
        setTheme(themeResult.theme);
        setSelectedId(nextSelected);
        setDraft(nextDraft);
        resetDraftHistory(nextDraft);
      } catch (err) {
        if (!disposed) {
          setError(
            err instanceof Error ? err.message : "Could not load editor.",
          );
        }
      } finally {
        if (!disposed) setLoading(false);
      }
    }

    void loadEditor();
    return () => {
      disposed = true;
    };
  }, [api, resetDraftHistory]);

  useEffect(() => {
    if (!selectedPage) return;
    if (draft?.id === selectedPage.id) return;
    setDraft(selectedPage);
    resetDraftHistory(selectedPage);
  }, [draft?.id, resetDraftHistory, selectedPage]);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    hostnameRef.current = hostname;
  }, [hostname]);

  useEffect(() => {
    canvasModeRef.current = canvasMode;
  }, [canvasMode]);

  useEffect(() => {
    setChatStorageKey(activeChatStorageKey);
    setChatMessages(
      readEditorChatHistory(editorChatStorage(), activeChatStorageKey),
    );
  }, [activeChatStorageKey]);

  useEffect(() => {
    writeEditorChatHistory(editorChatStorage(), chatStorageKey, chatMessages);
  }, [chatMessages, chatStorageKey]);

  useEffect(() => {
    if (!draft) {
      selectedBlockIdRef.current = null;
      setSelectedBlockId(null);
      setSelectedElementKey(null);
      setSelectedElementTarget(null);
      setBlockFrames([]);
      setElementFrames([]);
      return;
    }
    const nextBlockId = editorInitialSelectedBlockId({
      blockIds: draft.blocks.map((block) => block.id),
      currentBlockId: selectedBlockId,
    });
    if (nextBlockId !== selectedBlockId) {
      selectedBlockIdRef.current = nextBlockId;
      setSelectedBlockId(nextBlockId);
      setSelectedElementKey(null);
      setSelectedElementTarget(null);
    }
  }, [draft, selectedBlockId]);

  useEffect(() => {
    if (!isFullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (isFullscreen) return;
    setIsCanvasChromeHidden(false);
  }, [isFullscreen]);

  useEffect(() => {
    if (isClearView && isFullscreen) setIsCanvasChromeHidden(true);
  }, [isClearView, isFullscreen]);

  const enterFullscreenCanvas = useCallback(() => {
    const entryState = fullscreenCanvasEntryState();
    const hadSelection = Boolean(selectedBlockIdRef.current);
    suppressCanvasSelectionUntilRef.current = Date.now() + 450;
    setIsFullscreen(true);
    setCanvasPanelsHidden(entryState.panelsHidden);
    setIsCanvasChromeHidden(entryState.chromeHidden);
    setIsOutlineVisible(entryState.outlineVisible);
    setIsTargetMapVisible(entryState.targetMapVisible);
    setHoveredBlockId(null);
    setHoveredElementFrame(null);
    if (!hadSelection) {
      selectedBlockIdRef.current = null;
      setSelectedBlockId(null);
      setSelectedElementKey(null);
      setSelectedElementTarget(null);
      setQuickInspectorTargetKey(null);
      setInlineEditingKey(null);
    }
    window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        activeElement.closest(".preview-selection-layer")
      ) {
        activeElement.blur();
      }
    }, 0);
  }, [setCanvasPanelsHidden]);

  const exitFullscreenCanvas = useCallback(() => {
    setIsFullscreen(false);
    setIsCanvasChromeHidden(false);
  }, []);

  useEffect(() => {
    if (!draft || !theme) {
      setPreviewHtml("");
      setPreviewWarnings([]);
      setPreviewRenderErrors([]);
      return;
    }

    let disposed = false;
    const timeout = window.setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const preview = await api.previewPage(draft.id, {
          title: draft.title,
          seo: draft.seo,
          blocks: reorder(draft.blocks),
          theme,
        });
        if (disposed) return;
        setPreviewHtml(preview.html);
        setPreviewWarnings(preview.warnings);
        setPreviewRenderErrors(preview.errors);
      } catch (err) {
        if (!disposed) {
          setPreviewError(
            err instanceof Error ? err.message : "Could not render preview.",
          );
        }
      } finally {
        if (!disposed) setPreviewLoading(false);
      }
    }, 250);

    return () => {
      disposed = true;
      window.clearTimeout(timeout);
    };
  }, [api, draft, theme]);

  const measurePreviewBlocks = useCallback(
    (_focusedBlockId?: string | null) => {
      const iframe = previewFrameRef.current;
      const doc = iframe?.contentDocument;
      if (!iframe || !doc) {
        setBlockFrames([]);
        setElementFrames([]);
        setPreviewViewport((current) =>
          current.width === 0 && current.height === 0
            ? current
            : { height: 0, width: 0 },
        );
        return;
      }

      const viewportRect = iframe.getBoundingClientRect();
      const nextViewport = {
        height: Math.round(viewportRect.height),
        width: Math.round(viewportRect.width),
      };
      setPreviewViewport((current) =>
        current.width === nextViewport.width &&
        current.height === nextViewport.height
          ? current
          : nextViewport,
      );

      const nextElementFrames: ElementFrame[] = [];
      const frames = Array.from(
        doc.querySelectorAll<HTMLElement>("[data-gb-block-id]"),
      )
        .map((node) => {
          const id = node.dataset.gbBlockId;
          if (!id) return null;
          const rect = node.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return null;

          const childElements = Array.from(
            node.querySelectorAll<HTMLElement>(PREVIEW_ELEMENT_SELECTOR),
          );
          childElements.forEach((element) => {
            const elementFrame = previewElementFrame(id, element, node);
            if (elementFrame) {
              nextElementFrames.push(elementFrame);
            }
          });

          return {
            id,
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          };
        })
        .filter((frame): frame is BlockFrame => frame !== null);

      setBlockFrames(frames);
      setElementFrames(nextElementFrames);
    },
    [],
  );

  const selectBlock = useCallback(
    (
      id: string,
      opts: {
        elementKey?: string | null;
        elementTarget?: EditableElementTarget | null;
        scroll?: boolean;
      } = {},
    ) => {
      selectedBlockIdRef.current = id;
      setSelectedBlockId(id);
      setSelectedElementKey(opts.elementKey ?? null);
      setSelectedElementTarget(opts.elementTarget ?? null);
      setQuickInspectorTargetKey(null);
      setInlineEditingKey(null);
      if (!opts.scroll) return;
      const doc = previewFrameRef.current?.contentDocument;
      const node = doc ? findPreviewBlock(doc, id) : undefined;
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => measurePreviewBlocks(id), 220);
    },
    [measurePreviewBlocks],
  );

  const scrollCanvasElementIntoView = useCallback(
    (frame: Pick<ElementFrame, "blockId" | "key">) => {
      const doc = previewFrameRef.current?.contentDocument;
      if (!doc) return;
      const node =
        findPreviewElementByKey(doc, frame.blockId, frame.key) ??
        findPreviewBlock(doc, frame.blockId);
      node?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      window.setTimeout(() => measurePreviewBlocks(frame.blockId), 240);
    },
    [measurePreviewBlocks],
  );

  const switchPage = useCallback(
    (pageId: string) => {
      writeEditorChatHistory(
        editorChatStorage(),
        editorChatStorageKey({
          pageId: draft?.id,
          siteId: draft?.siteId,
        }),
        chatMessages,
      );
      setPages((current) =>
        draft
          ? current.map((page) => (page.id === draft.id ? draft : page))
          : current,
      );
      setSelectedId(pageId);
      selectedBlockIdRef.current = null;
      setSelectedBlockId(null);
      setSelectedElementKey(null);
      setSelectedElementTarget(null);
      setQuickInspectorTargetKey(null);
      setInlineEditingKey(null);
      setHoveredBlockId(null);
      setHoveredElementFrame(null);
    },
    [chatMessages, draft],
  );

  useEffect(() => {
    switchPageRef.current = switchPage;
  }, [switchPage]);

  useEffect(() => {
    selectedBlockIdRef.current = selectedBlockId;
    setSelectedElementKey((current) =>
      current && selectedBlockId && current.startsWith(`${selectedBlockId}:`)
        ? current
        : null,
    );
    setSelectedElementTarget((current) =>
      current && selectedBlockId && current.blockId === selectedBlockId
        ? current
        : null,
    );
    window.setTimeout(() => measurePreviewBlocks(selectedBlockId), 0);
  }, [measurePreviewBlocks, selectedBlockId]);

  useEffect(() => {
    if (!inlineEditingKey) return;
    if (inlineEditingKey !== selectedElementKey || !canInlineEditSelection) {
      setInlineEditingKey(null);
    }
  }, [canInlineEditSelection, inlineEditingKey, selectedElementKey]);

  useEffect(() => {
    const doc = previewFrameRef.current?.contentDocument;
    if (!doc) return;
    applyPreviewEditingState(doc, activeElementTarget, canvasMode);
  }, [activeElementTarget, canvasMode]);

  const handlePreviewLoad = useCallback(() => {
    previewCleanupRef.current?.();

    const iframe = previewFrameRef.current;
    const doc = iframe?.contentDocument;
    const win = iframe?.contentWindow;
    if (!doc || !win) {
      setBlockFrames([]);
      setElementFrames([]);
      return;
    }

    if (draft) annotatePreviewEditableFields(doc, draft.blocks);
    applyPreviewEditingState(doc, activeElementTarget, canvasModeRef.current);
    const renderedBlockIds = new Set(
      Array.from(doc.querySelectorAll<HTMLElement>("[data-gb-block-id]"))
        .map((node) => node.dataset.gbBlockId)
        .filter((id): id is string => Boolean(id)),
    );
    const selectedBlockStillExists = selectedBlockIdRef.current
      ? renderedBlockIds.has(selectedBlockIdRef.current)
      : false;
    const previewFocusBlockId = editorInitialSelectedBlockId({
      blockIds: draft?.blocks.map((block) => block.id) ?? [],
      currentBlockId:
        selectedBlockStillExists && selectedBlockIdRef.current
          ? selectedBlockIdRef.current
          : null,
      renderedBlockIds,
    });

    if (previewFocusBlockId !== selectedBlockIdRef.current) {
      selectedBlockIdRef.current = previewFocusBlockId;
      setSelectedBlockId(previewFocusBlockId);
      setSelectedElementKey(null);
      setSelectedElementTarget(null);
      setQuickInspectorTargetKey(null);
      setInlineEditingKey(null);
    }

    const selectCanvasTarget = (
      event: MouseEvent,
      shouldOpenEditor = false,
    ) => {
      const rawTarget = event.target;
      const rawElement =
        rawTarget && typeof (rawTarget as Element).closest === "function"
          ? (rawTarget as Element)
          : null;
      const target =
        rawElement?.closest<HTMLElement>("[data-gb-block-id]") ?? null;
      const id = target?.dataset.gbBlockId;
      if (!id) return false;
      event.preventDefault();
      event.stopPropagation();
      const elementTargetNode =
        rawTarget && typeof (rawTarget as Element).closest === "function"
          ? closestPreviewElement(rawTarget as Element, target)
          : null;
      const elementFrame = elementTargetNode
        ? previewElementFrame(id, elementTargetNode, target)
        : null;
      selectBlock(id, {
        elementKey: elementFrame?.key ?? null,
        elementTarget: elementFrame?.target ?? null,
      });
      measurePreviewBlocks(id);
      const shouldOpenElementEditor =
        shouldOpenEditor ||
        previewElementClickShouldOpenEditor({
          hasTarget: Boolean(elementFrame?.target),
          isAdvancedMode,
          isAlreadySelected: elementFrame?.key === selectedElementKey,
        });
      if (!shouldOpenElementEditor || !elementFrame?.target) return true;

      setCanvasPanelsHidden(true);
      if (canInlineEditTarget(elementFrame.target)) {
        setQuickInspectorTargetKey(null);
        setInlineEditingKey(elementFrame.key);
        return true;
      }
      setInlineEditingKey(null);
      setQuickInspectorTargetKey(elementFrame.key);
      return true;
    };

    const handleClick = (event: MouseEvent) => {
      const rawTarget = event.target;
      const rawElement =
        rawTarget && typeof (rawTarget as Element).closest === "function"
          ? (rawTarget as Element)
          : null;
      const anchor = rawElement?.closest<HTMLAnchorElement>("a[href]");

      if (canvasModeRef.current === "browse") {
        if (!anchor) return;
        event.preventDefault();
        event.stopPropagation();
        const href = anchor.getAttribute("href") ?? "";
        const targetPage = pageForHref(
          pagesRef.current,
          href,
          hostnameRef.current,
        );
        if (targetPage) {
          switchPageRef.current(targetPage.id);
          setNotice(`Opened ${targetPage.title}.`);
          setError(null);
          return;
        }
        if (/^https?:\/\//i.test(href)) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
        return;
      }

      selectCanvasTarget(event);
    };

    const handleDoubleClick = (event: MouseEvent) => {
      if (canvasModeRef.current !== "edit") return;
      selectCanvasTarget(event, true);
    };

    const handlePointerMove = (event: MouseEvent) => {
      if (canvasModeRef.current !== "edit") {
        setHoveredBlockId((current) => (current === null ? current : null));
        setHoveredElementFrame((current) =>
          current === null ? current : null,
        );
        return;
      }

      const rawTarget = event.target;
      const target =
        rawTarget && typeof (rawTarget as Element).closest === "function"
          ? (rawTarget as Element).closest<HTMLElement>("[data-gb-block-id]")
          : null;
      const id = target?.dataset.gbBlockId;
      if (!id) {
        setHoveredBlockId((current) => (current === null ? current : null));
        setHoveredElementFrame((current) =>
          current === null ? current : null,
        );
        return;
      }

      setHoveredBlockId((current) => (current === id ? current : id));
      const elementTarget =
        rawTarget && typeof (rawTarget as Element).closest === "function"
          ? closestPreviewElement(rawTarget as Element, target)
          : null;
      const nextFrame = elementTarget
        ? previewElementFrame(id, elementTarget, target)
        : null;
      setHoveredElementFrame((current) =>
        current?.key === nextFrame?.key ? current : nextFrame,
      );
    };

    const clearHover = () => {
      setHoveredBlockId((current) => (current === null ? current : null));
      setHoveredElementFrame((current) => (current === null ? current : null));
    };

    const handleScrollOrResize = () => {
      clearHover();
      measurePreviewBlocks();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      selectionShortcutHandlerRef.current?.(event);
    };

    doc.addEventListener("click", handleClick, true);
    doc.addEventListener("dblclick", handleDoubleClick, true);
    doc.addEventListener("keydown", handleKeyDown, true);
    doc.addEventListener("mousemove", handlePointerMove, true);
    doc.addEventListener("mouseleave", clearHover, true);
    win.addEventListener("scroll", handleScrollOrResize, { passive: true });
    win.addEventListener("resize", handleScrollOrResize);
    window.setTimeout(() => measurePreviewBlocks(previewFocusBlockId), 30);

    previewCleanupRef.current = () => {
      doc.removeEventListener("click", handleClick, true);
      doc.removeEventListener("dblclick", handleDoubleClick, true);
      doc.removeEventListener("keydown", handleKeyDown, true);
      doc.removeEventListener("mousemove", handlePointerMove, true);
      doc.removeEventListener("mouseleave", clearHover, true);
      win.removeEventListener("scroll", handleScrollOrResize);
      win.removeEventListener("resize", handleScrollOrResize);
    };
  }, [
    activeElementTarget,
    draft,
    isAdvancedMode,
    measurePreviewBlocks,
    selectBlock,
    selectedElementKey,
    setCanvasPanelsHidden,
  ]);

  useEffect(() => {
    return () => {
      previewCleanupRef.current?.();
    };
  }, []);

  const applyDraftChange = useCallback(
    (next: EditorPage, message?: string) => {
      if (!draft) return;
      if (draftVersionKey(next) === draftVersionKey(draft)) return;
      const history = draftHistoryRef.current;
      history.past = [
        ...history.past.slice(-(DRAFT_HISTORY_LIMIT - 1)),
        cloneEditorPage(draft),
      ];
      history.future = [];
      setDraft(next);
      refreshDraftHistoryStatus();
      if (message) setNotice(message);
      setError(null);
    },
    [draft, refreshDraftHistoryStatus],
  );

  const updateDraft = useCallback(
    (updater: (current: EditorPage) => EditorPage, message?: string) => {
      if (!draft) return;
      applyDraftChange(updater(draft), message);
    },
    [applyDraftChange, draft],
  );

  const undoDraft = useCallback(() => {
    if (!draft) return;
    const history = draftHistoryRef.current;
    const previous = history.past.at(-1);
    if (!previous) return;
    history.past = history.past.slice(0, -1);
    history.future = [
      cloneEditorPage(draft),
      ...history.future.slice(0, DRAFT_HISTORY_LIMIT - 1),
    ];
    setDraft(cloneEditorPage(previous));
    refreshDraftHistoryStatus();
    setHasPendingAgentChange(false);
    setAgentDraftSource(null);
    setAgentDraftReviewLabel(null);
    setNotice("Undid last edit.");
    setError(null);
  }, [draft, refreshDraftHistoryStatus]);

  const redoDraft = useCallback(() => {
    if (!draft) return;
    const history = draftHistoryRef.current;
    const next = history.future[0];
    if (!next) return;
    history.future = history.future.slice(1);
    history.past = [
      ...history.past.slice(-(DRAFT_HISTORY_LIMIT - 1)),
      cloneEditorPage(draft),
    ];
    setDraft(cloneEditorPage(next));
    refreshDraftHistoryStatus();
    setNotice("Redid last edit.");
    setError(null);
  }, [draft, refreshDraftHistoryStatus]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (!draft || isEditableKeyboardTarget(event.target)) return;
      const key = event.key.toLowerCase();
      const isModifier = event.metaKey || event.ctrlKey;
      if (!isModifier || (key !== "z" && key !== "y")) return;
      event.preventDefault();
      if (key === "y" || event.shiftKey) {
        redoDraft();
        return;
      }
      undoDraft();
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [draft, redoDraft, undoDraft]);

  const replaceDraft = (
    page: EditorPage,
    message?: string,
    opts: {
      markSaved?: boolean;
      preserveAgentReview?: boolean;
      resetHistory?: boolean;
    } = {},
  ) => {
    const {
      markSaved = true,
      preserveAgentReview = false,
      resetHistory = true,
    } = opts;
    setDraft(page);
    setSelectedId(page.id);
    setPages((current) => current.map((p) => (p.id === page.id ? page : p)));
    if (markSaved) setSavedDraftKey(draftVersionKey(page));
    if (resetHistory) resetDraftHistory(page);
    else refreshDraftHistoryStatus();
    if (!preserveAgentReview) {
      setHasPendingAgentChange(false);
      setAgentDraftSource(null);
      setAgentDraftReviewLabel(null);
    }
    if (message) setNotice(message);
    setError(null);
  };

  const persistDraft = async (
    opts: { preserveAgentReview?: boolean } = {},
  ): Promise<EditorPage> => {
    if (!draft) throw new Error("No page selected.");
    const { page } = await api.updatePage(draft.id, {
      title: draft.title,
      seo: draft.seo,
      blocks: reorder(draft.blocks),
    });
    replaceDraft(page, undefined, {
      preserveAgentReview: opts.preserveAgentReview,
      resetHistory: false,
    });
    return page;
  };

  const saveConnectedSiteDrafts = async (
    sourcePages: EditorPage[],
  ): Promise<{ changedPages: EditorPage[]; nextPages: EditorPage[] }> => {
    const preparedPages = prepareSitePagesForPublish(sourcePages, hostname);
    const changedPages = preparedPages.filter((page) => {
      const savedPage = pages.find((candidate) => candidate.id === page.id);
      return savedPage
        ? draftVersionKey(page) !== draftVersionKey(savedPage)
        : true;
    });
    if (changedPages.length === 0) {
      return { changedPages, nextPages: preparedPages };
    }
    const savedPages = await Promise.all(
      changedPages.map(async (page) => {
        const result = await api.updatePage(page.id, {
          title: page.title,
          seo: page.seo,
          blocks: reorder(page.blocks),
        });
        return result.page;
      }),
    );
    const savedById = new Map(savedPages.map((page) => [page.id, page]));
    return {
      changedPages,
      nextPages: preparedPages.map((page) => savedById.get(page.id) ?? page),
    };
  };

  const saveDraft = async () => {
    setWorking("save");
    try {
      await persistDraft({ preserveAgentReview: hasPendingAgentChange });
      setNotice("Draft saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save draft.");
    } finally {
      setWorking(null);
    }
  };

  const rebuild = async () => {
    if (!draft) return;
    setWorking("rebuild");
    try {
      const { page } = await api.rebuildPage(draft.id);
      replaceDraft(page, "Editable draft rebuilt from the captured mirror.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not rebuild page.");
    } finally {
      setWorking(null);
    }
  };

  const publish = async () => {
    if (!draft) return;
    setWorking("publish");
    try {
      const selectedPageId = draft.id;
      const sourcePages = pages.map((page) =>
        page.id === selectedPageId ? draft : page,
      );
      const { changedPages, nextPages } =
        await saveConnectedSiteDrafts(sourcePages);
      const pageToPublish =
        nextPages.find((page) => page.id === selectedPageId) ?? draft;
      const { page } = await api.publishPage(pageToPublish.id);
      const nextPublishedPages = nextPages.map((candidate) =>
        candidate.id === page.id ? page : candidate,
      );
      const connectedDraftCount = changedPages.filter(
        (candidate) => candidate.id !== selectedPageId,
      ).length;
      setPages(nextPublishedPages);
      setDraft(page);
      setSelectedId(page.id);
      setSavedDraftKey(draftVersionKey(page));
      refreshDraftHistoryStatus();
      setHasPendingAgentChange(false);
      setAgentDraftSource(null);
      setAgentDraftReviewLabel(null);
      setNotice(
        connectedDraftCount > 0
          ? `Saved connected drafts on ${connectedDraftCount} other page${
              connectedDraftCount === 1 ? "" : "s"
            } and published this page. Publish site pushes every backlink live.`
          : "Published this page to the live site.",
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not publish page.");
    } finally {
      setWorking(null);
    }
  };

  const publishSite = async () => {
    if (!draft) return;
    setWorking("publish-site");
    try {
      const selectedPageId = draft.id;
      const sourcePages = pages.map((page) =>
        page.id === draft.id ? draft : page,
      );
      const { changedPages } = await saveConnectedSiteDrafts(sourcePages);
      const result = await api.publishSite();
      const nextDraft =
        result.pages.find((page) => page.id === selectedPageId) ??
        result.pages[0] ??
        null;
      setPages(result.pages);
      if (nextDraft) {
        setDraft(nextDraft);
        setSelectedId(nextDraft.id);
        setSavedDraftKey(draftVersionKey(nextDraft));
      }
      refreshDraftHistoryStatus();
      setHasPendingAgentChange(false);
      setAgentDraftSource(null);
      setAgentDraftReviewLabel(null);
      const blogMessage =
        result.blogPostCount > 0
          ? ` and ${result.blogPostCount} blog post${
              result.blogPostCount === 1 ? "" : "s"
            }`
          : "";
      const publishMessage = `published ${result.publishedCount} page${
        result.publishedCount === 1 ? "" : "s"
      }${blogMessage} to the live site.`;
      setNotice(
        changedPages.length > 0
          ? `Saved site drafts on ${changedPages.length} page${
              changedPages.length === 1 ? "" : "s"
            } and ${publishMessage}`
          : `Published ${result.publishedCount} page${
              result.publishedCount === 1 ? "" : "s"
            }${blogMessage} to the live site.`,
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not publish site.");
    } finally {
      setWorking(null);
    }
  };

  const updatePreview = async () => {
    setWorking("update-preview");
    try {
      const result = await api.updatePreview();
      setHasPreview(true);
      const skipped =
        result.skippedPages.length > 0
          ? ` ${result.skippedPages.length} page${
              result.skippedPages.length === 1 ? " was" : "s were"
            } skipped (no content yet).`
          : "";
      // No auto window.open: after an await the user activation may have
      // lapsed and browsers popup-block it. The "Open preview" link is the
      // affordance, now enabled.
      setNotice(
        `Preview updated (${result.pageCount} page${
          result.pageCount === 1 ? "" : "s"
        }${
          result.blogPostCount > 0
            ? ` + ${result.blogPostCount} blog post${
                result.blogPostCount === 1 ? "" : "s"
              }`
            : ""
        }).${skipped} Use "Open preview" to test your unpublished changes — the preview stays out of search and your live inbox.`,
      );
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not update the preview.",
      );
    } finally {
      setWorking(null);
    }
  };

  const runAiEdit = async (prompt: string) => {
    if (!draft) return;
    const trimmed = prompt.trim();
    if (!trimmed) return;
    const chatHistory = aiEditChatHistory(chatMessages);
    setWorking("chat");
    setChatPrompt("");
    setChatMessages((current) => [
      ...current,
      { id: crypto.randomUUID(), role: "user", text: trimmed },
    ]);
    try {
      const { message, page, planSource } = await api.aiEditPage(draft.id, {
        chatHistory,
        prompt: trimmed,
        title: draft.title,
        seo: draft.seo,
        blocks: reorder(draft.blocks),
        ...(selectedBlock ? { insertAfterBlockId: selectedBlock.id } : {}),
        ...(aiEditTarget ? { target: aiEditTarget } : {}),
        ...(theme ? { theme } : {}),
      });
      const didChange = draftVersionKey(page) !== draftVersionKey(draft);
      const reviewSummary = agentDraftReviewSummary({
        after: page.blocks,
        before: draft.blocks,
      });
      applyDraftChange(page, "AI change previewed.");
      setHasPendingAgentChange(didChange);
      setAgentDraftSource(didChange ? planSource : null);
      setAgentDraftReviewLabel(didChange ? reviewSummary.label : null);
      const focusBlockId = reviewSummary.focusBlockId;
      if (didChange && focusBlockId) {
        window.setTimeout(() => {
          selectBlock(focusBlockId, { scroll: true });
        }, 300);
      }
      setChatMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), role: "assistant", text: message },
      ]);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not apply AI edit.";
      setError(message);
      setChatMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), role: "assistant", text: message },
      ]);
    } finally {
      setWorking(null);
    }
  };

  const clearAgentChat = useCallback(() => {
    setChatPrompt(AGENT_CHAT_INITIAL_PROMPT);
    setChatMessages([]);
    setError(null);
  }, []);

  const saveTheme = async () => {
    if (!theme) return;
    setWorking("theme");
    try {
      const { theme: saved } = await api.saveTheme(theme);
      setTheme(saved);
      setNotice("Theme published.");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save theme.");
    } finally {
      setWorking(null);
    }
  };

  const updateBlock = (id: string, data: Record<string, unknown>) => {
    updateDraft((current) => ({
      ...current,
      blocks: current.blocks.map((block) =>
        block.id === id ? { ...block, data } : block,
      ),
    }));
  };

  const duplicateSelectedItem = () => {
    if (!selectedBlock || !activeCollectionTarget) return;
    if (activeCollectionItems.length >= activeCollectionTarget.maxItems) return;
    updateBlock(
      selectedBlock.id,
      duplicateCollectionItem(selectedBlock.data, activeCollectionTarget),
    );
    setNotice(`Duplicated ${activeCollectionTarget.itemLabel}.`);
    setError(null);
    setCanvasPanelsHidden(true);
    window.setTimeout(() => measurePreviewBlocks(selectedBlock.id), 0);
  };

  const removeSelectedItem = () => {
    if (!selectedBlock || !activeCollectionTarget) return;
    if (activeCollectionItems.length <= activeCollectionTarget.minItems) return;
    updateBlock(
      selectedBlock.id,
      removeCollectionItem(selectedBlock.data, activeCollectionTarget),
    );
    setNotice(`Removed ${activeCollectionTarget.itemLabel}.`);
    setError(null);
    setSelectedElementKey(null);
    setSelectedElementTarget(null);
    setHoveredElementFrame(null);
    setQuickInspectorTargetKey(null);
    setInlineEditingKey(null);
    setCanvasPanelsHidden(true);
    window.setTimeout(() => measurePreviewBlocks(selectedBlock.id), 0);
  };

  const selectedItemActions: SelectionItemActions | null =
    activeCollectionTarget
      ? {
          canDuplicate:
            activeCollectionItems.length < activeCollectionTarget.maxItems,
          canRemove:
            activeCollectionItems.length > activeCollectionTarget.minItems,
          itemLabel: activeCollectionTarget.itemLabel,
          itemPosition: `${activeCollectionTarget.index + 1} / ${
            activeCollectionItems.length
          }`,
          onDuplicate: duplicateSelectedItem,
          onRemove: removeSelectedItem,
        }
      : null;

  const openInlineEditor = useCallback(() => {
    if (!selectedElementKey || !canInlineEditSelection) return;
    setQuickInspectorTargetKey(null);
    setCanvasPanelsHidden(true);
    setInlineEditingKey(selectedElementKey);
  }, [canInlineEditSelection, selectedElementKey, setCanvasPanelsHidden]);

  const closeInlineEditor = () => {
    setInlineEditingKey(null);
  };

  const patchBlock = (id: string, patch: Partial<PageBlock>) => {
    updateDraft((current) => ({
      ...current,
      blocks: current.blocks.map((block) =>
        block.id === id ? { ...block, ...patch } : block,
      ),
    }));
  };

  const moveBlock = (id: string, delta: number) => {
    if (!draft) return;
    const index = draft.blocks.findIndex((block) => block.id === id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= draft.blocks.length) return;
    const next = [...draft.blocks];
    const [block] = next.splice(index, 1);
    if (!block) return;
    next.splice(target, 0, block);
    applyDraftChange({ ...draft, blocks: reorder(next) });
  };

  const removeBlock = (id: string) => {
    if (!draft) return;
    applyDraftChange({
      ...draft,
      blocks: reorder(draft.blocks.filter((block) => block.id !== id)),
    });
  };

  const duplicateBlock = (id: string, opts: { hidePanels?: boolean } = {}) => {
    if (!draft) return;
    const index = draft.blocks.findIndex((block) => block.id === id);
    const source = draft.blocks[index];
    if (index < 0 || !source) return;
    const nextId = newEditorBlockId(source.blockType);
    const data = cloneBlockData(source.data);
    if (source.blockType === "contactForm") {
      data.formId = formIdFromBlockId(nextId);
    }
    const duplicate: PageBlock = {
      ...source,
      id: nextId,
      data,
      order: 0,
    };
    const nextBlocks = [...draft.blocks];
    nextBlocks.splice(index + 1, 0, duplicate);
    applyDraftChange(
      { ...draft, blocks: reorder(nextBlocks) },
      `Duplicated ${blockLabel(source.blockType)}.`,
    );
    selectedBlockIdRef.current = nextId;
    setSelectedBlockId(nextId);
    setSelectedElementKey(null);
    setSelectedElementTarget(null);
    setQuickInspectorTargetKey(null);
    setInlineEditingKey(null);
    if (opts.hidePanels) setCanvasPanelsHidden(true);
    setError(null);
    window.setTimeout(() => selectBlock(nextId, { scroll: true }), 240);
  };

  const insertSection = (templateId: SectionTemplateId) => {
    if (!draft) return;
    const template = SECTION_TEMPLATES.find(
      (candidate) => candidate.id === templateId,
    );
    if (!template) return;
    const id = newEditorBlockId(template.blockType);
    const nextBlock: PageBlock = {
      id,
      blockType: template.blockType,
      variant: template.variant,
      data: template.createData(id, draft.title),
      order: 0,
    };
    const insertAt =
      selectedBlockIndex >= 0 ? selectedBlockIndex + 1 : draft.blocks.length;
    const nextBlocks = [...draft.blocks];
    nextBlocks.splice(insertAt, 0, nextBlock);
    applyDraftChange(
      { ...draft, blocks: reorder(nextBlocks) },
      `Added ${template.label} after the selected section.`,
    );
    selectedBlockIdRef.current = id;
    setSelectedBlockId(id);
    setSelectedElementKey(null);
    setSelectedElementTarget(null);
    setQuickInspectorTargetKey(null);
    setCanvasPanelsHidden(false);
    setIsFocusCollapsed(false);
    setError(null);
    window.setTimeout(() => selectBlock(id, { scroll: true }), 360);
  };

  const patchLinkTarget = (link: LinkSummary, href: string) => {
    if (!draft || !link.blockId || !link.hrefPath) return;
    const block = draft.blocks.find(
      (candidate) => candidate.id === link.blockId,
    );
    if (!block) return;
    updateBlock(block.id, setPathValue(block.data, link.hrefPath, href));
    setNotice(`${link.source} now points to ${href}.`);
    setError(null);
  };

  const addHeaderLink = (targetPage: EditorPage) => {
    if (!draft) return;
    const headerIndex = draft.blocks.findIndex(
      (block) => block.blockType === "header",
    );
    const header = headerIndex >= 0 ? draft.blocks[headerIndex] : undefined;
    const links = arrayOfObjects(header?.data.links);
    const targetSlug = normalizeSlugPath(targetPage.slug);
    if (
      links.some((link) => {
        const slug =
          internalSlugFromHref(text(link.href), hostname) ??
          normalizeSlugPath(text(link.href));
        return slug === targetSlug;
      })
    ) {
      setNotice(`${targetPage.title} is already linked from the header.`);
      setError(null);
      return;
    }
    const nextHeader: PageBlock = {
      id: header?.id ?? newEditorBlockId("header"),
      blockType: "header",
      variant: header?.variant ?? "standard",
      order: header?.order ?? 0,
      data: {
        ...(header?.data ?? {}),
        brandName:
          text(header?.data.brandName) || fallbackBusinessName(hostname, pages),
        links: appendSiteNavLink(links, {
          href: targetPage.slug,
          label: navLinkLabelForPage(targetPage),
        }),
      },
    };
    const nextBlocks =
      headerIndex >= 0
        ? draft.blocks.map((block, index) =>
            index === headerIndex ? nextHeader : block,
          )
        : [nextHeader, ...draft.blocks];
    applyDraftChange(
      { ...draft, blocks: reorder(nextBlocks) },
      `Added ${targetPage.title} to the header links.`,
    );
    selectBlock(nextHeader.id, { scroll: true });
    setNotice(`Added ${targetPage.title} to the header links.`);
    setError(null);
  };

  const addFooterLink = (targetPage: EditorPage) => {
    if (!draft) return;
    const footerIndex = draft.blocks.findIndex(
      (block) => block.blockType === "footer",
    );
    const footer = footerIndex >= 0 ? draft.blocks[footerIndex] : undefined;
    const links = arrayOfObjects(footer?.data.links);
    const targetSlug = normalizeSlugPath(targetPage.slug);
    if (
      links.some((link) => {
        const slug =
          internalSlugFromHref(text(link.href), hostname) ??
          normalizeSlugPath(text(link.href));
        return slug === targetSlug;
      })
    ) {
      setNotice(`${targetPage.title} is already linked from the footer.`);
      setError(null);
      return;
    }
    const nextFooter: PageBlock = {
      id: footer?.id ?? newEditorBlockId("footer"),
      blockType: "footer",
      variant: footer?.variant ?? "simple",
      order: footer?.order ?? draft.blocks.length,
      data: {
        ...(footer?.data ?? {}),
        businessName:
          text(footer?.data.businessName) ||
          fallbackBusinessName(hostname, pages),
        links: appendSiteNavLink(links, {
          href: targetPage.slug,
          label: navLinkLabelForPage(targetPage),
        }),
      },
    };
    const nextBlocks =
      footerIndex >= 0
        ? draft.blocks.map((block, index) =>
            index === footerIndex ? nextFooter : block,
          )
        : [...draft.blocks, nextFooter];
    applyDraftChange(
      { ...draft, blocks: reorder(nextBlocks) },
      `Added ${targetPage.title} to the footer links.`,
    );
    selectBlock(nextFooter.id, { scroll: true });
    setNotice(`Added ${targetPage.title} to the footer links.`);
    setError(null);
  };

  const syncSiteNavigation = async () => {
    if (!draft) return;
    const sourcePages = pages.map((page) =>
      page.id === draft.id ? draft : page,
    );
    if (sourcePages.length < 2) {
      setNotice("Add more pages before syncing site navigation.");
      setError(null);
      return;
    }

    const nextPages = sourcePages.map((page) =>
      syncFooterNavigationForPage(
        syncHeaderNavigationForPage(page, sourcePages, hostname),
        sourcePages,
        hostname,
      ),
    );
    const changedPages = nextPages.filter((page, index) => {
      const current = sourcePages[index];
      return current
        ? draftVersionKey(page) !== draftVersionKey(current)
        : true;
    });

    if (changedPages.length === 0) {
      setNotice("Site navigation already links the site pages.");
      setError(null);
      return;
    }

    setWorking("site-links");
    setError(null);
    try {
      const savedPages = await Promise.all(
        changedPages.map(async (page) => {
          const result = await api.updatePage(page.id, {
            blocks: reorder(page.blocks),
          });
          return result.page;
        }),
      );
      const savedById = new Map(savedPages.map((page) => [page.id, page]));
      setPages((current) =>
        current.map((page) => savedById.get(page.id) ?? page),
      );
      const savedDraft = savedById.get(draft.id);
      const message = `Synced site navigation across ${changedPages.length} page draft${
        changedPages.length === 1 ? "" : "s"
      }.`;
      if (savedDraft) {
        replaceDraft(savedDraft, message, { resetHistory: false });
      } else {
        setNotice(message);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not sync site navigation.",
      );
    } finally {
      setWorking(null);
    }
  };

  const repairSiteNavigation = async () => {
    if (!draft) return;
    const sourcePages = pages.map((page) =>
      page.id === draft.id ? draft : page,
    );
    if (sourcePages.length < 2) {
      setNotice("Add another page before repairing navigation.");
      setError(null);
      return;
    }

    setWorking("site-links");
    setError(null);
    try {
      const { changedPages, nextPages } =
        await saveConnectedSiteDrafts(sourcePages);
      setPages(nextPages);
      const nextDraft = nextPages.find((page) => page.id === draft.id) ?? draft;
      const message =
        changedPages.length > 0
          ? `Repaired navigation across ${changedPages.length} page draft${
              changedPages.length === 1 ? "" : "s"
            }.`
          : "Navigation already looks ready.";
      replaceDraft(nextDraft, message, { resetHistory: false });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not repair navigation.",
      );
    } finally {
      setWorking(null);
    }
  };

  const createPage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = newPageTitle.trim();
    if (!title) {
      setError("Enter a page title.");
      return;
    }

    setWorking("create-page");
    setError(null);
    try {
      let sourcePages = draft
        ? pages.map((page) => (page.id === draft.id ? draft : page))
        : pages;
      if (draft && isDraftDirty) {
        const saved = await persistDraft();
        sourcePages = sourcePages.map((page) =>
          page.id === saved.id ? saved : page,
        );
      }

      const { page: created } = await api.createPage({
        title,
        ...(newPageSlug.trim() ? { slug: newPageSlug.trim() } : {}),
      });
      let nextPages = [...sourcePages, created];
      let selectedPage = created;
      let didConnectNewPage = false;

      if (shouldLinkNewPage && nextPages.length > 1) {
        const syncedPages = connectCreatedPageToSite(
          nextPages,
          created.id,
          draft?.id ?? sourcePages[0]?.id ?? null,
          hostname,
        );
        const changedPages = syncedPages.filter((page, index) => {
          const current = nextPages[index];
          return current
            ? draftVersionKey(page) !== draftVersionKey(current)
            : true;
        });

        if (changedPages.length > 0) {
          didConnectNewPage = true;
          const savedPages = await Promise.all(
            changedPages.map(async (page) => {
              const result = await api.updatePage(page.id, {
                blocks: reorder(page.blocks),
              });
              return result.page;
            }),
          );
          const savedById = new Map(savedPages.map((page) => [page.id, page]));
          nextPages = syncedPages.map((page) => savedById.get(page.id) ?? page);
          selectedPage = savedById.get(created.id) ?? created;
        }
      }

      setPages(nextPages);
      setSelectedId(selectedPage.id);
      setDraft(selectedPage);
      resetDraftHistory(selectedPage);
      const firstBlockId = selectedPage.blocks[0]?.id ?? null;
      selectedBlockIdRef.current = firstBlockId;
      setSelectedBlockId(firstBlockId);
      setSelectedElementKey(null);
      setSelectedElementTarget(null);
      setHoveredBlockId(null);
      setHoveredElementFrame(null);
      setQuickInspectorTargetKey(null);
      setInlineEditingKey(null);
      setChatMessages([]);
      setNewPageTitle("");
      setNewPageSlug("");
      setNotice(
        didConnectNewPage
          ? `Created ${selectedPage.title} with navigation and return links.`
          : `Created ${selectedPage.title}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create page.");
    } finally {
      setWorking(null);
    }
  };

  const selectBlogPost = (postId: string) => {
    const post = blogPosts.find((candidate) => candidate.id === postId);
    if (!post) return;
    setSelectedBlogPostId(post.id);
    setBlogDraft(blogPostDraftFromPost(post));
    setError(null);
  };

  const startNewBlogPost = useCallback(() => {
    setSelectedBlogPostId(null);
    setBlogDraft(emptyBlogPostDraft());
    setError(null);
  }, []);

  const updateBlogDraft = (patch: Partial<BlogPostEditorDraft>) => {
    setBlogDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const saveBlogPost = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!blogDraft) return;
    const payload = blogPostInputFromDraft(blogDraft);
    const title = payload.title;
    if (!title) {
      setError("Enter a blog post title.");
      return;
    }

    setWorking("blog-save");
    setError(null);
    try {
      const result = blogDraft.id
        ? await api.updateBlogPost(blogDraft.id, payload)
        : await api.createBlogPost({ ...payload, title });
      setBlogPosts((current) => {
        const exists = current.some((post) => post.id === result.post.id);
        return exists
          ? current.map((post) =>
              post.id === result.post.id ? result.post : post,
            )
          : [result.post, ...current];
      });
      setSelectedBlogPostId(result.post.id);
      setBlogDraft(blogPostDraftFromPost(result.post));
      setNotice(
        result.post.status === "published"
          ? "Blog post saved as published."
          : "Blog post saved as draft.",
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not save blog post.",
      );
    } finally {
      setWorking(null);
    }
  };

  const deleteBlogPost = async () => {
    if (!blogDraft?.id) return;
    const postTitle = blogDraft.title.trim() || "this blog post";
    if (!window.confirm(`Delete ${postTitle}?`)) return;

    setWorking("blog-delete");
    setError(null);
    try {
      await api.deleteBlogPost(blogDraft.id);
      const nextPosts = blogPosts.filter((post) => post.id !== blogDraft.id);
      const nextPost = nextPosts[0] ?? null;
      setBlogPosts(nextPosts);
      setSelectedBlogPostId(nextPost?.id ?? null);
      setBlogDraft(nextPost ? blogPostDraftFromPost(nextPost) : null);
      setNotice("Blog post deleted.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not delete blog post.",
      );
    } finally {
      setWorking(null);
    }
  };

  const uploadMedia = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const file = form.get("file");
    const alt = form.get("alt");
    if (!(file instanceof File) || file.size === 0) {
      setError("Choose an image to upload.");
      return;
    }

    setWorking("media-upload");
    setError(null);
    try {
      const result = await api.uploadMedia(
        file,
        typeof alt === "string" ? alt : undefined,
      );
      setMediaItems((current) => [result.item, ...current]);
      setMediaStorage((current) =>
        current
          ? {
              ...current,
              bytesUsed: current.bytesUsed + result.item.sizeBytes,
            }
          : current,
      );
      event.currentTarget.reset();
      setNotice("Media uploaded. It is now available in image pickers.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not upload media.");
    } finally {
      setWorking(null);
    }
  };

  const updateMediaItem = async (id: string, payload: MediaUpdateInput) => {
    setWorking("media-update");
    setError(null);
    try {
      const result = await api.updateMedia(id, payload);
      setMediaItems((current) =>
        current.map((item) =>
          item.id === result.item.id ? result.item : item,
        ),
      );
      setNotice("Media details saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save media.");
    } finally {
      setWorking(null);
    }
  };

  const deleteMediaItem = async (item: MediaItem) => {
    if (!window.confirm(`Delete ${item.filename}?`)) return;
    setWorking("media-delete");
    setError(null);
    try {
      await api.deleteMedia(item.id);
      setMediaItems((current) =>
        current.filter((candidate) => candidate.id !== item.id),
      );
      setMediaStorage((current) =>
        current
          ? {
              ...current,
              bytesUsed: Math.max(0, current.bytesUsed - item.sizeBytes),
            }
          : current,
      );
      setNotice("Media deleted.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete media.");
    } finally {
      setWorking(null);
    }
  };

  const updateImportedCmsEntry = async (
    id: string,
    payload: Partial<Pick<ImportedCmsEntry, "data" | "name" | "slug">>,
  ) => {
    setWorking("imported-cms");
    setError(null);
    try {
      const result = await api.updateImportedCmsEntry(id, payload);
      setImportedCms((current) =>
        current
          ? {
              ...current,
              collections: current.collections.map((collection) =>
                collection.id === result.entry.collectionId
                  ? {
                      ...collection,
                      entries: collection.entries.map((entry) =>
                        entry.id === result.entry.id ? result.entry : entry,
                      ),
                    }
                  : collection,
              ),
            }
          : current,
      );
      setNotice("Imported CMS entry saved.");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save imported CMS entry.",
      );
    } finally {
      setWorking(null);
    }
  };

  const connectSitePage = async (pageId: string) => {
    if (!draft) return;
    const sourcePages = pages.map((page) =>
      page.id === draft.id ? draft : page,
    );
    const targetPage = sourcePages.find((page) => page.id === pageId);
    if (!targetPage || targetPage.id === draft.id) {
      setNotice("Choose another site page to connect.");
      setError(null);
      return;
    }

    const nextPages = connectCreatedPageToSite(
      sourcePages,
      targetPage.id,
      draft.id,
      hostname,
    );
    const changedPages = nextPages.filter((page, index) => {
      const current = sourcePages[index];
      return current
        ? draftVersionKey(page) !== draftVersionKey(current)
        : true;
    });

    if (changedPages.length === 0) {
      setNotice(`${targetPage.title} is already connected to this page.`);
      setError(null);
      return;
    }

    setWorking("site-links");
    setError(null);
    try {
      const savedPages = await Promise.all(
        changedPages.map(async (page) => {
          const result = await api.updatePage(page.id, {
            blocks: reorder(page.blocks),
          });
          return result.page;
        }),
      );
      const savedById = new Map(savedPages.map((page) => [page.id, page]));
      const mergedPages = nextPages.map(
        (page) => savedById.get(page.id) ?? page,
      );
      setPages(mergedPages);
      const message = `Connected ${targetPage.title} to this page.`;
      const savedDraft = savedById.get(draft.id);
      if (savedDraft) {
        replaceDraft(savedDraft, message, { resetHistory: false });
      } else {
        setNotice(message);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not connect site page.",
      );
    } finally {
      setWorking(null);
    }
  };

  const syncBackLinks = async (draftOverride?: EditorPage) => {
    const sourceDraft = draftOverride ?? draft;
    if (!sourceDraft) return;
    const sourcePages = pages.map((page) =>
      page.id === sourceDraft.id ? sourceDraft : page,
    );
    const targetPagesById = new Map<string, EditorPage>();
    collectLinks(sourceDraft.blocks, sourcePages, hostname).forEach((link) => {
      if (link.targetPage && link.targetPage.id !== sourceDraft.id) {
        targetPagesById.set(link.targetPage.id, link.targetPage);
      }
    });

    if (targetPagesById.size === 0) {
      setNotice("This page does not link to other site pages yet.");
      setError(null);
      return;
    }

    const changedPages = Array.from(targetPagesById.values())
      .map((targetPage) => {
        const currentTarget =
          sourcePages.find((page) => page.id === targetPage.id) ?? targetPage;
        if (
          pageLinksToPage(currentTarget, sourceDraft, sourcePages, hostname)
        ) {
          return null;
        }
        return addFooterBackLinkForPage(
          currentTarget,
          sourceDraft,
          sourcePages,
          hostname,
        );
      })
      .filter((page): page is EditorPage => page !== null)
      .filter((page) => {
        const current = sourcePages.find(
          (candidate) => candidate.id === page.id,
        );
        return current
          ? draftVersionKey(page) !== draftVersionKey(current)
          : true;
      });

    if (changedPages.length === 0) {
      setNotice("Linked pages already point back here.");
      setError(null);
      return;
    }

    setWorking("back-links");
    setError(null);
    try {
      const savedPages = await Promise.all(
        changedPages.map(async (page) => {
          const result = await api.updatePage(page.id, {
            blocks: reorder(page.blocks),
          });
          return result.page;
        }),
      );
      const savedById = new Map(savedPages.map((page) => [page.id, page]));
      setPages((current) =>
        current.map((page) => savedById.get(page.id) ?? page),
      );
      setNotice(
        `Added return links on ${changedPages.length} linked page${
          changedPages.length === 1 ? "" : "s"
        }.`,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not add return links.",
      );
    } finally {
      setWorking(null);
    }
  };

  const selectPreset = (preset: (typeof THEME_PRESETS)[number]) => {
    if (!theme) return;
    setTheme({
      ...theme,
      name: preset.name,
      colors: {
        ...theme.colors,
        ...preset.colors,
      },
      buttonStyles: {
        ...theme.buttonStyles,
        primaryBg: preset.colors.primary,
        primaryHoverBg: preset.colors.secondary,
        secondaryText: preset.colors.primary,
        secondaryBorder: preset.colors.primary,
      },
    });
  };

  const clearCanvasTaskSurfaces = useCallback(() => {
    setInlineEditingKey(null);
    setQuickInspectorTargetKey(null);
    setIsFocusCollapsed(true);
    setIsChatCollapsed(true);
    setIsBlogCollapsed(true);
    setIsMediaCollapsed(true);
    setIsThemeCollapsed(true);
  }, []);

  const openOverlayPanel = useCallback(
    (panel: OverlayPanelId) => {
      setCanvasPanelsHidden(false);
      clearCanvasTaskSurfaces();
      setIsFocusCollapsed(panel !== "focus");
      setIsChatCollapsed(panel !== "agent");
      setIsBlogCollapsed(panel !== "blog");
      setIsMediaCollapsed(panel !== "media");
      setIsThemeCollapsed(panel !== "theme");
      const scrollOptions = overlayPanelScrollOptions({
        isFullscreen,
        overlayState: "panel-open",
      });
      if (scrollOptions) {
        window.requestAnimationFrame(() => {
          overlayStackRef.current?.scrollIntoView(scrollOptions);
        });
      }
    },
    [clearCanvasTaskSurfaces, isFullscreen, setCanvasPanelsHidden],
  );

  const activeOverlayPanel: OverlayPanelId | null = !isFocusCollapsed
    ? "focus"
    : !isChatCollapsed
      ? "agent"
      : !isBlogCollapsed
        ? "blog"
        : !isMediaCollapsed
          ? "media"
          : !isThemeCollapsed
            ? "theme"
            : null;
  const overlayStackState = editorOverlayStackState({
    activePanel: activeOverlayPanel,
    panelsHidden: arePanelsHidden,
  });

  const toggleOverlayPanel = useCallback(
    (panel: OverlayPanelId) => {
      if (activeOverlayPanel === panel) {
        if (panel === "focus") setIsFocusCollapsed(true);
        if (panel === "agent") setIsChatCollapsed(true);
        if (panel === "blog") setIsBlogCollapsed(true);
        if (panel === "media") setIsMediaCollapsed(true);
        if (panel === "theme") setIsThemeCollapsed(true);
        return;
      }
      openOverlayPanel(panel);
    },
    [activeOverlayPanel, openOverlayPanel],
  );

  const openSelectionPanel = useCallback(() => {
    openOverlayPanel("focus");
  }, [openOverlayPanel]);

  const openSelectionInspector = useCallback(() => {
    if (!canvasSelectionFrame?.target || !selectedElementKey) return;
    setInlineEditingKey(null);
    setQuickInspectorTargetKey(selectedElementKey);
    setCanvasPanelsHidden(true);
  }, [canvasSelectionFrame?.target, selectedElementKey, setCanvasPanelsHidden]);

  const openCanvasRailInspector = useCallback(() => {
    if (
      canvasSelectionFrame?.kind === "element" &&
      canvasSelectionFrame.target
    ) {
      openSelectionInspector();
      return;
    }
    openSelectionPanel();
  }, [canvasSelectionFrame, openSelectionInspector, openSelectionPanel]);

  const openSelectionAgent = useCallback(() => {
    if (!draft) return;
    openOverlayPanel("agent");
  }, [draft, openOverlayPanel]);

  const openThemePanel = useCallback(() => {
    openOverlayPanel("theme");
  }, [openOverlayPanel]);

  const openBlogPanel = useCallback(() => {
    if (!draft) return;
    if (!blogDraft) startNewBlogPost();
    openOverlayPanel("blog");
  }, [blogDraft, draft, openOverlayPanel, startNewBlogPost]);

  const openMediaPanel = useCallback(() => {
    if (!draft) return;
    openOverlayPanel("media");
  }, [draft, openOverlayPanel]);

  const changeCanvasMode = useCallback(
    (nextMode: CanvasMode) => {
      setCanvasMode(nextMode);
      if (nextMode !== "browse") return;
      setCanvasPanelsHidden(true);
      setQuickInspectorTargetKey(null);
      setInlineEditingKey(null);
      setHoveredBlockId(null);
      setHoveredElementFrame(null);
    },
    [setCanvasPanelsHidden],
  );

  const selectCanvasBlock = (blockId: string) => {
    if (
      canvasSelectionSuppressed(
        suppressCanvasSelectionUntilRef.current,
        Date.now(),
      )
    ) {
      setHoveredBlockId(null);
      return;
    }
    setCanvasPanelsHidden(true);
    setHoveredBlockId(blockId);
    selectBlock(blockId);
    window.setTimeout(() => measurePreviewBlocks(blockId), 0);
  };

  const selectCanvasElement = useCallback(
    (frame: ElementFrame, shouldOpenEditor = false, shouldScroll = false) => {
      if (
        canvasSelectionSuppressed(
          suppressCanvasSelectionUntilRef.current,
          Date.now(),
        )
      ) {
        setHoveredBlockId(null);
        setHoveredElementFrame(null);
        return;
      }
      setCanvasPanelsHidden(true);
      setHoveredBlockId(frame.blockId);
      setHoveredElementFrame(frame);
      selectBlock(frame.blockId, {
        elementKey: frame.key,
        elementTarget: frame.target,
      });
      if (shouldScroll) scrollCanvasElementIntoView(frame);
      window.setTimeout(() => measurePreviewBlocks(frame.blockId), 0);

      if (!shouldOpenEditor || !frame.target) return;
      setQuickInspectorTargetKey(null);
      if (canInlineEditTarget(frame.target)) {
        setInlineEditingKey(frame.key);
        return;
      }
      setInlineEditingKey(null);
      setQuickInspectorTargetKey(frame.key);
    },
    [
      measurePreviewBlocks,
      scrollCanvasElementIntoView,
      selectBlock,
      setCanvasPanelsHidden,
    ],
  );

  const selectCanvasElementFromPreview = useCallback(
    (frame: ElementFrame) => {
      selectCanvasElement(
        frame,
        previewElementClickShouldOpenEditor({
          hasTarget: Boolean(frame.target),
          isAdvancedMode,
          isAlreadySelected: frame.key === selectedElementKey,
        }),
      );
    },
    [isAdvancedMode, selectCanvasElement, selectedElementKey],
  );

  const openElementFrameEditor = useCallback(
    (frame: ElementFrame) => {
      selectCanvasElement(frame, true, true);
    },
    [selectCanvasElement],
  );

  const openElementFrameInspector = useCallback(
    (frame: ElementFrame) => {
      selectCanvasElement(frame, false, true);
      if (!frame.target) {
        openSelectionPanel();
        return;
      }
      setInlineEditingKey(null);
      setQuickInspectorTargetKey(frame.key);
      setCanvasPanelsHidden(true);
    },
    [openSelectionPanel, selectCanvasElement, setCanvasPanelsHidden],
  );

  const openElementFrameAgent = useCallback(
    (frame: ElementFrame) => {
      if (!draft) return;
      selectCanvasElement(frame, false, true);
      openOverlayPanel("agent");
    },
    [draft, openOverlayPanel, selectCanvasElement],
  );

  const exitClearView = useCallback(() => {
    const snapshot = clearViewSnapshotRef.current;
    clearViewSnapshotRef.current = null;
    setIsClearView(false);
    if (!snapshot) return;
    setCanvasMode(snapshot.canvasMode);
    setCanvasPanelsHidden(snapshot.panelsHidden);
    setIsTargetMapVisible(snapshot.targetMapVisible);
    setIsOutlineVisible(snapshot.outlineVisible);
    setIsStructurePanelOpen(snapshot.structurePanelOpen);
    setIsCanvasChromeHidden(snapshot.chromeHidden);
  }, [setCanvasPanelsHidden]);

  const enterClearView = useCallback(() => {
    if (isClearView) return;
    clearViewSnapshotRef.current = {
      canvasMode,
      chromeHidden: isCanvasChromeHidden,
      outlineVisible: isOutlineVisible,
      panelsHidden: arePanelsHidden,
      structurePanelOpen: isStructurePanelOpen,
      targetMapVisible: isTargetMapVisible,
    };
    setIsClearView(true);
    setCanvasMode("browse");
    setCanvasPanelsHidden(true);
    setIsTargetMapVisible(false);
    setIsOutlineVisible(false);
    setIsStructurePanelOpen(false);
    setIsCanvasChromeHidden(isFullscreen);
    setQuickInspectorTargetKey(null);
    setInlineEditingKey(null);
  }, [
    arePanelsHidden,
    canvasMode,
    isCanvasChromeHidden,
    isClearView,
    isFullscreen,
    isOutlineVisible,
    isStructurePanelOpen,
    isTargetMapVisible,
    setCanvasPanelsHidden,
  ]);

  const setClearViewEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enterClearView();
        return;
      }
      exitClearView();
    },
    [enterClearView, exitClearView],
  );

  const selectParentSection = useCallback(() => {
    if (!selectedBlock) return;
    setCanvasPanelsHidden(true);
    selectBlock(selectedBlock.id);
    window.setTimeout(() => measurePreviewBlocks(selectedBlock.id), 0);
  }, [measurePreviewBlocks, selectBlock, selectedBlock, setCanvasPanelsHidden]);

  const selectAdjacentElement = useCallback(
    (delta: number) => {
      if (navigableElementFrames.length === 0) return;
      const currentIndex =
        selectedElementIndex >= 0 ? selectedElementIndex : delta > 0 ? -1 : 0;
      const nextIndex =
        (currentIndex + delta + navigableElementFrames.length) %
        navigableElementFrames.length;
      const frame = navigableElementFrames[nextIndex];
      if (frame) selectCanvasElement(frame, false, true);
    },
    [navigableElementFrames, selectCanvasElement, selectedElementIndex],
  );

  useEffect(() => {
    selectionShortcutHandlerRef.current = (event: KeyboardEvent) => {
      if (isEditableKeyboardTarget(event.target)) return;
      const action = selectionShortcutAction(event, {
        hasDraft: Boolean(draft),
        hasOpenElementEditor: Boolean(
          inlineEditingKey || quickInspectorTargetKey,
        ),
        hasSelectedElement: Boolean(selectedElementKey),
        hasSelection: Boolean(canvasSelectionFrame),
        isClearView,
        isEditMode: canvasModeRef.current === "edit",
      });
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();

      if (action === "exit-clear-view") {
        exitClearView();
        return;
      }
      if (action === "close-element-editor") {
        setInlineEditingKey(null);
        setQuickInspectorTargetKey(null);
        return;
      }
      if (action === "select-parent") {
        selectParentSection();
        return;
      }
      if (action === "previous-element") {
        selectAdjacentElement(-1);
        return;
      }
      if (action === "next-element") {
        selectAdjacentElement(1);
        return;
      }
      if (action === "ask-agent") {
        openSelectionAgent();
        return;
      }
      if (action === "inspect-selection") {
        if (
          canvasSelectionFrame?.kind === "element" &&
          canvasSelectionFrame.editable
        ) {
          openSelectionInspector();
          return;
        }
        openSelectionPanel();
        return;
      }
      if (canvasSelectionFrame?.kind === "element") {
        if (canvasSelectionFrame.editable) {
          if (canInlineEditSelection) openInlineEditor();
          else openSelectionInspector();
          return;
        }
        openSelectionPanel();
        return;
      }
      openSelectionPanel();
    };
  }, [
    canInlineEditSelection,
    canvasSelectionFrame,
    draft,
    exitClearView,
    inlineEditingKey,
    isClearView,
    openInlineEditor,
    openSelectionAgent,
    openSelectionInspector,
    openSelectionPanel,
    quickInspectorTargetKey,
    selectAdjacentElement,
    selectedElementKey,
    selectParentSection,
  ]);

  useEffect(() => {
    const handleSelectionShortcut = (event: KeyboardEvent) => {
      selectionShortcutHandlerRef.current?.(event);
    };
    window.addEventListener("keydown", handleSelectionShortcut);
    return () => window.removeEventListener("keydown", handleSelectionShortcut);
  }, []);

  const previewIssues = Array.from(
    new Set([
      ...(draft?.warnings ?? []),
      ...previewWarnings,
      ...previewRenderErrors.map((issue) => issue.error),
    ]),
  );
  const siteNavigationStatus = useMemo<PageConnectivityStatus>(() => {
    const statuses = Array.from(pageConnectivity.values()).map(
      (summary) => summary.status,
    );
    if (statuses.includes("warning")) return "warning";
    if (statuses.includes("attention")) return "attention";
    if (statuses.includes("healthy")) return "healthy";
    return "single";
  }, [pageConnectivity]);
  const publishReadiness = draft
    ? publishReadinessChecklist({
        blockCount: draft.blocks.length,
        hasLeadCapture: pagesForAnalysis.some((page) =>
          page.blocks.some((block) => block.blockType === "contactForm"),
        ),
        navigationStatus: siteNavigationStatus,
        pageCount: pagesForAnalysis.length,
        pagesMissingSeo: pagesForAnalysis.filter(
          (page) =>
            !page.seo.metaTitle.trim() || !page.seo.metaDescription.trim(),
        ).length,
        warningCount: previewIssues.length,
      })
    : null;
  const topActionChrome = editorTopActionChrome({
    isAdvancedMode,
    isDraftDirty,
    liveUrl,
  });

  if (loading) {
    return (
      <section className="editor-card">
        <div className="spinner-wrap">
          <div className="spinner" />
        </div>
      </section>
    );
  }

  return (
    <section
      className={[
        "editor-card",
        isAdvancedMode ? "advanced-editor" : "guided-editor",
        isFullscreen ? "visual-fullscreen" : "",
        isClearView ? "canvas-clear-view" : "",
        isFullscreen && isCanvasChromeHidden ? "canvas-chrome-hidden" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="card-head editor-head">
        <div>
          <h2>Edit website</h2>
          <p>
            {isAdvancedMode
              ? "Pages, links, sections, and theme controls are available for deeper site work."
              : "Review the page, click anything you want to change, or open the full-screen editor."}
          </p>
        </div>
        <div
          className={[
            "editor-actions",
            isAdvancedMode ? "advanced-actions" : "guided-actions",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {draft && (
            <span
              className={
                topActionChrome.statusTone === "dirty"
                  ? "draft-status dirty"
                  : "draft-status"
              }
            >
              {isDraftDirty ? "Unsaved changes" : "Saved"}
            </span>
          )}
          {isAdvancedMode ? (
            <>
              {/* Grouped clusters, ONE filled primary (Publish site) — the
                  previous flat row wrapped into two ragged rows of equal-
                  weight buttons. */}
              <div className="action-cluster history-actions">
                <button
                  className="btn-secondary"
                  disabled={
                    !draft || !draftHistoryStatus.canUndo || working !== null
                  }
                  onClick={undoDraft}
                  title="Undo last edit"
                  type="button"
                >
                  Undo
                </button>
                <button
                  className="btn-secondary"
                  disabled={
                    !draft || !draftHistoryStatus.canRedo || working !== null
                  }
                  onClick={redoDraft}
                  title="Redo last edit"
                  type="button"
                >
                  Redo
                </button>
                {draft && (
                  <PageVersionHistory
                    api={api}
                    disabled={working !== null}
                    onRestored={(page, versionId) =>
                      replaceDraft(
                        page,
                        `Restored version #${versionId} as your draft — publish to make it live.`,
                      )
                    }
                    pageId={draft.id}
                  />
                )}
              </div>
              <span aria-hidden="true" className="action-divider" />
              <div className="action-cluster">
                {liveUrl && (
                  <a
                    className="btn-secondary"
                    href={liveUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View live
                  </a>
                )}
                <button
                  aria-pressed={isAdvancedMode}
                  className="btn-secondary active"
                  data-testid="site-editor-full-edit-toggle"
                  disabled={!draft}
                  onClick={() => setFullEditMode(!isAdvancedMode)}
                  type="button"
                >
                  Guided mode
                </button>
                <button
                  className="btn-secondary"
                  disabled={!draft}
                  onClick={
                    isFullscreen ? exitFullscreenCanvas : enterFullscreenCanvas
                  }
                  type="button"
                >
                  {isFullscreen ? "Exit full screen" : "Full screen"}
                </button>
              </div>
              <span aria-hidden="true" className="action-divider" />
              <div className="action-cluster">
                <button
                  className="btn-secondary"
                  disabled={!draft || working !== null}
                  onClick={updatePreview}
                  title="Snapshot the current draft to your preview site (noindex, kept out of search and your live inbox) to test before publishing."
                  type="button"
                >
                  {working === "update-preview"
                    ? "Updating preview..."
                    : "Update preview"}
                </button>
                {hostname &&
                  (hasPreview ? (
                    <a
                      className="btn-secondary"
                      href={previewUrlFor(hostname)}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Open preview
                    </a>
                  ) : (
                    <button
                      className="btn-secondary"
                      disabled
                      title="Run &quot;Update preview&quot; first to create your preview site."
                      type="button"
                    >
                      Open preview
                    </button>
                  ))}
              </div>
              <span aria-hidden="true" className="action-divider" />
              <div className="action-cluster action-cluster--ship">
                <button
                  className="btn-secondary"
                  disabled={!draft || working !== null}
                  onClick={saveDraft}
                  type="button"
                >
                  {working === "save" ? "Saving..." : "Save draft"}
                </button>
                <button
                  className="btn-secondary"
                  disabled={!draft || working !== null}
                  onClick={publish}
                  title="Publish this page and save connected site navigation as drafts."
                  type="button"
                >
                  {working === "publish" ? "Publishing..." : "Publish page"}
                </button>
                <button
                  disabled={!draft || working !== null}
                  onClick={publishSite}
                  title="Publish every page with connected navigation and return links."
                  type="button"
                >
                  {working === "publish-site"
                    ? "Publishing..."
                    : "Publish site"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="editor-primary-actions">
                {topActionChrome.primaryActions.includes("fullscreen") && (
                  <button
                    className="btn-secondary"
                    disabled={!draft}
                    onClick={
                      isFullscreen
                        ? exitFullscreenCanvas
                        : enterFullscreenCanvas
                    }
                    type="button"
                  >
                    {isFullscreen ? "Exit full screen" : "Full screen"}
                  </button>
                )}
                {topActionChrome.primaryActions.includes("publishReview") &&
                  publishReadiness && (
                    <details className="publish-review-menu">
                      <summary>Review & publish</summary>
                      <div className="publish-review-panel">
                        <div className="publish-review-head">
                          <span>{publishReadiness.summaryLabel}</span>
                          <strong>Publish checklist</strong>
                        </div>
                        <ul className="publish-review-list">
                          {publishReadiness.items.map((item) => (
                            <li className={item.status} key={item.id}>
                              <span aria-hidden="true" />
                              <div>
                                <strong>{item.label}</strong>
                                <small>{item.detail}</small>
                              </div>
                            </li>
                          ))}
                        </ul>
                        <button
                          disabled={!draft || working !== null}
                          onClick={publishSite}
                          title="Publish every page with connected navigation and return links."
                          type="button"
                        >
                          {working === "publish-site"
                            ? "Publishing..."
                            : "Publish site"}
                        </button>
                      </div>
                    </details>
                  )}
                {topActionChrome.primaryActions.includes("publishSite") && (
                  <button
                    disabled={!draft || working !== null}
                    onClick={publishSite}
                    title="Publish every page with connected navigation and return links."
                    type="button"
                  >
                    {working === "publish-site"
                      ? "Publishing..."
                      : "Publish site"}
                  </button>
                )}
              </div>
              <details className="editor-more-actions">
                <summary>{topActionChrome.summaryLabel}</summary>
                <div className="editor-more-actions-panel">
                  {topActionChrome.secondaryActions.includes("history") && (
                    <div className="history-actions">
                      <button
                        className="btn-secondary"
                        disabled={
                          !draft ||
                          !draftHistoryStatus.canUndo ||
                          working !== null
                        }
                        onClick={undoDraft}
                        title="Undo last edit"
                        type="button"
                      >
                        Undo
                      </button>
                      <button
                        className="btn-secondary"
                        disabled={
                          !draft ||
                          !draftHistoryStatus.canRedo ||
                          working !== null
                        }
                        onClick={redoDraft}
                        title="Redo last edit"
                        type="button"
                      >
                        Redo
                      </button>
                    </div>
                  )}
                  {liveUrl &&
                    topActionChrome.secondaryActions.includes("viewLive") && (
                      <a
                        className="btn-secondary"
                        href={liveUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View live
                      </a>
                    )}
                  {topActionChrome.secondaryActions.includes(
                    "fullEditToggle",
                  ) && (
                    <button
                      aria-pressed={isAdvancedMode}
                      className="btn-secondary"
                      data-testid="site-editor-full-edit-toggle"
                      disabled={!draft}
                      onClick={() => setFullEditMode(!isAdvancedMode)}
                      type="button"
                    >
                      Full edit
                    </button>
                  )}
                  {topActionChrome.secondaryActions.includes("saveDraft") && (
                    <button
                      className="btn-secondary"
                      disabled={!draft || working !== null}
                      onClick={saveDraft}
                      type="button"
                    >
                      {working === "save" ? "Saving..." : "Save draft"}
                    </button>
                  )}
                  {topActionChrome.secondaryActions.includes(
                    "updatePreview",
                  ) && (
                    <button
                      className="btn-secondary"
                      disabled={!draft || working !== null}
                      onClick={updatePreview}
                      title="Create a private preview of your unpublished changes — safe to test, never shown to visitors or search."
                      type="button"
                    >
                      {working === "update-preview"
                        ? "Updating preview..."
                        : "Update preview"}
                    </button>
                  )}
                  {topActionChrome.secondaryActions.includes("openPreview") &&
                    hostname &&
                    (hasPreview ? (
                      <a
                        className="btn-secondary"
                        href={previewUrlFor(hostname)}
                        rel="noreferrer"
                        target="_blank"
                      >
                        Open preview
                      </a>
                    ) : (
                      <button
                        className="btn-secondary"
                        disabled
                        title="Run &quot;Update preview&quot; first to create your preview site."
                        type="button"
                      >
                        Open preview
                      </button>
                    ))}
                </div>
              </details>
            </>
          )}
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}

      {pages.length === 0 || !draft ? (
        <>
          <p className="muted">
            Import a site first. Groundbeam will create editable page drafts
            from captured pages; managed Astro CMS rows can still be edited here
            after the runner imports them.
          </p>
          <ImportedCmsPanel
            importedCms={importedCms}
            onUpdateEntry={updateImportedCmsEntry}
            working={working}
          />
        </>
      ) : (
        <div
          className={[
            "editor-layout visual-editor-layout",
            !isAdvancedMode ? "guided-layout" : "full-edit-layout",
            !isFullscreen && !isStructurePanelOpen ? "structure-collapsed" : "",
            isPageRailCollapsed ? "pages-collapsed" : "pages-expanded",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <aside
            className={
              isPageRailCollapsed ? "page-list collapsed" : "page-list"
            }
            aria-label="Pages"
          >
            <div className="page-list-head">
              <div>
                <strong>Pages</strong>
                <small>
                  {pages.length > 1
                    ? `${connectedPageCount}/${pages.length} connected`
                    : `${pages.length} draft${pages.length === 1 ? "" : "s"}`}
                </small>
              </div>
              <button
                aria-expanded={!isPageRailCollapsed}
                onClick={() => setIsPageRailCollapsed((current) => !current)}
                title={
                  isPageRailCollapsed
                    ? "Open page manager"
                    : "Collapse page manager"
                }
                type="button"
              >
                {isPageRailCollapsed ? "Pages" : "Collapse"}
              </button>
            </div>
            {pages.map((page, index) => {
              const connectivity = pageConnectivity.get(page.id);
              const status = connectivity?.status ?? "single";
              const otherPageCount = connectivity?.otherPageCount ?? 0;
              const repairState = pageRailConnectionRepairState(
                page.id,
                draft.id,
                pages.length,
                status,
              );
              const title =
                connectivity && pages.length > 1
                  ? `${page.title} (${page.slug}) · ${
                      connectivity.statusLabel
                    } · ${connectivity.navLinkedCount}/${
                      connectivity.otherPageCount
                    } nav, ${connectivity.incomingCount} in, ${
                      connectivity.outgoingCount
                    } out`
                  : `${page.title} (${page.slug})`;

              return (
                <div
                  className={[
                    "page-option-row",
                    page.id === draft.id ? "active" : "",
                    `connectivity-${status}`,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  key={page.id}
                >
                  <button
                    className="page-option"
                    onClick={() => switchPage(page.id)}
                    title={title}
                    type="button"
                  >
                    <b>{index + 1}</b>
                    <span className="page-option-title">{page.title}</span>
                    <small className="page-option-path">
                      {page.slug} · {page.status}
                    </small>
                    {connectivity && pages.length > 1 && (
                      <div className="page-option-link-state">
                        <span className={`page-link-badge ${status}`}>
                          {connectivity.statusLabel}
                        </span>
                        <span
                          title={`In the shared navigation of ${connectivity.navLinkedCount} of ${otherPageCount} other pages`}
                        >
                          <strong>{connectivity.navLinkedCount}</strong>/
                          <strong>{otherPageCount}</strong> nav
                        </span>
                        <span
                          title={`${connectivity.incomingCount} other page${connectivity.incomingCount === 1 ? "" : "s"} link here`}
                        >
                          <strong>{connectivity.incomingCount}</strong> in
                        </span>
                        <span
                          title={`This page links out to ${connectivity.outgoingCount} other page${connectivity.outgoingCount === 1 ? "" : "s"}`}
                        >
                          <strong>{connectivity.outgoingCount}</strong> out
                        </span>
                      </div>
                    )}
                  </button>
                  {repairState !== "hidden" && (
                    <button
                      className="page-option-connect"
                      disabled={working !== null}
                      onClick={() => connectSitePage(page.id)}
                      title={`Connect ${page.title} to ${draft.title}`}
                      type="button"
                    >
                      <span>Connect</span>
                      <span>{repairState}</span>
                    </button>
                  )}
                </div>
              );
            })}
            <form className="new-page-form" onSubmit={createPage}>
              <input
                aria-label="New page title"
                onChange={(event) => setNewPageTitle(event.target.value)}
                placeholder="New page"
                value={newPageTitle}
              />
              <div className="new-page-meta">
                <input
                  aria-label="New page slug"
                  onChange={(event) => setNewPageSlug(event.target.value)}
                  placeholder={newPageSlugPlaceholder}
                  value={newPageSlug}
                />
                <button
                  disabled={working !== null || !newPageTitle.trim()}
                  type="submit"
                >
                  {working === "create-page"
                    ? "Adding"
                    : shouldLinkNewPage
                      ? "Add + connect"
                      : "Add"}
                </button>
              </div>
              <label className="new-page-toggle">
                <input
                  checked={shouldLinkNewPage}
                  onChange={(event) =>
                    setShouldLinkNewPage(event.target.checked)
                  }
                  type="checkbox"
                />
                <span>
                  <strong>Connect site</strong>
                  <small>{newPageConnectionLabel}</small>
                </span>
              </label>
            </form>
          </aside>

          <div className="editor-main" hidden={!isStructurePanelOpen}>
            <div className="editor-panel">
              <div className="editor-panel-head">
                <div>
                  <h3>Page</h3>
                  <p className="muted small">
                    {draft.renderMode === "mixed"
                      ? "Captured mirror with editable draft"
                      : draft.renderMode.replace("_", " ")}
                  </p>
                </div>
                {draft.hasCapturedSnapshot && (
                  <button
                    className="btn-secondary"
                    disabled={working !== null}
                    onClick={rebuild}
                    type="button"
                  >
                    Rebuild from mirror
                  </button>
                )}
              </div>

              <div className="field-grid">
                <label>
                  Page title
                  <input
                    value={draft.title}
                    onChange={(event) =>
                      updateDraft((current) => ({
                        ...current,
                        title: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  SEO title
                  <input
                    value={draft.seo.metaTitle}
                    onChange={(event) =>
                      updateDraft((current) => ({
                        ...current,
                        seo: {
                          ...current.seo,
                          metaTitle: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label className="span-2">
                  SEO description
                  <textarea
                    rows={3}
                    value={draft.seo.metaDescription}
                    onChange={(event) =>
                      updateDraft((current) => ({
                        ...current,
                        seo: {
                          ...current.seo,
                          metaDescription: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
              </div>
              <SiteLinkPanel
                currentPage={draft}
                inboundLinks={inboundLinks}
                isBackLinking={working === "back-links"}
                isPublishingSite={working === "publish-site"}
                isSyncing={working === "site-links"}
                onAddHeaderLink={(page) => addHeaderLink(page)}
                onAddFooterLink={(page) => addFooterLink(page)}
                onNavigate={(pageId) => switchPage(pageId)}
                onPatchLinkTarget={(link, href) => patchLinkTarget(link, href)}
                onPublishSite={publishSite}
                onSyncBackLinks={syncBackLinks}
                onSyncSiteNavigation={syncSiteNavigation}
                outgoingLinks={outgoingLinks}
                hostname={hostname}
                pages={pages}
              />
            </div>

            {previewIssues.length > 0 && (
              <div className="editor-panel warning-panel">
                {previewIssues.map((issue) => (
                  <p className="warning" key={issue}>
                    {issue}
                  </p>
                ))}
              </div>
            )}

            {isAdvancedMode && (
              <>
                <MediaLibraryPanel
                  items={mediaItems}
                  onDelete={deleteMediaItem}
                  onUpdate={updateMediaItem}
                  onUpload={uploadMedia}
                  storage={mediaStorage}
                  working={working}
                />
                <ContentLibraryPanel
                  draft={blogDraft}
                  mediaOptions={mediaOptions}
                  onDeletePost={deleteBlogPost}
                  onDraftChange={updateBlogDraft}
                  onNewPost={startNewBlogPost}
                  onSavePost={saveBlogPost}
                  onSelectPost={selectBlogPost}
                  posts={blogPosts}
                  selectedPostId={selectedBlogPostId}
                  working={working}
                />
                <ImportedCmsPanel
                  importedCms={importedCms}
                  onUpdateEntry={updateImportedCmsEntry}
                  working={working}
                />
              </>
            )}

            <div className="block-list">
              {draft.blocks.map((block, index) => (
                <BlockCard
                  block={block}
                  canMoveDown={index < draft.blocks.length - 1}
                  canMoveUp={index > 0}
                  hostname={hostname}
                  isSelected={block.id === selectedBlock?.id}
                  key={block.id}
                  mediaOptions={mediaOptions}
                  onChange={(data) => updateBlock(block.id, data)}
                  onDuplicate={() => duplicateBlock(block.id)}
                  onFocus={() => selectBlock(block.id, { scroll: true })}
                  onMoveDown={() => moveBlock(block.id, 1)}
                  onMoveUp={() => moveBlock(block.id, -1)}
                  onPatch={(patch) => patchBlock(block.id, patch)}
                  onRemove={() => removeBlock(block.id)}
                  pages={pages}
                />
              ))}
            </div>
          </div>

          <aside className="visual-panel">
            <section className="editor-panel preview-panel">
              <div className="editor-panel-head">
                <div>
                  <h3>Visual editor</h3>
                  <p className="muted small">
                    {previewLoading ? "Rendering draft" : "Draft preview"}
                  </p>
                </div>
              </div>
              {previewError && <p className="error">{previewError}</p>}
              <div className="preview-frame-wrap">
                <CanvasToolbar
                  currentPage={draft}
                  hostname={hostname}
                  inboundLinks={inboundLinks}
                  isAgentPanelActive={activeOverlayPanel === "agent"}
                  isAdvancedMode={isAdvancedMode}
                  isBackLinking={working === "back-links"}
                  isClearView={isClearView}
                  isFullscreen={isFullscreen}
                  isPublishingSite={working === "publish-site"}
                  isSyncing={working === "site-links"}
                  mode={canvasMode}
                  onAddFooterLink={(page) => addFooterLink(page)}
                  onAddHeaderLink={(page) => addHeaderLink(page)}
                  onAdvancedModeChange={setFullEditMode}
                  onClearViewChange={setClearViewEnabled}
                  onExitFullscreen={exitFullscreenCanvas}
                  onModeChange={changeCanvasMode}
                  onNavigate={(pageId) => switchPage(pageId)}
                  onOpenTaskSurface={clearCanvasTaskSurfaces}
                  onOutlineVisibleChange={setIsOutlineVisible}
                  onPanelsHiddenChange={setCanvasPanelsHidden}
                  onPatchLinkTarget={(link, href) =>
                    patchLinkTarget(link, href)
                  }
                  onPublishSite={publishSite}
                  onRepairNavigation={repairSiteNavigation}
                  onStructurePanelOpenChange={setIsStructurePanelOpen}
                  onTargetMapVisibleChange={setIsTargetMapVisible}
                  onSyncBackLinks={syncBackLinks}
                  onSizeChange={setCanvasSize}
                  onSyncSiteNavigation={syncSiteNavigation}
                  outlineVisible={isAdvancedMode && isOutlineVisible}
                  outgoingLinks={outgoingLinks}
                  panelsHidden={arePanelsHidden}
                  pages={pages}
                  size={canvasSize}
                  structurePanelOpen={isStructurePanelOpen}
                  targetMapVisible={effectiveTargetMapVisible}
                />
                {previewLoading && (
                  <span className="preview-badge">Rendering</span>
                )}
                <div
                  className={[
                    "preview-canvas-shell",
                    `canvas-${canvasSize}`,
                    `canvas-${canvasMode}`,
                    effectiveTargetMapVisible ? "targets-visible" : "",
                    canvasSectionHitTargetsEnabled({
                      canvasMode,
                      focusedElementEditorMode,
                    })
                      ? "section-hit-targets"
                      : "",
                    fieldTargetLabelMode === "tucked"
                      ? "field-labels-tucked"
                      : "",
                    fieldTargetLabelMode === "minimal"
                      ? "field-labels-minimal"
                      : "",
                    arePanelsHidden ? "canvas-panels-hidden" : "",
                    activeOverlayPanel === "agent" ? "agent-panel-active" : "",
                    focusedElementEditorMode ? "element-editor-open" : "",
                    focusedElementEditorMode
                      ? `element-editor-${focusedElementEditorMode}`
                      : "",
                    canvasElementActionFrame ? "element-action-active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {previewHtml ? (
                    <iframe
                      onLoad={handlePreviewLoad}
                      ref={previewFrameRef}
                      sandbox="allow-forms allow-same-origin"
                      srcDoc={previewHtml}
                      title="Draft preview"
                    />
                  ) : (
                    <div className="preview-empty" />
                  )}
                  <div className="preview-selection-layer">
                    {blockFrames.map((frame) => {
                      const block = draft.blocks.find(
                        (candidate) => candidate.id === frame.id,
                      );
                      const label = block
                        ? blockLabel(block.blockType)
                        : "Block";
                      const isSelected = frame.id === selectedBlock?.id;
                      const isHovered = frame.id === hoveredBlockId;
                      return (
                        <button
                          className={[
                            "preview-block-outline",
                            isSelected ? "selected" : "",
                            isHovered ? "hovered" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          aria-label={`Select ${label} page area`}
                          key={frame.id}
                          onBlur={() =>
                            setHoveredBlockId((current) =>
                              current === frame.id ? null : current,
                            )
                          }
                          onClick={() => selectCanvasBlock(frame.id)}
                          onDoubleClick={() => {
                            selectCanvasBlock(frame.id);
                            openSelectionPanel();
                          }}
                          onFocus={() => setHoveredBlockId(frame.id)}
                          onMouseEnter={() => setHoveredBlockId(frame.id)}
                          onMouseLeave={() =>
                            setHoveredBlockId((current) =>
                              current === frame.id ? null : current,
                            )
                          }
                          style={{
                            height: frame.height,
                            left: frame.left,
                            top: frame.top,
                            width: frame.width,
                          }}
                          title={`Select ${label} page area`}
                          type="button"
                        >
                          <span
                            aria-hidden="true"
                            className="preview-block-label"
                          >
                            <small>Area</small>
                            <span>{label}</span>
                          </span>
                        </button>
                      );
                    })}
                    {overlayElementFrames.map((frame) => (
                      <button
                        className={[
                          "preview-element-outline",
                          frame.target ? "editable" : "inspect",
                          frame.key === selectedElementKey ? "selected" : "",
                          frame.key === hoveredElementFrame?.key
                            ? "hovered"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-label={`${elementFrameActionLabel(frame)} ${frame.sectionLabel} ${frame.label}`}
                        key={frame.key}
                        onBlur={() =>
                          scheduleHoveredElementFrameClear(frame.key)
                        }
                        onClick={() => selectCanvasElementFromPreview(frame)}
                        onDoubleClick={() => selectCanvasElement(frame, true)}
                        onFocus={() => keepHoveredElementFrame(frame)}
                        onMouseEnter={() => keepHoveredElementFrame(frame)}
                        onMouseLeave={() =>
                          scheduleHoveredElementFrameClear(frame.key)
                        }
                        style={{
                          height: frame.height,
                          left: frame.left,
                          top: frame.top,
                          width: frame.width,
                        }}
                        title={`${elementFrameActionLabel(frame)} ${frame.sectionLabel} / ${frame.label}`}
                        type="button"
                      >
                        <span className="preview-element-action">
                          {previewElementActionBadgeLabel({
                            baseLabel: elementFrameActionLabel(frame),
                            editorMode: focusedElementEditorMode,
                            frameKey: frame.key,
                            selectedElementKey,
                          })}
                        </span>
                        <span
                          aria-hidden="true"
                          className="preview-selection-handles"
                        >
                          <i />
                          <i />
                          <i />
                          <i />
                        </span>
                        <span className="preview-element-label">
                          <small>
                            {frame.sectionLabel} /{" "}
                            {elementFrameActionLabel(frame)}
                          </small>
                          <span>{frame.label}</span>
                        </span>
                      </button>
                    ))}
                    {hoveredElementFrame &&
                      !overlayElementFrames.some(
                        (frame) => frame.key === hoveredElementFrame.key,
                      ) && (
                        <button
                          className={[
                            "preview-element-outline",
                            hoveredElementFrame.target ? "editable" : "inspect",
                            "hovered",
                          ].join(" ")}
                          aria-label={`${elementFrameActionLabel(
                            hoveredElementFrame,
                          )} ${hoveredElementFrame.sectionLabel} ${
                            hoveredElementFrame.label
                          }`}
                          onBlur={() =>
                            scheduleHoveredElementFrameClear(
                              hoveredElementFrame.key,
                            )
                          }
                          onClick={() =>
                            selectCanvasElementFromPreview(hoveredElementFrame)
                          }
                          onDoubleClick={() =>
                            selectCanvasElement(hoveredElementFrame, true)
                          }
                          onFocus={() =>
                            keepHoveredElementFrame(hoveredElementFrame)
                          }
                          onMouseEnter={() =>
                            keepHoveredElementFrame(hoveredElementFrame)
                          }
                          onMouseLeave={() =>
                            scheduleHoveredElementFrameClear(
                              hoveredElementFrame.key,
                            )
                          }
                          style={{
                            height: hoveredElementFrame.height,
                            left: hoveredElementFrame.left,
                            top: hoveredElementFrame.top,
                            width: hoveredElementFrame.width,
                          }}
                          title={`${elementFrameActionLabel(
                            hoveredElementFrame,
                          )} ${hoveredElementFrame.sectionLabel} / ${
                            hoveredElementFrame.label
                          }`}
                          type="button"
                        >
                          <span className="preview-element-action">
                            {previewElementActionBadgeLabel({
                              baseLabel:
                                elementFrameActionLabel(hoveredElementFrame),
                              editorMode: focusedElementEditorMode,
                              frameKey: hoveredElementFrame.key,
                              selectedElementKey,
                            })}
                          </span>
                          <span
                            aria-hidden="true"
                            className="preview-selection-handles"
                          >
                            <i />
                            <i />
                            <i />
                            <i />
                          </span>
                          <span className="preview-element-label">
                            <small>
                              {hoveredElementFrame.sectionLabel} /{" "}
                              {elementFrameActionLabel(hoveredElementFrame)}
                            </small>
                            <span>{hoveredElementFrame.label}</span>
                          </span>
                        </button>
                      )}
                    {canvasMode === "edit" &&
                      canvasSelectionFrame &&
                      !arePanelsHidden &&
                      !activeOverlayPanel &&
                      selectedBlock && (
                        <CanvasSelectionPath
                          blockName={blockLabel(selectedBlock.blockType)}
                          canInlineEdit={canInlineEditSelection}
                          elementCount={navigableElementFrames.length}
                          elementIndex={selectedElementIndex}
                          frame={canvasSelectionFrame}
                          hasPrimaryElementActions={hasFocusedElementActions}
                          isInlineEditorOpen={Boolean(
                            selectedElementKey &&
                              inlineEditingKey === selectedElementKey,
                          )}
                          isQuickInspectorOpen={Boolean(
                            selectedElementKey &&
                              quickInspectorTargetKey === selectedElementKey,
                          )}
                          linkTarget={selectedLinkTarget}
                          onAskAi={openSelectionAgent}
                          onInlineEdit={openInlineEditor}
                          onNextElement={() => selectAdjacentElement(1)}
                          onOpenInspector={openSelectionInspector}
                          onPrevElement={() => selectAdjacentElement(-1)}
                          onSelectSection={selectParentSection}
                          pageTitle={draft.title}
                        />
                      )}
                    {canvasSiteFlowStripVisible({
                      arePanelsHidden,
                      canvasMode,
                      connectivityStatus:
                        pageConnectivity.get(draft.id)?.status ?? "single",
                      focusedElementEditorMode,
                      hasOverlayPanelOpen: Boolean(activeOverlayPanel),
                      isAdvancedMode,
                      isFullscreenChromeHidden:
                        isFullscreen && isCanvasChromeHidden,
                      outlineVisible: isOutlineVisible,
                      targetMapVisible: effectiveTargetMapVisible,
                    }) && (
                      <CanvasSiteFlowStrip
                        currentPage={draft}
                        hostname={hostname}
                        inboundLinks={inboundLinks}
                        isBackLinking={working === "back-links"}
                        isAdvancedMode={isAdvancedMode}
                        isCompact={arePanelsHidden}
                        isSyncing={working === "site-links"}
                        onConnectPage={connectSitePage}
                        onOpenLinkReview={() => setFullEditMode(true)}
                        onRepairNavigation={repairSiteNavigation}
                        onSyncBackLinks={syncBackLinks}
                        onSyncSiteNavigation={syncSiteNavigation}
                        outgoingLinks={outgoingLinks}
                        pages={pages}
                      />
                    )}
                    {canvasMode === "edit" &&
                      canvasSelectionFrame &&
                      !isFullscreen &&
                      !arePanelsHidden &&
                      selectionChromeSurface === "toolbar" && (
                        <CanvasSelectionToolbar
                          canInlineEdit={canInlineEditSelection}
                          canMoveDown={
                            selectedBlockIndex >= 0 &&
                            selectedBlockIndex < draft.blocks.length - 1
                          }
                          canMoveUp={selectedBlockIndex > 0}
                          frame={canvasSelectionFrame}
                          isQuickInspectorOpen={Boolean(
                            selectedElementKey &&
                              quickInspectorTargetKey === selectedElementKey,
                          )}
                          isInlineEditorOpen={Boolean(
                            selectedElementKey &&
                              inlineEditingKey === selectedElementKey,
                          )}
                          linkTarget={selectedLinkTarget}
                          onAskAi={openSelectionAgent}
                          onDuplicate={() =>
                            selectedBlock &&
                            duplicateBlock(selectedBlock.id, {
                              hidePanels: true,
                            })
                          }
                          onInlineEdit={openInlineEditor}
                          onHidePanels={() => setCanvasPanelsHidden(true)}
                          onOpenInspector={openSelectionInspector}
                          onMoveDown={() =>
                            selectedBlock && moveBlock(selectedBlock.id, 1)
                          }
                          onMoveUp={() =>
                            selectedBlock && moveBlock(selectedBlock.id, -1)
                          }
                          onOpenPanel={openSelectionPanel}
                          itemActions={selectedItemActions}
                          onSectionVariantChange={(variant) =>
                            selectedBlock &&
                            patchBlock(selectedBlock.id, { variant })
                          }
                          sectionVariant={selectedBlock?.variant ?? ""}
                          sectionVariants={selectedBlockVariants}
                          showMoveControls={
                            canvasSelectionFrame.kind === "block"
                          }
                        />
                      )}
                    {canvasMode === "edit" &&
                      canvasSelectionFrame &&
                      arePanelsHidden &&
                      selectionChromeSurface === "status" &&
                      (canvasSelectionFrame.kind === "block" ||
                        !activeElementTarget) && (
                        <CanvasSelectionStatus
                          canInlineEdit={canInlineEditSelection}
                          canMoveDown={
                            selectedBlockIndex >= 0 &&
                            selectedBlockIndex < draft.blocks.length - 1
                          }
                          canMoveUp={selectedBlockIndex > 0}
                          elementCount={navigableElementFrames.length}
                          elementIndex={selectedElementIndex}
                          frame={canvasSelectionFrame}
                          isInlineEditorOpen={Boolean(
                            selectedElementKey &&
                              inlineEditingKey === selectedElementKey,
                          )}
                          isQuickInspectorOpen={Boolean(
                            selectedElementKey &&
                              quickInspectorTargetKey === selectedElementKey,
                          )}
                          linkTarget={selectedLinkTarget}
                          onAskAi={openSelectionAgent}
                          onDuplicate={() =>
                            selectedBlock &&
                            duplicateBlock(selectedBlock.id, {
                              hidePanels: true,
                            })
                          }
                          onInsertSection={insertSection}
                          onInlineEdit={openInlineEditor}
                          onMoveDown={() =>
                            selectedBlock && moveBlock(selectedBlock.id, 1)
                          }
                          onMoveUp={() =>
                            selectedBlock && moveBlock(selectedBlock.id, -1)
                          }
                          onNextElement={() => selectAdjacentElement(1)}
                          onOpenInspector={openSelectionInspector}
                          onOpenPanel={openSelectionPanel}
                          onPrevElement={() => selectAdjacentElement(-1)}
                          onSectionVariantChange={(variant) =>
                            selectedBlock &&
                            patchBlock(selectedBlock.id, { variant })
                          }
                          onSelectSection={selectParentSection}
                          position={selectionHudPosition}
                          itemActions={selectedItemActions}
                          sectionTemplates={SECTION_TEMPLATES}
                          sectionVariant={selectedBlock?.variant ?? ""}
                          sectionVariants={selectedBlockVariants}
                        />
                      )}
                    {canvasActionRailVisible({
                      arePanelsHidden,
                      canvasMode,
                      focusedElementEditorMode,
                      hasSelection: Boolean(canvasSelectionFrame),
                      isAdvancedMode,
                      outlineVisible: isOutlineVisible,
                      targetMapVisible: effectiveTargetMapVisible,
                    }) && (
                      <CanvasActionRail
                        hasSelection={Boolean(canvasSelectionFrame)}
                        onOpenAgent={openSelectionAgent}
                        onOpenInspector={openCanvasRailInspector}
                        onOpenTheme={openThemePanel}
                        onToggleOutline={() =>
                          setIsOutlineVisible((current) => !current)
                        }
                        onToggleTargets={() =>
                          setIsTargetMapVisible((current) => !current)
                        }
                        outlineVisible={isOutlineVisible}
                        inspectorLabel={canvasRailInspectorLabel(
                          canvasSelectionFrame,
                        )}
                        selectedLabel={canvasSelectionFrame?.label ?? null}
                        targetMapVisible={effectiveTargetMapVisible}
                      />
                    )}
                    {canvasMode === "edit" &&
                      !arePanelsHidden &&
                      canvasSelectionFrame?.kind === "block" && (
                        <CanvasSectionInsertHandle
                          frame={canvasSelectionFrame}
                          onInsertSection={insertSection}
                          sectionTemplates={SECTION_TEMPLATES}
                        />
                      )}
                    {canvasMode === "edit" &&
                      isAdvancedMode &&
                      selectedBlock &&
                      navigableElementFrames.length > 0 &&
                      !focusedElementEditorMode && (
                        <CanvasElementNavigator
                          blocks={draft.blocks}
                          blockName={
                            effectiveTargetMapVisible
                              ? "Page targets"
                              : blockLabel(selectedBlock.blockType)
                          }
                          collapsed={isElementNavigatorCollapsed}
                          elementFrames={navigableElementFrames}
                          hoveredElementKey={hoveredElementFrame?.key ?? null}
                          onCollapsedChange={setIsElementNavigatorCollapsed}
                          onHoverElement={setHoveredElementFrame}
                          onSelectElement={selectCanvasElement}
                          selectedElementKey={selectedElementKey}
                        />
                      )}
                    {canvasMode === "edit" &&
                      canvasSelectionFrame &&
                      isFullscreen &&
                      selectionChromeSurface === "dock" && (
                        <CanvasSelectionDock
                          canInlineEdit={canInlineEditSelection}
                          elementCount={navigableElementFrames.length}
                          elementIndex={selectedElementIndex}
                          frame={canvasSelectionFrame}
                          isQuickInspectorOpen={Boolean(
                            selectedElementKey &&
                              quickInspectorTargetKey === selectedElementKey,
                          )}
                          isInlineEditorOpen={Boolean(
                            selectedElementKey &&
                              inlineEditingKey === selectedElementKey,
                          )}
                          linkTarget={selectedLinkTarget}
                          onAskAi={openSelectionAgent}
                          onDuplicate={() =>
                            selectedBlock &&
                            duplicateBlock(selectedBlock.id, {
                              hidePanels: true,
                            })
                          }
                          onInsertSection={insertSection}
                          onInlineEdit={openInlineEditor}
                          onNextElement={() => selectAdjacentElement(1)}
                          onOpenInspector={openSelectionInspector}
                          onOpenPanel={openSelectionPanel}
                          onPanelsHiddenChange={setCanvasPanelsHidden}
                          onPrevElement={() => selectAdjacentElement(-1)}
                          itemActions={selectedItemActions}
                          onSectionVariantChange={(variant) =>
                            selectedBlock &&
                            patchBlock(selectedBlock.id, { variant })
                          }
                          onSelectSection={selectParentSection}
                          pageTitle={draft.title}
                          panelsHidden={arePanelsHidden}
                          sectionPosition={
                            selectedBlockIndex >= 0
                              ? selectedBlockIndex + 1
                              : null
                          }
                          sectionTemplates={SECTION_TEMPLATES}
                          sectionVariant={selectedBlock?.variant ?? ""}
                          sectionVariants={selectedBlockVariants}
                          showAdvancedActions={
                            selectionDockActionGroups.showAdvancedActions
                          }
                          totalSections={draft.blocks.length}
                        />
                      )}
                    {canvasMode === "edit" &&
                      isAdvancedMode &&
                      isOutlineVisible && (
                        <CanvasOutlinePanel
                          blocks={draft.blocks}
                          elementFrames={elementFrames}
                          hoveredBlockId={hoveredBlockId}
                          onClose={() => setIsOutlineVisible(false)}
                          onHoverBlock={setHoveredBlockId}
                          onHoverElement={setHoveredElementFrame}
                          onSelectBlock={(blockId) => {
                            setCanvasPanelsHidden(true);
                            selectBlock(blockId, { scroll: true });
                          }}
                          onSelectElement={(frame) => {
                            setCanvasPanelsHidden(true);
                            selectCanvasElement(frame, false, true);
                            if (frame.target) {
                              setInlineEditingKey(null);
                              setQuickInspectorTargetKey(frame.key);
                            }
                          }}
                          selectedBlockId={selectedBlock?.id ?? null}
                          selectedElementKey={selectedElementKey}
                        />
                      )}
                    {canvasMode === "edit" &&
                      canvasElementActionFrame &&
                      canvasElementActionFrame.target && (
                        <CanvasElementActionPill
                          canInlineEdit={canInlineEditTarget(
                            canvasElementActionFrame.target,
                          )}
                          elementCount={navigableElementFrames.length}
                          elementIndex={canvasElementActionIndex}
                          frame={canvasElementActionFrame}
                          isAdvancedMode={isAdvancedMode}
                          isSelected={
                            canvasElementActionFrame.key === selectedElementKey
                          }
                          itemActions={
                            canvasElementItemActionsVisible({
                              actionFrameKey: canvasElementActionFrame.key,
                              hasItemActions: Boolean(selectedItemActions),
                              selectedElementKey,
                            })
                              ? selectedItemActions
                              : null
                          }
                          linkTarget={canvasElementActionLinkTarget}
                          mode={
                            canvasElementActionBarVisibility === "hovered"
                              ? "hovered"
                              : "selected"
                          }
                          onAskAi={() =>
                            openElementFrameAgent(canvasElementActionFrame)
                          }
                          onInlineEdit={() =>
                            openElementFrameEditor(canvasElementActionFrame)
                          }
                          onOpenLinkTarget={() => {
                            if (canvasElementActionLinkTarget?.targetPageId) {
                              switchPage(
                                canvasElementActionLinkTarget.targetPageId,
                              );
                              setNotice(
                                `Opened ${canvasElementActionLinkTarget.label}.`,
                              );
                              setError(null);
                              return;
                            }
                            openElementFrameInspector(canvasElementActionFrame);
                          }}
                          onKeepHover={() =>
                            keepHoveredElementFrame(canvasElementActionFrame)
                          }
                          onNextElement={() => selectAdjacentElement(1)}
                          onOpenInspector={() =>
                            openElementFrameInspector(canvasElementActionFrame)
                          }
                          onPrevElement={() => selectAdjacentElement(-1)}
                          onSelectElement={() =>
                            selectCanvasElement(
                              canvasElementActionFrame,
                              false,
                              true,
                            )
                          }
                          onSelectSection={selectParentSection}
                          onStopHover={() =>
                            scheduleHoveredElementFrameClear(
                              canvasElementActionFrame.key,
                            )
                          }
                          target={canvasElementActionFrame.target}
                          valuePreview={canvasElementActionValuePreview}
                          viewport={previewViewport}
                        />
                      )}
                    {canvasMode === "edit" &&
                      selectedBlock &&
                      selectedElement &&
                      activeElementTarget &&
                      selectedElementKey &&
                      inlineEditingKey === selectedElementKey &&
                      canInlineEditSelection && (
                        <CanvasInlineEditor
                          block={selectedBlock}
                          currentPage={draft}
                          frame={selectedElement}
                          hostname={hostname}
                          isBackLinking={working === "back-links"}
                          key={`${selectedElementKey}:${activeElementTarget.path}:inline`}
                          onCancel={closeInlineEditor}
                          onCommit={(data, opts) => {
                            const nextDraft = {
                              ...draft,
                              blocks: draft.blocks.map((block) =>
                                block.id === selectedBlock.id
                                  ? { ...block, data }
                                  : block,
                              ),
                            };
                            applyDraftChange(nextDraft);
                            closeInlineEditor();
                            if (opts?.syncBackLinks) {
                              void syncBackLinks(nextDraft);
                            }
                          }}
                          pages={pages}
                          target={activeElementTarget}
                          viewport={previewViewport}
                        />
                      )}
                    {canvasMode === "edit" &&
                      selectedBlock &&
                      selectedElement &&
                      activeElementTarget &&
                      selectedElementKey &&
                      quickInspectorTargetKey === selectedElementKey && (
                        <CanvasQuickInspector
                          block={selectedBlock}
                          currentPage={draft}
                          frame={selectedElement}
                          hostname={hostname}
                          isBackLinking={working === "back-links"}
                          key={`${selectedElementKey}:${activeElementTarget.path}:quick`}
                          onChange={(data) =>
                            updateBlock(selectedBlock.id, data)
                          }
                          onClose={() => setQuickInspectorTargetKey(null)}
                          onSyncBackLinks={syncBackLinks}
                          pages={pages}
                          target={activeElementTarget}
                          viewport={previewViewport}
                        />
                      )}
                  </div>
                </div>
              </div>
            </section>

            <div
              aria-hidden={overlayStackState === "hidden" ? true : undefined}
              className={visualOverlayStackClassName({
                activePanel: activeOverlayPanel,
                overlayState: overlayStackState,
              })}
              ref={overlayStackRef}
            >
              {overlayStackState !== "hidden" && (
                <OverlayToolDock
                  activePanel={activeOverlayPanel}
                  canOpenAgent={Boolean(draft)}
                  canOpenFocus={Boolean(selectedBlock)}
                  onHide={() => setCanvasPanelsHidden(true)}
                  onOpenAgent={openSelectionAgent}
                  onOpenFocus={openSelectionPanel}
                  onOpenTheme={openThemePanel}
                />
              )}
              {overlayStackState !== "hidden" &&
                selectedBlock &&
                activeOverlayPanel === "focus" && (
                  <BlockFocusPanel
                    block={selectedBlock}
                    canMoveDown={
                      selectedBlockIndex >= 0 &&
                      selectedBlockIndex < draft.blocks.length - 1
                    }
                    canMoveUp={selectedBlockIndex > 0}
                    currentPage={draft}
                    hostname={hostname}
                    isBackLinking={working === "back-links"}
                    onChange={(data) => updateBlock(selectedBlock.id, data)}
                    mediaOptions={mediaOptions}
                    onMoveDown={() => moveBlock(selectedBlock.id, 1)}
                    onMoveUp={() => moveBlock(selectedBlock.id, -1)}
                    onDuplicate={() => duplicateBlock(selectedBlock.id)}
                    onInsertSection={insertSection}
                    onPatch={(patch) => patchBlock(selectedBlock.id, patch)}
                    onRemove={() => removeBlock(selectedBlock.id)}
                    onSyncBackLinks={syncBackLinks}
                    isCollapsed={isFocusCollapsed}
                    onToggleCollapse={() => toggleOverlayPanel("focus")}
                    selectedElementLabel={selectedElement?.label ?? null}
                    selectedTarget={activeElementTarget}
                    sectionTemplates={SECTION_TEMPLATES}
                    pages={pages}
                  />
                )}

              {overlayStackState !== "hidden" &&
                activeOverlayPanel === "agent" && (
                  <AgentChatPanel
                    agentDraftSource={agentDraftSource}
                    agentDraftReviewLabel={agentDraftReviewLabel}
                    canUndoDraft={draftHistoryStatus.canUndo}
                    chatMessages={chatMessages}
                    chatPrompt={chatPrompt}
                    hasPendingAgentChange={hasPendingAgentChange}
                    isCollapsed={isChatCollapsed}
                    isDraftDirty={isDraftDirty}
                    onClearChat={clearAgentChat}
                    onOpenMediaPanel={openMediaPanel}
                    onPublishSite={publishSite}
                    onPromptChange={setChatPrompt}
                    onOpenBlogPanel={openBlogPanel}
                    onRunPrompt={runAiEdit}
                    onSaveDraft={saveDraft}
                    onOpenThemePanel={openThemePanel}
                    onToggleCollapse={() => toggleOverlayPanel("agent")}
                    onUndoDraft={undoDraft}
                    selection={selectedAiContext}
                    working={working}
                  />
                )}

              {overlayStackState !== "hidden" &&
                activeOverlayPanel === "media" && (
                  <MediaLibraryPanel
                    isCollapsed={isMediaCollapsed}
                    items={mediaItems}
                    onDelete={deleteMediaItem}
                    onToggleCollapse={() => toggleOverlayPanel("media")}
                    onUpdate={updateMediaItem}
                    onUpload={uploadMedia}
                    storage={mediaStorage}
                    working={working}
                  />
                )}

              {overlayStackState !== "hidden" &&
                activeOverlayPanel === "blog" && (
                  <ContentLibraryPanel
                    draft={blogDraft}
                    isCollapsed={isBlogCollapsed}
                    mediaOptions={mediaOptions}
                    onDeletePost={deleteBlogPost}
                    onDraftChange={updateBlogDraft}
                    onNewPost={startNewBlogPost}
                    onSavePost={saveBlogPost}
                    onSelectPost={selectBlogPost}
                    onToggleCollapse={() => toggleOverlayPanel("blog")}
                    posts={blogPosts}
                    selectedPostId={selectedBlogPostId}
                    working={working}
                  />
                )}

              {overlayStackState !== "hidden" &&
                activeOverlayPanel === "theme" && (
                  <section
                    className={
                      isThemeCollapsed
                        ? "theme-panel overlay-panel collapsed"
                        : "theme-panel overlay-panel"
                    }
                  >
                    <div className="overlay-panel-head">
                      <div>
                        <h3>Theme</h3>
                        <p className="muted small">
                          {theme ? theme.name : "Unavailable"}
                        </p>
                      </div>
                      <button
                        aria-expanded={!isThemeCollapsed}
                        className="panel-toggle"
                        onClick={() => toggleOverlayPanel("theme")}
                        type="button"
                      >
                        {isThemeCollapsed ? "Open" : "Collapse"}
                      </button>
                    </div>
                    {theme ? (
                      <div className="overlay-panel-body">
                        <div className="preset-row">
                          {THEME_PRESETS.map((preset) => (
                            <button
                              className="preset"
                              aria-pressed={theme.name === preset.name}
                              key={preset.id}
                              onClick={() => selectPreset(preset)}
                              type="button"
                            >
                              <span
                                style={{
                                  background: `linear-gradient(135deg, ${preset.colors.primary}, ${preset.colors.accent})`,
                                }}
                              />
                              {preset.name}
                            </button>
                          ))}
                        </div>
                        <ColorField
                          label="Primary"
                          value={theme.colors.primary}
                          onChange={(value) =>
                            setTheme({
                              ...theme,
                              colors: { ...theme.colors, primary: value },
                              buttonStyles: {
                                ...theme.buttonStyles,
                                primaryBg: value,
                                secondaryText: value,
                                secondaryBorder: value,
                              },
                            })
                          }
                        />
                        <ColorField
                          label="Accent"
                          value={theme.colors.accent}
                          onChange={(value) =>
                            setTheme({
                              ...theme,
                              colors: { ...theme.colors, accent: value },
                            })
                          }
                        />
                        <ColorField
                          label="Background"
                          value={theme.colors.background}
                          onChange={(value) =>
                            setTheme({
                              ...theme,
                              colors: { ...theme.colors, background: value },
                            })
                          }
                        />
                        <button
                          className="theme-save"
                          disabled={working !== null}
                          onClick={saveTheme}
                          type="button"
                        >
                          {working === "theme" ? "Saving..." : "Save theme"}
                        </button>
                      </div>
                    ) : (
                      <p className="muted small">Theme unavailable.</p>
                    )}
                  </section>
                )}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}

function ColorField({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  const inputValue = themeColorInputValue(value);
  return (
    <label className={themeColorFieldClassName(label)}>
      <span className="color-field-head">
        <span>
          <strong>{label}</strong>
          <small>{inputValue}</small>
        </span>
        <span className="color-field-swatch-wrap">
          <span
            aria-hidden="true"
            className="color-field-swatch"
            style={{ backgroundColor: inputValue }}
          />
          <input
            aria-label={`${label} color picker`}
            className="color-field-picker"
            type="color"
            value={inputValue}
            onChange={(event) => onChange(event.target.value)}
          />
        </span>
      </span>
      <input
        aria-label={`${label} hex color`}
        className="color-field-value"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function formatMediaBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function mediaLibraryPanelChromeState({
  isOverlay,
  itemCount,
  uploadOpen,
}: {
  isOverlay: boolean;
  itemCount: number;
  uploadOpen?: boolean;
}): {
  emptyLabel: string;
  showInlineUpload: boolean;
  startsUploadOpen: boolean;
  uploadToggleLabel: string;
} {
  const startsUploadOpen = !isOverlay || itemCount === 0;
  const isUploadOpen = uploadOpen ?? startsUploadOpen;
  return {
    emptyLabel: isOverlay
      ? "No images yet. Add one here, then reuse it across the site."
      : "No media uploaded yet.",
    showInlineUpload: !isOverlay || isUploadOpen,
    startsUploadOpen,
    uploadToggleLabel: isUploadOpen ? "Hide upload" : "Add image",
  };
}

function MediaLibraryPanel({
  isCollapsed = false,
  items,
  onDelete,
  onToggleCollapse,
  onUpdate,
  onUpload,
  storage,
  working,
}: {
  isCollapsed?: boolean;
  items: MediaItem[];
  onDelete: (item: MediaItem) => Promise<void>;
  onToggleCollapse?: () => void;
  onUpdate: (id: string, payload: MediaUpdateInput) => Promise<void>;
  onUpload: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  storage: MediaStorage | null;
  working: string | null;
}) {
  const isOverlay = Boolean(onToggleCollapse);
  const initialChrome = mediaLibraryPanelChromeState({
    isOverlay,
    itemCount: items.length,
  });
  const [isUploadOpen, setIsUploadOpen] = useState(
    initialChrome.startsUploadOpen,
  );

  useEffect(() => {
    if (!isOverlay || items.length === 0) setIsUploadOpen(true);
  }, [isOverlay, items.length]);

  const usageLabel = storage
    ? storage.limitBytes === null
      ? `${formatMediaBytes(storage.bytesUsed)} used`
      : `${formatMediaBytes(storage.bytesUsed)} of ${formatMediaBytes(
          storage.limitBytes,
        )}`
    : `${items.length} item${items.length === 1 ? "" : "s"}`;
  const panelChrome = mediaLibraryPanelChromeState({
    isOverlay,
    itemCount: items.length,
    uploadOpen: isUploadOpen,
  });
  const uploadForm = (
    <form className="media-upload-form" onSubmit={onUpload}>
      <input
        accept="image/avif,image/gif,image/jpeg,image/png,image/webp"
        aria-label="Image file"
        name="file"
        type="file"
      />
      <input aria-label="Alt text" name="alt" placeholder="Alt text" />
      <button disabled={working !== null} type="submit">
        {working === "media-upload" ? "Uploading..." : "Upload"}
      </button>
    </form>
  );
  const library = (
    <div className="media-library-list">
      {items.length === 0 ? (
        <p className="content-library-empty">{panelChrome.emptyLabel}</p>
      ) : (
        <div className="media-library-grid">
          {items.map((item) => (
            <MediaLibraryCard
              item={item}
              key={item.id}
              onDelete={onDelete}
              onUpdate={onUpdate}
              working={working}
            />
          ))}
        </div>
      )}
    </div>
  );
  const content = isOverlay ? (
    <>
      {library}
      {panelChrome.showInlineUpload && (
        <div className="media-upload-card">
          <div className="media-upload-card-head">
            <strong>Add image</strong>
            <small>Upload once, then reuse it in sections and posts.</small>
          </div>
          {uploadForm}
        </div>
      )}
    </>
  ) : (
    <>
      {uploadForm}
      {library}
    </>
  );

  return (
    <section
      className={[
        "editor-panel media-library-panel",
        isOverlay ? "overlay-panel" : "",
        isCollapsed ? "collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={isOverlay ? "overlay-panel-head" : "editor-panel-head"}>
        <div>
          <h3>Media</h3>
          <p className="muted small">{usageLabel}</p>
        </div>
        {isOverlay && (
          <div className="overlay-panel-actions">
            {!isCollapsed && (
              <button
                aria-expanded={isUploadOpen}
                className="panel-toggle quiet"
                onClick={() => setIsUploadOpen((current) => !current)}
                type="button"
              >
                {panelChrome.uploadToggleLabel}
              </button>
            )}
            <button
              aria-expanded={!isCollapsed}
              className="panel-toggle"
              onClick={onToggleCollapse}
              type="button"
            >
              {isCollapsed ? "Open" : "Collapse"}
            </button>
          </div>
        )}
      </div>
      {isOverlay ? (
        <div className="overlay-panel-body">{content}</div>
      ) : (
        content
      )}
    </section>
  );
}

function MediaLibraryCard({
  item,
  onDelete,
  onUpdate,
  working,
}: {
  item: MediaItem;
  onDelete: (item: MediaItem) => Promise<void>;
  onUpdate: (id: string, payload: MediaUpdateInput) => Promise<void>;
  working: string | null;
}) {
  const [alt, setAlt] = useState(item.alt ?? "");
  const [filename, setFilename] = useState(item.filename);
  useEffect(() => {
    setAlt(item.alt ?? "");
    setFilename(item.filename);
  }, [item.alt, item.filename]);

  const hasChanges =
    alt.trim() !== (item.alt ?? "") || filename.trim() !== item.filename;
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges) return;
    await onUpdate(item.id, {
      alt: alt.trim() || null,
      filename: filename.trim() || item.filename,
    });
  };

  return (
    <article className="media-library-card">
      <div className="media-library-thumb">
        <img alt={item.alt ?? ""} src={item.url} />
      </div>
      <form className="media-library-fields" onSubmit={save}>
        <label>
          Name
          <input
            value={filename}
            onChange={(event) => setFilename(event.target.value)}
          />
        </label>
        <label>
          Alt text
          <input value={alt} onChange={(event) => setAlt(event.target.value)} />
        </label>
        <p className="muted small">
          {item.mimeType.replace("image/", "").toUpperCase()} ·{" "}
          {formatMediaBytes(item.sizeBytes)}
        </p>
        <div className="media-library-actions">
          <button disabled={working !== null || !hasChanges} type="submit">
            {working === "media-update" ? "Saving..." : "Save"}
          </button>
          <button
            className="btn-secondary danger"
            disabled={working !== null}
            onClick={() => void onDelete(item)}
            type="button"
          >
            {working === "media-delete" ? "Deleting..." : "Delete"}
          </button>
        </div>
      </form>
    </article>
  );
}

function ContentLibraryPanel({
  draft,
  isCollapsed = false,
  mediaOptions,
  onDeletePost,
  onDraftChange,
  onNewPost,
  onSavePost,
  onSelectPost,
  onToggleCollapse,
  posts,
  selectedPostId,
  working,
}: {
  draft: BlogPostEditorDraft | null;
  isCollapsed?: boolean;
  mediaOptions: MediaOption[];
  onDeletePost: () => Promise<void>;
  onDraftChange: (patch: Partial<BlogPostEditorDraft>) => void;
  onNewPost: () => void;
  onSavePost: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onSelectPost: (postId: string) => void;
  onToggleCollapse?: () => void;
  posts: BlogPost[];
  selectedPostId: string | null;
  working: string | null;
}) {
  const isSaving = working === "blog-save";
  const isDeleting = working === "blog-delete";
  const isOverlay = Boolean(onToggleCollapse);
  const initialChrome = contentLibraryPanelChromeState({
    isOverlay,
    postCount: posts.length,
  });
  const [isPostEditorOpen, setIsPostEditorOpen] = useState(
    initialChrome.startsEditorOpen,
  );

  useEffect(() => {
    if (!isOverlay || posts.length === 0) setIsPostEditorOpen(true);
  }, [isOverlay, posts.length]);

  const panelChrome = contentLibraryPanelChromeState({
    editorOpen: isPostEditorOpen,
    isOverlay,
    postCount: posts.length,
  });

  const openNewPost = () => {
    setIsPostEditorOpen(true);
    onNewPost();
  };

  const postEditor = draft ? (
    <form className="content-post-editor" onSubmit={onSavePost}>
      <div className="field-grid">
        <TextField
          label="Title"
          value={draft.title}
          onChange={(value) => onDraftChange({ title: value })}
        />
        <TextField
          label="Slug"
          value={draft.slug}
          onChange={(value) => onDraftChange({ slug: value })}
        />
        <TextAreaField
          label="Excerpt"
          value={draft.excerpt}
          onChange={(value) => onDraftChange({ excerpt: value })}
        />
        <MediaUrlField
          label="Featured image"
          mediaOptions={mediaOptions}
          value={draft.featuredImage}
          onChange={(value) => onDraftChange({ featuredImage: value })}
        />
        <TextField
          label="Author"
          value={draft.author}
          onChange={(value) => onDraftChange({ author: value })}
        />
        <TextField
          label="Tags"
          value={draft.tagsText}
          onChange={(value) => onDraftChange({ tagsText: value })}
        />
        <label>
          Status
          <select
            value={draft.status}
            onChange={(event) =>
              onDraftChange({
                status: event.target.value as BlogPost["status"],
              })
            }
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <TextField
          label="SEO title"
          value={draft.seoMetaTitle}
          onChange={(value) => onDraftChange({ seoMetaTitle: value })}
        />
        <TextAreaField
          label="SEO description"
          value={draft.seoMetaDescription}
          onChange={(value) => onDraftChange({ seoMetaDescription: value })}
        />
        <TextAreaField
          label="Post body"
          rows={10}
          value={draft.contentBody}
          onChange={(value) => onDraftChange({ contentBody: value })}
        />
      </div>
      <div className="content-post-actions">
        <button
          disabled={working !== null || !draft.title.trim()}
          type="submit"
        >
          {isSaving ? "Saving..." : "Save post"}
        </button>
        {draft.id && (
          <button
            className="btn-secondary danger"
            disabled={working !== null}
            onClick={() => void onDeletePost()}
            type="button"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        )}
      </div>
    </form>
  ) : (
    <div className="content-post-editor empty">
      <button onClick={openNewPost} type="button">
        Create first post
      </button>
    </div>
  );

  const postPreview = draft ? (
    <article className="content-post-preview">
      <div className="content-post-preview-head">
        <span className="content-post-status">
          {draft.status === "published" ? "Published" : "Draft"}
        </span>
        <strong>{draft.title.trim() || "Untitled post"}</strong>
        <small>{draft.slug.trim() || "/news/new-post"}</small>
      </div>
      <p>
        {draft.excerpt.trim() ||
          "Add a short summary so visitors know what this update is about."}
      </p>
      <div className="content-post-preview-meta">
        {draft.author.trim() && <span>{draft.author.trim()}</span>}
        {draft.tagsText.trim() && <span>{draft.tagsText.trim()}</span>}
      </div>
      <div className="content-post-preview-actions">
        <button
          disabled={working !== null}
          onClick={() => setIsPostEditorOpen(true)}
          type="button"
        >
          {panelChrome.editToggleLabel}
        </button>
      </div>
    </article>
  ) : (
    <div className="content-post-editor empty">
      <button onClick={openNewPost} type="button">
        Create first post
      </button>
    </div>
  );

  const content = (
    <div
      className={[
        "content-library-grid",
        isOverlay ? "overlay-library" : "",
        panelChrome.showEditor ? "editor-open" : "review-open",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="content-post-list">
        {posts.length === 0 ? (
          <p className="content-library-empty">{panelChrome.emptyLabel}</p>
        ) : (
          posts.map((post) => {
            const summary = blogPostEditorSummary(post);
            return (
              <button
                className={[
                  "content-post-row",
                  post.id === selectedPostId ? "active" : "",
                  post.status,
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={post.id}
                onClick={() => onSelectPost(post.id)}
                type="button"
              >
                <span className="content-post-status">
                  {summary.statusLabel}
                </span>
                <strong>{summary.title}</strong>
                <small>{summary.meta}</small>
              </button>
            );
          })
        )}
      </div>

      {panelChrome.showEditor ? postEditor : postPreview}
    </div>
  );

  return (
    <section
      className={[
        "editor-panel content-library-panel",
        isOverlay ? "overlay-panel" : "",
        isCollapsed ? "collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={isOverlay ? "overlay-panel-head" : "editor-panel-head"}>
        <div>
          <h3>Blog and news</h3>
          <p className="muted small">
            {posts.length} post{posts.length === 1 ? "" : "s"}
          </p>
        </div>
        {isOverlay ? (
          <div className="overlay-panel-actions">
            {!isCollapsed && (
              <>
                {posts.length > 0 && panelChrome.showEditor && (
                  <button
                    aria-expanded={isPostEditorOpen}
                    className="panel-toggle quiet"
                    disabled={working !== null}
                    onClick={() => setIsPostEditorOpen((current) => !current)}
                    type="button"
                  >
                    {panelChrome.editToggleLabel}
                  </button>
                )}
                <button
                  className="panel-toggle quiet"
                  disabled={working !== null}
                  onClick={openNewPost}
                  type="button"
                >
                  New post
                </button>
              </>
            )}
            <button
              aria-expanded={!isCollapsed}
              className="panel-toggle"
              onClick={onToggleCollapse}
              type="button"
            >
              {isCollapsed ? "Open" : "Collapse"}
            </button>
          </div>
        ) : (
          <button
            className="btn-secondary"
            disabled={working !== null}
            onClick={openNewPost}
            type="button"
          >
            New post
          </button>
        )}
      </div>

      {isOverlay ? (
        <div className="overlay-panel-body">{content}</div>
      ) : (
        content
      )}
    </section>
  );
}

export interface ImportedCmsCollectionSummary {
  count: number;
  description: string;
  id: string;
  label: string;
  sourceType: string;
}

export function importedCmsCollectionSummaries(
  importedCms: ImportedCms | null,
): ImportedCmsCollectionSummary[] {
  if (!importedCms) return [];
  return importedCms.collections.map((collection) => ({
    count: collection.entries.length || collection.documentCount,
    description:
      collection.description ??
      `Imported source collection "${collection.sourceType}".`,
    id: collection.id,
    label: collection.label,
    sourceType: collection.sourceType,
  }));
}

function firstAvailableImportedCmsCollection(
  summaries: readonly ImportedCmsCollectionSummary[],
): string | null {
  return summaries.find((summary) => summary.count > 0)?.id ?? null;
}

function importedCmsEntryDataText(entry: ImportedCmsEntry): string {
  return JSON.stringify(entry.data, null, 2);
}

function importedCmsEntryMeta(entry: ImportedCmsEntry): string {
  return [entry.sourceType, entry.slug, entry.sourceId]
    .filter(Boolean)
    .join(" · ");
}

function importedCmsPreviewFields(
  entry: ImportedCmsEntry,
): { key: string; value: string }[] {
  const fields =
    entry.data.fields &&
    typeof entry.data.fields === "object" &&
    !Array.isArray(entry.data.fields)
      ? (entry.data.fields as Record<string, unknown>)
      : entry.data;
  return Object.entries(fields)
    .map(([key, value]) => ({ key, value: shortValue(value) }))
    .filter((row) => row.value.length > 0)
    .slice(0, 8);
}

function shortValue(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.replace(/\s+/g, " ").trim();
    return trimmed.length > 140 ? `${trimmed.slice(0, 137)}...` : trimmed;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value === null) return "null";
  if (Array.isArray(value))
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (value && typeof value === "object") return "Object";
  return "";
}

export function ImportedCmsPanel({
  importedCms,
  onUpdateEntry,
  working,
}: {
  importedCms: ImportedCms | null;
  onUpdateEntry: (
    id: string,
    payload: Partial<Pick<ImportedCmsEntry, "data" | "name" | "slug">>,
  ) => Promise<void>;
  working: string | null;
}) {
  const saving = working === "imported-cms";
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(
    null,
  );
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const latestImport = importedCms?.importRuns[0] ?? null;
  const collections = importedCms?.collections ?? [];
  const collectionSummaries = useMemo(
    () => importedCmsCollectionSummaries(importedCms),
    [importedCms],
  );
  const visibleCollections = collectionSummaries.filter(
    (summary) => summary.count > 0,
  );
  const activeCollection =
    collections.find((collection) => collection.id === activeCollectionId) ??
    collections.find((collection) => collection.entries.length > 0) ??
    null;
  const activeSummary =
    collectionSummaries.find(
      (summary) => summary.id === activeCollection?.id,
    ) ?? null;
  const selectedEntry =
    activeCollection?.entries.find((entry) => entry.id === selectedEntryId) ??
    activeCollection?.entries[0] ??
    null;
  const hasImportedRows = collections.some(
    (collection) => collection.entries.length > 0,
  );

  useEffect(() => {
    const nextCollection =
      firstAvailableImportedCmsCollection(collectionSummaries);
    if (!nextCollection) {
      setActiveCollectionId(null);
      return;
    }
    if (
      !collectionSummaries.some((summary) => summary.id === activeCollectionId)
    ) {
      setActiveCollectionId(nextCollection);
    }
  }, [activeCollectionId, collectionSummaries]);

  useEffect(() => {
    if (!activeCollection) {
      setSelectedEntryId(null);
      return;
    }
    if (
      !activeCollection.entries.some((entry) => entry.id === selectedEntryId)
    ) {
      setSelectedEntryId(activeCollection.entries[0]?.id ?? null);
    }
  }, [activeCollection, selectedEntryId]);

  function submitEntry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedEntry) return;
    const form = new FormData(event.currentTarget);
    const name = form.get("name");
    const slug = form.get("slug");
    const dataText = form.get("data");
    if (typeof dataText !== "string") return;
    try {
      const data = JSON.parse(dataText) as Record<string, unknown>;
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        setDataError("Entry data must be a JSON object.");
        return;
      }
      setDataError(null);
      void onUpdateEntry(selectedEntry.id, {
        data,
        name:
          typeof name === "string" && name.trim()
            ? name.trim()
            : selectedEntry.name,
        slug: typeof slug === "string" && slug.trim() ? slug.trim() : null,
      });
    } catch {
      setDataError("Entry data must be valid JSON.");
    }
  }

  return (
    <section
      className="editor-panel imported-cms-panel"
      data-testid="imported-cms-panel"
    >
      <div className="editor-panel-head">
        <div>
          <h3>Imported CMS</h3>
          <p className="muted small">
            {latestImport
              ? `${latestImport.totals.totalDocuments} Sanity document${latestImport.totals.totalDocuments === 1 ? "" : "s"} imported`
              : "Source-owned collections"}
          </p>
        </div>
      </div>

      {!importedCms || !hasImportedRows ? (
        <p className="muted small">
          Imported CMS collections will appear here after a managed Astro/Sanity
          import.
        </p>
      ) : (
        <>
          <div className="imported-cms-collections" role="tablist">
            {visibleCollections.map((summary) => (
              <button
                aria-selected={activeCollection?.id === summary.id}
                className={activeCollection?.id === summary.id ? "active" : ""}
                key={summary.id}
                onClick={() => setActiveCollectionId(summary.id)}
                role="tab"
                type="button"
              >
                <strong>{summary.count}</strong>
                <span>{summary.label}</span>
              </button>
            ))}
          </div>

          {activeSummary && (
            <p className="imported-cms-collection-note">
              {activeSummary.description} Source type:{" "}
              <code>{activeSummary.sourceType}</code>
            </p>
          )}

          {activeCollection && (
            <div className="imported-cms-browser">
              <div className="imported-cms-item-list">
                {activeCollection.entries.map((entry) => (
                  <button
                    className={selectedEntry?.id === entry.id ? "active" : ""}
                    key={entry.id}
                    onClick={() => {
                      setDataError(null);
                      setSelectedEntryId(entry.id);
                    }}
                    type="button"
                  >
                    <strong>{entry.name}</strong>
                    <span>{importedCmsEntryMeta(entry)}</span>
                  </button>
                ))}
              </div>
              {selectedEntry && (
                <form
                  className="imported-cms-card imported-cms-detail"
                  data-cms-id={selectedEntry.id}
                  data-testid="imported-cms-entry-form"
                  key={selectedEntry.id}
                  onSubmit={submitEntry}
                >
                  <div className="imported-cms-card-head">
                    <strong>{selectedEntry.name}</strong>
                    <button
                      data-testid="imported-cms-entry-save"
                      disabled={working !== null}
                      type="submit"
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                  <div className="field-grid">
                    <TextField
                      defaultValue={selectedEntry.name}
                      label="Name"
                      name="name"
                    />
                    <TextField
                      defaultValue={selectedEntry.slug ?? ""}
                      label="Slug or path"
                      name="slug"
                    />
                  </div>
                  <dl className="imported-cms-fields">
                    {importedCmsPreviewFields(selectedEntry).map((field) => (
                      <div key={field.key}>
                        <dt>{field.key}</dt>
                        <dd>{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <TextAreaField
                    defaultValue={importedCmsEntryDataText(selectedEntry)}
                    label="Entry data"
                    name="data"
                    rows={10}
                  />
                  {dataError && (
                    <p className="form-error" role="alert">
                      {dataError}
                    </p>
                  )}
                </form>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function CanvasToolbar({
  currentPage,
  hostname,
  inboundLinks,
  isAgentPanelActive,
  isAdvancedMode,
  isBackLinking,
  isClearView,
  isFullscreen,
  isPublishingSite,
  isSyncing,
  mode,
  onAddFooterLink,
  onAddHeaderLink,
  onAdvancedModeChange,
  onClearViewChange,
  onExitFullscreen,
  onModeChange,
  onNavigate,
  onOpenTaskSurface,
  onOutlineVisibleChange,
  onPanelsHiddenChange,
  onPatchLinkTarget,
  onPublishSite,
  onRepairNavigation,
  onSyncBackLinks,
  onSizeChange,
  onSyncSiteNavigation,
  onStructurePanelOpenChange,
  onTargetMapVisibleChange,
  outlineVisible,
  outgoingLinks,
  panelsHidden,
  pages,
  size,
  structurePanelOpen,
  targetMapVisible,
}: {
  currentPage: EditorPage;
  hostname: string | null;
  inboundLinks: LinkSummary[];
  isAgentPanelActive: boolean;
  isAdvancedMode: boolean;
  isBackLinking: boolean;
  isClearView: boolean;
  isFullscreen: boolean;
  isPublishingSite: boolean;
  isSyncing: boolean;
  mode: CanvasMode;
  onAddFooterLink: (page: EditorPage) => void;
  onAddHeaderLink: (page: EditorPage) => void;
  onAdvancedModeChange: (enabled: boolean) => void;
  onClearViewChange: (enabled: boolean) => void;
  onExitFullscreen: () => void;
  onModeChange: (mode: CanvasMode) => void;
  onNavigate: (pageId: string) => void;
  onOpenTaskSurface: () => void;
  onOutlineVisibleChange: (visible: boolean) => void;
  onPanelsHiddenChange: (hidden: boolean) => void;
  onPatchLinkTarget: (link: LinkSummary, href: string) => void;
  onPublishSite: () => void;
  onRepairNavigation: () => void;
  onSyncBackLinks: () => void;
  onSizeChange: (size: CanvasSize) => void;
  onSyncSiteNavigation: () => void;
  onStructurePanelOpenChange: (open: boolean) => void;
  onTargetMapVisibleChange: (visible: boolean) => void;
  outlineVisible: boolean;
  outgoingLinks: LinkSummary[];
  panelsHidden: boolean;
  pages: EditorPage[];
  size: CanvasSize;
  structurePanelOpen: boolean;
  targetMapVisible: boolean;
}) {
  const [isLinkMenuOpen, setIsLinkMenuOpen] = useState(false);
  const [isSecondaryToolbarOpen, setIsSecondaryToolbarOpen] = useState(false);
  const linkedPages = new Set(
    outgoingLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const unresolvedCount = outgoingLinks.filter(
    (link) => link.internalSlug && !link.targetPage,
  ).length;
  const linkedPageCount = linkedPages.size;
  const inboundPageIds = new Set(
    inboundLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const headerLinkedPageIds = headerPageIds(currentPage, pages, hostname);
  const footerLinkedPageIds = footerPageIds(currentPage, pages, hostname);
  const disconnectedCount = pages.filter(
    (page) =>
      page.id !== currentPage.id &&
      !linkedPages.has(page.id) &&
      !inboundPageIds.has(page.id) &&
      !headerLinkedPageIds.has(page.id) &&
      !footerLinkedPageIds.has(page.id),
  ).length;
  const linkHealthLabel = canvasLinkHealthButtonLabel({
    disconnectedCount,
    linkedPageCount,
    unresolvedCount,
  });
  const toolbarLayoutMode = canvasToolbarLayoutMode({
    isClearView,
    isFullscreen,
    panelsHidden,
  });
  const isProgressiveToolbar =
    toolbarLayoutMode === "clean-fullscreen" || !isAdvancedMode;
  const clearViewToolbarSections = canvasClearViewToolbarSections({
    toolbarLayoutMode,
  });
  const toolbarClassName = [
    "canvas-toolbar",
    isAdvancedMode ? "advanced" : "guided",
    panelsHidden ? "panels-hidden" : "",
    isClearView ? "clear-view" : "",
    toolbarLayoutMode === "clean-fullscreen" ? "clean-fullscreen" : "",
    isProgressiveToolbar ? "progressive-toolbar" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const sizeControl = (
    <div className="segmented-control compact">
      {(["desktop", "tablet", "mobile"] as const).map((candidate) => (
        <button
          aria-pressed={size === candidate}
          className={size === candidate ? "active" : ""}
          key={candidate}
          onClick={() => onSizeChange(candidate)}
          type="button"
        >
          {candidate === "desktop"
            ? "Desktop"
            : candidate === "tablet"
              ? "Tablet"
              : "Mobile"}
        </button>
      ))}
    </div>
  );
  const pageSelect = (
    <label className="canvas-page-select">
      <span>Page</span>
      <select
        aria-label="Current page"
        value={currentPage.id}
        onChange={(event) => onNavigate(event.target.value)}
      >
        {pages.map((page) => (
          <option key={page.id} value={page.id}>
            {canvasPageOptionLabel({
              isAdvancedMode,
              isAgentPanelActive,
              page,
            })}
          </option>
        ))}
      </select>
    </label>
  );
  const modeControl = (
    <div className="segmented-control">
      {(["edit", "browse"] as const).map((candidate) => (
        <button
          aria-pressed={mode === candidate}
          className={mode === candidate ? "active" : ""}
          key={candidate}
          onClick={() => onModeChange(candidate)}
          type="button"
        >
          {candidate === "edit" ? "Edit" : "Browse"}
        </button>
      ))}
    </div>
  );
  const issueSummary = navigationIssueSummary({
    disconnectedPageCount: disconnectedCount,
    missingLinkCount: unresolvedCount,
    needsReturnLinkCount: 0,
    pageCount: pages.length,
  });
  const hasLinkIssues = issueSummary.status !== "healthy";
  const navigationWarningButton =
    !isAdvancedMode && hasLinkIssues ? (
      <button
        className="canvas-panel-toggle warning"
        onClick={() => {
          if (issueSummary.canAutoRepair) {
            onRepairNavigation();
            return;
          }
          onAdvancedModeChange(true);
          onOpenTaskSurface();
          setIsLinkMenuOpen(true);
        }}
        type="button"
      >
        {issueSummary.primaryActionLabel ?? "Review navigation"}
      </button>
    ) : null;
  const navigationWarningPlacement = canvasNavigationWarningPlacement({
    hasLinkIssues,
    isAdvancedMode,
    toolbarLayoutMode,
  });
  const showPrimaryNavigationWarning =
    navigationWarningPlacement === "primary" ? navigationWarningButton : null;
  const showSecondaryNavigationWarning =
    navigationWarningPlacement === "secondary" ? navigationWarningButton : null;
  const showSecondaryToolbar = canvasToolbarSecondaryVisible({
    hasSecondaryNavigationWarning: Boolean(showSecondaryNavigationWarning),
    isAdvancedMode,
    toolbarLayoutMode,
  });
  const secondaryToolbarState = canvasToolbarSecondaryState({
    isAdvancedMode,
    isOpen: isSecondaryToolbarOpen,
    isVisible: showSecondaryToolbar,
    toolbarLayoutMode,
  });
  const secondaryToolbarClassName = [
    "canvas-toolbar-more",
    isProgressiveToolbar ? "progressive" : "",
    isSecondaryToolbarOpen ? "open" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const secondaryToolbarToggle = secondaryToolbarState.showToggle ? (
    <button
      aria-expanded={isSecondaryToolbarOpen}
      className={
        isSecondaryToolbarOpen
          ? "canvas-panel-toggle tools active"
          : "canvas-panel-toggle tools"
      }
      onClick={() => setIsSecondaryToolbarOpen((current) => !current)}
      type="button"
    >
      Tools
    </button>
  ) : null;
  useEffect(() => {
    if (!isProgressiveToolbar) {
      setIsSecondaryToolbarOpen(false);
    }
  }, [isProgressiveToolbar]);
  const linkPopover = isLinkMenuOpen ? (
    <div className="canvas-link-popover-anchor">
      <CanvasLinkPopover
        currentPage={currentPage}
        hostname={hostname}
        inboundLinks={inboundLinks}
        isBackLinking={isBackLinking}
        isPublishingSite={isPublishingSite}
        isSyncing={isSyncing}
        onAddFooterLink={onAddFooterLink}
        onAddHeaderLink={onAddHeaderLink}
        onClose={() => setIsLinkMenuOpen(false)}
        onNavigate={(pageId) => {
          setIsLinkMenuOpen(false);
          onNavigate(pageId);
        }}
        onPatchLinkTarget={onPatchLinkTarget}
        onPublishSite={onPublishSite}
        onSyncBackLinks={onSyncBackLinks}
        onSyncSiteNavigation={onSyncSiteNavigation}
        outgoingLinks={outgoingLinks}
        pages={pages}
      />
    </div>
  ) : null;

  if (isClearView) {
    return (
      <div
        className={toolbarClassName}
        role="toolbar"
        aria-label="Canvas controls"
      >
        <div className="canvas-toolbar-primary">
          {pageSelect}
          {clearViewToolbarSections.showPrimarySizeControl && sizeControl}
          {secondaryToolbarToggle}
          <button
            aria-pressed={isClearView}
            className="canvas-panel-toggle clear-view active"
            onClick={() => onClearViewChange(false)}
            type="button"
          >
            Edit view
          </button>
          {isFullscreen && (
            <button
              className="canvas-panel-toggle exit"
              onClick={onExitFullscreen}
              type="button"
            >
              Exit
            </button>
          )}
        </div>
        {clearViewToolbarSections.showSecondarySizeControl && (
          <div
            className={secondaryToolbarClassName}
            hidden={secondaryToolbarState.hidden}
          >
            {sizeControl}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={toolbarClassName}
        role="toolbar"
        aria-label="Canvas controls"
      >
        <div className="canvas-toolbar-primary">
          {pageSelect}
          {!isProgressiveToolbar && modeControl}
          {!isProgressiveToolbar && sizeControl}
          {showPrimaryNavigationWarning}
          {secondaryToolbarToggle}
          {(isAdvancedMode || !isProgressiveToolbar) && (
            <button
              aria-pressed={isAdvancedMode}
              className={
                isAdvancedMode
                  ? "canvas-panel-toggle active"
                  : "canvas-panel-toggle"
              }
              onClick={() => onAdvancedModeChange(!isAdvancedMode)}
              type="button"
            >
              {isAdvancedMode ? "Guided" : "Full edit"}
            </button>
          )}
          {isFullscreen && (
            <button
              className="canvas-panel-toggle exit"
              onClick={onExitFullscreen}
              type="button"
            >
              Exit
            </button>
          )}
        </div>
        {showSecondaryToolbar && (
          <div
            className={secondaryToolbarClassName}
            hidden={secondaryToolbarState.hidden}
          >
            {showSecondaryNavigationWarning}
            {isProgressiveToolbar && modeControl}
            {isProgressiveToolbar && sizeControl}
            {!isAdvancedMode && (
              <button
                aria-pressed={isAdvancedMode}
                className="canvas-panel-toggle"
                onClick={() => onAdvancedModeChange(true)}
                type="button"
              >
                Full edit
              </button>
            )}
            {isAdvancedMode && (
              <>
                <div className="canvas-link-menu">
                  <button
                    aria-expanded={isLinkMenuOpen}
                    className={
                      hasLinkIssues
                        ? "link-health-button warning"
                        : "link-health-button"
                    }
                    aria-label={`Open link management: ${linkHealthLabel}`}
                    onClick={() => {
                      const nextState =
                        canvasLinkMenuToggleState(isLinkMenuOpen);
                      if (nextState.clearsTaskSurfaces) onOpenTaskSurface();
                      setIsLinkMenuOpen(nextState.nextOpen);
                    }}
                    type="button"
                  >
                    {linkHealthLabel}
                  </button>
                </div>
                <button
                  aria-pressed={isClearView}
                  className="canvas-panel-toggle clear-view"
                  onClick={() => onClearViewChange(true)}
                  type="button"
                >
                  Clear view
                </button>
                <button
                  aria-pressed={outlineVisible}
                  className={
                    outlineVisible
                      ? "canvas-panel-toggle active"
                      : "canvas-panel-toggle"
                  }
                  onClick={() => onOutlineVisibleChange(!outlineVisible)}
                  type="button"
                >
                  Outline
                </button>
                <button
                  aria-pressed={targetMapVisible}
                  className={
                    targetMapVisible
                      ? "canvas-panel-toggle active"
                      : "canvas-panel-toggle"
                  }
                  onClick={() => onTargetMapVisibleChange(!targetMapVisible)}
                  type="button"
                >
                  Targets
                </button>
                <button
                  aria-pressed={!panelsHidden}
                  className={
                    panelsHidden
                      ? "canvas-panel-toggle"
                      : "canvas-panel-toggle active"
                  }
                  onClick={() => {
                    onPanelsHiddenChange(!panelsHidden);
                    setIsSecondaryToolbarOpen(false);
                  }}
                  type="button"
                >
                  {panelsHidden ? "Show panels" : "Panels"}
                </button>
                {!isFullscreen && (
                  <button
                    aria-pressed={structurePanelOpen}
                    className={
                      structurePanelOpen
                        ? "canvas-panel-toggle active"
                        : "canvas-panel-toggle"
                    }
                    onClick={() =>
                      onStructurePanelOpenChange(!structurePanelOpen)
                    }
                    type="button"
                  >
                    {structurePanelOpen ? "Hide fields" : "Fields"}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {linkPopover}
    </>
  );
}

function CanvasLinkPopover({
  currentPage,
  hostname,
  inboundLinks,
  isBackLinking,
  isPublishingSite,
  isSyncing,
  onAddFooterLink,
  onAddHeaderLink,
  onClose,
  onNavigate,
  onPatchLinkTarget,
  onPublishSite,
  onSyncBackLinks,
  onSyncSiteNavigation,
  outgoingLinks,
  pages,
}: {
  currentPage: EditorPage;
  hostname: string | null;
  inboundLinks: LinkSummary[];
  isBackLinking: boolean;
  isPublishingSite: boolean;
  isSyncing: boolean;
  onAddFooterLink: (page: EditorPage) => void;
  onAddHeaderLink: (page: EditorPage) => void;
  onClose: () => void;
  onNavigate: (pageId: string) => void;
  onPatchLinkTarget: (link: LinkSummary, href: string) => void;
  onPublishSite: () => void;
  onSyncBackLinks: () => void;
  onSyncSiteNavigation: () => void;
  outgoingLinks: LinkSummary[];
  pages: EditorPage[];
}) {
  const pageLinks = outgoingLinks.filter((link) => link.targetPage);
  const editableLinks = outgoingLinks.filter(
    (link) => link.blockId && link.hrefPath,
  );
  const missingLinks = outgoingLinks.filter(
    (link) => link.internalSlug && !link.targetPage,
  );
  const editableMissingLinks = missingLinks.filter(
    (link) => link.blockId && link.hrefPath,
  );
  const nonEditableMissingLinks = missingLinks.filter(
    (link) => !link.blockId || !link.hrefPath,
  );
  const outboundPageIds = new Set(
    pageLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const inboundPageIds = new Set(
    inboundLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const needsBackLinkIds = new Set(
    Array.from(outboundPageIds).filter((pageId) => !inboundPageIds.has(pageId)),
  );
  const otherPages = pages.filter((page) => page.id !== currentPage.id);
  const headerLinkedPageIds = headerPageIds(currentPage, pages, hostname);
  const footerLinkedPageIds = footerPageIds(currentPage, pages, hostname);
  const disconnectedPages = otherPages.filter(
    (page) =>
      !headerLinkedPageIds.has(page.id) &&
      !footerLinkedPageIds.has(page.id) &&
      !outboundPageIds.has(page.id) &&
      !inboundPageIds.has(page.id),
  );
  const needsBackLinks = needsBackLinkIds.size > 0;
  const issueSummary = navigationIssueSummary({
    disconnectedPageCount: disconnectedPages.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
    pageCount: pages.length,
  });
  const chrome = linkReviewChromeState({
    disconnectedPageCount: disconnectedPages.length,
    editableMissingLinkCount: editableMissingLinks.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
  });
  const detailsCopy = linkDetailsDisclosureCopy({
    status: issueSummary.status,
  });
  const nextStepCopy = linkReviewNextStepCopy({
    disconnectedPageCount: disconnectedPages.length,
    editableMissingLinkCount: editableMissingLinks.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
    pageCount: pages.length,
  });
  const attentionPagePreview = disconnectedPages.slice(0, 4);

  return (
    <div className="canvas-link-popover" role="dialog" aria-label="Site links">
      <div className="canvas-link-popover-head">
        <div>
          <strong>Navigation review</strong>
          <small>{issueSummary.summary}</small>
        </div>
        <button onClick={onClose} type="button">
          Close
        </button>
      </div>
      <div className={`canvas-link-review ${issueSummary.status}`}>
        <small className="canvas-link-review-eyebrow">
          {nextStepCopy.eyebrow}
        </small>
        <strong>{nextStepCopy.title}</strong>
        <span>{nextStepCopy.description}</span>
      </div>
      {(chrome.showRepairAction || chrome.showReturnLinkAction) && (
        <div className="canvas-link-popover-actions primary">
          {chrome.showRepairAction && (
            <button
              disabled={pages.length < 2 || isSyncing}
              onClick={onSyncSiteNavigation}
              type="button"
            >
              {isSyncing ? "Repairing..." : "Repair navigation"}
            </button>
          )}
          {chrome.showReturnLinkAction && (
            <button
              disabled={!needsBackLinks || isBackLinking}
              onClick={onSyncBackLinks}
              type="button"
            >
              {isBackLinking
                ? "Adding return links..."
                : `Add ${needsBackLinkIds.size} return link${
                    needsBackLinkIds.size === 1 ? "" : "s"
                  }`}
            </button>
          )}
        </div>
      )}
      {chrome.showEditableMissingTargets && (
        <section className="canvas-link-review-section warning">
          <div className="canvas-link-review-section-head">
            <strong>Choose target pages</strong>
            <small>
              {editableMissingLinks.length} link
              {editableMissingLinks.length === 1 ? "" : "s"} need a page.
            </small>
          </div>
          <div className="canvas-link-target-list review-only">
            {editableMissingLinks.slice(0, 4).map((link) => (
              <label key={`review-target-${link.key}`}>
                <span>{link.source}</span>
                <select
                  aria-label={`${link.source} target page`}
                  value={link.targetPage?.slug ?? ""}
                  onChange={(event) => {
                    if (event.target.value) {
                      onPatchLinkTarget(link, event.target.value);
                    }
                  }}
                >
                  <option value="">Choose page</option>
                  {pages.map((page) => (
                    <option key={page.id} value={page.slug}>
                      {page.title}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </section>
      )}
      {chrome.showMissingLinks && nonEditableMissingLinks.length > 0 && (
        <section className="canvas-link-review-section warning">
          <div className="canvas-link-review-section-head">
            <strong>Manual link review</strong>
            <small>
              These imported links could not be retargeted directly.
            </small>
          </div>
          <div className="canvas-missing-links review-only">
            {nonEditableMissingLinks.slice(0, 4).map((link) => (
              <p key={`canvas-missing-${link.key}`}>
                <strong>{link.source}</strong>
                <span>{link.href}</span>
              </p>
            ))}
          </div>
        </section>
      )}
      {chrome.showDisconnectedPages && (
        <section className="canvas-link-review-section attention">
          <div className="canvas-link-review-section-head">
            <strong>Pages to add</strong>
            <small>
              {disconnectedPages.length} page
              {disconnectedPages.length === 1 ? "" : "s"} are not connected.
            </small>
          </div>
          <div className="canvas-page-link-list review-only">
            {attentionPagePreview.map((page) => {
              const inHeader = headerLinkedPageIds.has(page.id);
              const inFooter = footerLinkedPageIds.has(page.id);
              return (
                <div className="canvas-page-link-row" key={`review-${page.id}`}>
                  <button
                    className="canvas-page-open"
                    onClick={() => onNavigate(page.id)}
                    type="button"
                  >
                    <span>{page.title}</span>
                    <small>{page.slug}</small>
                  </button>
                  <div className="canvas-page-link-actions">
                    <button
                      disabled={inHeader}
                      onClick={() => onAddHeaderLink(page)}
                      type="button"
                    >
                      Top
                    </button>
                    <button
                      disabled={inFooter}
                      onClick={() => onAddFooterLink(page)}
                      type="button"
                    >
                      Footer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
      <details
        className="canvas-link-details"
        open={chrome.detailsOpenByDefault}
      >
        <summary>
          <span>{detailsCopy.title}</span>
          <small>{detailsCopy.description}</small>
        </summary>
        <div className="canvas-link-popover-actions secondary">
          <button
            disabled={pages.length < 2 || isSyncing}
            onClick={onSyncSiteNavigation}
            type="button"
          >
            {isSyncing ? "Syncing..." : "Sync all nav"}
          </button>
          <button
            disabled={!needsBackLinks || isBackLinking}
            onClick={onSyncBackLinks}
            type="button"
          >
            {isBackLinking
              ? "Adding..."
              : needsBackLinks
                ? `Add ${needsBackLinkIds.size} return`
                : "Return links done"}
          </button>
          <button
            disabled={isPublishingSite}
            onClick={onPublishSite}
            type="button"
          >
            {isPublishingSite ? "Publishing..." : "Publish site"}
          </button>
        </div>
        {otherPages.length > 0 && (
          <div className="canvas-page-link-list">
            {otherPages.map((page) => {
              const linkedOut = outboundPageIds.has(page.id);
              const linksHere = inboundPageIds.has(page.id);
              const inHeader = headerLinkedPageIds.has(page.id);
              const inFooter = footerLinkedPageIds.has(page.id);
              return (
                <div className="canvas-page-link-row" key={page.id}>
                  <button
                    className="canvas-page-open"
                    onClick={() => onNavigate(page.id)}
                    type="button"
                  >
                    <span>{page.title}</span>
                    <small>{page.slug}</small>
                  </button>
                  <div className="page-target-state">
                    {inHeader && <span className="good">Top</span>}
                    {inFooter && <span className="good">Footer</span>}
                    {linkedOut && <span className="good">Linked</span>}
                    {linksHere && <span>Inbound</span>}
                    {linkedOut && !linksHere && (
                      <span className="warn">Needs back</span>
                    )}
                    {!linkedOut && !linksHere && !inHeader && !inFooter && (
                      <span className="warn">Unlinked</span>
                    )}
                  </div>
                  <div className="canvas-page-link-actions">
                    <button
                      disabled={inHeader}
                      onClick={() => onAddHeaderLink(page)}
                      type="button"
                    >
                      Top
                    </button>
                    <button
                      disabled={inFooter}
                      onClick={() => onAddFooterLink(page)}
                      type="button"
                    >
                      Footer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {editableLinks.length > 0 && (
          <div className="canvas-link-target-list">
            {editableLinks.map((link) => (
              <label key={link.key}>
                <span>{link.source}</span>
                <select
                  aria-label={`${link.source} target page`}
                  value={link.targetPage?.slug ?? ""}
                  onChange={(event) => {
                    if (event.target.value) {
                      onPatchLinkTarget(link, event.target.value);
                    }
                  }}
                >
                  <option value="">Choose page</option>
                  {pages.map((page) => (
                    <option key={page.id} value={page.slug}>
                      {page.title}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}
        {inboundLinks.length > 0 && (
          <div className="canvas-link-target-list inbound">
            {inboundLinks.map((link) => (
              <button
                className="canvas-page-open"
                key={`popover-in-${link.key}`}
                onClick={() =>
                  link.targetPage && onNavigate(link.targetPage.id)
                }
                type="button"
              >
                <span>{link.source}</span>
                <small>{link.label}</small>
              </button>
            ))}
          </div>
        )}
        {missingLinks.length > 0 && (
          <div className="canvas-missing-links">
            {missingLinks.map((link) => (
              <p key={`canvas-detail-missing-${link.key}`}>
                <strong>{link.source}</strong>
                <span>{link.href}</span>
              </p>
            ))}
          </div>
        )}
      </details>
    </div>
  );
}

function CanvasSiteFlowStrip({
  currentPage,
  hostname,
  inboundLinks,
  isBackLinking,
  isAdvancedMode,
  isCompact,
  isSyncing,
  onConnectPage,
  onOpenLinkReview,
  onRepairNavigation,
  onSyncBackLinks,
  onSyncSiteNavigation,
  outgoingLinks,
  pages,
}: {
  currentPage: EditorPage;
  hostname: string | null;
  inboundLinks: LinkSummary[];
  isBackLinking: boolean;
  isAdvancedMode: boolean;
  isCompact: boolean;
  isSyncing: boolean;
  onConnectPage: (pageId: string) => void;
  onOpenLinkReview: () => void;
  onRepairNavigation: () => void;
  onSyncBackLinks: () => void;
  onSyncSiteNavigation: () => void;
  outgoingLinks: LinkSummary[];
  pages: EditorPage[];
}) {
  const pageLinks = outgoingLinks.filter((link) => link.targetPage);
  const missingLinks = outgoingLinks.filter(
    (link) => link.internalSlug && !link.targetPage,
  );
  const outboundPageIds = new Set(
    pageLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const inboundPageIds = new Set(
    inboundLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const needsBackLinkIds = new Set(
    Array.from(outboundPageIds).filter((pageId) => !inboundPageIds.has(pageId)),
  );
  const headerLinkedPageIds = headerPageIds(currentPage, pages, hostname);
  const footerLinkedPageIds = footerPageIds(currentPage, pages, hostname);
  const connectedPageIds = new Set([
    ...Array.from(outboundPageIds),
    ...Array.from(inboundPageIds),
    ...Array.from(headerLinkedPageIds),
    ...Array.from(footerLinkedPageIds),
  ]);
  const otherPages = pages.filter((page) => page.id !== currentPage.id);
  const connectedOtherPageIds = new Set(
    Array.from(connectedPageIds).filter((pageId) => pageId !== currentPage.id),
  );
  const disconnectedPages = otherPages.filter(
    (page) => !connectedOtherPageIds.has(page.id),
  );
  const issueSummary = navigationIssueSummary({
    disconnectedPageCount: disconnectedPages.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
    pageCount: pages.length,
  });
  const guidedPrimaryAction = guidedNavigationPrimaryAction({
    canAutoRepair: issueSummary.canAutoRepair,
    disconnectedPageCount: disconnectedPages.length,
    needsReturnLinkCount: needsBackLinkIds.size,
    pageCount: pages.length,
    primaryActionLabel: issueSummary.primaryActionLabel,
  });
  const gapPage = disconnectedPages[0] ?? null;
  const chromeMode = canvasSiteFlowChromeMode({
    isAdvancedMode,
    isCompact,
    status: issueSummary.status,
  });

  return (
    <section
      aria-label="Site navigation"
      className={[
        "canvas-site-flow",
        issueSummary.status,
        chromeMode,
        isAdvancedMode ? "advanced" : "guided",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="canvas-site-flow-copy">
        <small>Navigation</small>
        <strong>{issueSummary.summary}</strong>
      </div>
      {isAdvancedMode && (
        <div className="canvas-site-flow-metrics">
          <span>
            <b>{pageLinks.length}</b> from page
          </span>
          <span>
            <b>{inboundLinks.length}</b> to page
          </span>
          <span>
            <b>{connectedOtherPageIds.size}</b>/
            <b>{Math.max(0, pages.length - 1)}</b> in nav
          </span>
        </div>
      )}
      <div className="canvas-site-flow-actions">
        {!isAdvancedMode && guidedPrimaryAction && (
          <button
            disabled={guidedPrimaryAction.kind === "repair" && isSyncing}
            onClick={
              guidedPrimaryAction.kind === "repair"
                ? onRepairNavigation
                : onOpenLinkReview
            }
            type="button"
          >
            {isSyncing && guidedPrimaryAction.kind === "repair"
              ? "Repairing"
              : guidedPrimaryAction.label}
          </button>
        )}
        {isAdvancedMode && (
          <button
            disabled={pages.length < 2 || isSyncing}
            onClick={onSyncSiteNavigation}
            type="button"
          >
            {isSyncing ? "Syncing" : "Sync nav"}
          </button>
        )}
        {isAdvancedMode && (
          <button
            disabled={needsBackLinkIds.size === 0 || isBackLinking}
            onClick={onSyncBackLinks}
            type="button"
          >
            {isBackLinking ? "Adding" : "Return links"}
          </button>
        )}
        {isAdvancedMode && gapPage && (
          <button
            disabled={isSyncing}
            onClick={() => onConnectPage(gapPage.id)}
            type="button"
          >
            {isSyncing ? "Connecting" : "Connect page"}
          </button>
        )}
      </div>
    </section>
  );
}

function CanvasActionRail({
  hasSelection,
  inspectorLabel,
  onOpenAgent,
  onOpenInspector,
  onOpenTheme,
  onToggleOutline,
  onToggleTargets,
  outlineVisible,
  selectedLabel,
  targetMapVisible,
}: {
  hasSelection: boolean;
  inspectorLabel: string;
  onOpenAgent: () => void;
  onOpenInspector: () => void;
  onOpenTheme: () => void;
  onToggleOutline: () => void;
  onToggleTargets: () => void;
  outlineVisible: boolean;
  selectedLabel: string | null;
  targetMapVisible: boolean;
}) {
  return (
    <aside className="canvas-action-rail" aria-label="Canvas quick actions">
      <div className="canvas-action-rail-status">
        <small>Focus</small>
        <strong>{selectedLabel ?? "Canvas"}</strong>
      </div>
      <div className="canvas-action-rail-tools">
        <CanvasActionButton
          disabled={!hasSelection}
          icon="inspector"
          label={inspectorLabel}
          onClick={onOpenInspector}
        />
        <CanvasActionButton
          disabled={!hasSelection}
          icon="agent"
          label="Ask builder agent"
          onClick={onOpenAgent}
        />
        <CanvasActionButton
          icon="theme"
          label="Open theme controls"
          onClick={onOpenTheme}
        />
        <CanvasActionButton
          active={outlineVisible}
          icon="outline"
          label="Toggle page outline"
          onClick={onToggleOutline}
        />
        <CanvasActionButton
          active={targetMapVisible}
          icon="targets"
          label="Toggle selectable targets"
          onClick={onToggleTargets}
        />
      </div>
    </aside>
  );
}

function OverlayToolDock({
  activePanel,
  canOpenAgent,
  canOpenFocus,
  onHide,
  onOpenAgent,
  onOpenFocus,
  onOpenTheme,
}: {
  activePanel: OverlayPanelId | null;
  canOpenAgent: boolean;
  canOpenFocus: boolean;
  onHide: () => void;
  onOpenAgent: () => void;
  onOpenFocus: () => void;
  onOpenTheme: () => void;
}) {
  const items = overlayToolDockItems({
    activePanel,
    canOpenAgent,
    canOpenFocus,
  });
  const handlers: Record<OverlayToolDockItemId, () => void> = {
    agent: onOpenAgent,
    focus: onOpenFocus,
    hide: onHide,
    theme: onOpenTheme,
  };

  return (
    <nav className="overlay-tool-dock" aria-label="Editor panels">
      {items.map((item) => (
        <button
          aria-label={item.label}
          aria-pressed={item.id === "hide" ? undefined : item.active}
          className={[
            item.active ? "active" : "",
            item.id === "hide" ? "ghost" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          disabled={item.disabled}
          key={item.id}
          onClick={handlers[item.id]}
          title={item.label}
          type="button"
        >
          <span
            aria-hidden="true"
            className={`canvas-tool-icon ${item.icon}`}
          />
          <span className="sr-only">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

function CanvasActionButton({
  active = false,
  disabled = false,
  icon,
  label,
  onClick,
}: {
  active?: boolean;
  disabled?: boolean;
  icon: "agent" | "inspector" | "outline" | "targets" | "theme";
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={label}
      aria-pressed={active}
      className={active ? "active" : ""}
      disabled={disabled}
      onClick={onClick}
      title={label}
      type="button"
    >
      <span aria-hidden="true" className={`canvas-tool-icon ${icon}`} />
      <span className="sr-only">{label}</span>
    </button>
  );
}

function CanvasOutlinePanel({
  blocks,
  elementFrames,
  hoveredBlockId,
  onClose,
  onHoverBlock,
  onHoverElement,
  onSelectBlock,
  onSelectElement,
  selectedBlockId,
  selectedElementKey,
}: {
  blocks: PageBlock[];
  elementFrames: ElementFrame[];
  hoveredBlockId: string | null;
  onClose: () => void;
  onHoverBlock: (blockId: string | null) => void;
  onHoverElement: (frame: ElementFrame | null) => void;
  onSelectBlock: (blockId: string) => void;
  onSelectElement: (frame: ElementFrame) => void;
  selectedBlockId: string | null;
  selectedElementKey: string | null;
}) {
  const [query, setQuery] = useState("");
  const selectedBlock = blocks.find((block) => block.id === selectedBlockId);
  const isPageElementMap = elementFrames.some(
    (frame) => frame.blockId !== selectedBlockId,
  );
  const elementScopeLabel = isPageElementMap
    ? "All page targets"
    : selectedBlock
      ? blockLabel(selectedBlock.blockType)
      : "Section";
  const elementFramesWithPreviews = useMemo(
    () =>
      elementFrames.map((frame) => ({
        ...frame,
        valuePreview: canvasElementFrameValuePreview(blocks, frame),
      })),
    [blocks, elementFrames],
  );
  const visibleSections = canvasOutlineSections({
    blocks,
    query,
    selectedBlockId,
  });
  const visibleElementFrames = canvasOutlineElementFrames({
    frames: elementFramesWithPreviews,
    query,
    selectedElementKey,
  });
  const trimmedQuery = query.trim();
  const sectionCountLabel = trimmedQuery
    ? `${visibleSections.length}/${blocks.length} sections`
    : `${blocks.length} sections`;
  const elementCountLabel = trimmedQuery
    ? `${visibleElementFrames.length}/${elementFrames.length} ${
        isPageElementMap ? "page" : "section"
      } elements`
    : `${elementFrames.length} ${
        isPageElementMap ? "page" : "section"
      } elements`;
  const close = () => {
    onHoverBlock(null);
    onHoverElement(null);
    onClose();
  };

  return (
    <nav
      className="canvas-outline-panel"
      aria-label="Page outline"
      onMouseLeave={() => {
        onHoverBlock(null);
        onHoverElement(null);
      }}
    >
      <div className="canvas-outline-head">
        <div>
          <strong>Outline</strong>
          <small>{sectionCountLabel}</small>
        </div>
        <button aria-label="Hide outline" onClick={close} type="button">
          Hide
        </button>
      </div>
      <label className="canvas-outline-search">
        <span className="sr-only">Find section or element</span>
        <input
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find section or element"
          value={query}
        />
      </label>
      <div className="canvas-outline-list">
        {visibleSections.length === 0 ? (
          <p className="canvas-outline-empty">
            No sections match "{trimmedQuery}".
          </p>
        ) : (
          visibleSections.map(({ block, index }) => {
            const rowTone = canvasOutlineSectionRowTone({
              blockId: block.id,
              hoveredBlockId,
              selectedBlockId,
            });
            const elementSummary = canvasOutlineSectionElementSummary(
              block.id,
              elementFrames,
            );

            return (
              <button
                className={[
                  "outline-row",
                  "section",
                  rowTone === "idle" ? "" : rowTone,
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={block.id}
                onBlur={() => onHoverBlock(null)}
                onClick={() => {
                  onHoverBlock(null);
                  onSelectBlock(block.id);
                }}
                onFocus={() => {
                  onHoverBlock(block.id);
                  onHoverElement(null);
                }}
                onMouseEnter={() => {
                  onHoverBlock(block.id);
                  onHoverElement(null);
                }}
                onMouseLeave={() => onHoverBlock(null)}
                type="button"
              >
                <span>{blockLabel(block.blockType)}</span>
                <small>
                  {String(index + 1).padStart(2, "0")} · {elementSummary}
                </small>
              </button>
            );
          })
        )}
      </div>
      <div className="canvas-outline-elements">
        <div className="outline-subhead">
          <span>{elementScopeLabel}</span>
          <small>{elementCountLabel}</small>
        </div>
        {elementFrames.length === 0 ? (
          <p>No selectable elements measured yet.</p>
        ) : visibleElementFrames.length === 0 ? (
          <p>No elements match "{trimmedQuery}".</p>
        ) : (
          visibleElementFrames.map((frame) => (
            <button
              className={[
                "outline-row",
                "element",
                frame.target ? "editable" : "",
                frame.key === selectedElementKey ? "active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              key={frame.key}
              onBlur={() => onHoverElement(null)}
              onClick={() => onSelectElement(frame)}
              onFocus={() => onHoverElement(frame)}
              onMouseEnter={() => onHoverElement(frame)}
              onMouseLeave={() => onHoverElement(null)}
              type="button"
            >
              <span>{frame.label}</span>
              <small>
                {frame.sectionLabel} / {targetKindLabel(frame.target)}
              </small>
              {frame.valuePreview && (
                <em className="outline-row-preview">{frame.valuePreview}</em>
              )}
            </button>
          ))
        )}
      </div>
    </nav>
  );
}

function CanvasElementNavigator({
  blocks,
  blockName,
  collapsed,
  elementFrames,
  hoveredElementKey,
  onCollapsedChange,
  onHoverElement,
  onSelectElement,
  selectedElementKey,
}: {
  blocks: PageBlock[];
  blockName: string;
  collapsed: boolean;
  elementFrames: ElementFrame[];
  hoveredElementKey: string | null;
  onCollapsedChange: (collapsed: boolean) => void;
  onHoverElement: (frame: ElementFrame | null) => void;
  onSelectElement: (
    frame: ElementFrame,
    shouldOpenEditor?: boolean,
    shouldScroll?: boolean,
  ) => void;
  selectedElementKey: string | null;
}) {
  const [filter, setFilter] = useState<"all" | "editable">("editable");
  const [query, setQuery] = useState("");
  const framesWithPreviews = useMemo(
    () =>
      elementFrames.map((frame) => ({
        ...frame,
        valuePreview: canvasElementFrameValuePreview(blocks, frame),
      })),
    [blocks, elementFrames],
  );
  const editableCount = framesWithPreviews.filter(
    (frame) => frame.target,
  ).length;
  const visibleFrames = elementNavigatorFrames({
    filter,
    frames: framesWithPreviews,
    hoveredElementKey,
    query,
    selectedElementKey,
  });
  const emptyMessage = query.trim()
    ? `No ${filter === "editable" ? "fields" : "targets"} match "${query.trim()}".`
    : filter === "editable"
      ? "No editable fields."
      : "No selectable targets.";

  return (
    <section
      aria-label={`${blockName} selectable elements`}
      className={["canvas-element-navigator", collapsed ? "collapsed" : ""]
        .filter(Boolean)
        .join(" ")}
      onMouseLeave={() => onHoverElement(null)}
    >
      <button
        aria-expanded={!collapsed}
        className="element-navigator-toggle"
        onClick={() => onCollapsedChange(!collapsed)}
        type="button"
      >
        <span>{blockName}</span>
        <strong>
          {editableCount}/{elementFrames.length}
        </strong>
      </button>
      {!collapsed && (
        <>
          <div className="element-navigator-filters">
            <button
              aria-pressed={filter === "editable"}
              className={filter === "editable" ? "active" : ""}
              onClick={() => setFilter("editable")}
              type="button"
            >
              Fields
            </button>
            <button
              aria-pressed={filter === "all"}
              className={filter === "all" ? "active" : ""}
              onClick={() => setFilter("all")}
              type="button"
            >
              All
            </button>
          </div>
          <label className="element-navigator-search">
            <span className="sr-only">Find element</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                filter === "editable" ? "Find editable field" : "Find target"
              }
            />
          </label>
          <div className="element-navigator-list">
            {visibleFrames.map((frame) => {
              const canEdit = Boolean(frame.target);
              const actionLabel = elementFrameActionLabel(frame);

              return (
                <div
                  className={[
                    "element-navigator-row",
                    frame.target ? "editable" : "",
                    frame.key === selectedElementKey ? "selected" : "",
                    frame.key === hoveredElementKey ? "hovered" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  key={frame.key}
                >
                  <button
                    className="element-navigator-target"
                    onFocus={() => onHoverElement(frame)}
                    onMouseEnter={() => onHoverElement(frame)}
                    onClick={() => onSelectElement(frame, false, true)}
                    type="button"
                  >
                    <span>{frame.label}</span>
                    <small>
                      {frame.sectionLabel} / {targetKindLabel(frame.target)}
                    </small>
                    {frame.valuePreview && (
                      <em className="element-navigator-preview">
                        {frame.valuePreview}
                      </em>
                    )}
                  </button>
                  <button
                    className="element-navigator-action"
                    disabled={!canEdit}
                    onFocus={() => onHoverElement(frame)}
                    onMouseEnter={() => onHoverElement(frame)}
                    onClick={() => onSelectElement(frame, true, true)}
                    type="button"
                  >
                    {actionLabel}
                  </button>
                </div>
              );
            })}
            {visibleFrames.length === 0 && (
              <p className="element-navigator-empty">{emptyMessage}</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function SectionVariantControl({
  className,
  onChange,
  value,
  variants,
}: {
  className: string;
  onChange: (value: string) => void;
  value: string;
  variants: { id: string; name: string }[];
}) {
  if (variants.length <= 1) return null;
  const selectedValue = variants.some((variant) => variant.id === value)
    ? value
    : (variants[0]?.id ?? "");
  return (
    <label className={className}>
      <span>Style</span>
      <select
        aria-label="Section style"
        value={selectedValue}
        onChange={(event) => onChange(event.target.value)}
      >
        {variants.map((variant) => (
          <option key={variant.id} value={variant.id}>
            {variant.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function CanvasItemActions({
  actions,
  compact = false,
}: {
  actions: SelectionItemActions;
  compact?: boolean;
}) {
  return (
    <fieldset
      aria-label={`${actions.itemLabel} actions`}
      className={
        compact ? "selection-item-actions compact" : "selection-item-actions"
      }
    >
      <legend className="sr-only">Selected item actions</legend>
      <small>
        {actions.itemLabel} {actions.itemPosition}
      </small>
      <button
        disabled={!actions.canDuplicate}
        onClick={actions.onDuplicate}
        title={`Duplicate ${actions.itemLabel}`}
        type="button"
      >
        Duplicate
      </button>
      <button
        className="danger"
        disabled={!actions.canRemove}
        onClick={actions.onRemove}
        title={`Remove ${actions.itemLabel}`}
        type="button"
      >
        Remove
      </button>
    </fieldset>
  );
}

function SelectionLinkTargetChip({
  linkTarget,
  onOpenTarget,
}: {
  linkTarget: SelectionLinkTarget | null;
  onOpenTarget: () => void;
}) {
  if (!linkTarget) return null;
  const statusLabel =
    linkTarget.status === "internal"
      ? "Page"
      : linkTarget.status === "missing"
        ? "Missing"
        : linkTarget.status === "empty"
          ? "Target"
          : "External";

  return (
    <button
      className={["selection-link-target", linkTarget.status]
        .filter(Boolean)
        .join(" ")}
      onClick={onOpenTarget}
      title={linkTarget.href || "No link target selected"}
      type="button"
    >
      <span>{statusLabel}</span>
      <strong>{linkTarget.label}</strong>
    </button>
  );
}

function CanvasSelectionToolbar({
  canInlineEdit,
  canMoveDown,
  canMoveUp,
  frame,
  itemActions,
  isInlineEditorOpen,
  isQuickInspectorOpen,
  linkTarget,
  onAskAi,
  onDuplicate,
  onHidePanels,
  onInlineEdit,
  onOpenInspector,
  onMoveDown,
  onMoveUp,
  onOpenPanel,
  onSectionVariantChange,
  sectionVariant,
  sectionVariants,
  showMoveControls,
}: {
  canInlineEdit: boolean;
  canMoveDown: boolean;
  canMoveUp: boolean;
  frame: CanvasSelectionFrame;
  itemActions: SelectionItemActions | null;
  isInlineEditorOpen: boolean;
  isQuickInspectorOpen: boolean;
  linkTarget: SelectionLinkTarget | null;
  onAskAi: () => void;
  onDuplicate: () => void;
  onHidePanels: () => void;
  onInlineEdit: () => void;
  onOpenInspector: () => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onOpenPanel: () => void;
  onSectionVariantChange: (value: string) => void;
  sectionVariant: string;
  sectionVariants: { id: string; name: string }[];
  showMoveControls: boolean;
}) {
  const top = Math.max(8, frame.top - 42);
  const left = Math.max(8, frame.left);

  return (
    <div
      aria-label={`${frame.label} actions`}
      className="selection-toolbar"
      role="toolbar"
      style={{ left, top }}
    >
      <strong>{frame.label}</strong>
      {frame.kind === "element" && (
        <small className="selection-section-label">{frame.sectionLabel}</small>
      )}
      <span
        className={[
          "selection-kind",
          frame.kind,
          frame.editable ? "editable" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {selectionKindLabel(frame)}
      </span>
      <SelectionLinkTargetChip
        linkTarget={linkTarget}
        onOpenTarget={onOpenInspector}
      />
      {frame.kind === "element" && frame.editable && (
        <>
          <button
            className={isInlineEditorOpen ? "active" : ""}
            onClick={canInlineEdit ? onInlineEdit : onOpenInspector}
            type="button"
          >
            {isInlineEditorOpen ? "Typing" : targetActionLabel(frame.target)}
          </button>
          {canInlineEdit && (
            <button
              className={isQuickInspectorOpen ? "active" : ""}
              onClick={onOpenInspector}
              type="button"
            >
              Details
            </button>
          )}
        </>
      )}
      {frame.kind === "element" && itemActions && (
        <CanvasItemActions actions={itemActions} />
      )}
      <button onClick={onOpenPanel} type="button">
        Panel
      </button>
      <button onClick={onAskAi} type="button">
        AI
      </button>
      {showMoveControls && (
        <>
          <SectionVariantControl
            className="selection-variant-control"
            onChange={onSectionVariantChange}
            value={sectionVariant}
            variants={sectionVariants}
          />
          <button onClick={onDuplicate} type="button">
            Duplicate
          </button>
          <button disabled={!canMoveUp} onClick={onMoveUp} type="button">
            Up
          </button>
          <button disabled={!canMoveDown} onClick={onMoveDown} type="button">
            Down
          </button>
        </>
      )}
      <button onClick={onHidePanels} type="button">
        Hide
      </button>
    </div>
  );
}

function CanvasSelectionStatus({
  canInlineEdit,
  canMoveDown,
  canMoveUp,
  elementCount,
  elementIndex,
  frame,
  itemActions,
  isInlineEditorOpen,
  isQuickInspectorOpen,
  linkTarget,
  onAskAi,
  onDuplicate,
  onInsertSection,
  onInlineEdit,
  onMoveDown,
  onMoveUp,
  onNextElement,
  onOpenInspector,
  onOpenPanel,
  onPrevElement,
  onSectionVariantChange,
  onSelectSection,
  position,
  sectionTemplates,
  sectionVariant,
  sectionVariants,
}: {
  canInlineEdit: boolean;
  canMoveDown: boolean;
  canMoveUp: boolean;
  elementCount: number;
  elementIndex: number;
  frame: CanvasSelectionFrame;
  itemActions: SelectionItemActions | null;
  isInlineEditorOpen: boolean;
  isQuickInspectorOpen: boolean;
  linkTarget: SelectionLinkTarget | null;
  onAskAi: () => void;
  onDuplicate: () => void;
  onInsertSection: (templateId: SectionTemplateId) => void;
  onInlineEdit: () => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onNextElement: () => void;
  onOpenInspector: () => void;
  onOpenPanel: () => void;
  onPrevElement: () => void;
  onSectionVariantChange: (value: string) => void;
  onSelectSection: () => void;
  position: CanvasHudPosition | null;
  sectionTemplates: SectionTemplate[];
  sectionVariant: string;
  sectionVariants: { id: string; name: string }[];
}) {
  const hasElements = elementCount > 0;
  const elementSummary = selectionStepperSummary({
    elementCount,
    elementIndex,
    selectionKind: frame.kind,
  });
  const style: CSSProperties | undefined = position
    ? {
        left: position.left,
        maxWidth: position.maxWidth,
        top: position.top,
      }
    : undefined;

  return (
    <section
      className={["selection-status", position ? "contextual" : ""]
        .filter(Boolean)
        .join(" ")}
      aria-label="Current selection"
      style={style}
    >
      <div>
        <small>{frame.kind === "block" ? "Area" : frame.sectionLabel}</small>
        <strong>{frame.label}</strong>
      </div>
      <span className={frame.editable ? "editable" : ""}>
        {selectionKindLabel(frame)}
      </span>
      <SelectionLinkTargetChip
        linkTarget={linkTarget}
        onOpenTarget={onOpenInspector}
      />
      <fieldset className="selection-stepper">
        <legend className="sr-only">Selectable elements</legend>
        {frame.kind === "element" && (
          <button onClick={onSelectSection} type="button">
            Whole area
          </button>
        )}
        <button
          aria-label="Previous element"
          disabled={!hasElements}
          onClick={onPrevElement}
          type="button"
        >
          ‹
        </button>
        <span>{elementSummary}</span>
        <button
          aria-label="Next element"
          disabled={!hasElements}
          onClick={onNextElement}
          type="button"
        >
          ›
        </button>
      </fieldset>
      <div className="selection-status-actions">
        {frame.kind === "element" && frame.editable && (
          <>
            <button
              className={isInlineEditorOpen ? "active" : ""}
              onClick={canInlineEdit ? onInlineEdit : onOpenInspector}
              type="button"
            >
              {isInlineEditorOpen ? "Typing" : targetActionLabel(frame.target)}
            </button>
            {canInlineEdit && (
              <button
                className={isQuickInspectorOpen ? "active" : ""}
                onClick={onOpenInspector}
                type="button"
              >
                Details
              </button>
            )}
          </>
        )}
        {frame.kind === "element" && itemActions && (
          <CanvasItemActions actions={itemActions} compact />
        )}
        {frame.kind === "block" && (
          <>
            <SectionVariantControl
              className="selection-variant-control status"
              onChange={onSectionVariantChange}
              value={sectionVariant}
              variants={sectionVariants}
            />
            <button onClick={onDuplicate} type="button">
              Duplicate
            </button>
            <button disabled={!canMoveUp} onClick={onMoveUp} type="button">
              Up
            </button>
            <button disabled={!canMoveDown} onClick={onMoveDown} type="button">
              Down
            </button>
            <SectionInsertControl
              buttonLabel="Add"
              className="selection-status-insert-control"
              onInsertSection={onInsertSection}
              sectionTemplates={sectionTemplates}
            />
          </>
        )}
        <button onClick={onOpenPanel} type="button">
          Panel
        </button>
        <button onClick={onAskAi} type="button">
          AI
        </button>
      </div>
    </section>
  );
}

function CanvasSelectionPath({
  blockName,
  canInlineEdit,
  elementCount,
  elementIndex,
  frame,
  hasPrimaryElementActions,
  isInlineEditorOpen,
  isQuickInspectorOpen,
  linkTarget,
  onAskAi,
  onInlineEdit,
  onNextElement,
  onOpenInspector,
  onPrevElement,
  onSelectSection,
  pageTitle,
}: {
  blockName: string;
  canInlineEdit: boolean;
  elementCount: number;
  elementIndex: number;
  frame: CanvasSelectionFrame;
  hasPrimaryElementActions: boolean;
  isInlineEditorOpen: boolean;
  isQuickInspectorOpen: boolean;
  linkTarget: SelectionLinkTarget | null;
  onAskAi: () => void;
  onInlineEdit: () => void;
  onNextElement: () => void;
  onOpenInspector: () => void;
  onPrevElement: () => void;
  onSelectSection: () => void;
  pageTitle: string;
}) {
  const hasElements = elementCount > 0;
  const elementSummary = selectionStepperSummary({
    elementCount,
    elementIndex,
    selectionKind: frame.kind,
  });

  return (
    <nav
      className={[
        "selection-path",
        hasPrimaryElementActions ? "context-only" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Selection path"
    >
      <div className="selection-path-stack">
        <span>{pageTitle}</span>
        <button
          className={frame.kind === "block" ? "active" : ""}
          onClick={onSelectSection}
          type="button"
        >
          {blockName}
        </button>
        {frame.kind === "element" && (
          <>
            <i aria-hidden="true">/</i>
            <span className="active current-element">{frame.label}</span>
          </>
        )}
      </div>
      {!hasPrimaryElementActions && (
        <div className="selection-path-actions">
          <SelectionLinkTargetChip
            linkTarget={linkTarget}
            onOpenTarget={onOpenInspector}
          />
          <button
            aria-label="Previous element"
            disabled={!hasElements}
            onClick={onPrevElement}
            type="button"
          >
            ‹
          </button>
          <span>{elementSummary}</span>
          <button
            aria-label="Next element"
            disabled={!hasElements}
            onClick={onNextElement}
            type="button"
          >
            ›
          </button>
          {frame.kind === "element" && frame.editable && (
            <>
              <button
                className={isInlineEditorOpen ? "active" : ""}
                onClick={canInlineEdit ? onInlineEdit : onOpenInspector}
                type="button"
              >
                {isInlineEditorOpen
                  ? "Typing"
                  : targetActionLabel(frame.target)}
              </button>
              {canInlineEdit && (
                <button
                  className={isQuickInspectorOpen ? "active" : ""}
                  onClick={onOpenInspector}
                  type="button"
                >
                  Details
                </button>
              )}
            </>
          )}
          <button onClick={onAskAi} type="button">
            AI
          </button>
        </div>
      )}
    </nav>
  );
}

function SectionInsertControl({
  buttonLabel,
  className,
  onInsertSection,
  sectionTemplates,
}: {
  buttonLabel: string;
  className: string;
  onInsertSection: (templateId: SectionTemplateId) => void;
  sectionTemplates: SectionTemplate[];
}) {
  const [templateId, setTemplateId] = useState<SectionTemplateId>(
    sectionTemplates[0]?.id ?? "leadForm",
  );
  const selectedTemplate =
    sectionTemplates.find((template) => template.id === templateId) ??
    sectionTemplates[0];
  if (!selectedTemplate) return null;

  return (
    <div className={className}>
      <select
        aria-label="Section to add"
        title={selectedTemplate.description}
        value={templateId}
        onChange={(event) =>
          setTemplateId(event.target.value as SectionTemplateId)
        }
      >
        {sectionTemplates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.label}
          </option>
        ))}
      </select>
      <button onClick={() => onInsertSection(templateId)} type="button">
        {buttonLabel}
      </button>
    </div>
  );
}

function CanvasSectionInsertHandle({
  frame,
  onInsertSection,
  sectionTemplates,
}: {
  frame: CanvasSelectionFrame;
  onInsertSection: (templateId: SectionTemplateId) => void;
  sectionTemplates: SectionTemplate[];
}) {
  const left = frame.left + frame.width / 2;
  const top = Math.max(8, frame.top + frame.height - 18);

  return (
    <fieldset className="canvas-section-insert-handle" style={{ left, top }}>
      <legend className="sr-only">Add section after selected section</legend>
      <span aria-hidden="true" />
      <SectionInsertControl
        buttonLabel="+"
        className="canvas-section-insert-control"
        onInsertSection={onInsertSection}
        sectionTemplates={sectionTemplates}
      />
    </fieldset>
  );
}

function CanvasSelectionDock({
  canInlineEdit,
  elementCount,
  elementIndex,
  frame,
  itemActions,
  isInlineEditorOpen,
  isQuickInspectorOpen,
  linkTarget,
  onAskAi,
  onDuplicate,
  onInsertSection,
  onInlineEdit,
  onNextElement,
  onOpenInspector,
  onOpenPanel,
  onPanelsHiddenChange,
  onPrevElement,
  onSectionVariantChange,
  onSelectSection,
  pageTitle,
  panelsHidden,
  sectionPosition,
  sectionTemplates,
  sectionVariant,
  sectionVariants,
  showAdvancedActions,
  totalSections,
}: {
  canInlineEdit: boolean;
  elementCount: number;
  elementIndex: number;
  frame: CanvasSelectionFrame;
  itemActions: SelectionItemActions | null;
  isInlineEditorOpen: boolean;
  isQuickInspectorOpen: boolean;
  linkTarget: SelectionLinkTarget | null;
  onAskAi: () => void;
  onDuplicate: () => void;
  onInsertSection: (templateId: SectionTemplateId) => void;
  onInlineEdit: () => void;
  onNextElement: () => void;
  onOpenInspector: () => void;
  onOpenPanel: () => void;
  onPanelsHiddenChange: (hidden: boolean) => void;
  onPrevElement: () => void;
  onSectionVariantChange: (value: string) => void;
  onSelectSection: () => void;
  pageTitle: string;
  panelsHidden: boolean;
  sectionPosition: number | null;
  sectionTemplates: SectionTemplate[];
  sectionVariant: string;
  sectionVariants: { id: string; name: string }[];
  showAdvancedActions: boolean;
  totalSections: number;
}) {
  const hasElements = elementCount > 0;
  const elementSummary = selectionStepperSummary({
    elementCount,
    elementIndex,
    selectionKind: frame.kind,
  });
  const status = selectionStatusLabel(frame, sectionPosition, totalSections);

  return (
    <section className="selection-dock" aria-label="Current canvas selection">
      <div className="selection-dock-copy">
        <small>
          {pageTitle} · {frame.kind === "block" ? "Area" : frame.sectionLabel}
        </small>
        <strong>{frame.label}</strong>
      </div>
      <span
        className={["selection-dock-status", frame.editable ? "editable" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {status}
      </span>
      <SelectionLinkTargetChip
        linkTarget={linkTarget}
        onOpenTarget={onOpenInspector}
      />
      <fieldset className="selection-stepper dock">
        <legend className="sr-only">Selectable elements</legend>
        {frame.kind === "element" && (
          <button onClick={onSelectSection} type="button">
            Whole area
          </button>
        )}
        <button
          aria-label="Previous element"
          disabled={!hasElements}
          onClick={onPrevElement}
          type="button"
        >
          ‹
        </button>
        <span>{elementSummary}</span>
        <button
          aria-label="Next element"
          disabled={!hasElements}
          onClick={onNextElement}
          type="button"
        >
          ›
        </button>
      </fieldset>
      <div className="selection-dock-actions">
        {frame.kind === "element" && frame.editable && (
          <>
            <button
              className={isInlineEditorOpen ? "active" : ""}
              onClick={canInlineEdit ? onInlineEdit : onOpenInspector}
              type="button"
            >
              {isInlineEditorOpen ? "Typing" : targetActionLabel(frame.target)}
            </button>
            {canInlineEdit && (
              <button
                className={isQuickInspectorOpen ? "active" : ""}
                onClick={onOpenInspector}
                type="button"
              >
                Details
              </button>
            )}
          </>
        )}
        {frame.kind === "element" && itemActions && (
          <CanvasItemActions actions={itemActions} />
        )}
        <button onClick={onOpenPanel} type="button">
          Inspector
        </button>
        <button onClick={onAskAi} type="button">
          Ask AI
        </button>
        {showAdvancedActions && frame.kind === "block" && (
          <>
            <SectionVariantControl
              className="selection-variant-control"
              onChange={onSectionVariantChange}
              value={sectionVariant}
              variants={sectionVariants}
            />
            <button onClick={onDuplicate} type="button">
              Duplicate
            </button>
          </>
        )}
        {showAdvancedActions && (
          <SectionInsertControl
            buttonLabel="Add after"
            className="selection-insert-control"
            onInsertSection={onInsertSection}
            sectionTemplates={sectionTemplates}
          />
        )}
        <button
          onClick={() => onPanelsHiddenChange(!panelsHidden)}
          type="button"
        >
          {panelsHidden ? "Show panels" : "Hide panels"}
        </button>
      </div>
    </section>
  );
}

function CanvasElementActionPill({
  canInlineEdit,
  elementCount,
  elementIndex,
  frame,
  isAdvancedMode,
  isSelected,
  itemActions,
  linkTarget,
  mode,
  onAskAi,
  onInlineEdit,
  onKeepHover,
  onNextElement,
  onOpenLinkTarget,
  onOpenInspector,
  onPrevElement,
  onSelectElement,
  onSelectSection,
  onStopHover,
  target,
  valuePreview,
  viewport,
}: {
  canInlineEdit: boolean;
  elementCount: number;
  elementIndex: number;
  frame: ElementFrame;
  isAdvancedMode: boolean;
  isSelected: boolean;
  itemActions: SelectionItemActions | null;
  linkTarget: SelectionLinkTarget | null;
  mode: "hovered" | "selected";
  onAskAi: () => void;
  onInlineEdit: () => void;
  onKeepHover: () => void;
  onNextElement: () => void;
  onOpenLinkTarget: () => void;
  onOpenInspector: () => void;
  onPrevElement: () => void;
  onSelectElement: () => void;
  onSelectSection: () => void;
  onStopHover: () => void;
  target: EditableElementTarget;
  valuePreview: string | null;
  viewport: PreviewViewport;
}) {
  const hasElements = elementCount > 0;
  const elementSummary = selectionStepperSummary({
    elementCount,
    elementIndex,
    selectionKind: "element",
  });
  const position = canvasElementActionPosition(frame, viewport, {
    placement:
      mode === "selected" && !isAdvancedMode && viewport.width <= 520
        ? "mobile-bottom-dock"
        : "near-target",
  });
  const linkActionLabel = canvasLinkTargetActionLabel(linkTarget);
  const actionChrome = canvasElementActionChrome({
    canInlineEdit,
    isAdvancedMode,
    isSelected,
    linkActionLabel,
    targetActionLabel: targetActionLabel(target),
  });
  const onPrimaryAction =
    actionChrome.primaryAction === "select"
      ? onSelectElement
      : actionChrome.primaryAction === "inline-edit"
        ? onInlineEdit
        : onOpenInspector;

  return (
    <section
      aria-label={`${target.label} element actions`}
      className={[
        "canvas-element-action-pill",
        mode === "hovered" ? "hover-preview" : "selected",
      ].join(" ")}
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (nextTarget && event.currentTarget.contains(nextTarget)) return;
        onStopHover();
      }}
      onFocus={onKeepHover}
      onMouseEnter={onKeepHover}
      onMouseLeave={onStopHover}
      style={position}
    >
      <div className="canvas-element-action-head">
        <button
          className="canvas-element-action-copy"
          onClick={onSelectElement}
          type="button"
        >
          <small>
            {isSelected ? "Editing" : `Ready to edit · ${frame.sectionLabel}`}
          </small>
          <strong>{frame.label}</strong>
        </button>
        <button
          className={[
            "canvas-element-primary-action",
            actionChrome.primaryAction,
          ].join(" ")}
          onClick={onPrimaryAction}
          type="button"
        >
          {actionChrome.primaryLabel}
        </button>
      </div>
      {actionChrome.showContextDetails && valuePreview && (
        <p className="canvas-element-value-preview">{valuePreview}</p>
      )}
      {actionChrome.showContextDetails && linkTarget && (
        <SelectionLinkTargetChip
          linkTarget={linkTarget}
          onOpenTarget={onOpenLinkTarget}
        />
      )}
      {actionChrome.showContextDetails && itemActions && (
        <CanvasItemActions actions={itemActions} compact />
      )}
      {actionChrome.showFocusNavigation && (
        <fieldset className="canvas-element-focus-nav">
          <legend className="sr-only">Move between editable fields</legend>
          <button onClick={onSelectSection} type="button">
            Whole area
          </button>
          <button
            aria-label="Previous element"
            disabled={!hasElements}
            onClick={onPrevElement}
            type="button"
          >
            ‹
          </button>
          <span>{elementSummary}</span>
          <button
            aria-label="Next element"
            disabled={!hasElements}
            onClick={onNextElement}
            type="button"
          >
            ›
          </button>
        </fieldset>
      )}
      {actionChrome.showSecondaryActions && (
        <div className="canvas-element-action-buttons">
          <button
            aria-label="Previous element"
            disabled={!hasElements}
            onClick={onPrevElement}
            type="button"
          >
            ‹
          </button>
          <span>{elementSummary}</span>
          <button
            aria-label="Next element"
            disabled={!hasElements}
            onClick={onNextElement}
            type="button"
          >
            ›
          </button>
          <button onClick={onSelectSection} type="button">
            Whole area
          </button>
          {actionChrome.showLinkAction && linkActionLabel && (
            <button onClick={onOpenLinkTarget} type="button">
              {linkActionLabel}
            </button>
          )}
          {actionChrome.showDetailsAction && (
            <button onClick={onOpenInspector} type="button">
              Details
            </button>
          )}
          <button onClick={onAskAi} type="button">
            AI
          </button>
        </div>
      )}
    </section>
  );
}

function CanvasInlineEditor({
  block,
  currentPage,
  frame,
  hostname,
  isBackLinking,
  onCancel,
  onCommit,
  pages,
  target,
  viewport,
}: {
  block: PageBlock;
  currentPage: EditorPage;
  frame: ElementFrame;
  hostname: string | null;
  isBackLinking: boolean;
  onCancel: () => void;
  onCommit: (
    data: Record<string, unknown>,
    opts?: { syncBackLinks?: boolean },
  ) => void;
  pages: EditorPage[];
  target: EditableElementTarget;
  viewport: PreviewViewport;
}) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [value, setValue] = useState(() =>
    editablePathText(block, target.path),
  );
  const [href, setHref] = useState(() =>
    target.hrefPath ? text(getPathValue(block.data, target.hrefPath)) : "",
  );
  const setInputRef = (node: HTMLInputElement | HTMLTextAreaElement | null) => {
    inputRef.current = node;
  };
  const normalizedHref = href.trim() ? normalizeSlugPath(href) : "";
  const pageValue =
    normalizedHref === ""
      ? ""
      : (pages.find((page) => normalizeSlugPath(page.slug) === normalizedHref)
          ?.slug ?? "");
  const pagesForRelationship = pages.some((page) => page.id === currentPage.id)
    ? pages.map((page) => (page.id === currentPage.id ? currentPage : page))
    : [currentPage, ...pages];
  const linkRelationship =
    target.kind === "link" && target.hrefPath
      ? linkRelationshipForHref({
          currentPageId: currentPage.id,
          hostname,
          href,
          pages: pagesForRelationship,
        })
      : null;
  const inlineRepairActionLabel = linkRelationshipRepairActionLabel({
    mode: "inline",
    relationship: linkRelationship,
  });
  const position = floatingPanelPosition(frame, viewport, {
    estimatedHeight:
      target.kind === "textarea" ? 218 : target.kind === "link" ? 228 : 116,
    maxWidth: 560,
    minWidth: 280,
  });
  const collapsedPosition = floatingEditorChipPosition(frame, viewport);
  const headerSummary = inlineEditorHeaderSummary({
    sectionLabel: frame.sectionLabel || blockLabel(block.blockType),
    targetLabel: target.label,
  });

  useEffect(() => {
    setValue(editablePathText(block, target.path));
    setHref(
      target.hrefPath ? text(getPathValue(block.data, target.hrefPath)) : "",
    );
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [block, target.hrefPath, target.path]);

  useEffect(() => {
    if (isCollapsed) return;
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [isCollapsed]);

  const commit = () => {
    commitWithOptions();
  };

  const commitWithOptions = (opts?: { syncBackLinks?: boolean }) => {
    const trimmedValue = value.trim();
    const nextValue = target.kind === "textarea" ? value : trimmedValue;
    let nextData = setPathValue(block.data, target.path, nextValue);
    if (target.kind === "link" && target.hrefPath) {
      nextData = setPathValue(nextData, target.hrefPath, href.trim());
    }
    onCommit(nextData, opts);
  };

  const handleKeyDown = (
    event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    const wantsCommit =
      event.key === "Enter" &&
      (target.kind !== "textarea" || event.metaKey || event.ctrlKey);
    if (!wantsCommit) return;
    event.preventDefault();
    commit();
  };

  if (isCollapsed) {
    return (
      <section
        aria-label={`${target.label} editor minimized`}
        className="floating-editor-chip inline"
        style={collapsedPosition}
      >
        <button
          className="floating-editor-chip-main"
          onClick={() => setIsCollapsed(false)}
          type="button"
        >
          <small>{frame.sectionLabel || blockLabel(block.blockType)}</small>
          <strong>{target.label}</strong>
        </button>
        <button onClick={() => setIsCollapsed(false)} type="button">
          Open
        </button>
        <button onClick={onCancel} type="button">
          Close
        </button>
      </section>
    );
  }

  return (
    <form
      aria-label={`${target.label} inline editor`}
      className={["inline-editor", target.kind].join(" ")}
      onSubmit={(event) => {
        event.preventDefault();
        commit();
      }}
      style={position}
    >
      <div className="inline-editor-head">
        <div className="inline-editor-head-copy">
          <span>{headerSummary.title}</span>
          <small>{headerSummary.context}</small>
        </div>
        <button
          className="floating-editor-minimize"
          onClick={() => setIsCollapsed(true)}
          type="button"
        >
          Minimize
        </button>
      </div>
      {target.kind === "textarea" ? (
        <textarea
          ref={setInputRef}
          rows={Math.max(3, Math.min(8, Math.ceil(frame.height / 26)))}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <input
          ref={setInputRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      )}
      {target.kind === "link" && target.hrefPath && (
        <div className="inline-link-tools">
          <div className="inline-link-target">
            <input
              aria-label="Link target"
              value={href}
              onChange={(event) => setHref(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="/services or https://example.com"
            />
            <select
              aria-label="Choose internal page"
              value={pageValue}
              onChange={(event) => {
                if (event.target.value) setHref(event.target.value);
              }}
            >
              <option value="">Page</option>
              {pages.map((page) => (
                <option key={page.id} value={page.slug}>
                  {page.title}
                </option>
              ))}
            </select>
          </div>
          {linkRelationship && (
            <LinkRelationshipCard
              isWorking={isBackLinking}
              onRepair={() => commitWithOptions({ syncBackLinks: true })}
              relationship={linkRelationship}
              repairActionLabel={inlineRepairActionLabel}
            />
          )}
        </div>
      )}
      <div className="inline-editor-actions">
        <span>
          {target.kind === "textarea" ? "Cmd/Ctrl Enter saves" : "Enter saves"}
        </span>
        <button onClick={onCancel} type="button">
          Cancel
        </button>
        <button type="submit">Save</button>
      </div>
    </form>
  );
}

function CanvasQuickInspector({
  block,
  currentPage,
  frame,
  hostname,
  isBackLinking,
  onChange,
  onClose,
  onSyncBackLinks,
  pages,
  target,
  viewport,
}: {
  block: PageBlock;
  currentPage: EditorPage;
  frame: ElementFrame;
  hostname: string | null;
  isBackLinking: boolean;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onSyncBackLinks: () => void;
  pages: EditorPage[];
  target: EditableElementTarget;
  viewport: PreviewViewport;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const mediaOptions = useMemo(
    () => mediaOptionsForBlocks(currentPage.blocks),
    [currentPage.blocks],
  );
  const position = floatingPanelPosition(frame, viewport, {
    estimatedHeight: 300,
    maxWidth: 340,
    minWidth: 300,
  });
  const collapsedPosition = floatingEditorChipPosition(frame, viewport);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  if (isCollapsed) {
    return (
      <section
        aria-label={`${target.label} quick editor minimized`}
        className="floating-editor-chip inspector"
        style={collapsedPosition}
      >
        <button
          className="floating-editor-chip-main"
          onClick={() => setIsCollapsed(false)}
          type="button"
        >
          <small>{frame.sectionLabel || blockLabel(block.blockType)}</small>
          <strong>{target.label}</strong>
        </button>
        <button onClick={() => setIsCollapsed(false)} type="button">
          Open
        </button>
        <button onClick={onClose} type="button">
          Close
        </button>
      </section>
    );
  }

  return (
    <div
      aria-label={`${target.label} quick editor`}
      className="quick-inspector"
      onKeyDown={handleKeyDown}
      role="dialog"
      style={position}
    >
      <div className="quick-inspector-head">
        <div>
          <strong>{target.label}</strong>
          <small>
            {targetKindLabel(target)} ·{" "}
            {frame.sectionLabel || blockLabel(block.blockType)}
          </small>
        </div>
        <button
          className="floating-editor-minimize"
          onClick={() => setIsCollapsed(true)}
          type="button"
        >
          Minimize
        </button>
        <button aria-label="Close quick editor" onClick={onClose} type="button">
          Close
        </button>
      </div>
      <ElementTargetEditor
        block={block}
        currentPage={currentPage}
        hostname={hostname}
        isBackLinking={isBackLinking}
        mediaOptions={mediaOptions}
        onChange={onChange}
        sectionLabel={frame.sectionLabel || blockLabel(block.blockType)}
        onSyncBackLinks={onSyncBackLinks}
        pages={pages}
        target={target}
      />
    </div>
  );
}

function SiteLinkPanel({
  currentPage,
  hostname,
  inboundLinks,
  isBackLinking,
  isPublishingSite,
  isSyncing,
  onAddHeaderLink,
  onAddFooterLink,
  onNavigate,
  onPatchLinkTarget,
  onPublishSite,
  onSyncBackLinks,
  onSyncSiteNavigation,
  outgoingLinks,
  pages,
}: {
  currentPage: EditorPage;
  hostname: string | null;
  inboundLinks: LinkSummary[];
  isBackLinking: boolean;
  isPublishingSite: boolean;
  isSyncing: boolean;
  onAddHeaderLink: (page: EditorPage) => void;
  onAddFooterLink: (page: EditorPage) => void;
  onNavigate: (pageId: string) => void;
  onPatchLinkTarget: (link: LinkSummary, href: string) => void;
  onPublishSite: () => void;
  onSyncBackLinks: () => void;
  onSyncSiteNavigation: () => void;
  outgoingLinks: LinkSummary[];
  pages: EditorPage[];
}) {
  const pageLinks = outgoingLinks.filter((link) => link.targetPage);
  const editableLinks = outgoingLinks.filter(
    (link) => link.blockId && link.hrefPath,
  );
  const missingLinks = outgoingLinks.filter(
    (link) => link.internalSlug && !link.targetPage,
  );
  const editableMissingLinks = missingLinks.filter(
    (link) => link.blockId && link.hrefPath,
  );
  const nonEditableMissingLinks = missingLinks.filter(
    (link) => !link.blockId || !link.hrefPath,
  );
  const outboundPageIds = new Set(
    pageLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const inboundPageIds = new Set(
    inboundLinks
      .map((link) => link.targetPage?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const needsBackLinkIds = new Set(
    Array.from(outboundPageIds).filter((pageId) => !inboundPageIds.has(pageId)),
  );
  const otherPages = pages.filter((page) => page.id !== currentPage.id);
  const headerLinkedPageIds = headerPageIds(currentPage, pages, hostname);
  const footerLinkedPageIds = footerPageIds(currentPage, pages, hostname);
  const navLinkedPageIds = new Set([
    ...Array.from(headerLinkedPageIds),
    ...Array.from(footerLinkedPageIds),
  ]);
  const unlinkedPages = otherPages.filter(
    (page) =>
      !headerLinkedPageIds.has(page.id) &&
      !footerLinkedPageIds.has(page.id) &&
      !outboundPageIds.has(page.id) &&
      !inboundPageIds.has(page.id),
  );
  const navCoverage = pages.length
    ? Math.round((navLinkedPageIds.size / pages.length) * 100)
    : 0;
  const needsBackLinks = needsBackLinkIds.size > 0;
  const issueSummary = navigationIssueSummary({
    disconnectedPageCount: unlinkedPages.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
    pageCount: pages.length,
  });
  const chrome = canvasLinkPopoverChromeState({
    disconnectedPageCount: unlinkedPages.length,
    editableMissingLinkCount: editableMissingLinks.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
  });
  const detailsCopy = linkDetailsDisclosureCopy({
    status: issueSummary.status,
  });
  const nextStepCopy = linkReviewNextStepCopy({
    disconnectedPageCount: unlinkedPages.length,
    editableMissingLinkCount: editableMissingLinks.length,
    missingLinkCount: missingLinks.length,
    needsReturnLinkCount: needsBackLinkIds.size,
    pageCount: pages.length,
  });
  const attentionPagePreview = unlinkedPages.slice(0, 6);
  const editableMissingPreview = editableMissingLinks.slice(0, 6);
  const hiddenEditableMissingCount =
    editableMissingLinks.length - editableMissingPreview.length;

  return (
    <div className="site-link-panel review-first">
      <div className="site-link-head">
        <div>
          <strong>Navigation review</strong>
          <span>{issueSummary.summary}</span>
        </div>
      </div>
      <div className={`canvas-link-review ${issueSummary.status}`}>
        <small className="canvas-link-review-eyebrow">
          {nextStepCopy.eyebrow}
        </small>
        <strong>{nextStepCopy.title}</strong>
        <span>{nextStepCopy.description}</span>
      </div>
      {(chrome.showRepairAction || chrome.showReturnLinkAction) && (
        <div className="site-link-actions primary">
          {chrome.showRepairAction && (
            <button
              disabled={pages.length < 2 || isSyncing}
              onClick={onSyncSiteNavigation}
              type="button"
            >
              {isSyncing ? "Repairing..." : "Repair navigation"}
            </button>
          )}
          {chrome.showReturnLinkAction && (
            <button
              disabled={!needsBackLinks || isBackLinking}
              onClick={onSyncBackLinks}
              type="button"
            >
              {isBackLinking
                ? "Adding return links..."
                : `Add ${needsBackLinkIds.size} return link${
                    needsBackLinkIds.size === 1 ? "" : "s"
                  }`}
            </button>
          )}
        </div>
      )}
      {chrome.showEditableMissingTargets && (
        <section className="canvas-link-review-section warning">
          <div className="canvas-link-review-section-head">
            <strong>Choose target pages</strong>
            <small>
              {editableMissingLinks.length} link
              {editableMissingLinks.length === 1 ? "" : "s"} need a page.
            </small>
          </div>
          <div className="link-stack editable-links review-only">
            {editableMissingPreview.map((link) => (
              <div className="link-row editable-link-row" key={link.key}>
                <div className="editable-link-info">
                  <span>{link.label}</span>
                  <small>
                    {link.source}
                    {" -> "}
                    {link.targetPage?.title ?? link.href}
                  </small>
                </div>
                <select
                  aria-label={`${link.source} target page`}
                  value={link.targetPage?.slug ?? ""}
                  onChange={(event) => {
                    if (event.target.value) {
                      onPatchLinkTarget(link, event.target.value);
                    }
                  }}
                >
                  <option value="">Choose page</option>
                  {pages.map((page) => (
                    <option key={page.id} value={page.slug}>
                      {page.title}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {hiddenEditableMissingCount > 0 && (
            <p className="muted small">
              {hiddenEditableMissingCount} more link
              {hiddenEditableMissingCount === 1 ? "" : "s"} are in details.
            </p>
          )}
        </section>
      )}
      {chrome.showMissingLinks && nonEditableMissingLinks.length > 0 && (
        <section className="canvas-link-review-section warning">
          <div className="canvas-link-review-section-head">
            <strong>Manual link review</strong>
            <small>
              These imported links could not be retargeted directly.
            </small>
          </div>
          <div className="missing-link-stack review-only">
            {nonEditableMissingLinks.slice(0, 6).map((link) => (
              <p key={`missing-${link.key}`}>
                <strong>{link.source}</strong>
                <span>{link.href}</span>
              </p>
            ))}
          </div>
        </section>
      )}
      {chrome.showDisconnectedPages && (
        <section className="canvas-link-review-section attention">
          <div className="canvas-link-review-section-head">
            <strong>Pages to add</strong>
            <small>
              {unlinkedPages.length} page
              {unlinkedPages.length === 1 ? "" : "s"} are not connected.
            </small>
          </div>
          <div className="page-target-grid review-only">
            {attentionPagePreview.map((page) => {
              const inHeader = headerLinkedPageIds.has(page.id);
              const inFooter = footerLinkedPageIds.has(page.id);
              return (
                <div className="page-target-row" key={`review-${page.id}`}>
                  <div>
                    <span>{page.title}</span>
                    <small>{page.slug}</small>
                  </div>
                  <button onClick={() => onNavigate(page.id)} type="button">
                    Open
                  </button>
                  <button
                    disabled={inHeader}
                    onClick={() => onAddHeaderLink(page)}
                    type="button"
                  >
                    {inHeader ? "In top nav" : "Add top"}
                  </button>
                  <button
                    disabled={inFooter}
                    onClick={() => onAddFooterLink(page)}
                    type="button"
                  >
                    {inFooter ? "In footer" : "Add footer"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
      {pageLinks.length === 0 &&
        inboundLinks.length === 0 &&
        missingLinks.length === 0 &&
        otherPages.length === 0 && (
          <p className="muted small">No internal page links on this draft.</p>
        )}
      <details
        className="canvas-link-details site-link-details"
        open={chrome.detailsOpenByDefault}
      >
        <summary>
          <span>{detailsCopy.title}</span>
          <small>{detailsCopy.description}</small>
        </summary>
        <div className="site-link-summary">
          <span>
            <strong>{pages.length}</strong>
            pages
          </span>
          <span>
            <strong>{headerLinkedPageIds.size}</strong>
            in header nav
          </span>
          <span>
            <strong>{footerLinkedPageIds.size}</strong>
            in footer nav
          </span>
          <span>
            <strong>{navCoverage}%</strong>
            coverage
          </span>
        </div>
        <div className="site-link-actions secondary">
          <button
            disabled={pages.length < 2 || isSyncing}
            onClick={onSyncSiteNavigation}
            type="button"
          >
            {isSyncing ? "Syncing..." : "Sync site nav"}
          </button>
          <button
            disabled={!needsBackLinks || isBackLinking}
            onClick={onSyncBackLinks}
            type="button"
          >
            {isBackLinking
              ? "Adding return links..."
              : needsBackLinks
                ? `Add ${needsBackLinkIds.size} return link${
                    needsBackLinkIds.size === 1 ? "" : "s"
                  }`
                : "Return links done"}
          </button>
          <button
            disabled={isPublishingSite}
            onClick={onPublishSite}
            type="button"
          >
            {isPublishingSite ? "Publishing site..." : "Publish site"}
          </button>
        </div>
        {otherPages.length > 0 && (
          <div className="page-target-grid">
            {otherPages.map((page) => {
              const linkedOut = outboundPageIds.has(page.id);
              const linksHere = inboundPageIds.has(page.id);
              const inHeader = headerLinkedPageIds.has(page.id);
              const inFooter = footerLinkedPageIds.has(page.id);
              return (
                <div className="page-target-row" key={page.id}>
                  <div>
                    <span>{page.title}</span>
                    <small>{page.slug}</small>
                  </div>
                  <div className="page-target-state">
                    {inHeader && <span className="good">Header</span>}
                    {inFooter && <span className="good">Footer</span>}
                    {linkedOut && <span className="good">Linked</span>}
                    {linksHere && <span>Inbound</span>}
                    {linkedOut && !linksHere && (
                      <span className="warn">Needs back</span>
                    )}
                    {!linkedOut && !linksHere && !inHeader && !inFooter && (
                      <span className="warn">Unlinked</span>
                    )}
                  </div>
                  <button onClick={() => onNavigate(page.id)} type="button">
                    Open
                  </button>
                  <button
                    disabled={inHeader}
                    onClick={() => onAddHeaderLink(page)}
                    type="button"
                  >
                    {inHeader ? "In top nav" : "Add top"}
                  </button>
                  <button
                    disabled={inFooter}
                    onClick={() => onAddFooterLink(page)}
                    type="button"
                  >
                    {inFooter ? "In footer" : "Add footer"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {editableLinks.length > 0 && (
          <div className="link-stack editable-links">
            {editableLinks.map((link) => (
              <div className="link-row editable-link-row" key={link.key}>
                <button
                  disabled={!link.targetPage}
                  onClick={() =>
                    link.targetPage && onNavigate(link.targetPage.id)
                  }
                  type="button"
                >
                  <span>{link.label}</span>
                  <small>
                    {link.source}
                    {" -> "}
                    {link.targetPage?.title ?? link.href}
                  </small>
                </button>
                <select
                  aria-label={`${link.source} target page`}
                  value={link.targetPage?.slug ?? ""}
                  onChange={(event) => {
                    if (event.target.value) {
                      onPatchLinkTarget(link, event.target.value);
                    }
                  }}
                >
                  <option value="">Choose page</option>
                  {pages.map((page) => (
                    <option key={page.id} value={page.slug}>
                      {page.title}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
        {inboundLinks.length > 0 && (
          <div className="link-stack inbound">
            {inboundLinks.map((link) => (
              <button
                className="link-row"
                key={`in-${link.key}`}
                onClick={() =>
                  link.targetPage && onNavigate(link.targetPage.id)
                }
                type="button"
              >
                <span>{link.source}</span>
                <small>{link.label}</small>
              </button>
            ))}
          </div>
        )}
        {nonEditableMissingLinks.length > 0 && (
          <div className="missing-link-stack">
            {nonEditableMissingLinks.map((link) => (
              <p key={`missing-detail-${link.key}`}>
                <strong>{link.source}</strong>
                <span>{link.href}</span>
              </p>
            ))}
          </div>
        )}
      </details>
    </div>
  );
}

function PageUrlField({
  label,
  onChange,
  pages,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  pages: EditorPage[];
  value: string;
}) {
  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue ? normalizeSlugPath(trimmedValue) : "";
  const pageValue =
    normalizedValue === ""
      ? ""
      : (pages.find((page) => normalizeSlugPath(page.slug) === normalizedValue)
          ?.slug ?? "");
  return (
    <label className="url-field">
      <span>{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="/services or https://example.com"
      />
      <select
        aria-label={`${label} internal page`}
        value={pageValue}
        onChange={(event) => {
          if (event.target.value) onChange(event.target.value);
        }}
      >
        <option value="">Choose internal page</option>
        {pages.map((page) => (
          <option key={page.id} value={page.slug}>
            {page.title} ({page.slug})
          </option>
        ))}
      </select>
    </label>
  );
}

function MediaUrlField({
  label,
  mediaOptions = [],
  onChange,
  value,
}: {
  label: string;
  mediaOptions?: MediaOption[];
  onChange: (value: string) => void;
  value: string;
}) {
  const matchingOption = mediaOptions.find((option) => option.src === value);
  return (
    <label className="media-url-field">
      <span>{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="/images/project.webp"
      />
      {mediaOptions.length > 0 && (
        <select
          aria-label={`${label} reusable media`}
          value={matchingOption?.src ?? ""}
          onChange={(event) => {
            if (event.target.value) onChange(event.target.value);
          }}
        >
          <option value="">Reuse media</option>
          {mediaOptions.map((option) => (
            <option key={option.src} value={option.src}>
              {option.label}
              {option.usageCount > 1 ? ` (${option.usageCount})` : ""}
            </option>
          ))}
        </select>
      )}
    </label>
  );
}

function ContentItemCard({
  blockType,
  children,
  canDuplicate,
  canRemove,
  index,
  item,
  onDuplicate,
  onRemove,
}: {
  blockType: string;
  children: ReactNode;
  canDuplicate: boolean;
  canRemove: boolean;
  index: number;
  item: Record<string, unknown>;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const summary = cmsCollectionItemSummary(blockType, item, index);
  return (
    <div className={["content-item-card", summary.tone].join(" ")}>
      <div className="content-item-head">
        {(summary.tone === "media" || summary.imageSrc) && (
          <div className="content-item-media-thumb">
            {summary.imageSrc ? (
              <img alt={summary.imageAlt ?? ""} src={summary.imageSrc} />
            ) : (
              <span>No image</span>
            )}
          </div>
        )}
        <div className="content-item-title">
          <small>{summary.itemLabel}</small>
          <strong>{summary.title}</strong>
          <span>{summary.meta}</span>
        </div>
        <div className="content-item-actions">
          <button disabled={!canDuplicate} onClick={onDuplicate} type="button">
            Duplicate
          </button>
          <button disabled={!canRemove} onClick={onRemove} type="button">
            Remove
          </button>
        </div>
      </div>
      <div className="content-item-fields">{children}</div>
    </div>
  );
}

function NavigationLinkCard({
  canDuplicate,
  canRemove,
  children,
  hostname,
  index,
  itemLabel,
  link,
  onDuplicate,
  onRemove,
  pages,
}: {
  canDuplicate: boolean;
  canRemove: boolean;
  children: ReactNode;
  hostname: string | null;
  index: number;
  itemLabel: string;
  link: Record<string, unknown>;
  onDuplicate: () => void;
  onRemove: () => void;
  pages: EditorPage[];
}) {
  const summary = navigationLinkSummary(
    link,
    pages,
    hostname,
    index,
    itemLabel,
  );
  return (
    <div className={["navigation-link-card", summary.status].join(" ")}>
      <div className="navigation-link-head">
        <div className="navigation-link-title">
          <small>{summary.itemLabel}</small>
          <strong>{summary.title}</strong>
          <span>{summary.meta}</span>
        </div>
        <span className="navigation-link-status">{summary.statusLabel}</span>
        <div className="content-item-actions">
          <button disabled={!canDuplicate} onClick={onDuplicate} type="button">
            Duplicate
          </button>
          <button disabled={!canRemove} onClick={onRemove} type="button">
            Remove
          </button>
        </div>
      </div>
      <div className="content-item-fields">{children}</div>
    </div>
  );
}

function LinkListEditor({
  hostname,
  itemLabel = "Link",
  label,
  links,
  maxLinks = 12,
  minLinks = 0,
  onChange,
  pages,
}: {
  hostname: string | null;
  itemLabel?: string;
  label: string;
  links: Record<string, unknown>[];
  maxLinks?: number;
  minLinks?: number;
  onChange: (links: Record<string, unknown>[]) => void;
  pages: EditorPage[];
}) {
  const canAdd = links.length < maxLinks;
  const defaultHref = pages[0]?.slug ?? "/";
  return (
    <div className="link-list-editor">
      <div className="mini-group-head">
        <strong>{label}</strong>
        <button
          disabled={!canAdd}
          onClick={() =>
            onChange([...links, { label: "New page", href: defaultHref }])
          }
          type="button"
        >
          Add link
        </button>
      </div>
      {links.length === 0 ? (
        <p className="muted small">No links yet.</p>
      ) : (
        links.map((link, index) => (
          <NavigationLinkCard
            canDuplicate={links.length < maxLinks}
            canRemove={links.length > minLinks}
            hostname={hostname}
            index={index}
            itemLabel={itemLabel}
            key={`${label}-${text(link.label)}-${text(link.href)}`}
            link={link}
            onDuplicate={() => {
              const nextLinks = [...links];
              nextLinks.splice(index + 1, 0, cloneBlockData(link));
              onChange(nextLinks);
            }}
            onRemove={() =>
              onChange(links.filter((_, linkIndex) => linkIndex !== index))
            }
            pages={pages}
          >
            <TextField
              label="Label"
              value={text(link.label)}
              onChange={(value) =>
                onChange(
                  links.map((candidate, linkIndex) =>
                    linkIndex === index
                      ? { ...candidate, label: value }
                      : candidate,
                  ),
                )
              }
            />
            <PageUrlField
              label="Target"
              pages={pages}
              value={text(link.href)}
              onChange={(value) =>
                onChange(
                  links.map((candidate, linkIndex) =>
                    linkIndex === index
                      ? { ...candidate, href: value }
                      : candidate,
                  ),
                )
              }
            />
          </NavigationLinkCard>
        ))
      )}
    </div>
  );
}

function OptionalLinkFields({
  addLabel,
  defaultLabel,
  label,
  link,
  onChange,
  pages,
}: {
  addLabel: string;
  defaultLabel: string;
  label: string;
  link: Record<string, unknown>;
  onChange: (link: Record<string, string> | undefined) => void;
  pages: EditorPage[];
}) {
  const hasLink = Boolean(text(link.label) || text(link.href));
  if (!hasLink) {
    return (
      <button
        className="inline-add span-2"
        onClick={() => onChange({ label: defaultLabel, href: "/" })}
        type="button"
      >
        {addLabel}
      </button>
    );
  }

  return (
    <div className="mini-group optional-link-fields span-2">
      <div className="mini-group-head">
        <strong>{label}</strong>
        <button onClick={() => onChange(undefined)} type="button">
          Remove
        </button>
      </div>
      <TextField
        label="Label"
        value={text(link.label)}
        onChange={(value) => onChange({ ...link, label: value })}
      />
      <PageUrlField
        label="Target"
        pages={pages}
        value={text(link.href)}
        onChange={(value) => onChange({ ...link, href: value })}
      />
    </div>
  );
}

function BlockCard({
  block,
  canMoveDown,
  canMoveUp,
  hostname,
  isSelected = false,
  mediaOptions,
  onChange,
  onDuplicate,
  onFocus,
  onMoveDown,
  onMoveUp,
  onPatch,
  onRemove,
  pages,
}: {
  block: PageBlock;
  canMoveDown: boolean;
  canMoveUp: boolean;
  hostname: string | null;
  isSelected?: boolean;
  mediaOptions: MediaOption[];
  onChange: (data: Record<string, unknown>) => void;
  onDuplicate: () => void;
  onFocus?: () => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onPatch: (patch: Partial<PageBlock>) => void;
  onRemove: () => void;
  pages: EditorPage[];
}) {
  const variants = BLOCK_VARIANTS[block.blockType] ?? [];
  return (
    <article className={isSelected ? "block-card selected" : "block-card"}>
      <div className="block-head">
        <div>
          <h3>{blockLabel(block.blockType)}</h3>
          <p className="muted small">{block.variant}</p>
        </div>
        <div className="block-tools">
          {onFocus && (
            <button disabled={isSelected} onClick={onFocus} type="button">
              Focus
            </button>
          )}
          <button disabled={!canMoveUp} onClick={onMoveUp} type="button">
            Up
          </button>
          <button disabled={!canMoveDown} onClick={onMoveDown} type="button">
            Down
          </button>
          <button onClick={onDuplicate} type="button">
            Duplicate
          </button>
          <button onClick={onRemove} type="button">
            Remove
          </button>
        </div>
      </div>
      {variants.length > 0 && (
        <label className="variant-field">
          Style
          <select
            value={block.variant}
            onChange={(event) => onPatch({ variant: event.target.value })}
          >
            {variants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <BlockFields
        block={block}
        hostname={hostname}
        mediaOptions={mediaOptions}
        onChange={onChange}
        pages={pages}
      />
    </article>
  );
}

function BlockFocusPanel({
  block,
  canMoveDown,
  canMoveUp,
  currentPage,
  hostname,
  isBackLinking,
  isCollapsed,
  mediaOptions,
  onChange,
  onDuplicate,
  onInsertSection,
  onMoveDown,
  onMoveUp,
  onPatch,
  onRemove,
  onSyncBackLinks,
  onToggleCollapse,
  selectedElementLabel,
  selectedTarget,
  sectionTemplates,
  pages,
}: {
  block: PageBlock;
  canMoveDown: boolean;
  canMoveUp: boolean;
  currentPage: EditorPage;
  hostname: string | null;
  isBackLinking: boolean;
  isCollapsed: boolean;
  mediaOptions: MediaOption[];
  onChange: (data: Record<string, unknown>) => void;
  onDuplicate: () => void;
  onInsertSection: (templateId: SectionTemplateId) => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onPatch: (patch: Partial<PageBlock>) => void;
  onRemove: () => void;
  onSyncBackLinks: () => void;
  onToggleCollapse: () => void;
  selectedElementLabel: string | null;
  selectedTarget: EditableElementTarget | null;
  sectionTemplates: SectionTemplate[];
  pages: EditorPage[];
}) {
  const variants = BLOCK_VARIANTS[block.blockType] ?? [];
  const selectedTargetPath = selectedTarget?.path ?? null;
  const hasSelectedTarget = selectedTargetPath !== null;
  const initialChrome = blockFocusPanelChromeState({
    hasSelectedTarget,
  });
  const [areSectionToolsOpen, setAreSectionToolsOpen] = useState(
    initialChrome.startsToolsOpen,
  );

  useEffect(() => {
    setAreSectionToolsOpen(selectedTargetPath === null);
  }, [selectedTargetPath]);

  const panelChrome = blockFocusPanelChromeState({
    hasSelectedTarget,
    toolsOpen: areSectionToolsOpen,
  });

  return (
    <section
      className={[
        "editor-panel focus-panel overlay-panel",
        selectedTarget ? "target-focused" : "section-focused",
        panelChrome.showSectionTools ? "tools-open" : "tools-tucked",
        isCollapsed ? "collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="overlay-panel-head">
        <div>
          <h3>{blockLabel(block.blockType)}</h3>
          <p className="muted small">{selectedElementLabel ?? block.variant}</p>
        </div>
        <div className="overlay-panel-actions">
          {!isCollapsed && panelChrome.showToolsToggle && (
            <button
              aria-expanded={panelChrome.showSectionTools}
              className="panel-toggle quiet"
              onClick={() => setAreSectionToolsOpen((current) => !current)}
              type="button"
            >
              {panelChrome.toolsToggleLabel}
            </button>
          )}
          <button
            aria-expanded={!isCollapsed}
            className="panel-toggle"
            onClick={onToggleCollapse}
            type="button"
          >
            {isCollapsed ? "Open" : "Collapse"}
          </button>
        </div>
      </div>
      <div className="overlay-panel-body">
        {selectedElementLabel && (
          <p className="focus-target">{selectedElementLabel}</p>
        )}
        {selectedTarget ? (
          <ElementTargetEditor
            block={block}
            currentPage={currentPage}
            hostname={hostname}
            isBackLinking={isBackLinking}
            mediaOptions={mediaOptions}
            onChange={onChange}
            sectionLabel={blockLabel(block.blockType)}
            onSyncBackLinks={onSyncBackLinks}
            pages={pages}
            target={selectedTarget}
          />
        ) : (
          <p className="inspector-empty">
            Select text, an image, a link, or a form control in the preview.
          </p>
        )}
        {panelChrome.showSectionTools && (
          <div className="focus-section-tools">
            <div className="focus-section-tools-head">
              <strong>Section tools</strong>
              <small>
                Move, restyle, duplicate, or add around this section.
              </small>
            </div>
            <div className="block-tools focus-tools">
              <button disabled={!canMoveUp} onClick={onMoveUp} type="button">
                Up
              </button>
              <button
                disabled={!canMoveDown}
                onClick={onMoveDown}
                type="button"
              >
                Down
              </button>
              <button onClick={onDuplicate} type="button">
                Duplicate
              </button>
              <button onClick={onRemove} type="button">
                Remove
              </button>
            </div>
            <div className="panel-section-insert">
              <div>
                <strong>Add section</strong>
                <small>Places a new block after this section.</small>
              </div>
              <SectionInsertControl
                buttonLabel="Add after"
                className="panel-section-insert-control"
                onInsertSection={onInsertSection}
                sectionTemplates={sectionTemplates}
              />
            </div>
            {variants.length > 0 && (
              <label className="variant-field">
                Style
                <select
                  value={block.variant}
                  onChange={(event) => onPatch({ variant: event.target.value })}
                >
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <details className="advanced-fields">
              <summary>All section fields</summary>
              <BlockFields
                block={block}
                hostname={hostname}
                mediaOptions={mediaOptions}
                onChange={onChange}
                pages={pages}
              />
            </details>
          </div>
        )}
      </div>
    </section>
  );
}

function LinkRelationshipCard({
  isWorking,
  onRepair,
  repairActionLabel,
  relationship,
}: {
  isWorking: boolean;
  onRepair: () => void;
  repairActionLabel?: string | null;
  relationship: LinkRelationship;
}) {
  const actionLabel =
    repairActionLabel ??
    linkRelationshipRepairActionLabel({ mode: "inspector", relationship });
  return (
    <div className={["link-relationship-card", relationship.status].join(" ")}>
      <div>
        <small>Site flow</small>
        <strong>{relationship.label}</strong>
        <span>{relationship.copy}</span>
      </div>
      {relationship.canRepair && actionLabel && (
        <button disabled={isWorking} onClick={onRepair} type="button">
          {isWorking ? "Adding" : actionLabel}
        </button>
      )}
    </div>
  );
}

function ElementTargetEditor({
  block,
  currentPage,
  hostname,
  isBackLinking,
  mediaOptions,
  onChange,
  sectionLabel,
  onSyncBackLinks,
  pages,
  target,
}: {
  block: PageBlock;
  currentPage: EditorPage;
  hostname: string | null;
  isBackLinking: boolean;
  mediaOptions: MediaOption[];
  onChange: (data: Record<string, unknown>) => void;
  sectionLabel?: string | null;
  onSyncBackLinks: () => void;
  pages: EditorPage[];
  target: EditableElementTarget;
}) {
  const data = block.data;
  const updatePath = (path: string, value: unknown) =>
    onChange(setPathValue(data, path, value));
  const linkBasePath = target.path.endsWith(".label")
    ? target.path.slice(0, -".label".length)
    : null;
  const isOptionalLink =
    linkBasePath !== null &&
    target.hrefPath === `${linkBasePath}.href` &&
    ((block.blockType === "heroSection" &&
      (linkBasePath === "primaryCta" || linkBasePath === "secondaryCta")) ||
      (block.blockType === "textImage" && linkBasePath === "cta"));
  const updateLink = (patch: Record<string, unknown>) => {
    if (isOptionalLink && linkBasePath) {
      onChange(
        setPathValue(
          data,
          linkBasePath,
          optionalLink(getPathValue(data, linkBasePath), patch),
        ),
      );
      return;
    }
    if (patch.label !== undefined) updatePath(target.path, text(patch.label));
    if (patch.href !== undefined && target.hrefPath) {
      updatePath(target.hrefPath, text(patch.href));
    }
  };
  const updateImageSrc = (next: string) => {
    if (block.blockType === "heroSection" && target.path === "image.src") {
      const src = next.trim();
      updatePath(
        "image",
        src
          ? {
              ...object(data.image),
              src,
              alt: text(getPathValue(data, target.altPath ?? "")),
            }
          : undefined,
      );
      return;
    }
    updatePath(target.path, next);
  };
  const updateImageAlt = (next: string) => {
    if (block.blockType === "heroSection" && target.altPath === "image.alt") {
      const image = object(data.image);
      if (!text(image.src)) return;
      updatePath("image", { ...image, alt: next });
      return;
    }
    if (target.altPath) updatePath(target.altPath, next);
  };
  const value = editablePathText(block, target.path);
  const hrefValue = target.hrefPath
    ? text(getPathValue(data, target.hrefPath))
    : "";
  const pagesForRelationship = pages.some((page) => page.id === currentPage.id)
    ? pages.map((page) => (page.id === currentPage.id ? currentPage : page))
    : [currentPage, ...pages];
  const linkRelationship =
    target.kind === "link" && target.hrefPath
      ? linkRelationshipForHref({
          currentPageId: currentPage.id,
          hostname,
          href: hrefValue,
          pages: pagesForRelationship,
        })
      : null;
  const altValue = target.altPath
    ? text(getPathValue(data, target.altPath))
    : "";
  const collectionTarget = collectionTargetFor(block, target);
  const collectionItems = collectionTarget
    ? arrayOfObjects(data[collectionTarget.collection])
    : [];
  const canDuplicateItem = Boolean(
    collectionTarget && collectionItems.length < collectionTarget.maxItems,
  );
  const canRemoveItem = Boolean(
    collectionTarget && collectionItems.length > collectionTarget.minItems,
  );
  const itemLabel = collectionTarget?.itemLabel ?? "item";
  const itemPosition = collectionTarget
    ? `${collectionTarget.index + 1} of ${collectionItems.length}`
    : "";
  const headerSummary = elementInspectorHeaderSummary({
    sectionLabel,
    target,
  });

  return (
    <div className="element-inspector">
      <div
        className="inspector-head"
        data-target-path={targetPathLabel(target)}
      >
        <div className="inspector-head-copy">
          <span>{headerSummary.title}</span>
          <small>{headerSummary.detail}</small>
        </div>
        <span className="inspector-kind">{headerSummary.kindLabel}</span>
      </div>
      {target.kind === "textarea" ? (
        <TextAreaField
          label="Content"
          rows={4}
          value={value}
          onChange={(next) => updatePath(target.path, next)}
        />
      ) : target.kind === "image" ? (
        <div className="field-grid">
          <MediaUrlField
            label="Image URL"
            mediaOptions={mediaOptions}
            value={value}
            onChange={updateImageSrc}
          />
          {target.altPath && (
            <TextField
              label="Alt text"
              value={altValue}
              onChange={updateImageAlt}
            />
          )}
        </div>
      ) : target.kind === "link" ? (
        <div className="field-grid">
          <TextField
            label="Link text"
            value={value}
            onChange={(next) => updateLink({ label: next })}
          />
          {target.hrefPath && (
            <PageUrlField
              label="Target page or URL"
              pages={pages}
              value={hrefValue}
              onChange={(next) => updateLink({ href: next })}
            />
          )}
          {linkRelationship && (
            <LinkRelationshipCard
              isWorking={isBackLinking}
              onRepair={onSyncBackLinks}
              relationship={linkRelationship}
            />
          )}
        </div>
      ) : (
        <TextField
          label="Text"
          value={value}
          onChange={(next) => updatePath(target.path, next)}
        />
      )}
      {collectionTarget && (
        <div className="element-collection-tools">
          <div>
            <strong>{itemLabel}</strong>
            <small>{itemPosition}</small>
          </div>
          <button
            disabled={!canDuplicateItem}
            onClick={() =>
              onChange(duplicateCollectionItem(data, collectionTarget))
            }
            type="button"
          >
            Duplicate
          </button>
          <button
            disabled={!canRemoveItem}
            onClick={() =>
              onChange(removeCollectionItem(data, collectionTarget))
            }
            type="button"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

function AgentChatPanel({
  agentDraftSource,
  agentDraftReviewLabel,
  canUndoDraft,
  chatMessages,
  chatPrompt,
  hasPendingAgentChange,
  isCollapsed,
  isDraftDirty,
  onClearChat,
  onOpenBlogPanel,
  onOpenMediaPanel,
  onOpenThemePanel,
  onPublishSite,
  onPromptChange,
  onRunPrompt,
  onSaveDraft,
  onToggleCollapse,
  onUndoDraft,
  selection,
  working,
}: {
  agentDraftSource: AiEditPlanSource | null;
  agentDraftReviewLabel: string | null;
  canUndoDraft: boolean;
  chatMessages: ChatMessage[];
  chatPrompt: string;
  hasPendingAgentChange: boolean;
  isCollapsed: boolean;
  isDraftDirty: boolean;
  onClearChat: () => void;
  onOpenBlogPanel: () => void;
  onOpenMediaPanel: () => void;
  onOpenThemePanel: () => void;
  onPublishSite: () => Promise<void>;
  onPromptChange: (value: string) => void;
  onRunPrompt: (prompt: string) => Promise<void>;
  onSaveDraft: () => Promise<void>;
  onToggleCollapse: () => void;
  onUndoDraft: () => void;
  selection: AgentChatSelection | null;
  working: string | null;
}) {
  const chatLogRef = useRef<HTMLDivElement | null>(null);
  const [areMorePromptsOpen, setAreMorePromptsOpen] = useState(false);
  const isWorking = working === "chat";
  const selectionSummary = agentChatSelectionSummary({
    isWorking,
    messageCount: chatMessages.length,
    selection,
  });
  const contextualPrompts = agentChatPromptSuggestions(selection);
  const suggestionGroups = agentPromptSuggestionGroups(contextualPrompts);
  const panelChrome = agentChatPanelChromeState({
    hasPendingAgentChange,
    hasSelection: Boolean(selection),
    isWorking,
    messageCount: chatMessages.length,
  });
  const reviewState = agentDraftReviewState({
    isDraftDirty,
    reviewLabel: agentDraftReviewLabel,
    source: agentDraftSource,
  });
  const submitState = agentChatSubmitState({
    prompt: chatPrompt,
    working,
  });
  const suggestionScopeKey = selection
    ? [
        selection.blockName,
        selection.canPatchField ? "field" : "section",
        selection.fieldLabel ?? "",
      ].join(":")
    : "page";

  useEffect(() => {
    const log = chatLogRef.current;
    if (!log || isCollapsed) return;
    log.scrollTo({
      behavior: chatMessages.length > 1 ? "smooth" : "auto",
      top: log.scrollHeight,
    });
  }, [chatMessages.length, isCollapsed]);

  useEffect(() => {
    if (suggestionScopeKey) setAreMorePromptsOpen(false);
  }, [suggestionScopeKey]);

  const renderPromptChip = (item: AgentPromptSuggestion) => (
    <button
      className="chip"
      disabled={working !== null}
      key={item.label}
      onClick={() => {
        if (item.action === "open_blog_cms") {
          onOpenBlogPanel();
          return;
        }
        if (item.action === "open_media_library") {
          onOpenMediaPanel();
          return;
        }
        if (item.action === "open_theme_panel") {
          onOpenThemePanel();
          return;
        }
        onPromptChange(item.prompt);
        if (item.run) void onRunPrompt(item.prompt);
      }}
      type="button"
    >
      {item.label}
    </button>
  );

  return (
    <section
      className={[
        "editor-panel ai-chat-panel overlay-panel",
        panelChrome.mode === "starter" ? "starter" : "conversation",
        isCollapsed ? "collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="overlay-panel-head">
        <div>
          <h3>Builder agent</h3>
          <p className="muted small">{selectionSummary.statusLabel}</p>
        </div>
        <div className="overlay-panel-actions">
          {panelChrome.showNewChatAction && (
            <button
              className="panel-toggle quiet"
              disabled={working !== null}
              onClick={onClearChat}
              type="button"
            >
              New chat
            </button>
          )}
          <button
            aria-expanded={!isCollapsed}
            className="panel-toggle"
            onClick={onToggleCollapse}
            type="button"
          >
            {isCollapsed ? "Open" : "Collapse"}
          </button>
        </div>
      </div>
      <div className="overlay-panel-body">
        {panelChrome.showContext && (
          <div
            className={[
              "agent-context",
              selection?.fieldLabel ? "field" : "section",
            ].join(" ")}
          >
            <span>{selectionSummary.scopeKind}</span>
            <strong>{selectionSummary.scopeLabel}</strong>
            {selectionSummary.valuePreview && (
              <small>{selectionSummary.valuePreview}</small>
            )}
          </div>
        )}
        {panelChrome.showChatLog && (
          <div
            className="chat-log agent-chat-log"
            aria-live="polite"
            ref={chatLogRef}
          >
            {chatMessages.map((message) => (
              <p className={`chat-message ${message.role}`} key={message.id}>
                {message.text}
              </p>
            ))}
          </div>
        )}
        {hasPendingAgentChange && (
          <div className="agent-review" role="status">
            <div>
              <span>{reviewState.sourceLabel}</span>
              <strong>{reviewState.title}</strong>
              <small>{reviewState.stateLabel}</small>
              <p>{reviewState.hint}</p>
            </div>
            <div className="agent-review-actions">
              <button
                className="btn-secondary"
                disabled={working !== null || !isDraftDirty || !canUndoDraft}
                onClick={onUndoDraft}
                type="button"
              >
                Discard
              </button>
              <button
                className="btn-secondary"
                disabled={working !== null || !isDraftDirty}
                onClick={() => void onSaveDraft()}
                type="button"
              >
                {working === "save" ? "Saving..." : "Save draft"}
              </button>
              <button
                disabled={working !== null}
                onClick={() => void onPublishSite()}
                type="button"
              >
                {working === "publish-site" ? "Publishing..." : "Publish site"}
              </button>
            </div>
          </div>
        )}
        <form
          className="ai-row"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void onRunPrompt(chatPrompt);
          }}
        >
          <input
            placeholder={selectionSummary.placeholder}
            value={chatPrompt}
            onChange={(event) => onPromptChange(event.target.value)}
          />
          <button
            aria-label={submitState.ariaLabel}
            className={submitState.className}
            disabled={submitState.disabled}
            type="submit"
          >
            {submitState.label}
          </button>
        </form>
        {panelChrome.showSuggestions && (
          <div
            className={[
              "agent-suggestions",
              selection?.canPatchField ? "field-prompts" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="agent-suggestions-head">
              <span>Suggested edits</span>
              {suggestionGroups.secondary.length > 0 && (
                <button
                  aria-expanded={areMorePromptsOpen}
                  className="agent-more-prompts"
                  disabled={working !== null}
                  onClick={() => setAreMorePromptsOpen((current) => !current)}
                  type="button"
                >
                  {areMorePromptsOpen
                    ? "Fewer"
                    : `More ideas (${suggestionGroups.secondary.length})`}
                </button>
              )}
            </div>
            <div className="chip-row agent-suggestion-row">
              {suggestionGroups.primary.map(renderPromptChip)}
            </div>
            {areMorePromptsOpen && suggestionGroups.secondary.length > 0 && (
              <div className="chip-row agent-suggestion-row secondary">
                {suggestionGroups.secondary.map(renderPromptChip)}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function BlockFields({
  block,
  hostname,
  mediaOptions,
  onChange,
  pages,
}: {
  block: PageBlock;
  hostname: string | null;
  mediaOptions: MediaOption[];
  onChange: (data: Record<string, unknown>) => void;
  pages: EditorPage[];
}) {
  const data = block.data;
  const update = (patch: Record<string, unknown>) =>
    onChange({ ...data, ...patch });

  if (block.blockType === "header") {
    const links = arrayOfObjects(data.links);
    return (
      <div className="stack">
        <div className="field-grid">
          <TextField
            label="Brand name"
            value={text(data.brandName)}
            onChange={(value) => update({ brandName: value })}
          />
          <label className="checkbox-field">
            <input
              checked={bool(data.sticky)}
              type="checkbox"
              onChange={(event) => update({ sticky: event.target.checked })}
            />
            Sticky header
          </label>
          <TextField
            label="Action label"
            value={text(data.ctaLabel)}
            onChange={(value) => update({ ctaLabel: value || undefined })}
          />
          <PageUrlField
            label="Action link"
            pages={pages}
            value={text(data.ctaHref)}
            onChange={(value) => update({ ctaHref: value || undefined })}
          />
        </div>
        <LinkListEditor
          hostname={hostname}
          itemLabel="Navigation link"
          label="Header links"
          links={links}
          onChange={(value) => update({ links: value })}
          pages={pages}
        />
      </div>
    );
  }

  if (block.blockType === "heroSection") {
    const image = object(data.image);
    const primaryCta = object(data.primaryCta);
    const secondaryCta = object(data.secondaryCta);
    return (
      <div className="field-grid">
        <TextField
          label="Headline"
          value={text(data.headline)}
          onChange={(value) => update({ headline: value })}
        />
        <TextAreaField
          label="Subheadline"
          value={text(data.subheadline)}
          onChange={(value) => update({ subheadline: value })}
        />
        <OptionalLinkFields
          addLabel="Add primary button"
          defaultLabel="Get a quote"
          label="Primary button"
          link={primaryCta}
          onChange={(value) => update({ primaryCta: value })}
          pages={pages}
        />
        <OptionalLinkFields
          addLabel="Add secondary button"
          defaultLabel="Learn more"
          label="Secondary button"
          link={secondaryCta}
          onChange={(value) => update({ secondaryCta: value })}
          pages={pages}
        />
        <MediaUrlField
          label="Image URL"
          mediaOptions={mediaOptions}
          value={text(image.src)}
          onChange={(value) =>
            update({
              image: value.trim()
                ? { ...image, src: value.trim(), alt: text(image.alt) }
                : undefined,
            })
          }
        />
        <TextField
          label="Image alt"
          value={text(image.alt)}
          onChange={(value) =>
            update({
              image: text(image.src)
                ? { ...image, src: text(image.src), alt: value }
                : undefined,
            })
          }
        />
      </div>
    );
  }

  if (block.blockType === "richText") {
    return (
      <TextAreaField
        label="HTML content"
        rows={7}
        value={text(data.html)}
        onChange={(value) => update({ html: value })}
      />
    );
  }

  if (block.blockType === "services") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        {items.map((item, index) => (
          <div
            className="mini-group"
            key={`${block.id}-service-${text(item.title)}-${text(
              item.description,
            )}`}
          >
            <div className="mini-group-head">
              <strong>Service {index + 1}</strong>
              <button
                disabled={items.length <= 1}
                onClick={() =>
                  update({
                    items: items.filter((_, i) => i !== index),
                  })
                }
                type="button"
              >
                Remove
              </button>
            </div>
            <TextField
              label={`Service ${index + 1}`}
              value={text(item.title)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, title: value } : next,
                  ),
                })
              }
            />
            <TextAreaField
              label="Description"
              value={text(item.description)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, description: value } : next,
                  ),
                })
              }
            />
            <PageUrlField
              label="Link"
              pages={pages}
              value={text(item.href)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, href: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </div>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  title: "New service",
                  description: "Describe this service.",
                },
              ],
            })
          }
          type="button"
        >
          Add service
        </button>
      </div>
    );
  }

  if (block.blockType === "stats") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => (
          <div
            className="mini-group"
            key={`${block.id}-stat-${text(item.value)}-${text(item.label)}`}
          >
            <div className="mini-group-head">
              <strong>Stat {index + 1}</strong>
              <button
                disabled={items.length <= 1}
                onClick={() =>
                  update({
                    items: items.filter((_, i) => i !== index),
                  })
                }
                type="button"
              >
                Remove
              </button>
            </div>
            <TextField
              label="Value"
              value={text(item.value)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Label"
              value={text(item.label)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, label: value } : next,
                  ),
                })
              }
            />
            <TextAreaField
              label="Description"
              value={text(item.description)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, description: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </div>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  value: "100+",
                  label: "New metric",
                  description: "Describe why this number matters.",
                },
              ],
            })
          }
          type="button"
        >
          Add stat
        </button>
      </div>
    );
  }

  if (block.blockType === "testimonials") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => (
          <div
            className="mini-group"
            key={`${block.id}-testimonial-${text(item.name)}-${text(
              item.quote,
            )}`}
          >
            <div className="mini-group-head">
              <strong>Testimonial {index + 1}</strong>
              <button
                disabled={items.length <= 1}
                onClick={() =>
                  update({
                    items: items.filter((_, i) => i !== index),
                  })
                }
                type="button"
              >
                Remove
              </button>
            </div>
            <TextAreaField
              label="Quote"
              value={text(item.quote)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, quote: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Name"
              value={text(item.name)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, name: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Role or context"
              value={text(item.role)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, role: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </div>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  quote: "Add a short customer quote here.",
                  name: "Customer name",
                  role: "Local customer",
                },
              ],
            })
          }
          type="button"
        >
          Add testimonial
        </button>
      </div>
    );
  }

  if (block.blockType === "newsList") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => (
          <ContentItemCard
            blockType={block.blockType}
            canDuplicate={items.length < 6}
            canRemove={items.length > 1}
            index={index}
            item={item}
            key={`${block.id}-news-${text(item.title)}-${text(item.excerpt)}`}
            onDuplicate={() => {
              const nextItems = [...items];
              nextItems.splice(index + 1, 0, cloneBlockData(item));
              update({ items: nextItems });
            }}
            onRemove={() =>
              update({
                items: items.filter((_, i) => i !== index),
              })
            }
          >
            <TextField
              label="Title"
              value={text(item.title)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, title: value } : next,
                  ),
                })
              }
            />
            <TextAreaField
              label="Excerpt"
              value={text(item.excerpt)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, excerpt: value } : next,
                  ),
                })
              }
            />
            <PageUrlField
              label="Link"
              pages={pages}
              value={text(item.href)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, href: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <MediaUrlField
              label="Featured image"
              mediaOptions={mediaOptions}
              value={text(item.image)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, image: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextField
              label="Image alt"
              value={text(item.imageAlt)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, imageAlt: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextField
              label="Date"
              value={text(item.date)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, date: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextField
              label="Author"
              value={text(item.author)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, author: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextField
              label="Category"
              value={text(item.tag)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, tag: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </ContentItemCard>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  title: "New update",
                  excerpt: "Add a short summary for this update.",
                  tag: "News",
                  href: "/news/new-update",
                },
              ],
            })
          }
          type="button"
        >
          Add update
        </button>
      </div>
    );
  }

  if (block.blockType === "gallery") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => (
          <ContentItemCard
            blockType={block.blockType}
            canDuplicate={items.length < 12}
            canRemove={items.length > 1}
            index={index}
            item={item}
            key={`${block.id}-gallery-${text(item.src)}-${text(item.title)}`}
            onDuplicate={() => {
              const nextItems = [...items];
              nextItems.splice(index + 1, 0, cloneBlockData(item));
              update({ items: nextItems });
            }}
            onRemove={() =>
              update({
                items: items.filter((_, i) => i !== index),
              })
            }
          >
            <MediaUrlField
              label="Image URL"
              mediaOptions={mediaOptions}
              value={text(item.src)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, src: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Alt text"
              value={text(item.alt)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, alt: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Title"
              value={text(item.title)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, title: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <PageUrlField
              label="Link"
              pages={pages}
              value={text(item.href)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, href: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextAreaField
              label="Caption"
              value={text(item.caption)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, caption: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </ContentItemCard>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  src: "/images/project.webp",
                  alt: "Project image",
                  title: "New image",
                  caption: "Describe what this image shows.",
                },
              ],
            })
          }
          type="button"
        >
          Add image
        </button>
      </div>
    );
  }

  if (block.blockType === "pricing") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => {
          const features = Array.isArray(item.features)
            ? item.features.map(text).filter(Boolean)
            : [];
          return (
            <div
              className="mini-group"
              key={`${block.id}-pricing-${text(item.name)}-${text(item.price)}`}
            >
              <div className="mini-group-head">
                <strong>Plan {index + 1}</strong>
                <button
                  disabled={items.length <= 1}
                  onClick={() =>
                    update({
                      items: items.filter((_, i) => i !== index),
                    })
                  }
                  type="button"
                >
                  Remove
                </button>
              </div>
              <TextField
                label="Name"
                value={text(item.name)}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index ? { ...next, name: value } : next,
                    ),
                  })
                }
              />
              <TextField
                label="Price"
                value={text(item.price)}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index ? { ...next, price: value } : next,
                    ),
                  })
                }
              />
              <TextField
                label="Cadence"
                value={text(item.cadence)}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index
                        ? { ...next, cadence: optionalNonEmpty(value) }
                        : next,
                    ),
                  })
                }
              />
              <TextAreaField
                label="Description"
                value={text(item.description)}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index
                        ? { ...next, description: optionalNonEmpty(value) }
                        : next,
                    ),
                  })
                }
              />
              <TextAreaField
                label="Features"
                value={features.join("\n")}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index
                        ? {
                            ...next,
                            features: value
                              .split(/\n+/)
                              .map((feature) => feature.trim())
                              .filter(Boolean),
                          }
                        : next,
                    ),
                  })
                }
              />
              <TextField
                label="Button label"
                value={text(item.ctaLabel)}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index
                        ? { ...next, ctaLabel: optionalNonEmpty(value) }
                        : next,
                    ),
                  })
                }
              />
              <PageUrlField
                label="Button link"
                pages={pages}
                value={text(item.ctaHref)}
                onChange={(value) =>
                  update({
                    items: items.map((next, i) =>
                      i === index
                        ? { ...next, ctaHref: optionalNonEmpty(value) }
                        : next,
                    ),
                  })
                }
              />
            </div>
          );
        })}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  name: "New plan",
                  price: "Custom quote",
                  description: "Describe who this option is for.",
                  features: ["Feature one", "Feature two"],
                  ctaLabel: "Ask for a quote",
                  ctaHref: "#contact",
                },
              ],
            })
          }
          type="button"
        >
          Add plan
        </button>
      </div>
    );
  }

  if (block.blockType === "team") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => (
          <div
            className="mini-group"
            key={`${block.id}-team-${text(item.name)}-${text(item.role)}`}
          >
            <div className="mini-group-head">
              <strong>Person {index + 1}</strong>
              <button
                disabled={items.length <= 1}
                onClick={() =>
                  update({
                    items: items.filter((_, i) => i !== index),
                  })
                }
                type="button"
              >
                Remove
              </button>
            </div>
            <TextField
              label="Name"
              value={text(item.name)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, name: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Role"
              value={text(item.role)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, role: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextAreaField
              label="Bio"
              value={text(item.bio)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, bio: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <MediaUrlField
              label="Image URL"
              mediaOptions={mediaOptions}
              value={text(item.image)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, image: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
            <TextField
              label="Image alt"
              value={text(item.imageAlt)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, imageAlt: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </div>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  name: "New team member",
                  role: "Role or specialty",
                  bio: "Add a short introduction.",
                },
              ],
            })
          }
          type="button"
        >
          Add team member
        </button>
      </div>
    );
  }

  if (block.blockType === "openingHours") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        {items.map((item, index) => (
          <div
            className="mini-group"
            key={`${block.id}-hours-${text(item.day)}-${text(item.hours)}`}
          >
            <div className="mini-group-head">
              <strong>Hours {index + 1}</strong>
              <button
                disabled={items.length <= 1}
                onClick={() =>
                  update({
                    items: items.filter((_, i) => i !== index),
                  })
                }
                type="button"
              >
                Remove
              </button>
            </div>
            <TextField
              label="Day"
              value={text(item.day)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, day: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Hours"
              value={text(item.hours)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, hours: value } : next,
                  ),
                })
              }
            />
            <TextField
              label="Note"
              value={text(item.note)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index
                      ? { ...next, note: optionalNonEmpty(value) }
                      : next,
                  ),
                })
              }
            />
          </div>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  day: "New day",
                  hours: "9:00 - 17:00",
                },
              ],
            })
          }
          type="button"
        >
          Add hours row
        </button>
      </div>
    );
  }

  if (block.blockType === "contactForm") {
    return (
      <div className="field-grid">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextField
          label="Button"
          value={editablePathText(block, "submitLabel")}
          onChange={(value) => update({ submitLabel: value })}
        />
        <TextAreaField
          label="Success message"
          value={editablePathText(block, "successMessage")}
          onChange={(value) => update({ successMessage: value })}
        />
        <TextAreaField
          label="Intro"
          value={text(data.intro)}
          onChange={(value) => update({ intro: value })}
        />
        <TextField
          label="Name label"
          value={editablePathText(block, "nameLabel")}
          onChange={(value) => update({ nameLabel: value })}
        />
        <TextField
          label="Name placeholder"
          value={text(data.namePlaceholder)}
          onChange={(value) => update({ namePlaceholder: value })}
        />
        <TextField
          label="Email label"
          value={editablePathText(block, "emailLabel")}
          onChange={(value) => update({ emailLabel: value })}
        />
        <TextField
          label="Email placeholder"
          value={text(data.emailPlaceholder)}
          onChange={(value) => update({ emailPlaceholder: value })}
        />
        <TextField
          label="Phone label"
          value={editablePathText(block, "phoneLabel")}
          onChange={(value) => update({ phoneLabel: value })}
        />
        <TextField
          label="Phone placeholder"
          value={text(data.phonePlaceholder)}
          onChange={(value) => update({ phonePlaceholder: value })}
        />
        <TextField
          label="Message label"
          value={editablePathText(block, "messageLabel")}
          onChange={(value) => update({ messageLabel: value })}
        />
        <TextField
          label="Message placeholder"
          value={text(data.messagePlaceholder)}
          onChange={(value) => update({ messagePlaceholder: value })}
        />
        <label className="checkbox-field">
          <input
            checked={bool(data.askPhone)}
            type="checkbox"
            onChange={(event) => update({ askPhone: event.target.checked })}
          />
          Ask for phone number
        </label>
      </div>
    );
  }

  if (block.blockType === "faqAccordion") {
    const items = arrayOfObjects(data.items);
    return (
      <div className="stack">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        {items.map((item, index) => (
          <div
            className="mini-group"
            key={`${block.id}-faq-${text(item.question)}-${text(item.answer)}`}
          >
            <div className="mini-group-head">
              <strong>FAQ {index + 1}</strong>
              <button
                disabled={items.length <= 1}
                onClick={() =>
                  update({
                    items: items.filter((_, i) => i !== index),
                  })
                }
                type="button"
              >
                Remove
              </button>
            </div>
            <TextField
              label={`Question ${index + 1}`}
              value={text(item.question)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, question: value } : next,
                  ),
                })
              }
            />
            <TextAreaField
              label="Answer"
              value={text(item.answer)}
              onChange={(value) =>
                update({
                  items: items.map((next, i) =>
                    i === index ? { ...next, answer: value } : next,
                  ),
                })
              }
            />
          </div>
        ))}
        <button
          className="inline-add"
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  question: "New question",
                  answer: "Add the answer here.",
                },
              ],
            })
          }
          type="button"
        >
          Add FAQ
        </button>
      </div>
    );
  }

  if (block.blockType === "ctaBanner") {
    const cta = object(data.cta);
    return (
      <div className="field-grid">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextField
          label="Button label"
          value={text(cta.label)}
          onChange={(value) => update({ cta: { ...cta, label: value } })}
        />
        <TextAreaField
          label="Text"
          value={text(data.text)}
          onChange={(value) => update({ text: value })}
        />
        <PageUrlField
          label="Button link"
          pages={pages}
          value={text(cta.href)}
          onChange={(value) => update({ cta: { ...cta, href: value } })}
        />
      </div>
    );
  }

  if (block.blockType === "footer") {
    const links = arrayOfObjects(data.links);
    const legalLinks = arrayOfObjects(data.legalLinks);
    return (
      <div className="stack">
        <div className="field-grid">
          <TextField
            label="Business name"
            value={text(data.businessName)}
            onChange={(value) => update({ businessName: value })}
          />
          <TextField
            label="Tagline"
            value={text(data.tagline)}
            onChange={(value) => update({ tagline: value })}
          />
          <TextField
            label="Phone"
            value={text(data.phone)}
            onChange={(value) => update({ phone: value })}
          />
          <TextField
            label="Email"
            value={text(data.email)}
            onChange={(value) => update({ email: value })}
          />
          <TextField
            label="Address"
            value={text(data.address)}
            onChange={(value) => update({ address: value })}
          />
        </div>
        <LinkListEditor
          hostname={hostname}
          itemLabel="Footer link"
          label="Footer links"
          links={links}
          onChange={(value) => update({ links: value })}
          pages={pages}
        />
        <LinkListEditor
          hostname={hostname}
          itemLabel="Legal link"
          label="Legal links"
          links={legalLinks}
          maxLinks={6}
          onChange={(value) => update({ legalLinks: value })}
          pages={pages}
        />
      </div>
    );
  }

  if (block.blockType === "textImage") {
    const image = object(data.image);
    const cta = object(data.cta);
    return (
      <div className="field-grid">
        <TextField
          label="Heading"
          value={text(data.heading)}
          onChange={(value) => update({ heading: value })}
        />
        <TextAreaField
          label="Body"
          value={text(data.body)}
          onChange={(value) => update({ body: value })}
        />
        <MediaUrlField
          label="Image URL"
          mediaOptions={mediaOptions}
          value={text(image.src)}
          onChange={(value) => update({ image: { ...image, src: value } })}
        />
        <TextField
          label="Image alt"
          value={text(image.alt)}
          onChange={(value) => update({ image: { ...image, alt: value } })}
        />
        <OptionalLinkFields
          addLabel="Add button"
          defaultLabel="Learn more"
          label="Button"
          link={cta}
          onChange={(value) => update({ cta: value })}
          pages={pages}
        />
      </div>
    );
  }

  return (
    <TextAreaField
      label="Block JSON"
      rows={8}
      value={JSON.stringify(data, null, 2)}
      onChange={(value) => {
        try {
          onChange(JSON.parse(value) as Record<string, unknown>);
        } catch {
          /* keep editing until JSON is valid */
        }
      }}
    />
  );
}

function TextField({
  defaultValue,
  label,
  name,
  onChange,
  value,
}: {
  defaultValue?: string;
  label: string;
  name?: string;
  onChange?: (value: string) => void;
  value?: string;
}) {
  return (
    <label>
      {label}
      <input
        defaultValue={value === undefined ? defaultValue : undefined}
        name={name}
        value={value}
        onChange={
          onChange ? (event) => onChange(event.target.value) : undefined
        }
      />
    </label>
  );
}

function TextAreaField({
  defaultValue,
  label,
  name,
  onChange,
  rows = 3,
  value,
}: {
  defaultValue?: string;
  label: string;
  name?: string;
  onChange?: (value: string) => void;
  rows?: number;
  value?: string;
}) {
  return (
    <label className="span-2">
      {label}
      <textarea
        defaultValue={value === undefined ? defaultValue : undefined}
        name={name}
        rows={rows}
        value={value}
        onChange={
          onChange ? (event) => onChange(event.target.value) : undefined
        }
      />
    </label>
  );
}
